import { apiFactory, headers } from '~/src/dash/api/api-factory';
import {
  EAllowStatus,
  IPublisher,
  ResourceActive,
  retryFunc,
  SegmentTargeting,
} from '~/src/dash/api/console-api';
import {
  parseArray,
  parseNumber,
  parseString,
  transformModel,
} from '~/src/dash/api/resource-transforms';
import {
  addDomainAndTokenToUrl,
  createUrl,
  IHttpResponse,
  restDelete,
  restGet,
  restPatch,
  restPost,
} from '~/src/dash/api/rest';
import { Auth } from '~/src/dash/api/transform-auth';
import { EBool } from '~/src/dash/api/types/bool';
import { generateComscoreContextualSegmentList } from '~/src/dash/components/comscore/contextual/utils';
import { IMenuList } from '~/src/dash/types/menuList';
import { getICTagFromPartnerTag } from '~/src/dash/utils/iris-map';

// used when a resource is only needed for its very basic info so we don't have loops in transform functions
export interface IGeneralResource {
  ID: number;
  Name: string;
  AdserverOrderID: string; // need this for line items since the route has both ids
}

export const transformGeneralResource = transformModel<IGeneralResource>({
  ID: parseNumber(['ID']),
  Name: parseString(['Name']),
  AdserverOrderID: parseString(['AdserverOrderID']),
});

export type ThirdParty = number;
export const ThirdPartyUnknown: ThirdParty = 0;
export const ThirdPartyIAS: ThirdParty = 1;
export const ThirdPartyIris: ThirdParty = 2;
export const ThirdPartyComscore: ThirdParty = 3;
export const ThirdPartyLiveContextualScore: ThirdParty = 4;
export const ThirdPartyPersonality: ThirdParty = 5;
export const ThirdPartyLiveContextualSuitability: ThirdParty = 6;

export type IrisPartner = number;
export const IrisPartnerUnknown: IrisPartner = 0;
export const IrisPartnerGumGum: IrisPartner = 1;
export const IrisPartnerKerv: IrisPartner = 2;
export const IrisPartnerSilverBullet: IrisPartner = 3;
export const IrisPartnerPreciseTV: IrisPartner = 4;
export const IrisPartnerHotSpex: IrisPartner = 5;
export const IrisPartnerSilverpush: IrisPartner = 6;
export const IrisPartnerGrapeshot: IrisPartner = 7;
export const IrisPartnerIlluma: IrisPartner = 8;
export const IrisPartnercomScore: IrisPartner = 9;
export const IrisPartnerZefr: IrisPartner = 10;
export const IrisPartnerAdvancedContextual: IrisPartner = 11;

export const NO_MAX_CONTENT_LENGTH = 210; // represents no maxmimum (number is 210 since slider is to scale)
export const NO_MIN_CONTENT_LENGTH = 0; // represents no minimum

interface IThirdPartyMenuItem {
  key: number;
  text: string;
}

export interface IContentCollectionMenuItem {
  key: number;
  text: string;
}

export const generateThirdPartyMenu = (
  isSuperUser: boolean,
  publisher: IPublisher,
): IThirdPartyMenuItem[] => {
  const thirdPartyMenu = [
    { key: ThirdPartyIAS, text: 'IAS' },
    { key: ThirdPartyIris, text: 'IRIS.TV' },
  ] as IThirdPartyMenuItem[];

  if (isSuperUser || publisher.ComscoreContextualSegmentsEnabled === EBool.EBool_TRUE) {
    thirdPartyMenu.push({ key: ThirdPartyComscore, text: 'COMSCORE' });
  }
  thirdPartyMenu.push({ key: ThirdPartyLiveContextualScore, text: 'Live Contextual Score' });
  thirdPartyMenu.push({
    key: ThirdPartyLiveContextualSuitability,
    text: 'Live Context Suitability',
  });
  thirdPartyMenu.push({ key: ThirdPartyPersonality, text: 'Personality' });
  return thirdPartyMenu;
};

export const generateIrisPartnersMenu = (): IThirdPartyMenuItem[] => {
  const thirdPartyMenu = [
    // { key: IrisPartnerAdvancedContextual, text: 'Advanced Contextual' },
    // { key: IrisPartnercomScore, text: 'comScore' },
    // { key: IrisPartnerGrapeshot, text: 'Grapeshot' },
    { key: IrisPartnerGumGum, text: 'GumGum' },
    { key: IrisPartnerHotSpex, text: 'Hot Spex' },
    { key: IrisPartnerIlluma, text: 'Illuma' },
    { key: IrisPartnerKerv, text: 'Kerv' },
    { key: IrisPartnerPreciseTV, text: 'Precise TV' },
    { key: IrisPartnerSilverBullet, text: 'Silverbullet 4D' },
    { key: IrisPartnerSilverpush, text: 'Silverpush' },
    // { key: IrisPartnerZefr, text: 'Zefr' },
  ] as IThirdPartyMenuItem[];

  return thirdPartyMenu;
};

export interface IContextualSegmentLabel {
  ID: number;
  CreatedAt?: string;
  UpdatedAt?: string;
  DeletedAt?: string;

  Name: string;
  PublisherID: number;
  ContextualSegments: IContextualSegment[];
}

export interface IPersonalities {
  ContextualPersonalities: string[];
  success: boolean;
}

export const transformContextualSegmentLabel = transformModel<IContextualSegmentLabel>({
  ID: parseNumber(['ID']),
  CreatedAt: 'CreatedAt',
  UpdatedAt: 'UpdatedAt',
  DeletedAt: 'DeletedAt',

  Name: parseString(['Name']),
  Publisherid: parseNumber(['PublisherID']),
});

export interface IThirdPartyContextualSegment {
  ID: number;
  CreatedAt?: string;
  UpdatedAt?: string;
  DeletedAt?: string;

  ContextualSegmentID: number;
  TargetingSegment: string;
  TargetingSegmentIsAllowed: EAllowStatus;
  ThirdParty: ThirdParty;
  IrisPartner: IrisPartner;
}

export const transformThirdPartyContextualSegment = transformModel<IThirdPartyContextualSegment>({
  ID: parseNumber(['ID']),
  CreatedAt: 'CreatedAt',
  UpdatedAt: 'UpdatedAt',
  DeletedAt: 'DeletedAt',

  ContextualSegmentID: parseNumber(['ContextualSegmentID']),
  TargetingSegment: parseString(['TargetingSegment']),
  TargetingSegmentIsAllowed: parseNumber(['TargetingSegmentIsAllowed']),
  ThirdParty: parseNumber(['ThirdParty']),
  IrisPartner: parseNumber(['IrisPartner']),
});

export interface IContentCollectionData {
  ID: number;
  Custom10Targeting: number;
  Custom10TargetingV2: string;
  Custom10TargetingAlias: string;
  ContentCollectionID: number;
}

export const transformContentCollectionData = transformModel<IContentCollectionData>({
  ID: parseNumber(['ID', 'id']),
  Custom10Targeting: parseNumber(['Custom10Targeting']),
  Custom10TargetingV2: parseString(['Custom10TargetingV2']),
  Custom10TargetingAlias: parseString(['Custom10TargetingAlias']),
  ContentCollectionID: parseNumber(['ContentCollectionID']),
});

export const toAPIContentCollectionData = transformModel<IContentCollection>({
  ID: parseString(['ID', 'id']),
  Custom10Targeting: parseNumber(['Custom10Targeting', 'custom10_targeting']),
  Custom10TargetingV2: parseString(['Custom10TargetingV2', 'custom10_targeting_v2']),
  Custom10TargetingAlias: parseString(['Custom10TargetingAlias', 'custom10_targeting_alias']),
  ContentCollectionID: parseNumber(['ContentCollectionID', 'content_collection_id']),
});

export interface IContentCollection {
  ID: number;
  Name: string;
  PublisherID: number;
  ContentCollectionData: IContentCollectionData[];
}

export const toAPIContentCollection = transformModel<IContentCollection>({
  ID: parseString(['ID', 'id']),
  Name: parseString(['Name', 'name']),
  PublisherID: parseNumber(['PublisherID', 'publisher_id']),
});

export const transformContentCollection = transformModel<IContentCollection>({
  ID: parseNumber(['ID', 'id']),
  Name: parseString(['Name']),
  PublisherID: parseNumber(['PublisherID']),
  ContentCollectionData: parseArray(['ContentCollectionData'], transformContentCollectionData),
});

export interface IContextualSegment {
  ID: number;
  CreatedAt?: string;
  UpdatedAt?: string;
  DeletedAt?: string;

  PublisherID: number;
  Name: string;
  Active: ResourceActive;
  TargetingContentRating: string;
  TargetingContentRatingIsAllowed: EAllowStatus;
  TargetingContentGenre: string;
  TargetingContentGenreIsAllowed: EAllowStatus;
  MaxContentLength: number;
  MinContentLength: number;
  ThirdPartyTargetingType: SegmentTargeting;

  OnlyTargetCertifiedFullyOnScreen: EBool;

  // these preloads are needed only for the list-table, so we only need the id and name
  Deals: IGeneralResource[];
  Headerbidders: IGeneralResource[];
  AdserverCampaigns: IGeneralResource[];

  ContextualSegmentLabels: IContextualSegmentLabel[];
  ThirdPartyContextualSegments: IThirdPartyContextualSegment[];
  ContentCollection: IContentCollection;
  ContentCollectionID: number;
}

export const transformContextualSegment = transformModel<IContextualSegment>({
  ID: parseNumber(['ID']),
  CreatedAt: 'CreatedAt',
  UpdatedAt: 'UpdatedAt',
  DeletedAt: 'DeletedAt',

  PublisherID: parseNumber(['PublisherID']),
  Name: parseString(['Name']),
  Active: parseNumber(['Active']),
  TargetingContentRating: parseString(['TargetingContentRating']),
  TargetingContentRatingIsAllowed: parseNumber(['TargetingContentRatingIsAllowed']),
  TargetingContentGenre: parseString(['TargetingContentGenre']),
  TargetingContentGenreIsAllowed: parseNumber(['TargetingContentGenreIsAllowed']),
  MaxContentLength: parseNumber(['MaxContentLength']),
  MinContentLength: parseNumber(['MinContentLength']),
  ThirdPartyTargetingType: parseNumber(['ThirdPartyTargetingType']),
  OnlyTargetCertifiedFullyOnScreen: parseNumber(['OnlyTargetCertifiedFullyOnScreen']),

  // these preloads are needed only for the list-table, so we only need the id and name
  Deals: parseArray(['Deals'], transformGeneralResource),
  Headerbidders: parseArray(['Headerbidders'], transformGeneralResource),
  AdserverCampaigns: parseArray(['AdserverCampaigns'], transformGeneralResource),

  ContextualSegmentLabels: parseArray(['ContextualSegmentLabels'], transformContextualSegmentLabel),
  ThirdPartyContextualSegments: parseArray(
    ['ThirdPartyContextualSegments'],
    transformThirdPartyContextualSegment,
  ),
  ContentCollection: parseArray(['ContentCollection'], transformContentCollection),
  ContentCollectionID: parseNumber(['ContentCollectionID']),
});

export async function getContextualSegmentsByPublisherID(
  publisherID: string,
): Promise<[IContextualSegment[]?, Error?]> {
  const reqUrl = addDomainAndTokenToUrl(`/publishers/${publisherID}/contextual_segments`);
  try {
    const resp = await restGet<any>(reqUrl, {});
    const ContextualSegments = [] as IContextualSegment[];
    for (const ContextualSegment of resp.parsedBody) {
      const parsedContextualSegment = transformContextualSegment(ContextualSegment);
      ContextualSegments.push(parsedContextualSegment);
    }
    return [ContextualSegments, undefined];
  } catch (e) {
    console.error(e);
    return [undefined, e];
  }
}

export async function getContextualSegmentByID(
  publisherID: string,
  contextualSegmentID: string,
): Promise<[IContextualSegment?, Error?]> {
  const reqUrl = addDomainAndTokenToUrl(
    `/publishers/${publisherID}/contextual_segments/${contextualSegmentID}`,
  );

  try {
    const resp = (await restGet<any>(reqUrl, {})).parsedBody;
    return [transformContextualSegment(resp), undefined];
  } catch (e) {
    console.error(e);
    throw e;
  }
}

export async function updateContextualSegment(
  contextualSegment: IContextualSegment,
  publisherID: string,
): Promise<[IContextualSegment?, Error?]> {
  const reqUrl = addDomainAndTokenToUrl(
    `/publishers/${publisherID}/contextual_segments/${contextualSegment.ID}`,
  );
  try {
    const resp = (await restPatch<IContextualSegment>(reqUrl, contextualSegment)).parsedBody;
    return [transformContextualSegment(resp), undefined];
  } catch (e) {
    console.error(e);
    return [undefined, e];
  }
}

export async function createContextualSegment(
  contextualSegment: IContextualSegment,
  publisherID: string,
): Promise<[IContextualSegment?, Error?]> {
  const reqUrl = addDomainAndTokenToUrl(`/publishers/${publisherID}/contextual_segments`);
  try {
    const resp = (await restPost<IContextualSegment>(reqUrl, contextualSegment)).parsedBody;
    return [transformContextualSegment(resp), undefined];
  } catch (e) {
    console.error(e);
    return [undefined, e];
  }
}

export async function deleteContextualSegment(
  contextualSegmentID: string,
  publisherID: string,
): Promise<IHttpResponse<IContextualSegment>> {
  const reqUrl = addDomainAndTokenToUrl(
    `/publishers/${publisherID}/contextual_segments/${contextualSegmentID}`,
  );
  return await restDelete<IContextualSegment>(reqUrl, undefined);
}

export async function getContextualSegmentLabelsByPublisherID(
  publisherID: string,
): Promise<[IContextualSegmentLabel[]?, Error?]> {
  const reqUrl = addDomainAndTokenToUrl(`/publishers/${publisherID}/contextual_segment_labels`);
  try {
    const resp = await restGet<any>(reqUrl, {});
    const ContextualSegmentLabels = [] as IContextualSegmentLabel[];
    for (const ContextualSegmentLabel of resp.parsedBody) {
      const parsedContextualSegmentLabel = transformContextualSegmentLabel(ContextualSegmentLabel);
      ContextualSegmentLabels.push(parsedContextualSegmentLabel);
    }
    return [ContextualSegmentLabels, undefined];
  } catch (e) {
    console.error(e);
    return [undefined, e];
  }
}

export async function createContextualSegmentLabel(
  contextualSegmentLabel: IContextualSegmentLabel,
  publisherID: string,
): Promise<[IContextualSegmentLabel?, Error?]> {
  const reqUrl = addDomainAndTokenToUrl(`/publishers/${publisherID}/contextual_segment_labels`);
  try {
    const resp = (await restPost<IContextualSegmentLabel>(reqUrl, contextualSegmentLabel))
      .parsedBody;
    return [transformContextualSegmentLabel(resp), undefined];
  } catch (e) {
    console.error(e);
    return [undefined, e];
  }
}

export const generateContextualSegmentLabelsList = (
  allLabels: IContextualSegmentLabel[],
  currentLabels: IContextualSegmentLabel[],
): IMenuList => {
  const contextualSegmentLabelsList = {} as IMenuList;
  const currentLabelIDs = currentLabels.map((label) => label.ID);
  for (const label of allLabels) {
    contextualSegmentLabelsList[label.ID] = {
      title: label.Name,
      selected: currentLabelIDs.includes(label.ID),
      ID: label.ID.toString(),
      object: label, // list uses whole object since we need it to create the label
    };
  }
  return contextualSegmentLabelsList;
};

export const generateLiveContextualSuitabilitySegmentList = (
  currentValues: string[],
): IMenuList => {
  const options = [{ id: 'Weather', title: 'Weather', abbrevTitle: 'Weather' }];

  const csList = {} as IMenuList;
  for (const option of options) {
    csList[option.title] = {
      title: option.title,
      abbrevTitle: option.abbrevTitle,
      selected: currentValues.includes(option.id),
      ID: option.id,
    };
  }
  return csList;
};

export const generateThirdPartyContextualSegmentList = (
  thirdParty: ThirdParty,
  irisPartner: IrisPartner | undefined,
  currentValues: string[],
): IMenuList => {
  if (thirdParty === ThirdPartyIAS || thirdParty === ThirdPartyLiveContextualScore) {
    return generateIASContextualSegmentList(currentValues);
  }
  if (thirdParty === ThirdPartyLiveContextualSuitability) {
    return generateLiveContextualSuitabilitySegmentList(currentValues);
  }
  if (thirdParty === ThirdPartyComscore) {
    return generateComscoreContextualSegmentList(currentValues);
  }

  switch (irisPartner) {
    case IrisPartnerGumGum:
      return generateGumGumContextualSegmentList(currentValues);
    case IrisPartnerKerv:
      return generateKervContextualSegmentList(currentValues);
    case IrisPartnerSilverBullet:
      return generateSilverBulletContextualSegmentList(currentValues);
    case IrisPartnerPreciseTV:
      return generatePreciseTVContextualSegmentList(currentValues);
    case IrisPartnerHotSpex:
      return generateHotSpexContextualSegmentList(currentValues);
    case IrisPartnerSilverpush:
      return generateSilverpushContextualSegmentList(currentValues);
    case IrisPartnerGrapeshot:
      return generateGrapeshotContextualSegmentList(currentValues);
    case IrisPartnerIlluma:
      return generateIllumaContextualSegmentList(currentValues);
    case IrisPartnercomScore:
      return generateComScoreContextualSegmentList(currentValues);
    case IrisPartnerZefr:
      return generateZefrContextualSegmentList(currentValues);
    case IrisPartnerAdvancedContextual:
      return generateAdvancedContextualContextualSegmentList(currentValues);
    default:
      return {} as IMenuList;
  }
};

// Content Collection CRUD functions

export async function createContentCollection(
  contentCollection: IContentCollection,
): Promise<[IContentCollection?, Error?]> {
  const reqUrl = createUrl('/content_collection', contentCollection.PublisherID.toString());
  const parsedCG = toAPIContentCollection(contentCollection);
  // delete ID field since contentCollection is new
  // @ts-ignore
  delete parsedCG.ID;
  try {
    const ret = (await restPost<IContentCollection>(reqUrl, parsedCG)).parsedBody;
    return [transformContentCollection(ret), undefined];
  } catch (e) {
    console.error(e);
    return [undefined, e];
  }
}

export const getContentCollections = apiFactory({
  method: 'GET',
  headers,
  body: {
    core_section: 'settings',
    core_action: 'content_collections',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: Auth.appendToken,
  transformResponse: (err: any, json: any) => {
    if (err) {
      return new Error((json && json.msg) || 'request failed');
    }
    const contentCollection = (json || []).map(transformContentCollection);
    return {
      contentCollection,
    };
  },
});

export async function updateContentCollection(
  contentCollection: IContentCollection,
): Promise<[IContentCollection?, Error?]> {
  const reqUrl = createUrl('/content_collection', contentCollection.PublisherID.toString());
  const parsedCG = toAPIContentCollection(contentCollection);

  try {
    const ret = (await restPatch<IContentCollection>(reqUrl, parsedCG)).parsedBody;
    return [transformContentCollection(ret), undefined];
  } catch (e) {
    console.error(e);
    return [undefined, e];
  }
}

export const deleteContentCollection = apiFactory({
  method: 'DELETE',
  headers,
  body: {
    core_section: 'settings',
    core_action: 'content_collection',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: Auth.appendToken,
});

// Content Collection Data CRUD functions

export async function createContentCollectionData(
  contentCollectionData: IContentCollectionData,
  publisherID: string,
): Promise<[IContentCollectionData?, Error?]> {
  const reqUrl = createUrl('/content_collection_data', publisherID);
  const parsedCG = toAPIContentCollectionData(contentCollectionData);
  // delete ID field since contentCollection is new
  // @ts-ignore
  delete parsedCG.ID;
  try {
    const ret = (await restPost<IContentCollectionData>(reqUrl, parsedCG)).parsedBody;
    return [transformContentCollectionData(ret), undefined];
  } catch (e) {
    console.error(e);
    return [undefined, e];
  }
}

export const getContentCollectionData = apiFactory({
  method: 'GET',
  headers,
  body: {
    core_section: 'settings',
    core_action: 'content_collection_data',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: Auth.appendToken,
  transformResponse: (err: any, json: any) => {
    if (err) {
      return new Error((json && json.msg) || 'request failed');
    }
    const contentCollectionData = (json || []).map(transformContentCollectionData);
    return {
      contentCollectionData,
    };
  },
});

export async function updateContentCollectionData(
  contentCollectionData: IContentCollectionData,
  publisherID: string,
): Promise<[IContentCollectionData?, Error?]> {
  const reqUrl = createUrl('/content_collection_data', publisherID.toString());
  const parsedCG = toAPIContentCollectionData(contentCollectionData);

  try {
    const ret = (await restPatch<IContentCollectionData>(reqUrl, parsedCG)).parsedBody;
    return [transformContentCollectionData(ret), undefined];
  } catch (e) {
    console.error(e);
    return [undefined, e];
  }
}

export const deleteContentCollectionData = apiFactory({
  method: 'DELETE',
  headers,
  body: {
    core_section: 'settings',
    core_action: 'content_collection_data',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: Auth.appendToken,
});

export const generateIASContextualSegmentList = (currentValues: string[]): IMenuList => {
  const options = [
    { title: 'Adult & Explicit Sexual Content', abbrevTitle: 'Adult', id: 'Adult' },
    { title: 'Arms & Ammunition', abbrevTitle: 'Arms', id: 'Arms' },
    { title: 'Crime & Harmful Acts', abbrevTitle: 'Crime', id: 'Crime' },
    { title: 'Death, Injury or Military Conflict', abbrevTitle: 'Death', id: 'DeathOrInjury' },
    { title: 'Online Piracy', abbrevTitle: 'Piracy', id: 'OnlinePiracy' },
    { title: 'Hate Speech & Acts of Aggression', abbrevTitle: 'Hate', id: 'HateSpeech' },
    { title: 'Obscenity & Profanity', abbrevTitle: 'Obscenity', id: 'Obscenity' },
    { title: 'Illegal Drugs / Smoking / Alcohol', abbrevTitle: 'Drugs', id: 'Drugs' },
    { title: 'Spam or Harmful Content', abbrevTitle: 'Spam', id: 'Spam' },
    { title: 'Terrorism', abbrevTitle: 'Terrorism', id: 'Terrorism' },
    {
      title: 'Debated Sensitive Social Issue',
      abbrevTitle: 'Sensitive',
      id: 'SensitiveSocialIssue',
    },
  ];

  const csList = {} as IMenuList;
  for (const option of options) {
    csList[option.title] = {
      title: option.title,
      abbrevTitle: option.abbrevTitle,
      selected: currentValues.includes(option.id),
      ID: option.id,
    };
  }
  return csList;
};

interface IIRISOption {
  id: string;
  title: string;
}

const generateIRISSegmentList = (options: IIRISOption[], currentValues: string[]): IMenuList => {
  const csList = {} as IMenuList;
  for (const option of options) {
    const ICTag = getICTagFromPartnerTag(option.id);
    csList[option.title] = {
      title: option.title,
      abbrevTitle: option.title,
      selected: currentValues.includes(ICTag),
      ID: ICTag,
    };
  }
  return csList;
};

export async function getPersonalityOptions(query: string): Promise<IPersonalities | undefined> {
  let reqUrl = addDomainAndTokenToUrl(`/contextual_personalities`);
  reqUrl += '&search=' + encodeURIComponent(query);
  try {
    const resp = await restGet<IPersonalities>(reqUrl, {});

    return resp.parsedBody;
  } catch (e) {
    console.error(e);
    return undefined;
  }
}

export const generateGumGumContextualSegmentList = (currentValues: string[]): IMenuList => {
  const options: IIRISOption[] = [
    { id: 'gg_iab_1', title: 'Automotive' },
    { id: 'gg_iab_2', title: 'Auto Body Styles' },
    { id: 'gg_iab_3', title: 'Commercial Trucks' },
    { id: 'gg_iab_4', title: 'Sedan' },
    { id: 'gg_iab_5', title: 'Station Wagon' },
    { id: 'gg_iab_6', title: 'SUV' },
    { id: 'gg_iab_7', title: 'Van' },
    { id: 'gg_iab_8', title: 'Convertible' },
    { id: 'gg_iab_9', title: 'Coupe' },
    { id: 'gg_iab_10', title: 'Crossover' },
    { id: 'gg_iab_11', title: 'Hatchback' },
    { id: 'gg_iab_12', title: 'Microcar' },
    { id: 'gg_iab_13', title: 'Minivan' },
    { id: 'gg_iab_14', title: 'Off-Road Vehicles' },
    { id: 'gg_iab_15', title: 'Pickup Trucks' },
    { id: 'gg_iab_16', title: 'Auto Type' },
    { id: 'gg_iab_17', title: 'Budget Cars' },
    { id: 'gg_iab_18', title: 'Certified Pre-Owned Cars' },
    { id: 'gg_iab_19', title: 'Classic Cars' },
    { id: 'gg_iab_20', title: 'Concept Cars' },
    { id: 'gg_iab_21', title: 'Driverless Cars' },
    { id: 'gg_iab_22', title: 'Green Vehicles' },
    { id: 'gg_iab_23', title: 'Luxury Cars' },
    { id: 'gg_iab_24', title: 'Performance Cars' },
    { id: 'gg_iab_25', title: 'Car Culture' },
    { id: 'gg_iab_26', title: 'Dash Cam Videos' },
    { id: 'gg_iab_27', title: 'Motorcycles' },
    { id: 'gg_iab_28', title: 'Road-Side Assistance' },
    { id: 'gg_iab_29', title: 'Scooters' },
    { id: 'gg_iab_30', title: 'Auto Buying and Selling' },
    { id: 'gg_iab_31', title: 'Auto Insurance' },
    { id: 'gg_iab_32', title: 'Auto Parts' },
    { id: 'gg_iab_33', title: 'Auto Recalls' },
    { id: 'gg_iab_34', title: 'Auto Repair' },
    { id: 'gg_iab_35', title: 'Auto Safety' },
    { id: 'gg_iab_36', title: 'Auto Shows' },
    { id: 'gg_iab_37', title: 'Auto Technology' },
    { id: 'gg_iab_38', title: 'Auto Infotainment Technologies' },
    { id: 'gg_iab_39', title: 'Auto Navigation Systems' },
    { id: 'gg_iab_40', title: 'Auto Safety Technologies' },
    { id: 'gg_iab_41', title: 'Auto Rentals' },
    { id: 'gg_iab_42', title: 'Books and Literature' },
    { id: 'gg_iab_43', title: 'Art and Photography Books' },
    { id: 'gg_iab_44', title: 'Biographies' },
    { id: 'gg_iab_45', title: 'Childrens Literature' },
    { id: 'gg_iab_46', title: 'Comics and Graphic Novels' },
    { id: 'gg_iab_47', title: 'Cookbooks' },
    { id: 'gg_iab_48', title: 'Fiction' },
    { id: 'gg_iab_49', title: 'Poetry' },
    { id: 'gg_iab_50', title: 'Travel Books' },
    { id: 'gg_iab_51', title: 'Young Adult Literature' },
    { id: 'gg_iab_52', title: 'Business and Finance' },
    { id: 'gg_iab_53', title: 'Business' },
    { id: 'gg_iab_54', title: 'Business Accounting & Finance' },
    { id: 'gg_iab_55', title: 'Human Resources' },
    { id: 'gg_iab_56', title: 'Large Business' },
    { id: 'gg_iab_57', title: 'Logistics' },
    { id: 'gg_iab_58', title: 'Marketing and Advertising' },
    { id: 'gg_iab_59', title: 'Sales' },
    { id: 'gg_iab_60', title: 'Small and Medium-sized Business' },
    { id: 'gg_iab_61', title: 'Startups' },
    { id: 'gg_iab_62', title: 'Business Administration' },
    { id: 'gg_iab_63', title: 'Business Banking & Finance' },
    { id: 'gg_iab_64', title: 'Angel Investment' },
    { id: 'gg_iab_65', title: 'Bankruptcy' },
    { id: 'gg_iab_66', title: 'Business Loans' },
    { id: 'gg_iab_67', title: 'Debt Factoring & Invoice Discounting' },
    { id: 'gg_iab_68', title: 'Mergers and Acquisitions' },
    { id: 'gg_iab_69', title: 'Private Equity' },
    { id: 'gg_iab_70', title: 'Sale & Lease Back' },
    { id: 'gg_iab_71', title: 'Venture Capital' },
    { id: 'gg_iab_72', title: 'Business I.T.' },
    { id: 'gg_iab_73', title: 'Business Operations' },
    { id: 'gg_iab_74', title: 'Consumer Issues' },
    { id: 'gg_iab_75', title: 'Recalls' },
    { id: 'gg_iab_76', title: 'Executive Leadership & Management' },
    { id: 'gg_iab_77', title: 'Government Business' },
    { id: 'gg_iab_78', title: 'Green Solutions' },
    { id: 'gg_iab_79', title: 'Business Utilities' },
    { id: 'gg_iab_80', title: 'Economy' },
    { id: 'gg_iab_81', title: 'Commodities' },
    { id: 'gg_iab_82', title: 'Currencies' },
    { id: 'gg_iab_83', title: 'Financial Crisis' },
    { id: 'gg_iab_84', title: 'Financial Reform' },
    { id: 'gg_iab_85', title: 'Financial Regulation' },
    { id: 'gg_iab_86', title: 'Gasoline Prices' },
    { id: 'gg_iab_87', title: 'Housing Market' },
    { id: 'gg_iab_88', title: 'Interest Rates' },
    { id: 'gg_iab_89', title: 'Job Market' },
    { id: 'gg_iab_90', title: 'Industries' },
    { id: 'gg_iab_91', title: 'Advertising Industry' },
    { id: 'gg_iab_92', title: 'Education industry' },
    { id: 'gg_iab_93', title: 'Entertainment Industry' },
    { id: 'gg_iab_94', title: 'Environmental Services Industry' },
    { id: 'gg_iab_95', title: 'Financial Industry' },
    { id: 'gg_iab_96', title: 'Food Industry' },
    { id: 'gg_iab_97', title: 'Healthcare Industry' },
    { id: 'gg_iab_98', title: 'Hospitality Industry' },
    { id: 'gg_iab_99', title: 'Information Services Industry' },
    { id: 'gg_iab_100', title: 'Legal Services Industry' },
    { id: 'gg_iab_101', title: 'Logistics and Transportation Industry' },
    { id: 'gg_iab_102', title: 'Agriculture' },
    { id: 'gg_iab_103', title: 'Management Consulting Industry' },
    { id: 'gg_iab_104', title: 'Manufacturing Industry' },
    { id: 'gg_iab_105', title: 'Mechanical and Industrial Engineering Industry' },
    { id: 'gg_iab_106', title: 'Media Industry' },
    { id: 'gg_iab_107', title: 'Metals Industry' },
    { id: 'gg_iab_108', title: 'Non-Profit Organizations' },
    { id: 'gg_iab_109', title: 'Pharmaceutical Industry' },
    { id: 'gg_iab_110', title: 'Power and Energy Industry' },
    { id: 'gg_iab_111', title: 'Publishing Industry' },
    { id: 'gg_iab_112', title: 'Real Estate Industry' },
    { id: 'gg_iab_113', title: 'Apparel Industry' },
    { id: 'gg_iab_114', title: 'Retail Industry' },
    { id: 'gg_iab_115', title: 'Technology Industry' },
    { id: 'gg_iab_116', title: 'Telecommunications Industry' },
    { id: 'gg_iab_117', title: 'Automotive Industry' },
    { id: 'gg_iab_118', title: 'Aviation Industry' },
    { id: 'gg_iab_119', title: 'Biotech and Biomedical Industry' },
    { id: 'gg_iab_120', title: 'Civil Engineering Industry' },
    { id: 'gg_iab_121', title: 'Construction Industry' },
    { id: 'gg_iab_122', title: 'Defense Industry' },
    { id: 'gg_iab_123', title: 'Careers' },
    { id: 'gg_iab_124', title: 'Apprenticeships' },
    { id: 'gg_iab_125', title: 'Career Advice' },
    { id: 'gg_iab_126', title: 'Career Planning' },
    { id: 'gg_iab_127', title: 'Job Search' },
    { id: 'gg_iab_128', title: 'Job Fairs' },
    { id: 'gg_iab_129', title: 'Resume Writing and Advice' },
    { id: 'gg_iab_130', title: 'Remote Working' },
    { id: 'gg_iab_131', title: 'Vocational Training' },
    { id: 'gg_iab_132', title: 'Education' },
    { id: 'gg_iab_133', title: 'Adult Education' },
    { id: 'gg_iab_134', title: 'Private School' },
    { id: 'gg_iab_135', title: 'Secondary Education' },
    { id: 'gg_iab_136', title: 'Special Education' },
    { id: 'gg_iab_137', title: 'College Education' },
    { id: 'gg_iab_138', title: 'College Planning' },
    { id: 'gg_iab_139', title: 'Postgraduate Education' },
    { id: 'gg_iab_140', title: 'Professional School' },
    { id: 'gg_iab_141', title: 'Undergraduate Education' },
    { id: 'gg_iab_142', title: 'Early Childhood Education' },
    { id: 'gg_iab_143', title: 'Educational Assessment' },
    { id: 'gg_iab_144', title: 'Standardized Testing' },
    { id: 'gg_iab_145', title: 'Homeschooling' },
    { id: 'gg_iab_146', title: 'Homework and Study' },
    { id: 'gg_iab_147', title: 'Language Learning' },
    { id: 'gg_iab_148', title: 'Online Education' },
    { id: 'gg_iab_149', title: 'Primary Education' },
    { id: 'gg_iab_150', title: 'Events and Attractions' },
    { id: 'gg_iab_151', title: 'Amusement and Theme Parks' },
    { id: 'gg_iab_152', title: 'Fashion Events' },
    { id: 'gg_iab_153', title: 'Historic Site and Landmark Tours' },
    { id: 'gg_iab_154', title: 'Malls & Shopping Centers' },
    { id: 'gg_iab_155', title: 'Museums & Galleries' },
    { id: 'gg_iab_156', title: 'Musicals' },
    { id: 'gg_iab_157', title: 'National & Civic Holidays' },
    { id: 'gg_iab_158', title: 'Nightclubs' },
    { id: 'gg_iab_159', title: 'Outdoor Activities' },
    { id: 'gg_iab_160', title: 'Parks & Nature' },
    { id: 'gg_iab_161', title: 'Party Supplies and Decorations' },
    { id: 'gg_iab_162', title: 'Awards Shows' },
    { id: 'gg_iab_163', title: 'Personal Celebrations & Life Events' },
    { id: 'gg_iab_164', title: 'Anniversary' },
    { id: 'gg_iab_165', title: 'Wedding' },
    { id: 'gg_iab_166', title: 'Baby Shower' },
    { id: 'gg_iab_167', title: 'Bachelor Party' },
    { id: 'gg_iab_168', title: 'Bachelorette Party' },
    { id: 'gg_iab_169', title: 'Birth' },
    { id: 'gg_iab_170', title: 'Birthday' },
    { id: 'gg_iab_171', title: 'Funeral' },
    { id: 'gg_iab_172', title: 'Graduation' },
    { id: 'gg_iab_173', title: 'Prom' },
    { id: 'gg_iab_174', title: 'Political Event' },
    { id: 'gg_iab_175', title: 'Religious Events' },
    { id: 'gg_iab_176', title: 'Sporting Events' },
    { id: 'gg_iab_177', title: 'Theater Venues and Events' },
    { id: 'gg_iab_178', title: 'Zoos & Aquariums' },
    { id: 'gg_iab_179', title: 'Bars & Restaurants' },
    { id: 'gg_iab_180', title: 'Business Expos & Conferences' },
    { id: 'gg_iab_181', title: 'Casinos & Gambling' },
    { id: 'gg_iab_182', title: 'Cinemas and Events' },
    { id: 'gg_iab_183', title: 'Comedy Events' },
    { id: 'gg_iab_184', title: 'Concerts & Music Events' },
    { id: 'gg_iab_185', title: 'Fan Conventions' },
    { id: 'gg_iab_186', title: 'Family and Relationships' },
    { id: 'gg_iab_187', title: 'Bereavement' },
    { id: 'gg_iab_188', title: 'Dating' },
    { id: 'gg_iab_189', title: 'Divorce' },
    { id: 'gg_iab_190', title: 'Eldercare' },
    { id: 'gg_iab_191', title: 'Marriage and Civil Unions' },
    { id: 'gg_iab_192', title: 'Parenting' },
    { id: 'gg_iab_193', title: 'Adoption and Fostering' },
    { id: 'gg_iab_194', title: 'Daycare and Pre-School' },
    { id: 'gg_iab_195', title: 'Internet Safety' },
    { id: 'gg_iab_196', title: 'Parenting Babies and Toddlers' },
    { id: 'gg_iab_197', title: 'Parenting Children Aged 4-11' },
    { id: 'gg_iab_198', title: 'Parenting Teens' },
    { id: 'gg_iab_199', title: 'Special Needs Kids' },
    { id: 'gg_iab_200', title: 'Single Life' },
    { id: 'gg_iab_201', title: 'Fine Art' },
    { id: 'gg_iab_202', title: 'Costume' },
    { id: 'gg_iab_203', title: 'Dance' },
    { id: 'gg_iab_204', title: 'Design' },
    { id: 'gg_iab_205', title: 'Digital Arts' },
    { id: 'gg_iab_206', title: 'Fine Art Photography' },
    { id: 'gg_iab_207', title: 'Modern Art' },
    { id: 'gg_iab_208', title: 'Opera' },
    { id: 'gg_iab_209', title: 'Theater' },
    { id: 'gg_iab_210', title: 'Food & Drink' },
    { id: 'gg_iab_211', title: 'Alcoholic Beverages' },
    { id: 'gg_iab_212', title: 'Vegan Diets' },
    { id: 'gg_iab_213', title: 'Vegetarian Diets' },
    { id: 'gg_iab_214', title: 'World Cuisines' },
    { id: 'gg_iab_215', title: 'Barbecues and Grilling' },
    { id: 'gg_iab_216', title: 'Cooking' },
    { id: 'gg_iab_217', title: 'Desserts and Baking' },
    { id: 'gg_iab_218', title: 'Dining Out' },
    { id: 'gg_iab_219', title: 'Food Allergies' },
    { id: 'gg_iab_220', title: 'Food Movements' },
    { id: 'gg_iab_221', title: 'Healthy Cooking and Eating' },
    { id: 'gg_iab_222', title: 'Non-Alcoholic Beverages' },
    { id: 'gg_iab_223', title: 'Healthy Living' },
    { id: 'gg_iab_224', title: 'Childrens Health' },
    { id: 'gg_iab_225', title: 'Fitness and Exercise' },
    { id: 'gg_iab_226', title: 'Participant Sports' },
    { id: 'gg_iab_227', title: 'Running and Jogging' },
    { id: 'gg_iab_228', title: 'Mens Health' },
    { id: 'gg_iab_229', title: 'Nutrition' },
    { id: 'gg_iab_230', title: 'Senior Health' },
    { id: 'gg_iab_231', title: 'Weight Loss' },
    { id: 'gg_iab_232', title: 'Wellness' },
    { id: 'gg_iab_233', title: 'Alternative Medicine' },
    { id: 'gg_iab_234', title: 'Herbs and Supplements' },
    { id: 'gg_iab_235', title: 'Holistic Health' },
    { id: 'gg_iab_236', title: 'Physical Therapy' },
    { id: 'gg_iab_237', title: 'Smoking Cessation' },
    { id: 'gg_iab_238', title: 'Womens Health' },
    { id: 'gg_iab_239', title: 'Hobbies & Interests' },
    { id: 'gg_iab_240', title: 'Antiquing and Antiques' },
    { id: 'gg_iab_241', title: 'Magic and Illusion' },
    { id: 'gg_iab_242', title: 'Model Toys' },
    { id: 'gg_iab_243', title: 'Musical Instruments' },
    { id: 'gg_iab_244', title: 'Paranormal Phenomena' },
    { id: 'gg_iab_245', title: 'Radio Control' },
    { id: 'gg_iab_246', title: 'Sci-fi and Fantasy' },
    { id: 'gg_iab_247', title: 'Workshops and Classes' },
    { id: 'gg_iab_248', title: 'Arts and Crafts' },
    { id: 'gg_iab_249', title: 'Beadwork' },
    { id: 'gg_iab_250', title: 'Candle and Soap Making' },
    { id: 'gg_iab_251', title: 'Drawing and Sketching' },
    { id: 'gg_iab_252', title: 'Jewelry Making' },
    { id: 'gg_iab_253', title: 'Needlework' },
    { id: 'gg_iab_254', title: 'Painting' },
    { id: 'gg_iab_255', title: 'Photography' },
    { id: 'gg_iab_256', title: 'Scrapbooking' },
    { id: 'gg_iab_257', title: 'Woodworking' },
    { id: 'gg_iab_258', title: 'Beekeeping' },
    { id: 'gg_iab_259', title: 'Birdwatching' },
    { id: 'gg_iab_260', title: 'Cigars' },
    { id: 'gg_iab_261', title: 'Collecting' },
    { id: 'gg_iab_262', title: 'Comic Books' },
    { id: 'gg_iab_263', title: 'Stamps and Coins' },
    { id: 'gg_iab_264', title: 'Content Production' },
    { id: 'gg_iab_265', title: 'Audio Production' },
    { id: 'gg_iab_266', title: 'Freelance Writing' },
    { id: 'gg_iab_267', title: 'Screenwriting' },
    { id: 'gg_iab_268', title: 'Video Production' },
    { id: 'gg_iab_269', title: 'Games and Puzzles' },
    { id: 'gg_iab_270', title: 'Board Games and Puzzles' },
    { id: 'gg_iab_271', title: 'Card Games' },
    { id: 'gg_iab_272', title: 'Roleplaying Games' },
    { id: 'gg_iab_273', title: 'Genealogy and Ancestry' },
    { id: 'gg_iab_274', title: 'Home & Garden' },
    { id: 'gg_iab_275', title: 'Gardening' },
    { id: 'gg_iab_276', title: 'Remodeling & Construction' },
    { id: 'gg_iab_277', title: 'Smart Home' },
    { id: 'gg_iab_278', title: 'Home Appliances' },
    { id: 'gg_iab_279', title: 'Home Entertaining' },
    { id: 'gg_iab_280', title: 'Home Improvement' },
    { id: 'gg_iab_281', title: 'Home Security' },
    { id: 'gg_iab_282', title: 'Indoor Environmental Quality' },
    { id: 'gg_iab_283', title: 'Interior Decorating' },
    { id: 'gg_iab_284', title: 'Landscaping' },
    { id: 'gg_iab_285', title: 'Outdoor Decorating' },
    { id: 'gg_iab_286', title: 'Medical Health' },
    { id: 'gg_iab_287', title: 'Diseases and Conditions' },
    { id: 'gg_iab_288', title: 'Allergies' },
    { id: 'gg_iab_289', title: 'Ear, Nose and Throat Conditions' },
    { id: 'gg_iab_290', title: 'Endocrine and Metabolic Diseases' },
    { id: 'gg_iab_291', title: 'Hormonal Disorders' },
    { id: 'gg_iab_292', title: 'Menopause' },
    { id: 'gg_iab_293', title: 'Thyroid Disorders' },
    { id: 'gg_iab_294', title: 'Eye and Vision Conditions' },
    { id: 'gg_iab_295', title: 'Foot Health' },
    { id: 'gg_iab_296', title: 'Heart and Cardiovascular Diseases' },
    { id: 'gg_iab_297', title: 'Infectious Diseases' },
    { id: 'gg_iab_298', title: 'Injuries' },
    { id: 'gg_iab_299', title: 'First Aid' },
    { id: 'gg_iab_300', title: 'Lung and Respiratory Health' },
    { id: 'gg_iab_301', title: 'Mental Health' },
    { id: 'gg_iab_302', title: 'Reproductive Health' },
    { id: 'gg_iab_303', title: 'Birth Control' },
    { id: 'gg_iab_304', title: 'Infertility' },
    { id: 'gg_iab_305', title: 'Pregnancy' },
    { id: 'gg_iab_306', title: 'Blood Disorders' },
    { id: 'gg_iab_307', title: 'Sexual Health' },
    { id: 'gg_iab_308', title: 'Sexual Conditions' },
    { id: 'gg_iab_309', title: 'Skin and Dermatology' },
    { id: 'gg_iab_310', title: 'Sleep Disorders' },
    { id: 'gg_iab_311', title: 'Substance Abuse' },
    { id: 'gg_iab_312', title: 'Bone and Joint Conditions' },
    { id: 'gg_iab_313', title: 'Brain and Nervous System Disorders' },
    { id: 'gg_iab_314', title: 'Cancer' },
    { id: 'gg_iab_315', title: 'Cold and Flu' },
    { id: 'gg_iab_316', title: 'Dental Health' },
    { id: 'gg_iab_317', title: 'Diabetes' },
    { id: 'gg_iab_318', title: 'Digestive Disorders' },
    { id: 'gg_iab_319', title: 'Medical Tests' },
    { id: 'gg_iab_320', title: 'Pharmaceutical Drugs' },
    { id: 'gg_iab_321', title: 'Surgery' },
    { id: 'gg_iab_322', title: 'Vaccines' },
    { id: 'gg_iab_323', title: 'Cosmetic Medical Services' },
    { id: 'gg_iab_324', title: 'Movies' },
    { id: 'gg_iab_325', title: 'Action and Adventure Movies' },
    { id: 'gg_iab_326', title: 'Romance Movies' },
    { id: 'gg_iab_327', title: 'Science Fiction Movies' },
    { id: 'gg_iab_328', title: 'Special Interest Movies' },
    { id: 'gg_iab_329', title: 'Animation Movies' },
    { id: 'gg_iab_330', title: 'Comedy Movies' },
    { id: 'gg_iab_331', title: 'Crime and Mystery Movies' },
    { id: 'gg_iab_332', title: 'Documentary Movies' },
    { id: 'gg_iab_333', title: 'Drama Movies' },
    { id: 'gg_iab_334', title: 'Family and Children Movies' },
    { id: 'gg_iab_335', title: 'Fantasy Movies' },
    { id: 'gg_iab_336', title: 'Horror Movies' },
    { id: 'gg_iab_337', title: 'World Movies' },
    { id: 'gg_iab_338', title: 'Music and Audio' },
    { id: 'gg_iab_339', title: 'Adult Contemporary Music' },
    { id: 'gg_iab_340', title: 'Soft AC Music' },
    { id: 'gg_iab_341', title: 'Urban AC Music' },
    { id: 'gg_iab_342', title: 'Adult Album Alternative' },
    { id: 'gg_iab_343', title: 'Alternative Music' },
    { id: 'gg_iab_344', title: 'Childrens Music' },
    { id: 'gg_iab_345', title: 'Classic Hits' },
    { id: 'gg_iab_346', title: 'Classical Music' },
    { id: 'gg_iab_347', title: 'College Radio' },
    { id: 'gg_iab_348', title: 'Comedy (Music and Audio)' },
    { id: 'gg_iab_349', title: 'Contemporary Hits/Pop/Top 40' },
    { id: 'gg_iab_350', title: 'Country Music' },
    { id: 'gg_iab_351', title: 'Dance and Electronic Music' },
    { id: 'gg_iab_352', title: 'World/International Music' },
    { id: 'gg_iab_353', title: 'Songwriters/Folk' },
    { id: 'gg_iab_354', title: 'Gospel Music' },
    { id: 'gg_iab_355', title: 'Hip Hop Music' },
    { id: 'gg_iab_356', title: 'Inspirational/New Age Music' },
    { id: 'gg_iab_357', title: 'Jazz' },
    { id: 'gg_iab_358', title: 'Oldies/Adult Standards' },
    { id: 'gg_iab_359', title: 'Reggae' },
    { id: 'gg_iab_360', title: 'Blues' },
    { id: 'gg_iab_361', title: 'Religious (Music and Audio)' },
    { id: 'gg_iab_362', title: 'R&B/Soul/Funk' },
    { id: 'gg_iab_363', title: 'Rock Music' },
    { id: 'gg_iab_364', title: 'Album-oriented Rock' },
    { id: 'gg_iab_365', title: 'Alternative Rock' },
    { id: 'gg_iab_366', title: 'Classic Rock' },
    { id: 'gg_iab_367', title: 'Hard Rock' },
    { id: 'gg_iab_368', title: 'Soft Rock' },
    { id: 'gg_iab_369', title: 'Soundtracks, TV and Showtunes' },
    { id: 'gg_iab_370', title: 'Sports Radio' },
    { id: 'gg_iab_371', title: 'Talk Radio' },
    { id: 'gg_iab_372', title: 'Business News Radio' },
    { id: 'gg_iab_373', title: 'Educational Radio' },
    { id: 'gg_iab_374', title: 'News Radio' },
    { id: 'gg_iab_375', title: 'News/Talk Radio' },
    { id: 'gg_iab_376', title: 'Public Radio' },
    { id: 'gg_iab_377', title: 'Urban Contemporary Music' },
    { id: 'gg_iab_378', title: 'Variety (Music and Audio)' },
    { id: 'gg_iab_379', title: 'News and Politics' },
    { id: 'gg_iab_380', title: 'Crime' },
    { id: 'gg_iab_381', title: 'Disasters' },
    { id: 'gg_iab_382', title: 'International News' },
    { id: 'gg_iab_383', title: 'Law' },
    { id: 'gg_iab_384', title: 'Local News' },
    { id: 'gg_iab_385', title: 'National News' },
    { id: 'gg_iab_386', title: 'Politics' },
    { id: 'gg_iab_387', title: 'Elections' },
    { id: 'gg_iab_388', title: 'Political Issues' },
    { id: 'gg_iab_389', title: 'War and Conflicts' },
    { id: 'gg_iab_390', title: 'Weather' },
    { id: 'gg_iab_391', title: 'Personal Finance' },
    { id: 'gg_iab_392', title: 'Consumer Banking' },
    { id: 'gg_iab_393', title: 'Financial Assistance' },
    { id: 'gg_iab_394', title: 'Government Support and Welfare' },
    { id: 'gg_iab_395', title: 'Student Financial Aid' },
    { id: 'gg_iab_396', title: 'Financial Planning' },
    { id: 'gg_iab_397', title: 'Frugal Living' },
    { id: 'gg_iab_398', title: 'Insurance' },
    { id: 'gg_iab_399', title: 'Health Insurance' },
    { id: 'gg_iab_400', title: 'Home Insurance' },
    { id: 'gg_iab_401', title: 'Life Insurance' },
    { id: 'gg_iab_402', title: 'Motor Insurance' },
    { id: 'gg_iab_403', title: 'Pet Insurance' },
    { id: 'gg_iab_404', title: 'Travel Insurance' },
    { id: 'gg_iab_405', title: 'Personal Debt' },
    { id: 'gg_iab_406', title: 'Credit Cards' },
    { id: 'gg_iab_407', title: 'Home Financing' },
    { id: 'gg_iab_408', title: 'Personal Loans' },
    { id: 'gg_iab_409', title: 'Student Loans' },
    { id: 'gg_iab_410', title: 'Personal Investing' },
    { id: 'gg_iab_411', title: 'Hedge Funds' },
    { id: 'gg_iab_412', title: 'Mutual Funds' },
    { id: 'gg_iab_413', title: 'Options' },
    { id: 'gg_iab_414', title: 'Stocks and Bonds' },
    { id: 'gg_iab_415', title: 'Personal Taxes' },
    { id: 'gg_iab_416', title: 'Retirement Planning' },
    { id: 'gg_iab_417', title: 'Home Utilities' },
    { id: 'gg_iab_418', title: 'Gas and Electric' },
    { id: 'gg_iab_419', title: 'Internet Service Providers' },
    { id: 'gg_iab_420', title: 'Phone Services' },
    { id: 'gg_iab_421', title: 'Water Services' },
    { id: 'gg_iab_422', title: 'Pets' },
    { id: 'gg_iab_423', title: 'Birds' },
    { id: 'gg_iab_424', title: 'Cats' },
    { id: 'gg_iab_425', title: 'Dogs' },
    { id: 'gg_iab_426', title: 'Fish and Aquariums' },
    { id: 'gg_iab_427', title: 'Large Animals' },
    { id: 'gg_iab_428', title: 'Pet Adoptions' },
    { id: 'gg_iab_429', title: 'Reptiles' },
    { id: 'gg_iab_430', title: 'Veterinary Medicine' },
    { id: 'gg_iab_431', title: 'Pet Supplies' },
    { id: 'gg_iab_432', title: 'Pop Culture' },
    { id: 'gg_iab_433', title: 'Celebrity Deaths' },
    { id: 'gg_iab_434', title: 'Celebrity Families' },
    { id: 'gg_iab_435', title: 'Celebrity Homes' },
    { id: 'gg_iab_436', title: 'Celebrity Pregnancy' },
    { id: 'gg_iab_437', title: 'Celebrity Relationships' },
    { id: 'gg_iab_438', title: 'Celebrity Scandal' },
    { id: 'gg_iab_439', title: 'Celebrity Style' },
    { id: 'gg_iab_440', title: 'Humor and Satire' },
    { id: 'gg_iab_441', title: 'Real Estate' },
    { id: 'gg_iab_442', title: 'Apartments' },
    { id: 'gg_iab_443', title: 'Retail Property' },
    { id: 'gg_iab_444', title: 'Vacation Properties' },
    { id: 'gg_iab_445', title: 'Developmental Sites' },
    { id: 'gg_iab_446', title: 'Hotel Properties' },
    { id: 'gg_iab_447', title: 'Houses' },
    { id: 'gg_iab_448', title: 'Industrial Property' },
    { id: 'gg_iab_449', title: 'Land and Farms' },
    { id: 'gg_iab_450', title: 'Office Property' },
    { id: 'gg_iab_451', title: 'Real Estate Buying and Selling' },
    { id: 'gg_iab_452', title: 'Real Estate Renting and Leasing' },
    { id: 'gg_iab_453', title: 'Religion & Spirituality' },
    { id: 'gg_iab_454', title: 'Agnosticism' },
    { id: 'gg_iab_455', title: 'Spirituality' },
    { id: 'gg_iab_456', title: 'Astrology' },
    { id: 'gg_iab_457', title: 'Atheism' },
    { id: 'gg_iab_458', title: 'Buddhism' },
    { id: 'gg_iab_459', title: 'Christianity' },
    { id: 'gg_iab_460', title: 'Hinduism' },
    { id: 'gg_iab_461', title: 'Islam' },
    { id: 'gg_iab_462', title: 'Judaism' },
    { id: 'gg_iab_463', title: 'Sikhism' },
    { id: 'gg_iab_464', title: 'Science' },
    { id: 'gg_iab_465', title: 'Biological Sciences' },
    { id: 'gg_iab_466', title: 'Chemistry' },
    { id: 'gg_iab_467', title: 'Environment' },
    { id: 'gg_iab_468', title: 'Genetics' },
    { id: 'gg_iab_469', title: 'Geography' },
    { id: 'gg_iab_470', title: 'Geology' },
    { id: 'gg_iab_471', title: 'Physics' },
    { id: 'gg_iab_472', title: 'Space and Astronomy' },
    { id: 'gg_iab_473', title: 'Shopping' },
    { id: 'gg_iab_474', title: 'Coupons and Discounts' },
    { id: 'gg_iab_475', title: 'Flower Shopping' },
    { id: 'gg_iab_476', title: 'Gifts and Greetings Cards' },
    { id: 'gg_iab_477', title: 'Grocery Shopping' },
    { id: 'gg_iab_478', title: 'Holiday Shopping' },
    { id: 'gg_iab_479', title: 'Household Supplies' },
    { id: 'gg_iab_480', title: 'Lotteries and Scratchcards' },
    { id: 'gg_iab_481', title: 'Sales and Promotions' },
    { id: 'gg_iab_482', title: 'Childrens Games and Toys' },
    { id: 'gg_iab_483', title: 'Sports' },
    { id: 'gg_iab_484', title: 'American Football' },
    { id: 'gg_iab_485', title: 'Boxing' },
    { id: 'gg_iab_486', title: 'Cheerleading' },
    { id: 'gg_iab_487', title: 'College Sports' },
    { id: 'gg_iab_488', title: 'College Football' },
    { id: 'gg_iab_489', title: 'College Basketball' },
    { id: 'gg_iab_490', title: 'College Baseball' },
    { id: 'gg_iab_491', title: 'Cricket' },
    { id: 'gg_iab_492', title: 'Cycling' },
    { id: 'gg_iab_493', title: 'Darts' },
    { id: 'gg_iab_494', title: 'Disabled Sports' },
    { id: 'gg_iab_495', title: 'Diving' },
    { id: 'gg_iab_496', title: 'Equine Sports' },
    { id: 'gg_iab_497', title: 'Horse Racing' },
    { id: 'gg_iab_498', title: 'Extreme Sports' },
    { id: 'gg_iab_499', title: 'Canoeing and Kayaking' },
    { id: 'gg_iab_500', title: 'Climbing' },
    { id: 'gg_iab_501', title: 'Paintball' },
    { id: 'gg_iab_502', title: 'Scuba Diving' },
    { id: 'gg_iab_503', title: 'Skateboarding' },
    { id: 'gg_iab_504', title: 'Snowboarding' },
    { id: 'gg_iab_505', title: 'Surfing and Bodyboarding' },
    { id: 'gg_iab_506', title: 'Waterskiing and Wakeboarding' },
    { id: 'gg_iab_507', title: 'Australian Rules Football' },
    { id: 'gg_iab_508', title: 'Fantasy Sports' },
    { id: 'gg_iab_509', title: 'Field Hockey' },
    { id: 'gg_iab_510', title: 'Figure Skating' },
    { id: 'gg_iab_511', title: 'Fishing Sports' },
    { id: 'gg_iab_512', title: 'Golf' },
    { id: 'gg_iab_513', title: 'Gymnastics' },
    { id: 'gg_iab_514', title: 'Hunting and Shooting' },
    { id: 'gg_iab_515', title: 'Ice Hockey' },
    { id: 'gg_iab_516', title: 'Inline Skating' },
    { id: 'gg_iab_517', title: 'Lacrosse' },
    { id: 'gg_iab_518', title: 'Auto Racing' },
    { id: 'gg_iab_519', title: 'Motorcycle Sports' },
    { id: 'gg_iab_520', title: 'Martial Arts' },
    { id: 'gg_iab_521', title: 'Olympic Sports' },
    { id: 'gg_iab_522', title: 'Summer Olympic Sports' },
    { id: 'gg_iab_523', title: 'Winter Olympic Sports' },
    { id: 'gg_iab_524', title: 'Poker and Professional Gambling' },
    { id: 'gg_iab_525', title: 'Rodeo' },
    { id: 'gg_iab_526', title: 'Rowing' },
    { id: 'gg_iab_527', title: 'Rugby' },
    { id: 'gg_iab_528', title: 'Rugby League' },
    { id: 'gg_iab_529', title: 'Rugby Union' },
    { id: 'gg_iab_530', title: 'Sailing' },
    { id: 'gg_iab_531', title: 'Skiing' },
    { id: 'gg_iab_532', title: 'Snooker/Pool/Billiards' },
    { id: 'gg_iab_533', title: 'Soccer' },
    { id: 'gg_iab_534', title: 'Badminton' },
    { id: 'gg_iab_535', title: 'Softball' },
    { id: 'gg_iab_536', title: 'Squash' },
    { id: 'gg_iab_537', title: 'Swimming' },
    { id: 'gg_iab_538', title: 'Table Tennis' },
    { id: 'gg_iab_539', title: 'Tennis' },
    { id: 'gg_iab_540', title: 'Track and Field' },
    { id: 'gg_iab_541', title: 'Volleyball' },
    { id: 'gg_iab_542', title: 'Walking' },
    { id: 'gg_iab_543', title: 'Water Polo' },
    { id: 'gg_iab_544', title: 'Weightlifting' },
    { id: 'gg_iab_545', title: 'Baseball' },
    { id: 'gg_iab_546', title: 'Wrestling' },
    { id: 'gg_iab_547', title: 'Basketball' },
    { id: 'gg_iab_548', title: 'Beach Volleyball' },
    { id: 'gg_iab_549', title: 'Bodybuilding' },
    { id: 'gg_iab_550', title: 'Bowling' },
    { id: 'gg_iab_551', title: 'Sports Equipment' },
    { id: 'gg_iab_552', title: 'Style & Fashion' },
    { id: 'gg_iab_553', title: 'Beauty' },
    { id: 'gg_iab_554', title: 'Hair Care' },
    { id: 'gg_iab_555', title: 'Makeup and Accessories' },
    { id: 'gg_iab_556', title: 'Nail Care' },
    { id: 'gg_iab_557', title: 'Natural and Organic Beauty' },
    { id: 'gg_iab_558', title: 'Perfume and Fragrance' },
    { id: 'gg_iab_559', title: 'Skin Care' },
    { id: 'gg_iab_560', title: 'Womens Fashion' },
    { id: 'gg_iab_561', title: 'Womens Accessories' },
    { id: 'gg_iab_562', title: 'Womens Glasses' },
    { id: 'gg_iab_563', title: 'Womens Handbags and Wallets' },
    { id: 'gg_iab_564', title: 'Womens Hats and Scarves' },
    { id: 'gg_iab_565', title: 'Womens Jewelry and Watches' },
    { id: 'gg_iab_566', title: 'Womens Clothing' },
    { id: 'gg_iab_567', title: 'Womens Business Wear' },
    { id: 'gg_iab_568', title: 'Womens Casual Wear' },
    { id: 'gg_iab_569', title: 'Womens Formal Wear' },
    { id: 'gg_iab_570', title: 'Womens Intimates and Sleepwear' },
    { id: 'gg_iab_571', title: 'Womens Outerwear' },
    { id: 'gg_iab_572', title: 'Womens Sportswear' },
    { id: 'gg_iab_573', title: 'Womens Shoes and Footwear' },
    { id: 'gg_iab_574', title: 'Body Art' },
    { id: 'gg_iab_575', title: 'Childrens Clothing' },
    { id: 'gg_iab_576', title: 'Designer Clothing' },
    { id: 'gg_iab_577', title: 'Fashion Trends' },
    { id: 'gg_iab_578', title: 'High Fashion' },
    { id: 'gg_iab_579', title: 'Mens Fashion' },
    { id: 'gg_iab_580', title: 'Mens Accessories' },
    { id: 'gg_iab_581', title: 'Mens Jewelry and Watches' },
    { id: 'gg_iab_582', title: 'Mens Clothing' },
    { id: 'gg_iab_583', title: 'Mens Business Wear' },
    { id: 'gg_iab_584', title: 'Mens Casual Wear' },
    { id: 'gg_iab_585', title: 'Mens Formal Wear' },
    { id: 'gg_iab_586', title: 'Mens Outerwear' },
    { id: 'gg_iab_587', title: 'Mens Sportswear' },
    { id: 'gg_iab_588', title: 'Mens Underwear and Sleepwear' },
    { id: 'gg_iab_589', title: 'Mens Shoes and Footwear' },
    { id: 'gg_iab_590', title: 'Personal Care' },
    { id: 'gg_iab_591', title: 'Bath and Shower' },
    { id: 'gg_iab_592', title: 'Deodorant and Antiperspirant' },
    { id: 'gg_iab_593', title: 'Oral care' },
    { id: 'gg_iab_594', title: 'Shaving' },
    { id: 'gg_iab_595', title: 'Street Style' },
    { id: 'gg_iab_596', title: 'Technology & Computing' },
    { id: 'gg_iab_597', title: 'Artificial Intelligence' },
    { id: 'gg_iab_598', title: 'Augmented Reality' },
    { id: 'gg_iab_599', title: 'Computing' },
    { id: 'gg_iab_600', title: 'Computer Networking' },
    { id: 'gg_iab_601', title: 'Computer Peripherals' },
    { id: 'gg_iab_602', title: 'Computer Software and Applications' },
    { id: 'gg_iab_603', title: '3-D Graphics' },
    { id: 'gg_iab_604', title: 'Photo Editing Software' },
    { id: 'gg_iab_605', title: 'Shareware and Freeware' },
    { id: 'gg_iab_606', title: 'Video Software' },
    { id: 'gg_iab_607', title: 'Web Conferencing' },
    { id: 'gg_iab_608', title: 'Antivirus Software' },
    { id: 'gg_iab_609', title: 'Browsers' },
    { id: 'gg_iab_610', title: 'Computer Animation' },
    { id: 'gg_iab_611', title: 'Databases' },
    { id: 'gg_iab_612', title: 'Desktop Publishing' },
    { id: 'gg_iab_613', title: 'Digital Audio' },
    { id: 'gg_iab_614', title: 'Graphics Software' },
    { id: 'gg_iab_615', title: 'Operating Systems' },
    { id: 'gg_iab_616', title: 'Data Storage and Warehousing' },
    { id: 'gg_iab_617', title: 'Desktops' },
    { id: 'gg_iab_618', title: 'Information and Network Security' },
    { id: 'gg_iab_619', title: 'Internet' },
    { id: 'gg_iab_620', title: 'Cloud Computing' },
    { id: 'gg_iab_621', title: 'Web Development' },
    { id: 'gg_iab_622', title: 'Web Hosting' },
    { id: 'gg_iab_623', title: 'Email' },
    { id: 'gg_iab_624', title: 'Internet for Beginners' },
    { id: 'gg_iab_625', title: 'Internet of Things' },
    { id: 'gg_iab_626', title: 'IT and Internet Support' },
    { id: 'gg_iab_627', title: 'Search' },
    { id: 'gg_iab_628', title: 'Social Networking' },
    { id: 'gg_iab_629', title: 'Web Design and HTML' },
    { id: 'gg_iab_630', title: 'Laptops' },
    { id: 'gg_iab_631', title: 'Programming Languages' },
    { id: 'gg_iab_632', title: 'Consumer Electronics' },
    { id: 'gg_iab_633', title: 'Cameras and Camcorders' },
    { id: 'gg_iab_634', title: 'Home Entertainment Systems' },
    { id: 'gg_iab_635', title: 'Smartphones' },
    { id: 'gg_iab_636', title: 'Tablets and E-readers' },
    { id: 'gg_iab_637', title: 'Wearable Technology' },
    { id: 'gg_iab_638', title: 'Robotics' },
    { id: 'gg_iab_639', title: 'Virtual Reality' },
    { id: 'gg_iab_640', title: 'Television' },
    { id: 'gg_iab_641', title: 'Animation TV' },
    { id: 'gg_iab_642', title: 'Soap Opera TV' },
    { id: 'gg_iab_643', title: 'Special Interest TV' },
    { id: 'gg_iab_644', title: 'Sports TV' },
    { id: 'gg_iab_645', title: 'Childrens TV' },
    { id: 'gg_iab_646', title: 'Comedy TV' },
    { id: 'gg_iab_647', title: 'Drama TV' },
    { id: 'gg_iab_648', title: 'Factual TV' },
    { id: 'gg_iab_649', title: 'Holiday TV' },
    { id: 'gg_iab_650', title: 'Music TV' },
    { id: 'gg_iab_651', title: 'Reality TV' },
    { id: 'gg_iab_652', title: 'Science Fiction TV' },
    { id: 'gg_iab_653', title: 'Travel' },
    { id: 'gg_iab_654', title: 'Travel Accessories' },
    { id: 'gg_iab_655', title: 'Travel Locations' },
    { id: 'gg_iab_656', title: 'Africa Travel' },
    { id: 'gg_iab_657', title: 'Asia Travel' },
    { id: 'gg_iab_658', title: 'Australia and Oceania Travel' },
    { id: 'gg_iab_659', title: 'Europe Travel' },
    { id: 'gg_iab_660', title: 'North America Travel' },
    { id: 'gg_iab_661', title: 'Polar Travel' },
    { id: 'gg_iab_662', title: 'South America Travel' },
    { id: 'gg_iab_663', title: 'Travel Preparation and Advice' },
    { id: 'gg_iab_664', title: 'Travel Type' },
    { id: 'gg_iab_665', title: 'Adventure Travel' },
    { id: 'gg_iab_666', title: 'Family Travel' },
    { id: 'gg_iab_667', title: 'Honeymoons and Getaways' },
    { id: 'gg_iab_668', title: 'Hotels and Motels' },
    { id: 'gg_iab_669', title: 'Rail Travel' },
    { id: 'gg_iab_670', title: 'Road Trips' },
    { id: 'gg_iab_671', title: 'Spas' },
    { id: 'gg_iab_672', title: 'Air Travel' },
    { id: 'gg_iab_673', title: 'Beach Travel' },
    { id: 'gg_iab_674', title: 'Bed & Breakfasts' },
    { id: 'gg_iab_675', title: 'Budget Travel' },
    { id: 'gg_iab_676', title: 'Business Travel' },
    { id: 'gg_iab_677', title: 'Camping' },
    { id: 'gg_iab_678', title: 'Cruises' },
    { id: 'gg_iab_679', title: 'Day Trips' },
    { id: 'gg_iab_680', title: 'Video Gaming' },
    { id: 'gg_iab_681', title: 'Console Games' },
    { id: 'gg_iab_682', title: 'eSports' },
    { id: 'gg_iab_683', title: 'Mobile Games' },
    { id: 'gg_iab_684', title: 'PC Games' },
    { id: 'gg_iab_685', title: 'Video Game Genres' },
    { id: 'gg_iab_686', title: 'Action Video Games' },
    { id: 'gg_iab_687', title: 'Role-Playing Video Games' },
    { id: 'gg_iab_688', title: 'Simulation Video Games' },
    { id: 'gg_iab_689', title: 'Sports Video Games' },
    { id: 'gg_iab_690', title: 'Strategy Video Games' },
    { id: 'gg_iab_691', title: 'Action-Adventure Video Games' },
    { id: 'gg_iab_692', title: 'Adventure Video Games' },
    { id: 'gg_iab_693', title: 'Casual Games' },
    { id: 'gg_iab_694', title: 'Educational Video Games' },
    { id: 'gg_iab_695', title: 'Exercise and Fitness Video Games' },
    { id: 'gg_iab_696', title: 'MMOs' },
    { id: 'gg_iab_697', title: 'Music and Party Video Games' },
    { id: 'gg_iab_698', title: 'Puzzle Video Games' },
    { id: 'gg_threat_GGT0', title: 'safe' },
    { id: 'gg_threat_GGT1', title: 'violence & gore' },
    { id: 'gg_threat_GGT2', title: 'criminal' },
    { id: 'gg_threat_GGT3', title: 'drugs & alcohol' },
    { id: 'gg_threat_GGT4', title: 'sexually charged' },
    { id: 'gg_threat_GGT5', title: 'obscene / disgust' },
    { id: 'gg_threat_GGT6', title: 'hate logos' },
    { id: 'gg_threat_GGT7', title: 'disasters (natural / man-made)' },
    { id: 'gg_threat_GGT8', title: 'maleware' },
    { id: 'gg_threat_GGT9', title: 'medical / illness' },
    { id: 'gg_sentiment_neutral', title: 'Neutral Sentiment' },
    { id: 'gg_sentiment_positive', title: 'Postiive Sentiment' },
    { id: 'gg_sentiment_negative', title: 'Negative Sentiment' },
    { id: 'gg_safety_true', title: 'Brand Safe' },
    { id: 'gg_safety_false', title: 'Not Brand Safe' },
  ];

  return generateIRISSegmentList(options, currentValues);
};

export const generateKervContextualSegmentList = (currentValues: string[]): IMenuList => {
  const options: IIRISOption[] = [
    { id: 'kv_alcohol', title: 'Alcohol' },
    { id: 'kv_alcohol_beer', title: 'Alcohol-Beer' },
    { id: 'kv_alcohol_beer_blue-moon', title: 'Alcohol-Beer-Blue Moon' },
    { id: 'kv_alcohol_beer_bud-light', title: 'Alcohol-Beer-Bud Light' },
    { id: 'kv_alcohol_beer_budweiser', title: 'Alcohol-Beer-Budweiser' },
    { id: 'kv_alcohol_beer_busch', title: 'Alcohol-Beer-Busch' },
    { id: 'kv_alcohol_beer_coors', title: 'Alcohol-Beer-Coors' },
    { id: 'kv_alcohol_beer_corona', title: 'Alcohol-Beer-Corona' },
    { id: 'kv_alcohol_beer_dos-equis', title: 'Alcohol-Beer-Dos Equis' },
    { id: 'kv_alcohol_beer_fosters', title: 'Alcohol-Beer-Fosters' },
    { id: 'kv_alcohol_beer_guiness', title: 'Alcohol-Beer-Guiness' },
    { id: 'kv_alcohol_beer_heineken', title: 'Alcohol-Beer-Heineken' },
    { id: 'kv_alcohol_beer_michelob', title: 'Alcohol-Beer-Michelob' },
    { id: 'kv_alcohol_beer_miller', title: 'Alcohol-Beer-Miller' },
    { id: 'kv_alcohol_beer_miller-lite', title: 'Alcohol-Beer-Miller Lite' },
    { id: 'kv_alcohol_beer_modelo', title: 'Alcohol-Beer-Modelo' },
    { id: 'kv_alcohol_beer_natural-light', title: 'Alcohol-Beer-Natural Light' },
    { id: 'kv_alcohol_beer_newcastle', title: 'Alcohol-Beer-Newcastle' },
    { id: 'kv_alcohol_beer_pbr', title: 'Alcohol-Beer-PBR' },
    { id: 'kv_alcohol_beer_sam-adams', title: 'Alcohol-Beer-Sam Adams' },
    { id: 'kv_alcohol_beer_sierra-nevada', title: 'Alcohol-Beer-Sierra Nevada' },
    { id: 'kv_alcohol_beer_stella-artois', title: 'Alcohol-Beer-Stella Artois' },
    { id: 'kv_alcohol_beer_tecate', title: 'Alcohol-Beer-Tecate' },
    { id: 'kv_alcohol_beer_yuengling', title: 'Alcohol-Beer-Yuengling' },
    { id: 'kv_alcohol_hard-cider', title: 'Alcohol-Hard Cider' },
    { id: 'kv_alcohol_hard-cider_angry-orchard', title: 'Alcohol-Hard Cider-Angry Orchard' },
    { id: 'kv_alcohol_hard-seltzer', title: 'Alcohol-Hard Seltzer' },
    { id: 'kv_alcohol_hard-seltzer_mikes-hard', title: 'Alcohol-Hard Seltzer-Mikes Hard' },
    { id: 'kv_alcohol_hard-seltzer_white-claw', title: 'Alcohol-Hard Seltzer-White Claw' },
    { id: 'kv_alcohol_liquor', title: 'Alcohol-Liquor' },
    { id: 'kv_alcohol_liquor_absolut', title: 'Alcohol-Liquor-Absolut' },
    { id: 'kv_alcohol_liquor_bacardi', title: 'Alcohol-Liquor-Bacardi' },
    { id: 'kv_alcohol_liquor_baileys', title: 'Alcohol-Liquor-Baileys' },
    { id: 'kv_alcohol_liquor_captain-morgan', title: 'Alcohol-Liquor-Captain Morgan' },
    { id: 'kv_alcohol_liquor_crown-royal', title: 'Alcohol-Liquor-Crown Royal' },
    { id: 'kv_alcohol_liquor_grey-goose', title: 'Alcohol-Liquor-Grey Goose' },
    { id: 'kv_alcohol_liquor_hennessy', title: 'Alcohol-Liquor-Hennessy' },
    { id: 'kv_alcohol_liquor_j&b', title: 'Alcohol-Liquor-J&B' },
    { id: 'kv_alcohol_liquor_jack-daniels', title: 'Alcohol-Liquor-Jack Daniels' },
    { id: 'kv_alcohol_liquor_jagermeister', title: 'Alcohol-Liquor-Jagermeister' },
    { id: 'kv_alcohol_liquor_jameson', title: 'Alcohol-Liquor-Jameson' },
    { id: 'kv_alcohol_liquor_jim-beam', title: 'Alcohol-Liquor-Jim Beam' },
    { id: 'kv_alcohol_liquor_johnnie-walker', title: 'Alcohol-Liquor-Johnnie Walker' },
    { id: 'kv_alcohol_liquor_jose-cuervo', title: 'Alcohol-Liquor-Jose Cuervo' },
    { id: 'kv_alcohol_liquor_kahlua', title: 'Alcohol-Liquor-Kahlua' },
    { id: 'kv_alcohol_liquor_ketel-one', title: 'Alcohol-Liquor-Ketel One' },
    { id: 'kv_alcohol_liquor_makers-mark', title: 'Alcohol-Liquor-Makers Mark' },
    { id: 'kv_alcohol_liquor_malibu', title: 'Alcohol-Liquor-Malibu' },
    { id: 'kv_alcohol_liquor_patron', title: 'Alcohol-Liquor-Patron' },
    { id: 'kv_alcohol_liquor_remy-martin', title: 'Alcohol-Liquor-Remy Martin' },
    { id: 'kv_alcohol_liquor_seagrams', title: 'Alcohol-Liquor-Seagrams' },
    { id: 'kv_alcohol_liquor_skyy', title: 'Alcohol-Liquor-SKYY' },
    { id: 'kv_alcohol_liquor_smirnoff', title: 'Alcohol-Liquor-Smirnoff' },
    { id: 'kv_alcohol_liquor_wild-turkey', title: 'Alcohol-Liquor-Wild Turkey' },
    { id: 'kv_apparel', title: 'Apparel' },
    { id: 'kv_apparel_abercrombie-&-fitch', title: 'Apparel-Abercrombie & Fitch' },
    { id: 'kv_apparel_adidas', title: 'Apparel-Adidas' },
    { id: 'kv_apparel_american-eagle', title: 'Apparel-American Eagle' },
    { id: 'kv_apparel_asics', title: 'Apparel-Asics' },
    { id: 'kv_apparel_banana-republic', title: 'Apparel-Banana Republic' },
    { id: 'kv_apparel_brooks-brothers', title: 'Apparel-Brooks Brothers' },
    { id: 'kv_apparel_calvin-klein', title: 'Apparel-Calvin Klein' },
    { id: 'kv_apparel_carhartt', title: 'Apparel-Carhartt' },
    { id: 'kv_apparel_coach', title: 'Apparel-Coach' },
    { id: 'kv_apparel_converse', title: 'Apparel-Converse' },
    { id: 'kv_apparel_dickies', title: 'Apparel-Dickies' },
    { id: 'kv_apparel_eddie-bauer', title: 'Apparel-Eddie Bauer' },
    { id: 'kv_apparel_gap', title: 'Apparel-Gap' },
    { id: 'kv_apparel_hanes', title: 'Apparel-Hanes' },
    { id: 'kv_apparel_hollister', title: 'Apparel-Hollister' },
    { id: 'kv_apparel_j.-crew', title: 'Apparel-J. Crew' },
    { id: 'kv_apparel_kate-spade', title: 'Apparel-Kate Spade' },
    { id: 'kv_apparel_levi', title: 'Apparel-Levi' },
    { id: 'kv_apparel_ll-bean', title: 'Apparel-LL Bean' },
    { id: 'kv_apparel_new-balance', title: 'Apparel-New Balance' },
    { id: 'kv_apparel_nike', title: 'Apparel-Nike' },
    { id: 'kv_apparel_old-navy', title: 'Apparel-Old Navy' },
    { id: 'kv_apparel_patagonia', title: 'Apparel-Patagonia' },
    { id: 'kv_apparel_polo-ralph-lauren', title: 'Apparel-Polo Ralph Lauren' },
    { id: 'kv_apparel_reebok', title: 'Apparel-Reebok' },
    { id: 'kv_apparel_starter', title: 'Apparel-Starter' },
    { id: 'kv_apparel_supreme', title: 'Apparel-Supreme' },
    { id: 'kv_apparel_the-north-face', title: 'Apparel-The North Face' },
    { id: 'kv_apparel_tommy-hilfiger', title: 'Apparel-Tommy Hilfiger' },
    { id: 'kv_apparel_under-armour', title: 'Apparel-Under Armour' },
    { id: 'kv_apparel_vans', title: 'Apparel-Vans' },
    { id: 'kv_apparel_victorias-secret', title: 'Apparel-Victorias Secret' },
    { id: 'kv_auto', title: 'Auto' },
    { id: 'kv_auto_american', title: 'Auto-American' },
    { id: 'kv_auto_american_buick', title: 'Auto-American-Buick' },
    { id: 'kv_auto_american_chevrolet', title: 'Auto-American-Chevrolet' },
    { id: 'kv_auto_american_chrysler', title: 'Auto-American-Chrysler' },
    { id: 'kv_auto_american_dodge', title: 'Auto-American-Dodge' },
    { id: 'kv_auto_american_ford', title: 'Auto-American-Ford' },
    { id: 'kv_auto_american_gmc', title: 'Auto-American-GMC' },
    { id: 'kv_auto_american_jeep', title: 'Auto-American-Jeep' },
    { id: 'kv_auto_american_pontiac', title: 'Auto-American-Pontiac' },
    { id: 'kv_auto_american_ram', title: 'Auto-American-Ram' },
    { id: 'kv_auto_import', title: 'Auto-Import' },
    { id: 'kv_auto_import_fiat', title: 'Auto-Import-Fiat' },
    { id: 'kv_auto_import_honda', title: 'Auto-Import-Honda' },
    { id: 'kv_auto_import_hyundai', title: 'Auto-Import-Hyundai' },
    { id: 'kv_auto_import_kia', title: 'Auto-Import-Kia' },
    { id: 'kv_auto_import_mazda', title: 'Auto-Import-Mazda' },
    { id: 'kv_auto_import_mini-cooper', title: 'Auto-Import-Mini Cooper' },
    { id: 'kv_auto_import_mitsubishi', title: 'Auto-Import-Mitsubishi' },
    { id: 'kv_auto_import_nissan', title: 'Auto-Import-Nissan' },
    { id: 'kv_auto_import_subaru', title: 'Auto-Import-Subaru' },
    { id: 'kv_auto_import_suzuki', title: 'Auto-Import-Suzuki' },
    { id: 'kv_auto_import_toyota', title: 'Auto-Import-Toyota' },
    { id: 'kv_auto_import_volkswagen', title: 'Auto-Import-Volkswagen' },
    { id: 'kv_auto_import_volvo', title: 'Auto-Import-Volvo' },
    { id: 'kv_auto_luxury', title: 'Auto-Luxury' },
    { id: 'kv_auto_luxury_acura', title: 'Auto-Luxury-Acura' },
    { id: 'kv_auto_luxury_audi', title: 'Auto-Luxury-Audi' },
    { id: 'kv_auto_luxury_bmw', title: 'Auto-Luxury-BMW' },
    { id: 'kv_auto_luxury_bugatti', title: 'Auto-Luxury-Bugatti' },
    { id: 'kv_auto_luxury_cadillac', title: 'Auto-Luxury-Cadillac' },
    { id: 'kv_auto_luxury_ferrari', title: 'Auto-Luxury-Ferrari' },
    { id: 'kv_auto_luxury_infiniti', title: 'Auto-Luxury-Infiniti' },
    { id: 'kv_auto_luxury_jaguar', title: 'Auto-Luxury-Jaguar' },
    { id: 'kv_auto_luxury_lamborghini', title: 'Auto-Luxury-Lamborghini' },
    { id: 'kv_auto_luxury_land-rover', title: 'Auto-Luxury-Land Rover' },
    { id: 'kv_auto_luxury_lexus', title: 'Auto-Luxury-Lexus' },
    { id: 'kv_auto_luxury_lincoln', title: 'Auto-Luxury-Lincoln' },
    { id: 'kv_auto_luxury_maserati', title: 'Auto-Luxury-Maserati' },
    { id: 'kv_auto_luxury_mercedes', title: 'Auto-Luxury-Mercedes' },
    { id: 'kv_auto_luxury_porsche', title: 'Auto-Luxury-Porsche' },
    { id: 'kv_auto_luxury_tesla', title: 'Auto-Luxury-Tesla' },
    { id: 'kv_baby', title: 'Baby' },
    { id: 'kv_baby_diapers', title: 'Baby-Diapers' },
    { id: 'kv_baby_diapers_luvs', title: 'Baby-Diapers-Luvs' },
    { id: 'kv_baby_diapers_pampers', title: 'Baby-Diapers-Pampers' },
    { id: 'kv_beauty', title: 'Beauty' },
    { id: 'kv_beauty_makeup', title: 'Beauty-Makeup' },
    { id: 'kv_beauty_makeup_avon', title: 'Beauty-Makeup-Avon' },
    { id: 'kv_beauty_makeup_bareminerals', title: 'Beauty-Makeup-bareMinerals' },
    { id: 'kv_beauty_makeup_bobbi-brown', title: 'Beauty-Makeup-Bobbi Brown' },
    { id: 'kv_beauty_makeup_clarins', title: 'Beauty-Makeup-Clarins' },
    { id: 'kv_beauty_makeup_clinique', title: 'Beauty-Makeup-Clinique' },
    { id: 'kv_beauty_makeup_cover-fx', title: 'Beauty-Makeup-Cover FX' },
    { id: 'kv_beauty_makeup_covergirl', title: 'Beauty-Makeup-Covergirl' },
    { id: 'kv_beauty_makeup_estee-lauder', title: 'Beauty-Makeup-Estee Lauder' },
    { id: 'kv_beauty_makeup_lancome', title: 'Beauty-Makeup-Lancome' },
    { id: 'kv_beauty_makeup_mac', title: 'Beauty-Makeup-MAC' },
    { id: 'kv_beauty_makeup_maybelline', title: 'Beauty-Makeup-Maybelline' },
    { id: 'kv_beauty_makeup_nars', title: 'Beauty-Makeup-NARS' },
    { id: 'kv_beauty_makeup_nivea', title: 'Beauty-Makeup-Nivea' },
    { id: 'kv_beauty_makeup_nyx', title: 'Beauty-Makeup-NYX' },
    { id: 'kv_beauty_makeup_olay', title: 'Beauty-Makeup-Olay' },
    { id: 'kv_beauty_makeup_revlon', title: 'Beauty-Makeup-Revlon' },
    { id: 'kv_beauty_makeup_sephora', title: 'Beauty-Makeup-Sephora' },
    { id: 'kv_beauty_makeup_shiseido', title: 'Beauty-Makeup-Shiseido' },
    { id: 'kv_beauty_makeup_urban-decay', title: 'Beauty-Makeup-Urban Decay' },
    { id: 'kv_beauty_makeup_yves-saint-laurent', title: 'Beauty-Makeup-Yves Saint Laurent' },
    { id: 'kv_beauty_perfume', title: 'Beauty-Perfume' },
    { id: 'kv_beauty_perfume_axe', title: 'Beauty-Perfume-Axe' },
    { id: 'kv_beauty_perfume_chanel', title: 'Beauty-Perfume-Chanel' },
    { id: 'kv_beauty_perfume_dior', title: 'Beauty-Perfume-Dior' },
    { id: 'kv_beauty_perfume_givenchy', title: 'Beauty-Perfume-Givenchy' },
    { id: 'kv_beauty_shampoo', title: 'Beauty-Shampoo' },
    { id: 'kv_beauty_shampoo_loreal', title: 'Beauty-Shampoo-LOreal' },
    { id: 'kv_beauty_biore', title: 'Beauty-Biore' },
    { id: 'kv_beauty_garnier', title: 'Beauty-Garnier' },
    { id: 'kv_beauty_johnson-&-johnson', title: 'Beauty-Johnson & Johnson' },
    { id: 'kv_beauty_neutrogena', title: 'Beauty-Neutrogena' },
    { id: 'kv_convenience', title: 'Convenience' },
    { id: 'kv_convenience_7-eleven', title: 'Convenience-7-Eleven' },
    { id: 'kv_energy', title: 'Energy' },
    { id: 'kv_energy_electricity', title: 'Energy-Electricity' },
    { id: 'kv_energy_electricity_ge', title: 'Energy-Electricity-GE' },
    { id: 'kv_energy_oil/gas', title: 'Energy-Oil/Gas' },
    { id: 'kv_energy_oil/gas_bp', title: 'Energy-Oil/Gas-BP' },
    { id: 'kv_energy_oil/gas_exxon-mobil', title: 'Energy-Oil/Gas-Exxon Mobil' },
    { id: 'kv_energy_oil/gas_shell-oil-co', title: 'Energy-Oil/Gas-Shell Oil Co' },
    { id: 'kv_entertainment', title: 'Entertainment' },
    { id: 'kv_entertainment_abc', title: 'Entertainment-ABC' },
    { id: 'kv_entertainment_bbc', title: 'Entertainment-BBC' },
    { id: 'kv_entertainment_cbs', title: 'Entertainment-CBS' },
    { id: 'kv_entertainment_cnn', title: 'Entertainment-CNN' },
    { id: 'kv_entertainment_comedy-central', title: 'Entertainment-Comedy Central' },
    { id: 'kv_entertainment_disney', title: 'Entertainment-Disney' },
    { id: 'kv_entertainment_dreamworks', title: 'Entertainment-Dreamworks' },
    { id: 'kv_entertainment_fox', title: 'Entertainment-Fox' },
    { id: 'kv_entertainment_mtv', title: 'Entertainment-MTV' },
    { id: 'kv_entertainment_nbc', title: 'Entertainment-NBC' },
    { id: 'kv_entertainment_nickelodeon', title: 'Entertainment-Nickelodeon' },
    { id: 'kv_entertainment_npr', title: 'Entertainment-NPR' },
    { id: 'kv_entertainment_paramount', title: 'Entertainment-Paramount' },
    { id: 'kv_entertainment_pbs', title: 'Entertainment-PBS' },
    { id: 'kv_entertainment_universal', title: 'Entertainment-Universal' },
    { id: 'kv_entertainment_warner-bros', title: 'Entertainment-Warner Bros' },
    { id: 'kv_finance', title: 'Finance' },
    { id: 'kv_finance_bank', title: 'Finance-Bank' },
    { id: 'kv_finance_bank_bank-of-america', title: 'Finance-Bank-Bank of America' },
    { id: 'kv_finance_bank_fifth-third-bank', title: 'Finance-Bank-Fifth Third Bank' },
    { id: 'kv_finance_bank_wells-fargo', title: 'Finance-Bank-Wells Fargo' },
    { id: 'kv_finance_credit-card', title: 'Finance-Credit Card' },
    {
      id: 'kv_finance_credit-card_american-express',
      title: 'Finance-Credit Card-American Express',
    },
    { id: 'kv_finance_credit-card_discover', title: 'Finance-Credit Card-Discover' },
    { id: 'kv_finance_credit-card_mastercard', title: 'Finance-Credit Card-Mastercard' },
    { id: 'kv_finance_credit-card_visa', title: 'Finance-Credit Card-Visa' },
    { id: 'kv_finance_capital-one', title: 'Finance-Capital One' },
    { id: 'kv_finance_charles-schwab', title: 'Finance-Charles Schwab' },
    { id: 'kv_finance_citigroup', title: 'Finance-Citigroup' },
    { id: 'kv_finance_goldman-sachs', title: 'Finance-Goldman Sachs' },
    { id: 'kv_finance_hsbc', title: 'Finance-HSBC' },
    { id: 'kv_finance_jpmorgan-chase', title: 'Finance-JPMorgan Chase' },
    { id: 'kv_finance_morgan-stanley', title: 'Finance-Morgan Stanley' },
    { id: 'kv_finance_pnc', title: 'Finance-PNC' },
    { id: 'kv_finance_td-bank', title: 'Finance-TD Bank' },
    { id: 'kv_finance_us-bancorp', title: 'Finance-US Bancorp' },
    { id: 'kv_finance_usaa', title: 'Finance-USAA' },
    { id: 'kv_grocery', title: 'Grocery' },
    { id: 'kv_grocery_albertsons', title: 'Grocery-Albertsons' },
    { id: 'kv_grocery_aldi', title: 'Grocery-Aldi' },
    { id: 'kv_grocery_bi-lo', title: 'Grocery-BI-LO' },
    { id: 'kv_grocery_food-lion', title: 'Grocery-Food Lion' },
    { id: 'kv_grocery_heb', title: 'Grocery-HEB' },
    { id: 'kv_grocery_king-soopers', title: 'Grocery-King Soopers' },
    { id: 'kv_grocery_kroger', title: 'Grocery-Kroger' },
    { id: 'kv_grocery_publix', title: 'Grocery-Publix' },
    { id: 'kv_grocery_randalls', title: 'Grocery-Randalls' },
    { id: 'kv_grocery_sweetbay', title: 'Grocery-Sweetbay' },
    { id: 'kv_grocery_trader-joes', title: 'Grocery-Trader Joes' },
    { id: 'kv_grocery_wegmans', title: 'Grocery-Wegmans' },
    { id: 'kv_grocery_whole-foods', title: 'Grocery-Whole Foods' },
    { id: 'kv_health', title: 'Health' },
    { id: 'kv_health_dignity-health', title: 'Health-Dignity Health' },
    { id: 'kv_home-goods', title: 'Home Goods' },
    { id: 'kv_home-goods_cleaning', title: 'Home Goods-Cleaning' },
    { id: 'kv_home-goods_cleaning_bounce', title: 'Home Goods-Cleaning-Bounce' },
    { id: 'kv_home-goods_cleaning_bounty', title: 'Home Goods-Cleaning-Bounty' },
    { id: 'kv_home-goods_cleaning_cascade', title: 'Home Goods-Cleaning-Cascade' },
    { id: 'kv_home-goods_cleaning_cheer', title: 'Home Goods-Cleaning-Cheer' },
    { id: 'kv_home-goods_cleaning_clorox', title: 'Home Goods-Cleaning-Clorox' },
    { id: 'kv_home-goods_cleaning_dawn', title: 'Home Goods-Cleaning-Dawn' },
    { id: 'kv_home-goods_cleaning_downy', title: 'Home Goods-Cleaning-Downy' },
    { id: 'kv_home-goods_cleaning_era', title: 'Home Goods-Cleaning-Era' },
    { id: 'kv_home-goods_cleaning_febreze', title: 'Home Goods-Cleaning-Febreze' },
    { id: 'kv_home-goods_cleaning_gain', title: 'Home Goods-Cleaning-Gain' },
    { id: 'kv_home-goods_cleaning_lysol', title: 'Home Goods-Cleaning-Lysol' },
    { id: 'kv_home-goods_cleaning_mr.-clean', title: 'Home Goods-Cleaning-Mr. Clean' },
    { id: 'kv_home-goods_cleaning_swiffer', title: 'Home Goods-Cleaning-Swiffer' },
    { id: 'kv_home-goods_cleaning_tide', title: 'Home Goods-Cleaning-Tide' },
    { id: 'kv_home-goods_kitchen', title: 'Home Goods-Kitchen' },
    { id: 'kv_home-goods_kitchen_palmolive', title: 'Home Goods-Kitchen-Palmolive' },
    { id: 'kv_home-goods_mens', title: 'Home Goods-Mens' },
    { id: 'kv_home-goods_mens_braun', title: 'Home Goods-Mens-Braun' },
    { id: 'kv_home-goods_mens_gillette', title: 'Home Goods-Mens-Gillette' },
    { id: 'kv_home-goods_mens_old-spice', title: 'Home Goods-Mens-Old Spice' },
    { id: 'kv_home-goods_oral-hygiene', title: 'Home Goods-Oral Hygiene' },
    { id: 'kv_home-goods_oral-hygiene_crest', title: 'Home Goods-Oral Hygiene-Crest' },
    { id: 'kv_home-goods_oral-hygiene_fixodent', title: 'Home Goods-Oral Hygiene-Fixodent' },
    { id: 'kv_home-goods_oral-hygiene_oral-b', title: 'Home Goods-Oral Hygiene-Oral-B' },
    { id: 'kv_home-goods_oral-hygiene_scope', title: 'Home Goods-Oral Hygiene-Scope' },
    { id: 'kv_home-goods_shampoo', title: 'Home Goods-Shampoo' },
    { id: 'kv_home-goods_shampoo_aussie', title: 'Home Goods-Shampoo-Aussie' },
    { id: 'kv_home-goods_shampoo_head-&-shoulders', title: 'Home Goods-Shampoo-Head & Shoulders' },
    { id: 'kv_home-goods_shampoo_herbal-essences', title: 'Home Goods-Shampoo-Herbal Essences' },
    { id: 'kv_home-goods_shampoo_pantene', title: 'Home Goods-Shampoo-Pantene' },
    { id: 'kv_home-goods_soap', title: 'Home Goods-Soap' },
    { id: 'kv_home-goods_soap_ivory', title: 'Home Goods-Soap-Ivory' },
    { id: 'kv_home-goods_toiletry', title: 'Home Goods-Toiletry' },
    { id: 'kv_home-goods_toiletry_charmin', title: 'Home Goods-Toiletry-Charmin' },
    { id: 'kv_home-goods_womens', title: 'Home Goods-Womens' },
    { id: 'kv_home-goods_womens_always', title: 'Home Goods-Womens-Always' },
    { id: 'kv_home-goods_womens_secret', title: 'Home Goods-Womens-Secret' },
    { id: 'kv_home-goods_womens_tampax', title: 'Home Goods-Womens-Tampax' },
    { id: 'kv_home-goods_puffs', title: 'Home Goods-Puffs' },
    { id: 'kv_insurance', title: 'Insurance' },
    { id: 'kv_insurance_auto', title: 'Insurance-Auto' },
    { id: 'kv_insurance_auto_allstate', title: 'Insurance-Auto-Allstate' },
    { id: 'kv_insurance_auto_geico', title: 'Insurance-Auto-Geico' },
    { id: 'kv_insurance_auto_state-farm', title: 'Insurance-Auto-State Farm' },
    { id: 'kv_insurance_homeowner', title: 'Insurance-Homeowner' },
    { id: 'kv_insurance_homeowner_travelers', title: 'Insurance-Homeowner-Travelers' },
    { id: 'kv_medicine', title: 'Medicine' },
    { id: 'kv_medicine_otc', title: 'Medicine-OTC' },
    { id: 'kv_medicine_otc_pepto-bismol', title: 'Medicine-OTC-Pepto Bismol' },
    { id: 'kv_medicine_otc_prilosec', title: 'Medicine-OTC-Prilosec' },
    { id: 'kv_medicine_otc_vicks', title: 'Medicine-OTC-Vicks' },
    { id: 'kv_medicine_supplement', title: 'Medicine-Supplement' },
    { id: 'kv_medicine_supplement_metamucil', title: 'Medicine-Supplement-Metamucil' },
    { id: 'kv_pharmacy', title: 'Pharmacy' },
    { id: 'kv_pharmacy_cvs', title: 'Pharmacy-CVS' },
    { id: 'kv_pharmacy_rite-aid', title: 'Pharmacy-Rite Aid' },
    { id: 'kv_pharmacy_walgreens', title: 'Pharmacy-Walgreens' },
    { id: 'kv_qsr', title: 'QSR' },
    { id: 'kv_qsr_asian', title: 'QSR-Asian' },
    { id: 'kv_qsr_asian_panda-express', title: 'QSR-Asian-Panda Express' },
    { id: 'kv_qsr_burgers', title: 'QSR-Burgers' },
    { id: 'kv_qsr_burgers_burger-king', title: 'QSR-Burgers-Burger King' },
    { id: 'kv_qsr_burgers_carls-jr.', title: 'QSR-Burgers-Carls Jr.' },
    { id: 'kv_qsr_burgers_checkers/rallys', title: 'QSR-Burgers-Checkers/Rallys' },
    { id: 'kv_qsr_burgers_five-guys', title: 'QSR-Burgers-Five Guys' },
    { id: 'kv_qsr_burgers_hardees', title: 'QSR-Burgers-Hardees' },
    { id: 'kv_qsr_burgers_in-n-out-burger', title: 'QSR-Burgers-In-N-Out Burger' },
    { id: 'kv_qsr_burgers_jack-in-the-box', title: 'QSR-Burgers-Jack in the Box' },
    { id: 'kv_qsr_burgers_krystal', title: 'QSR-Burgers-Krystal' },
    { id: 'kv_qsr_burgers_mcdonalds', title: 'QSR-Burgers-McDonalds' },
    { id: 'kv_qsr_burgers_sonic', title: 'QSR-Burgers-Sonic' },
    { id: 'kv_qsr_burgers_steak-n-shake', title: 'QSR-Burgers-Steak N Shake' },
    { id: 'kv_qsr_burgers_wendys', title: 'QSR-Burgers-Wendys' },
    { id: 'kv_qsr_burgers_whataburger', title: 'QSR-Burgers-Whataburger' },
    { id: 'kv_qsr_burgers_white-castle', title: 'QSR-Burgers-White Castle' },
    { id: 'kv_qsr_chicken', title: 'QSR-Chicken' },
    { id: 'kv_qsr_chicken_bojangles', title: 'QSR-Chicken-Bojangles' },
    { id: 'kv_qsr_chicken_boston-market', title: 'QSR-Chicken-Boston Market' },
    { id: 'kv_qsr_chicken_chick-fil-a', title: 'QSR-Chicken-Chick-Fil-A' },
    { id: 'kv_qsr_chicken_churchs-chicken', title: 'QSR-Chicken-Churchs Chicken' },
    { id: 'kv_qsr_chicken_kfc', title: 'QSR-Chicken-KFC' },
    { id: 'kv_qsr_chicken_popeyes', title: 'QSR-Chicken-Popeyes' },
    { id: 'kv_qsr_chicken_zaxbys', title: 'QSR-Chicken-Zaxbys' },
    { id: 'kv_qsr_coffee', title: 'QSR-Coffee' },
    { id: 'kv_qsr_coffee_dunkin', title: 'QSR-Coffee-Dunkin' },
    { id: 'kv_qsr_coffee_starbucks', title: 'QSR-Coffee-Starbucks' },
    { id: 'kv_qsr_ice-cream', title: 'QSR-Ice Cream' },
    { id: 'kv_qsr_ice-cream_baskin-robbins', title: 'QSR-Ice Cream-Baskin Robbins' },
    { id: 'kv_qsr_ice-cream_dairy-queen', title: 'QSR-Ice Cream-Dairy Queen' },
    { id: 'kv_qsr_mexican', title: 'QSR-Mexican' },
    { id: 'kv_qsr_mexican_chipotle', title: 'QSR-Mexican-Chipotle' },
    { id: 'kv_qsr_mexican_del-taco', title: 'QSR-Mexican-Del Taco' },
    { id: 'kv_qsr_mexican_qdoba', title: 'QSR-Mexican-Qdoba' },
    { id: 'kv_qsr_mexican_taco-bell', title: 'QSR-Mexican-Taco Bell' },
    { id: 'kv_qsr_pizza', title: 'QSR-Pizza' },
    { id: 'kv_qsr_pizza_cicis-pizza', title: 'QSR-Pizza-CiCis Pizza' },
    { id: 'kv_qsr_pizza_dominos-pizza', title: 'QSR-Pizza-Dominos PIzza' },
    { id: 'kv_qsr_pizza_little-caesars', title: 'QSR-Pizza-Little Caesars' },
    { id: 'kv_qsr_pizza_papa-johns', title: 'QSR-Pizza-Papa Johns' },
    { id: 'kv_qsr_pizza_pizza-hut', title: 'QSR-Pizza-Pizza Hut' },
    { id: 'kv_qsr_sandwiches', title: 'QSR-Sandwiches' },
    { id: 'kv_qsr_sandwiches_arbys', title: 'QSR-Sandwiches-Arbys' },
    { id: 'kv_qsr_sandwiches_jasons-deli', title: 'QSR-Sandwiches-Jasons Deli' },
    { id: 'kv_qsr_sandwiches_panera-bread-co', title: 'QSR-Sandwiches-Panera Bread Co' },
    { id: 'kv_qsr_sandwiches_quiznos', title: 'QSR-Sandwiches-Quiznos' },
    { id: 'kv_qsr_sandwiches_subway', title: 'QSR-Sandwiches-Subway' },
    { id: 'kv_qsr_seafood', title: 'QSR-Seafood' },
    { id: 'kv_qsr_seafood_captain-ds', title: 'QSR-Seafood-Captain Ds' },
    { id: 'kv_qsr_seafood_long-john-silvers', title: 'QSR-Seafood-Long John Silvers' },
    { id: 'kv_retail', title: 'Retail' },
    { id: 'kv_retail_academy-sports', title: 'Retail-Academy Sports' },
    { id: 'kv_retail_ace-hardware', title: 'Retail-Ace Hardware' },
    { id: 'kv_retail_ashley-home', title: 'Retail-Ashley Home' },
    { id: 'kv_retail_autozone', title: 'Retail-AutoZone' },
    { id: 'kv_retail_barnes-and-noble', title: 'Retail-Barnes and Noble' },
    { id: 'kv_retail_bass-pro-shops', title: 'Retail-Bass Pro Shops' },
    { id: 'kv_retail_bed,-bath-and-beyond', title: 'Retail-Bed, Bath and Beyond' },
    { id: 'kv_retail_belk', title: 'Retail-Belk' },
    { id: 'kv_retail_best-buy', title: 'Retail-Best Buy' },
    { id: 'kv_retail_big-lots', title: 'Retail-Big Lots' },
    { id: 'kv_retail_bjs', title: 'Retail-BJs' },
    { id: 'kv_retail_bloomingdales', title: 'Retail-Bloomingdales' },
    { id: 'kv_retail_cabelas', title: 'Retail-Cabelas' },
    { id: 'kv_retail_costco', title: 'Retail-Costco' },
    { id: 'kv_retail_crate-&-barrel', title: 'Retail-Crate & Barrel' },
    { id: 'kv_retail_dicks-sporting-goods', title: 'Retail-Dicks Sporting Goods' },
    { id: 'kv_retail_dillards', title: 'Retail-Dillards' },
    { id: 'kv_retail_foot-locker', title: 'Retail-Foot Locker' },
    { id: 'kv_retail_gamestop', title: 'Retail-Gamestop' },
    { id: 'kv_retail_havertys', title: 'Retail-Havertys' },
    { id: 'kv_retail_hobby-lobby', title: 'Retail-Hobby Lobby' },
    { id: 'kv_retail_ikea', title: 'Retail-Ikea' },
    { id: 'kv_retail_jcpenny', title: 'Retail-JCPenny' },
    { id: 'kv_retail_kohls', title: 'Retail-Kohls' },
    { id: 'kv_retail_lowes', title: 'Retail-Lowes' },
    { id: 'kv_retail_macys', title: 'Retail-Macys' },
    { id: 'kv_retail_michaels', title: 'Retail-Michaels' },
    { id: 'kv_retail_neiman-marcus', title: 'Retail-Neiman Marcus' },
    { id: 'kv_retail_nordstrom', title: 'Retail-Nordstrom' },
    { id: 'kv_retail_oreilly-auto-parts', title: 'Retail-OReilly Auto Parts' },
    { id: 'kv_retail_office-depot', title: 'Retail-Office Depot' },
    { id: 'kv_retail_party-city', title: 'Retail-Party City' },
    { id: 'kv_retail_petsmart', title: 'Retail-PetSmart' },
    { id: 'kv_retail_proctor-&-gamble', title: 'Retail-Proctor & Gamble' },
    { id: 'kv_retail_rooms-to-go', title: 'Retail-Rooms To Go' },
    { id: 'kv_retail_ross', title: 'Retail-Ross' },
    { id: 'kv_retail_staples', title: 'Retail-Staples' },
    { id: 'kv_retail_target', title: 'Retail-Target' },
    { id: 'kv_retail_the-container-store', title: 'Retail-The Container Store' },
    { id: 'kv_retail_the-home-depot', title: 'Retail-The Home Depot' },
    { id: 'kv_retail_walmart', title: 'Retail-Walmart' },
    { id: 'kv_retail_wayfair', title: 'Retail-Wayfair' },
    { id: 'kv_retail_william-sonoma', title: 'Retail-William Sonoma' },
    { id: 'kv_retail_zulily', title: 'Retail-Zulily' },
    { id: 'kv_shipping', title: 'Shipping' },
    { id: 'kv_shipping_dhl', title: 'Shipping-DHL' },
    { id: 'kv_shipping_fedex', title: 'Shipping-FedEx' },
    { id: 'kv_shipping_ups', title: 'Shipping-UPS' },
    { id: 'kv_soft-drink', title: 'Soft Drink' },
    { id: 'kv_soft-drink_coffee/tea', title: 'Soft Drink-Coffee/Tea' },
    { id: 'kv_soft-drink_coffee/tea_folgers', title: 'Soft Drink-Coffee/Tea-Folgers' },
    { id: 'kv_soft-drink_coffee/tea_lipton', title: 'Soft Drink-Coffee/Tea-Lipton' },
    { id: 'kv_soft-drink_energy-drink', title: 'Soft Drink-Energy Drink' },
    { id: 'kv_soft-drink_energy-drink_gatorade', title: 'Soft Drink-Energy Drink-Gatorade' },
    { id: 'kv_soft-drink_energy-drink_monster', title: 'Soft Drink-Energy Drink-Monster' },
    { id: 'kv_soft-drink_energy-drink_red-buil', title: 'Soft Drink-Energy Drink-Red Buil' },
    { id: 'kv_soft-drink_seltzer', title: 'Soft Drink-Seltzer' },
    { id: 'kv_soft-drink_seltzer_la-croix', title: 'Soft Drink-Seltzer-La Croix' },
    { id: 'kv_soft-drink_seltzer_perrier', title: 'Soft Drink-Seltzer-Perrier' },
    { id: 'kv_soft-drink_seltzer_topo-chico', title: 'Soft Drink-Seltzer-Topo Chico' },
    { id: 'kv_soft-drink_soda', title: 'Soft Drink-Soda' },
    { id: 'kv_soft-drink_soda_coca-cola', title: 'Soft Drink-Soda-Coca Cola' },
    { id: 'kv_soft-drink_soda_dr.-pepper', title: 'Soft Drink-Soda-Dr. Pepper' },
    { id: 'kv_soft-drink_soda_fanta', title: 'Soft Drink-Soda-Fanta' },
    { id: 'kv_soft-drink_soda_minute-maid', title: 'Soft Drink-Soda-Minute Maid' },
    { id: 'kv_soft-drink_soda_mountain-dew', title: 'Soft Drink-Soda-Mountain Dew' },
    { id: 'kv_soft-drink_soda_pepsi', title: 'Soft Drink-Soda-Pepsi' },
    { id: 'kv_soft-drink_soda_sprite', title: 'Soft Drink-Soda-Sprite' },
    { id: 'kv_soft-drink_soda_sunkist', title: 'Soft Drink-Soda-Sunkist' },
    { id: 'kv_soft-drink_water', title: 'Soft Drink-Water' },
    { id: 'kv_soft-drink_water_aquafina', title: 'Soft Drink-Water-Aquafina' },
    { id: 'kv_soft-drink_water_dasani', title: 'Soft Drink-Water-Dasani' },
    { id: 'kv_soft-drink_water_deer-park', title: 'Soft Drink-Water-Deer Park' },
    { id: 'kv_soft-drink_water_essentia', title: 'Soft Drink-Water-Essentia' },
    { id: 'kv_soft-drink_water_evian', title: 'Soft Drink-Water-Evian' },
    { id: 'kv_soft-drink_water_fiji', title: 'Soft Drink-Water-FIJI' },
    { id: 'kv_soft-drink_water_nestle', title: 'Soft Drink-Water-Nestle' },
    { id: 'kv_soft-drink_water_ozarka', title: 'Soft Drink-Water-Ozarka' },
    { id: 'kv_soft-drink_water_poland-spring', title: 'Soft Drink-Water-Poland Spring' },
    { id: 'kv_soft-drink_water_smart-water', title: 'Soft Drink-Water-Smart Water' },
    { id: 'kv_soft-drink_water_voss', title: 'Soft Drink-Water-VOSS' },
    { id: 'kv_tech', title: 'Tech' },
    { id: 'kv_tech_3m', title: 'Tech-3M' },
    { id: 'kv_tech_activision', title: 'Tech-Activision' },
    { id: 'kv_tech_adobe', title: 'Tech-Adobe' },
    { id: 'kv_tech_airbnb', title: 'Tech-Airbnb' },
    { id: 'kv_tech_amazon', title: 'Tech-Amazon' },
    { id: 'kv_tech_apple', title: 'Tech-Apple' },
    { id: 'kv_tech_at&t', title: 'Tech-AT&T' },
    { id: 'kv_tech_canon', title: 'Tech-Canon' },
    { id: 'kv_tech_cisco', title: 'Tech-Cisco' },
    { id: 'kv_tech_dell', title: 'Tech-Dell' },
    { id: 'kv_tech_dropbox', title: 'Tech-Dropbox' },
    { id: 'kv_tech_ea', title: 'Tech-EA' },
    { id: 'kv_tech_ebay', title: 'Tech-eBay' },
    { id: 'kv_tech_emerson', title: 'Tech-Emerson' },
    { id: 'kv_tech_facebook', title: 'Tech-Facebook' },
    { id: 'kv_tech_fujitsu', title: 'Tech-Fujitsu' },
    { id: 'kv_tech_google', title: 'Tech-Google' },
    { id: 'kv_tech_hp', title: 'Tech-HP' },
    { id: 'kv_tech_ibm', title: 'Tech-IBM' },
    { id: 'kv_tech_instagram', title: 'Tech-Instagram' },
    { id: 'kv_tech_intel', title: 'Tech-Intel' },
    { id: 'kv_tech_lenovo', title: 'Tech-Lenovo' },
    { id: 'kv_tech_lg', title: 'Tech-LG' },
    { id: 'kv_tech_linkedin', title: 'Tech-LinkedIn' },
    { id: 'kv_tech_lyft', title: 'Tech-Lyft' },
    { id: 'kv_tech_microsoft', title: 'Tech-Microsoft' },
    { id: 'kv_tech_netflix', title: 'Tech-Netflix' },
    { id: 'kv_tech_nintendo', title: 'Tech-Nintendo' },
    { id: 'kv_tech_nokia', title: 'Tech-Nokia' },
    { id: 'kv_tech_nvidia', title: 'Tech-Nvidia' },
    { id: 'kv_tech_oracle', title: 'Tech-Oracle' },
    { id: 'kv_tech_panasonic', title: 'Tech-Panasonic' },
    { id: 'kv_tech_playstation', title: 'Tech-Playstation' },
    { id: 'kv_tech_red-hat', title: 'Tech-Red Hat' },
    { id: 'kv_tech_salesforce', title: 'Tech-Salesforce' },
    { id: 'kv_tech_samsung', title: 'Tech-Samsung' },
    { id: 'kv_tech_sharp', title: 'Tech-Sharp' },
    { id: 'kv_tech_shopify', title: 'Tech-Shopify' },
    { id: 'kv_tech_sony', title: 'Tech-Sony' },
    { id: 'kv_tech_spotify', title: 'Tech-Spotify' },
    { id: 'kv_tech_sprint', title: 'Tech-Sprint' },
    { id: 'kv_tech_t-mobile', title: 'Tech-T-Mobile' },
    { id: 'kv_tech_tiktok', title: 'Tech-Tiktok' },
    { id: 'kv_tech_twitter', title: 'Tech-Twitter' },
    { id: 'kv_tech_uber', title: 'Tech-Uber' },
    { id: 'kv_tech_verizon', title: 'Tech-Verizon' },
    { id: 'kv_tech_vizio', title: 'Tech-Vizio' },
    { id: 'kv_tech_vmware', title: 'Tech-VMWare' },
    { id: 'kv_tech_western-digital', title: 'Tech-Western Digital' },
    { id: 'kv_tech_xbox', title: 'Tech-XBox' },
    { id: 'kv_tech_yahoo', title: 'Tech-Yahoo' },
    { id: 'kv_tech_youtube', title: 'Tech-YouTube' },
    { id: 'kv_tech_zillow', title: 'Tech-Zillow' },
  ];

  return generateIRISSegmentList(options, currentValues);
};

export const generateSilverBulletContextualSegmentList = (currentValues: string[]): IMenuList => {
  const options: IIRISOption[] = [
    { id: 'sb_ESeuGq34R4uuxiQ5DHPO0Q', title: '50 Cent' },
    { id: 'sb_TdmHpKyCQQeLxBfpjCfoGw', title: '6LACK' },
    { id: 'sb_M9ljQnDdQQuAMGciKgobDg', title: 'AA Insurance' },
    { id: 'sb_P41h7sRtSvCEWxBN9D1jNA', title: 'Aar√≥n Sanchez' },
    { id: 'sb_Vt7WaIwCSxiMOqAM3MAUdw', title: 'Aaron Donald' },
    { id: 'sb_T00QYMhHTG-2uZhVOV1xZw', title: 'Aaron Eckhart' },
    { id: 'sb_MEiJ0rxBRB6n_eMIIWYk7g', title: 'Aaron Franklin' },
    { id: 'sb_cLSlTeRWTzqC3_7BqRHCbQ', title: 'Aaron Judge' },
    { id: 'sb_Q-MI20LuRcKaXJN822GP9w', title: 'Aaron Paul' },
    { id: 'sb_VlA84fJXT0G69PU-7hVK9w', title: 'Aaron Rodgers' },
    { id: 'sb_JBWUgPmPRki9s8aV0rDgTA', title: 'Aaron Taylor-Johnson' },
    { id: 'sb_S3VnctC0TJeyUVS3qKXkWQ', title: 'Abell Pest Control' },
    { id: 'sb_d_qg0_jkSoSHA7S3OSc3DQ', title: 'Abubacarr Tambadou' },
    { id: 'sb_a9nrXWCoQOiVs3hDdBJD2Q', title: 'Acer' },
    { id: 'sb_Ot5ExfTgTVyx2vBx36B6uw', title: 'Adam Brody' },
    { id: 'sb_RMca1MC6SWakJYNUZe04yA', title: 'Adidas' },
    { id: 'sb_ayWPI7ukQfSF_0ZaRnBVVA', title: 'Admiral Insurance' },
    { id: 'sb_IxDYdLKuTz6MvRqGdZbEQg', title: 'Adobe' },
    { id: 'sb_FgYOGjE7SWOt_6qKrG55GQ', title: 'Adrien Brody' },
    { id: 'sb_cGiH-S1hQMmx9D82CSUY5A', title: 'Adventure travel' },
    { id: 'sb_IVoqYK53QYK-S-0EVWKDKw', title: 'Ady Barkan' },
    { id: 'sb_Fg38uekbQ3aBlcN7sEUncQ', title: 'Aegon Insurance' },
    { id: 'sb_UeQ-l_HnQxmQOMSOydvAYQ', title: 'Aetna Insurance' },
    { id: 'sb_RqzDvCgCT-unIze6hRQSdQ', title: 'Ageas Insurance' },
    { id: 'sb_bFt9F4JqQpmo8f3RXMbVaQ', title: 'AIG' },
    { id: 'sb_RmuRWJ7TTw2iw3LHLcwtww', title: 'Air Canada' },
    { id: 'sb_e-H3OYAUTV6mb-EGo90CYw', title: 'Al Pacino' },
    { id: 'sb_Okp313FWSxiZpKxNlbV7GA', title: 'Alan Tudyk' },
    { id: 'sb_NXQXsMd6Rfez7BKaKh2cTg', title: 'Alaska Airlines' },
    { id: 'sb_Mfm6krSaS5WJ9rKoqqvspw', title: 'Alcoholic Drinks' },
    { id: 'sb_FG52oNNJSKOAfLJWClNPrw', title: 'Aldi' },
    { id: 'sb_B9keGhInQm69KTlc09FbFA', title: 'Alec Baldwin' },
    { id: 'sb_H4RCsHyoSZaY2hp5I_HWSg', title: 'Alex Trebek' },
    { id: 'sb_axJXqZGRRsa79gBHI4Nu9A', title: 'Alexander Ovechkin' },
    { id: 'sb_Z2kfxAxgQSWT96gLRKxJpQ', title: 'Alexander Skarsg√•rd' },
    { id: 'sb_ZHDdH1-rSz-5E-OR689fRw', title: 'Alexandra Guarnaschelli' },
    { id: 'sb_VzNVsNqjRnayHZRPiXtTDg', title: 'Alexandria Ocasio-Cortez' },
    { id: 'sb_CbbEFEH0QjSDEvAmguFmOA', title: 'Ali Wong' },
    { id: 'sb_K9N0v3z4Sxe26AQX-aJHYg', title: 'Alicia Keys' },
    { id: 'sb_eVXHustDQR-hlJUT_ORkBA', title: 'Alistair Brownlee' },
    { id: 'sb_HaEuirHYTuKdGBTQz_c6eg', title: 'Allianz' },
    { id: 'sb_I1Em2VfzRd-MeEjysw5Uqg', title: 'Allstate' },
    { id: 'sb_EuFYMW5VRGaPJArGOw4rEg', title: 'Allyson Felix' },
    { id: 'sb_WcpyRLSUQJ6heUfrZUFr5g', title: 'Alton Brown' },
    { id: 'sb_ByrBpstNT8-uY7zZssjtGw', title: 'Alvin Kamara' },
    { id: 'sb_Uk9kaQp6SC683wqZjtr7dA', title: 'Amanda Freitag' },
    { id: 'sb_bZiF36_nQLG6K7X2bmwO4Q', title: 'Amanda Seyfried' },
    { id: 'sb_dHCn6Sv7RtuwZpHRzDaKhw', title: 'Amazon' },
    { id: 'sb_Hq-b9MhxTLCrJRpvuUR56w', title: 'Amber Heard' },
    { id: 'sb_G_qBO-D0RFCr-HD8L87j5g', title: 'American Airlines' },
    { id: 'sb_IdRtOeWWTcueJM-mQ_ughQ', title: 'American Football' },
    { id: 'sb_BMWigRkqTa-1K3ntDP9Jww', title: 'AmerisourceBergen' },
    { id: 'sb_EY_cp8-AQye4eM9yKO_5-w', title: 'AmFam' },
    { id: 'sb_VsPXNkRFR9Oi_VxvjsxtGA', title: 'Amy Adams' },
    { id: 'sb_KyivIGZsSGaWcymN4ujkWQ', title: 'Ana Ivanovic' },
    { id: 'sb_VwDwPvpsTwaCW2man63LDg', title: 'Andr√©s Iniesta' },
    { id: 'sb_fPSEoKM1Qe-MroET2sW3pQ', title: 'Andy Serkis' },
    { id: 'sb_QO1WKqPsQciBGrLDOArCgw', title: 'Angela Davis' },
    { id: 'sb_Av8yhyVmTTmdiIcVrmOe5Q', title: 'Angela Merkel' },
    { id: 'sb_EJhbkJxPQ6qtA8MRUyJoYA', title: 'Anna Kendrick' },
    { id: 'sb_PJPM7NRcQcmM34MzqpFC7w', title: 'Anne Burrell' },
    { id: 'sb_bphzHzupTOGIinjWF8f0ew', title: 'Anne Hidalgo' },
    { id: 'sb_LiIqGewCQtqQOJkvQezi3w', title: 'Anthony Bourdain' },
    { id: 'sb_TRA2XFunRfCW6htFeqerxw', title: 'Anthony Fauci' },
    { id: 'sb_KQ_QkkNhSs2D6nEgQK4Jbw', title: 'Anthony Hopkins' },
    { id: 'sb_QB5H4W3xRlyTyvrxsbJzkw', title: 'Anthony Mackie' },
    { id: 'sb_R-xFqVl1TLCIOdqDbL8DAw', title: 'Antonia Lofaso' },
    { id: 'sb_cRswlRRhT9-WrD15RXFEjQ', title: 'Antonio Brown' },
    { id: 'sb_XBVX8BiXRlm-v3yuFwepog', title: 'Antony Starr' },
    { id: 'sb_bwsnE8MrTwuwHfePl_c1BQ', title: 'Aon Insurance' },
    { id: 'sb_YNDGjre4SJCXL240e64J5Q', title: 'Apple' },
    { id: 'sb_YLyPu0P4SRi2-RJq4yUG_A', title: 'aptive' },
    { id: 'sb_RSfF5mIkQbeuOasR93sNIw', title: 'Aquariums & Zoos' },
    { id: 'sb_bP7JlC6FTRa-VPeMv3WKZA', title: 'Ariana Grande' },
    { id: 'sb_I3kZd4d6QumDIrjf79pmxQ', title: 'Arnold Schwarzenegger' },
    { id: 'sb_AnNuKFbwTzmPIJ_l9YkXIw', title: 'Arrow Exterminators' },
    { id: 'sb_H13uCdMDQ0iZ1s9nE8l3Tg', title: 'Arussi Unda' },
    { id: 'sb_SW8_mhrJSpmajsNMHEac0g', title: 'Asics' },
    { id: 'sb_L3cFmQONTOGOCabtHlQS-g', title: 'AT&T' },
    { id: 'sb_QWaa60laQsOzd4UpHZjkFQ', title: 'Automotive' },
    { id: 'sb_GVri2DcbT8axHiUR1AfpnQ', title: 'Automotive Convertible' },
    { id: 'sb_Ds3awb5sR4CDzmEPsL_Byg', title: 'Automotive Hybrid and Electric' },
    { id: 'sb_TgTbIQS-SMm7BmQcJazVQA', title: 'Automotive Luxury and Sport' },
    { id: 'sb_bIzYjc9yT46mDhVPaVdOCA', title: 'Aviva' },
    { id: 'sb_QeKRdQowQauVucHE2bt9-g', title: 'Avril Lavigne' },
    { id: 'sb_M5HlR6epQJOmqNDJG872TQ', title: 'Awkwafina' },
    { id: 'sb_CFuk4719QzKZcEIQSb_vCA', title: 'AXA' },
    { id: 'sb_BMWGjH60TJW5jLr56mUtTg', title: 'Babies' },
    { id: 'sb_PrnRs972TNegDi9LGmVAog', title: 'Backpacking' },
    { id: 'sb_T6TL1RPgSjqBpMH90LmQ4A', title: 'Bank of America' },
    { id: 'sb_V5PFd6WZRyia2CaupfDrGA', title: 'Barack Obama' },
    { id: 'sb_TFtMlf4ST0uDmGf9ofXgmA', title: 'Barclays PLC' },
    { id: 'sb_TOSZDM1FQNKcHeCAgDAyFQ', title: 'Baseball' },
    { id: 'sb_RdCExTyiQe-7kaVLj20c9A', title: 'BASF' },
    { id: 'sb_IitdTUMfRKmTwDakyyb9QQ', title: 'Basketball' },
    { id: 'sb_R0Fp684OQreTxaEctKYjZw', title: 'Bayer' },
    { id: 'sb_fQU0Ch_uRGKgsQKhonC0Vg', title: 'Bebe Rexha' },
    { id: 'sb_A46cAbmcQlqrY2m9cLUz7Q', title: 'Ben Affleck' },
    { id: 'sb_PjmbaWy3QAKatJy0GNLWBg', title: 'Ben Feldman' },
    { id: 'sb_S8_82g9aRWyIFGOUHkdjNw', title: 'Ben Johnson' },
    { id: 'sb_VECLPqCoTDeoCIgXX-zX-w', title: 'Ben Kingsley' },
    { id: 'sb_ZHSZ8nUoTSa3W7YBtukwZQ', title: 'Benedict Cumberbatch' },
    { id: 'sb_PHKJ91ylQHCNIXjv9Sz1HA', title: 'Benicio Del Toro' },
    { id: 'sb_VSOJ7y-lSgWhtwcuhugQQA', title: 'Beyonc√© Knowles' },
    { id: 'sb_LvhyAzriSRC7XEDIWLYgHQ', title: 'Bicester Village' },
    { id: 'sb_JOkcmQtiTBykeYVXWoJ9eg', title: 'Bilkis Dadi' },
    { id: 'sb_Awu6kRXRRpGLp7QfBJJMBg', title: 'Bill Gates' },
    { id: 'sb_FNUMj2B_RLKa7geBuU5ecQ', title: 'Bill Nye' },
    { id: 'sb_O87l_Nx_S1qdgD0NP4RbfA', title: 'Billie Eilish' },
    { id: 'sb_RVHol1VwQjuCgtiXN4mKYA', title: 'Billy Bob Thornton' },
    { id: 'sb_I0f4kRzhTDGdsWUdSw3Zwg', title: 'Billy Porter' },
    { id: 'sb_bcEKK5gFQqeixTcUaKQ8NQ', title: 'Blake Griffin' },
    { id: 'sb_I_vSOD8_RCGcRHeeAK0BZg', title: 'Blake Lively' },
    { id: 'sb_CmcnFjqtTSmM5M35YTLi0g', title: 'Blake Shelton' },
    { id: 'sb_DgtrlVUiTZizakUGHSttcg', title: 'BMW' },
    { id: 'sb_ZTibR8AmT1SUdS3m54so5w', title: 'Bob Odenkirk' },
    { id: 'sb_KnEgocNNRZW2QIfBOyW6Ww', title: 'Bobby Flay' },
    { id: 'sb_IaK-spc2T1OhAKCVxD9gKA', title: 'Books & Literature' },
    { id: 'sb_Fpw7wqs3SKqr4iDj-CxoXw', title: 'Boris Johnson' },
    { id: 'sb_CeL9KLkCT0WskQ3CJAYlmw', title: 'Bose' },
    { id: 'sb_AHTR82caQbyd1KUT7aBRFA', title: 'Brad Pitt' },
    { id: 'sb_QEHQue75RTS8mIp1OnkkYw', title: 'Bradley Cooper' },
    { id: 'sb_Uh6sH6CjR6a_rxTivjCCTA', title: 'Bristol-Myers Squibb' },
    { id: 'sb_fCQFWVnNTNGSP7G9qAzzPg', title: 'British Airways' },
    { id: 'sb_bGAUkNxZRvSulIHdA3r1OA', title: 'Brooke Shields' },
    { id: 'sb_WLA2nFkyTFm4kAX4GsO3xw', title: 'Bruno Mars' },
    { id: 'sb_MWnUbJGYQNKsnonI4wtwdg', title: 'Bryan Cranston' },
    { id: 'sb_dtCdp9wcQNG79q6-P_cMtw', title: 'BT' },
    { id: 'sb_FnNu10NDRhq_W7kiI-IBYA', title: 'Budweiser' },
    { id: 'sb_EASguXfxRyKo0TrBnsj24Q', title: 'Bulleit' },
    { id: 'sb_AIYdNzmtRMCJPs3Nshd29g', title: 'Bulwark Exterminating' },
    { id: 'sb_W9EtDVC0QcOtlNMqSin_iA', title: 'Bupa Insurance' },
    { id: 'sb_RJ8Bvp5CTxe-jCmwNfPCKg', title: 'Business & Finance' },
    { id: 'sb_Z3KVwGSXSZGwhpw0XJ2qIg', title: 'Cadbury' },
    { id: 'sb_VM32VJzYQ22zG7uN4eePIg', title: 'Caitriona Balfe' },
    { id: 'sb_SP32YgIhR0-3B0bnVI-1VQ', title: 'Cam Newton' },
    { id: 'sb_ahThYoTTR9ehfHk4QnIEkw', title: 'Capital One' },
    { id: 'sb_H97snjlpTLeuTDOUFAV0yg', title: 'Cardi B' },
    { id: 'sb_TcDePMe5Q2Ob17S25KxLzg', title: 'Carl Sagan' },
    { id: 'sb_fAk_yZBkSFa4jTXrim8-2w', title: 'Carla Hall' },
    { id: 'sb_GkpiSCeUS6ieyywLe2VtRA', title: 'Carli Lloyd' },
    { id: 'sb_DZsXc4vrTTyuIvIigw2vKw', title: 'Carlsberg' },
    { id: 'sb_QKtAdaCdQI-ztTgY6NydVQ', title: 'Carly Rae Jepsen' },
    { id: 'sb_eo0Po4jERL2FdAAE2UhdKg', title: 'Carrie Fisher' },
    { id: 'sb_U-hmrlbAT-az4CHRzzjbNA', title: 'Carrie Underwood' },
    { id: 'sb_MOdVChvaTWyPMyC6YkzE3w', title: 'Carrie-Anne Moss' },
    { id: 'sb_ZeRfpPbxTyCc4-iJGm7V8g', title: 'Carson Wentz' },
    { id: 'sb_bVD5kXpxQQ2q2jzCjD7tlQ', title: 'Casamigos' },
    { id: 'sb_bebbZxLhTbCvocwdN652nw', title: 'Catalana Occidente Insurance' },
    { id: 'sb_IsRwit-pRD6jBwIO2K1yVA', title: 'Catherine OHara' },
    { id: 'sb_YMigOMuMStKBIzYGOIZ-ZA', title: 'Cats' },
    { id: 'sb_RuVg03YNT92bA4nAIfsd_g', title: 'Celebrities' },
    { id: 'sb_CCYQkn2JTamHLjl6uqU0oQ', title: 'Central Exterminating' },
    { id: 'sb_FcEWkyNpT1qJMl1ksAIoCQ', title: 'Chanel' },
    { id: 'sb_ZHfs75OgRnyN_0dwwAACZA', title: 'Channel4' },
    { id: 'sb_YokE86-YQv-XGfwoJnMEbg', title: 'Charli XCX' },
    { id: 'sb_A2JkODvdTB6oeBJc7B83Hg', title: 'Chase Strangio' },
    { id: 'sb_fY6k-xrnTNmXJXH1_FBWLA', title: 'Chlo√´ Grace Moretz' },
    { id: 'sb_Cs1enwbESyS659UOXYeMUw', title: 'Chloe Kim' },
    { id: 'sb_NdfvxRTgS1OiYnV-Kw_WHw', title: 'Chris Brown' },
    { id: 'sb_P-Slx1I6SYKZ6A40UdVbPg', title: 'Chris Evans' },
    { id: 'sb_SyKQY36iT1G1g-3zEPcIkA', title: 'Chris Hadfield' },
    { id: 'sb_GGxP7tDSSWu9cxLljX1xvQ', title: 'Chris Hemsworth' },
    { id: 'sb_YAMIeS7lQmaYuFPDH6We3Q', title: 'Chris Lane' },
    { id: 'sb_Xy0XXmOER7irbCG22Q6Dhg', title: 'Chris Parnell' },
    { id: 'sb_QZeSge2vTTyKx-rUIGFuWQ', title: 'Chris Paul' },
    { id: 'sb_aVz2fzsuTE2S3-9Nly93Xg', title: 'Chris Pine' },
    { id: 'sb_OMjbAjJ1QsCTOgGquwCQeA', title: 'Chris Pratt' },
    { id: 'sb_GxuFTpQmSCW3yXR3NN2Ifw', title: 'Chris Stapleton' },
    { id: 'sb_EpGQO-DMQDqiE9mZVsytJw', title: 'Christian Bale' },
    { id: 'sb_HzZRjMnCQbqIRpZ0MgEj0Q', title: 'Christina Aguilera' },
    { id: 'sb_CEyDmgMpRHCHsl7fw-mgMA', title: 'Christina Perri' },
    { id: 'sb_eOkzXoywQvidbCtCQcTTnQ', title: 'Christopher Lloyd' },
    { id: 'sb_YjgOz6t6QHCvEm88C-dB4w', title: 'Chubb' },
    { id: 'sb_dZGt3d_fSziW3mmdVdbxuA', title: 'Chuck Hughes' },
    { id: 'sb_ECi7Aa_wQ7-J89jO32e_Uw', title: 'Churchill Insurance' },
    { id: 'sb_MfH_1oK9TGivIUvoYYJdpg', title: 'Ciara Bravo' },
    { id: 'sb_I1Grwn9sQI6YkMWiQ0b6hQ', title: 'Cigna Insurance' },
    { id: 'sb_b1ykZhJtStuMx1UIysDAwA', title: 'Cillian Murphy' },
    { id: 'sb_XsYJHz2rQT-NlfEcnPdMBw', title: 'Cisco' },
    { id: 'sb_L_0IErYeQTSMKFLVYqC1rw', title: 'Citi' },
    { id: 'sb_SQ3gj-5aQS6FqaKMsLCA2w', title: 'Claire Babineaux-Fontenot' },
    { id: 'sb_MbTJ7HOvQJWCiRPVR6r6wQ', title: 'Claire Foy' },
    { id: 'sb_UL7ru7iYRryqB-GA6VQjQQ', title: 'Clark Pest Control' },
    { id: 'sb_UK28t06USzS8Phz8jn9GgQ', title: 'Clayton Kershaw' },
    { id: 'sb_Pteyp3hHS8yGHEi2s-LSQQ', title: 'Clint Eastwood' },
    { id: 'sb_SelfYyYnS5eCI_avT4NUjQ', title: 'CNA Insurance' },
    { id: 'sb_MAPoPSQMTBy-SMkN6HFJqg', title: 'Coca-Cola' },
    { id: 'sb_WtAieEw4QuSgwx1e7C8tRg', title: 'Colin Farrell' },
    { id: 'sb_KVfSwW5rTymArOFhHLa2hQ', title: 'Colin Hanks' },
    { id: 'sb_OkcOVvPuR5-vSZX8TuytPw', title: 'College & University' },
    { id: 'sb_frNtf6K5Q8urtFoylNe6XA', title: 'combat' },
    { id: 'sb_YKChLArqQgOF-Ge8iu_Z1g', title: 'Conor McGregor' },
    { id: 'sb_IHuqpqZwQXqYecOe086zPA', title: 'Coors' },
    { id: 'sb_cCFgVpdNT822Y2G9Sy55EA', title: 'Corona' },
    { id: 'sb_XQfdYgZYRjCBwMo9E19uoA', title: 'Costco' },
    { id: 'sb_XQuHLeJ-QS60-4KZ0nxyEw', title: 'Courteney Cox' },
    { id: 'sb_W_RAgyqbSxi6Djtm6yJrrw', title: 'Covea Insurance' },
    { id: 'sb_CXJqGCtHTtWkLHqqtTyblA', title: 'Cristiano Ronaldo' },
    { id: 'sb_C578RToaQIyaj6RPab-Emg', title: 'Crown Royal' },
    { id: 'sb_NY5f5SNVQtORULItUlhNXQ', title: 'Cutter' },
    { id: 'sb_CS-CSM4dRuiTYe-sjYhOUw', title: 'CVS' },
    { id: 'sb_Zcf4VjbZTmqgesgomn2xTw', title: 'Daisy Ridley' },
    { id: 'sb_EOGEfPHMSAeyy83v1N3MmQ', title: 'Damian Lewis' },
    { id: 'sb_X7Nx3Vk3S6-hA4O8YZHUZA', title: 'Dan Levy' },
    { id: 'sb_YUhbnJo7SGOMLvzpJFKxdA', title: 'Danica Patrick' },
    { id: 'sb_fQ5BgSbiRnCYKnI8KG61kQ', title: 'Daniel Dae Kim' },
    { id: 'sb_evoUrCVYQDeByVv_pRAojw', title: 'Daniel Kaluuya' },
    { id: 'sb_dV7rL5P6R52by2oLfmrUVA', title: 'Daniel Zhang' },
    { id: 'sb_GfiGaEuiTqK7Phb6oTVpqQ', title: 'DAS Insurance' },
    { id: 'sb_c6nO1xSqRcCnU0GxezyYyQ', title: 'Dating' },
    { id: 'sb_Xoa7ZNcPR5S9PYPQGMPaqg', title: 'Daveed Diggs' },
    { id: 'sb_L7JAulhgSWqO3KIwgeTBFg', title: 'David Attenborough' },
    { id: 'sb_cjLEGdH2QB2y7nVg_-Gx9Q', title: 'David Beckham' },
    { id: 'sb_JrTsGcFqQCi095JESTNx7w', title: 'David Chang' },
    { id: 'sb_YDv3kwVyQSSRPfZIzW9l6Q', title: 'Daya' },
    { id: 'sb_P_h5PMaDQbyFXPMqYnEXdA', title: 'Dell Technologies' },
    { id: 'sb_EGCvS7uOQgKEOD3OKP3CvQ', title: 'Delta Airlines' },
    { id: 'sb_bGpf9fWOQgG-TfCmIK_9lA', title: 'Demi Lovato' },
    { id: 'sb_QCDbi5A_Rfa4-e1a9PB3kw', title: 'Denzel Washington' },
    { id: 'sb_cqOHvrkZRc-anQtfZ8pfYg', title: 'Dermot Mulroney' },
    { id: 'sb_Ru3EfQzuSgu8HnQRa2v-YA', title: 'Deutsche Telekom' },
    { id: 'sb_dAIGZ4uBTHaoy8o70JVB8Q', title: 'Diane Keaton' },
    { id: 'sb_J6fkZGzOSFWVupJL5uSIBQ', title: 'Dior' },
    { id: 'sb_WK4vJF2jR3KnivuLOObolA', title: 'Direct Line' },
    { id: 'sb_VA6vhR3pSLyTNvdEhsNR_g', title: 'Disasters News' },
    { id: 'sb_GOfWFM-3RyyQvMd2Y6sIwg', title: 'Discovery Insurance' },
    { id: 'sb_EGIv58q8QyO9RmV2kryMwg', title: 'Disney' },
    { id: 'sb_UgCMnY2VTd-EJjvlzfTn4g', title: 'Dogs' },
    { id: 'sb_IzCou4ESQQqzaqSuBhSebg', title: 'Doja Cat' },
    { id: 'sb_Yn5mRwdqTZK8o6QI5Ntq-A', title: 'Dolce & Gabbana' },
    { id: 'sb_fq33OTw-TqSdoSMc6GDzOg', title: 'Dominic West' },
    { id: 'sb_EQLPm9kxSg6RVXtuwm-KLQ', title: 'Don Cheadle' },
    { id: 'sb_D3LB0GQRT8208jX8cu-yew', title: 'Donald Trump' },
    { id: 'sb_XY5cZtC-Tl2jt8kqlaGTNQ', title: 'Dos Equis' },
    { id: 'sb_PZaksukeT_qB0Q65SIBtuA', title: 'Drake' },
    { id: 'sb_MU1pmeIIQySHk0I8x4lL1w', title: 'Drew Brees' },
    { id: 'sb_RoQfysM6SCOUflmwQAVhDQ', title: 'Dua Lipa' },
    { id: 'sb_SfvBWkkgR9yetb5z9SZNyg', title: 'Dwayne Johnson' },
    { id: 'sb_M3dHZrzFQ9C0_XNvoukc7g', title: 'Dwyane Wade' },
    { id: 'sb_DcPImHKwSdeACZc0sWxtkg', title: 'Eastside Exterminators' },
    { id: 'sb_N3W_BFo2R5yZ2E8j9dVddQ', title: 'Easyjet' },
    { id: 'sb_aVu1BvOWTKO0iBe6b-YO8Q', title: 'eBay' },
    { id: 'sb_a0yZGdAtSqiUpYXj9Z6FGA', title: 'Ecolab' },
    { id: 'sb_QN2vk5pFSvSQWQtb84roHA', title: 'Ed ONeill' },
    { id: 'sb_brJYEl_UT0a7yDNOkZr2qA', title: 'Ed Sheeran' },
    { id: 'sb_dM3bFPLwTSCJkw5XvjbbGA', title: 'Eddie Murphy' },
    { id: 'sb_PZfSGGzaTpCoooQSxGU_Zw', title: 'Eddie Redmayne' },
    { id: 'sb_eVOJePIUTUGGiG_N_rsnUQ', title: 'Edgar Ram√≠rez' },
    { id: 'sb_aOMDqf5gQPep4qIy7FroXQ', title: 'Edie Falco' },
    { id: 'sb_bSBLoDI5S5OwFBJrJSSePQ', title: 'Education' },
    { id: 'sb_afZBVIp7Tqy6pXp65QSbnA', title: 'Edward Burns' },
    { id: 'sb_ewDvBF7rS7KUoeWqSMUiNw', title: 'Edward Furlong' },
    { id: 'sb_fMC1guHkTEWv2wR_9cxN6g', title: 'Edward Norton' },
    { id: 'sb_K5gF-62RTUmBfsQ5eKrkfw', title: 'Ehlrich' },
    { id: 'sb_Z9AvdJXZT0ySP1y5kLPlAw', title: 'Eiza Gonz√°lez' },
    { id: 'sb_RwbYBAW9QZ20rK_gtvadNw', title: 'Elizabeth Olsen' },
    { id: 'sb_HcMraM9VRa-Hefc9fkpKeg', title: 'Ella Mai' },
    { id: 'sb_S6w5eUqST06-TEEtQUStxA', title: 'Ellen Burstyn' },
    { id: 'sb_GLxiqkq8Qq-PjKDs3OWF0g', title: 'Ellen DeGeneres' },
    { id: 'sb_H8SJWNCtTuyBK2-ho_crWg', title: 'Ellen Pompeo' },
    { id: 'sb_YlPzyE-TRSyHZFgDzxeCmg', title: 'Ellie Goulding' },
    { id: 'sb_KtXOjlZ3RfaPqZcioTRKTQ', title: 'Elliot Page' },
    { id: 'sb_SsYXTBORS_-HdGktrdCI7g', title: 'Elon Musk' },
    { id: 'sb_Xz0kdIw3TnODP9HUaoR80Q', title: 'Emeril Lagasse' },
    { id: 'sb_U9KJ9Z54QsyzKgI0N7zFjg', title: 'Emilia Clarke' },
    { id: 'sb_PkAkM3oBR7ebfyC5zZLdFA', title: 'Emilio Estevez' },
    { id: 'sb_csHrrSNDS06VqOIpweNkOQ', title: 'Emily Browning' },
    { id: 'sb_GW80KAOuT8ii_UPBKG0oAw', title: 'Emma Watson' },
    { id: 'sb_INgkCemXRfu3CFdAHV_RQQ', title: 'Enrique Iglesias' },
    { id: 'sb_PeehcN2TRqC1htH8UyQYlQ', title: 'Enterprise' },
    { id: 'sb_amtKAn10SMOvYTTcdvjs0A', title: 'Environment' },
    { id: 'sb_dm48kzGETiCE7AODf_GXAA', title: 'Equitable Insurance' },
    { id: 'sb_cEXCsk8wR12_ILfeWG3BJg', title: 'Eric Yuan' },
    { id: 'sb_E8eU4k7RSn2Tw009HVljDg', title: 'ESPN' },
    { id: 'sb_KmuT0FJESUKhGLHFOPYnbQ', title: 'Eugene Levy' },
    { id: 'sb_KbERMdrGRTeAlE3c4BBTrg', title: 'Events & Attractions' },
    { id: 'sb_OMU2ydueQeKr2NzvJOzipg', title: 'Ezra Miller' },
    { id: 'sb_UYccjmsKSZCZ1LGHd0YWQQ', title: 'Facebook' },
    { id: 'sb_aJeGC29hRSqoqNxzfQaBFg', title: 'Family & Relationships' },
    { id: 'sb_beoidYjqRrKzRlcWZDlRig', title: 'Family Adoption' },
    { id: 'sb_bXXhD77zRXmRl7Z6wqod_w', title: 'Farmers Ins Group' },
    { id: 'sb_Kr2Je5hvRiC5yVpGXcY8lw', title: 'Fashion Trends & Seasonal Fashion' },
    { id: 'sb_dK9Ow0LuSlazRzaXDKgwfA', title: 'Florence Pugh' },
    { id: 'sb_OuYhoZN1RF60QFBAvdIokw', title: 'Food & Drink' },
    { id: 'sb_H3eYpqZaSiWeIqtiDcIYPQ', title: 'Ford' },
    { id: 'sb_GD8aEjyATZWCMIjbWLFWTA', title: 'Fosters' },
    { id: 'sb_QyJUuqB0R9iwa7xge53Lfg', title: 'Fox' },
    { id: 'sb_dOAMw55rS-ydldsYjBzKvA', title: 'Frances McDormand' },
    { id: 'sb_aoSND6GzQsSanNEtyJED2g', title: 'Gabriel Byrne' },
    { id: 'sb_SCIidrp6QJujOn4p5TgDJA', title: 'Gabriela C√°mara' },
    { id: 'sb_Mz8EutR0Sq2aShC3iFN5Ag', title: 'Gabrielle Union' },
    { id: 'sb_etw-stEkTHSZTXi3gOKtQA', title: 'Gal Gadot' },
    { id: 'sb_ftqlSr-zRBOBJioxTHRv3g', title: 'Gambling & Betting' },
    { id: 'sb_TBGvp05LQjCiFADSif2fig', title: 'Game consoles' },
    { id: 'sb_M3veApdAQbCng5Tw8nVp3Q', title: 'Gardening' },
    { id: 'sb_G4OghW8CRg-UogD5Ahs9Rw', title: 'Gary Oldman' },
    { id: 'sb_dNNU5LDwQgiGCFlfh0r48w', title: 'Geico' },
    { id: 'sb_AaGrmm2yT3Wr3jLcxV8Rdg', title: 'Generali Group' },
    { id: 'sb_J-Vwa_IoT1SruxWKDkeyVA', title: 'George Harrison' },
    { id: 'sb_RQ5gcuGPS0mNA74Kx2Wtfg', title: 'Giada De Laurentiis' },
    { id: 'sb_AO81JohvTD-OjOjJrzmvdg', title: 'Giancarlo Esposito' },
    { id: 'sb_euea-3wdRE24PalcHMSMtQ', title: 'Giancarlo Stanton' },
    { id: 'sb_F5776Ql3RzuKSy90zl5B_g', title: 'Giannis Antetokounmpo' },
    { id: 'sb_RbLyjia8RBykRdu5p4dfbw', title: 'Gina Carano' },
    { id: 'sb_dDWh9O9OT3Kdy-VwAvBsUg', title: 'Gina Torres' },
    { id: 'sb_cSoJUiL0QXq4yhDuBqYl7g', title: 'Gino DAcampo' },
    { id: 'sb_TtcIriv2T-K9g0YNyx5erA', title: 'Glenn Close' },
    { id: 'sb_dkQepz46Q9eeQ0930paehw', title: 'Gleyber Torres' },
    { id: 'sb_BEFwvb4pTQqjhuN3plpcUA', title: 'Google' },
    { id: 'sb_P_r9Op6JR8u3apzbfSubxw', title: 'Gordon Ramsay' },
    { id: 'sb_WYjGtArfRmaDPzxcmndmDw', title: 'Graham Norton' },
    { id: 'sb_FD_LpDb6RXCB0gRqJZv5gA', title: 'Greta Thunberg' },
    { id: 'sb_Als0EyVpR-SXOzA6ybs7BA', title: 'Guardian Life Insurance' },
    { id: 'sb_KPTS6i3nQ-i5nvBBATo2JQ', title: 'GUCCI' },
    { id: 'sb_fs_Z4qSlQ-60OXsYQxI40w', title: 'Guy Fieri' },
    { id: 'sb_NNFfJajRTyCi0R6X9lPEIA', title: 'Gwynne Shotwell' },
    { id: 'sb_VbqgWE1aQ9Wg1WZQGAAx_w', title: 'Gym & Fitness' },
    { id: 'sb_d0hQ124NSKqRnzGLOqFzAw', title: 'H.E.R.' },
    { id: 'sb_aW27vcDNQeWbk43f2rWv9A', title: 'H&M' },
    { id: 'sb_Yos8N-nJQZSs5-VOH9GitA', title: 'Harley Morenstein' },
    { id: 'sb_FUgGwu89Q2OCX1hkB6n1aQ', title: 'Harrison Ford' },
    { id: 'sb_V6WzScDhQ5KRUr9jvxiS7w', title: 'HBO' },
    { id: 'sb_EBf9srwDRgeguVHsRgcoeg', title: 'Healthy Living' },
    { id: 'sb_ODJmT-EzRxWGqOgfp3vPEw', title: 'Heath Ledger' },
    { id: 'sb_J_3ga3MJRU2p-kf5X_hZUg', title: 'Heineken' },
    { id: 'sb_SNEoTwaCQCuSF6Aq6p4t6A', title: 'Henry Cavill' },
    { id: 'sb_GWlONgkPSOuXBJxNdHZxZQ', title: 'Herm√®s' },
    { id: 'sb_EWMMckTcR_CDmqEOJYz17g', title: 'Hertz' },
    { id: 'sb_Y0H9kMzBQtGcEnj2guomeg', title: 'Hiscox Insurance' },
    { id: 'sb_V_o25o8iQYKY7SnTCfVnYg', title: 'Hisham Baeshen' },
    { id: 'sb_FQ-ZdQKKTruM76icTOXUSQ', title: 'Hitachi' },
    { id: 'sb_GqwPw5h9RwCWFpWLNuWn3w', title: 'Hobbies & Interests' },
    { id: 'sb_I-Dp8_NCSLW98U9jGkQR3g', title: 'Home & Garden' },
    { id: 'sb_U5i_dkfeTZaKWwXCzkDxfA', title: 'Home Cooking' },
    { id: 'sb_FP69ZO6nQ1mJyeDfhQFW-w', title: 'Home Depot' },
    { id: 'sb_P2HM_FD6Tf6BEmsw4NCkUg', title: 'Honda' },
    { id: 'sb_L6vaqU-oSa2TEnOvuANZaQ', title: 'Hope Solo' },
    { id: 'sb_dMywXwikR2uRMW9VuZXSVg', title: 'Howard Stern' },
    { id: 'sb_SyC6VYzmTqe-vjy_D8lU8g', title: 'HSBC Holdings' },
    { id: 'sb_Nk7Icqk0QKWDeDpHYAjwVw', title: 'Huawei' },
    { id: 'sb_X1zws6aVTXOqgWcfVrVJTA', title: 'Hugh Jackman' },
    { id: 'sb_Rrsvjp6LTvmbMvWgpynY4g', title: 'Hyundai Group' },
    { id: 'sb_MqvK20nLREiIGQzi3bxGOA', title: 'IBM' },
    { id: 'sb_YVYqNyOwQhmdi95Q3HV2pg', title: 'Ice-T' },
    { id: 'sb_VESDWoocTF2leoB4mWzI1w', title: 'Idris Elba' },
    { id: 'sb_BP5897x4TQCqyDc_PPIDqg', title: 'Ike Barinholtz' },
    { id: 'sb_RIDCyStdTqW1buzu8ThSdw', title: 'IKEA' },
    { id: 'sb_SJAmjG3hQX2KHsZs2E_tXw', title: 'Imelda Staunton' },
    { id: 'sb_NWbNMHW3REuO8YPjM2zJiQ', title: 'Ina Garten' },
    { id: 'sb_drAQ-XvWQ56jH_KPbsL7Lg', title: 'ING Group' },
    { id: 'sb_JgjQkiWuQDe9eoyvhru1gQ', title: 'Instagram' },
    { id: 'sb_YQ3lprjQTHKVz7qIZcOejA', title: 'ITV' },
    { id: 'sb_dJZjCXgGQ0Sz1uLr1UnF6w', title: 'J. D. Martinez' },
    { id: 'sb_CI1Oee78Rtyj3lhsc2wnGA', title: 'J. J. Watt' },
    { id: 'sb_elNeo3ZVS160yVqVApwaQg', title: 'J.K. Simmons' },
    { id: 'sb_QiM1nt3CSTWZLL0tTrp1Gw', title: 'J√§germeister' },
    { id: 'sb_KDgg_ACMTRuJakVCmzeYHw', title: 'Jacinda Ardern' },
    { id: 'sb_bBGBZ9AVSWa7wmKzCcJdmg', title: 'Jack Daniels' },
    { id: 'sb_Re-aoBx8SD6NZpLa6AfuZw', title: 'Jack Nicholson' },
    { id: 'sb_DkERGHcuTjyHapRD0_-4vg', title: 'Jack Quaid' },
    { id: 'sb_L2ZUvCy-T3iFAG7sU-f2wg', title: 'Jacob deGrom' },
    { id: 'sb_SEYpxd68QeOg1fvnuVlYTg', title: 'Jair Bolsonaro' },
    { id: 'sb_ErwTzbZ_SmeHUKat61GGpg', title: 'James Arthur' },
    { id: 'sb_X6g52sNmRluZ1dNUqIrvqg', title: 'James Beard' },
    { id: 'sb_GrFUkMcmRYKDON2ZGRPxrQ', title: 'James Caan' },
    { id: 'sb_NMzcSLwPRS2abKeEbX1Dtg', title: 'James Gandolfini' },
    { id: 'sb_XB3PLO59RH6_kv13HM5v0g', title: 'James Harden' },
    { id: 'sb_d-5QTfqFQm6NP5TFqmDKmA', title: 'James Marsden' },
    { id: 'sb_X6UnkOyBQBmW5L016K24JA', title: 'James Spader' },
    { id: 'sb_W08jAiexTc2B7i0wDdyDwA', title: 'Jameson' },
    { id: 'sb_XuPZqgs3TrG5_-rZt0g6xg', title: 'Jamie Foxx' },
    { id: 'sb_A-bxKBV9RE-GEucPmWmkmw', title: 'Jamie Oliver' },
    { id: 'sb_GZo8xnvDQ8-qyeHM2SXa3Q', title: 'Janelle Mon√°e' },
    { id: 'sb_SNDjDdWkSfqrqjOrA08XUA', title: 'Jared Harris' },
    { id: 'sb_LBso9mLkTyCpeuxZ17rpjQ', title: 'Jared Leto' },
    { id: 'sb_NJtO4TxdSd2E-_Tyl6zdFA', title: 'Jason Alexander' },
    { id: 'sb_EqRE-_C8SFGsKGDWlDQ8hw', title: 'Jason Derulo' },
    { id: 'sb_HKpZW1WqR7KoisG42HheWQ', title: 'Jason Kenny' },
    { id: 'sb_W6kgPkQtTraSRzBugaAp4A', title: 'Jason Momoa' },
    { id: 'sb_e_6-eEZzQVa8F6FMzu6Yyg', title: 'Jason Ritter' },
    { id: 'sb_eSK8qD57Qii8NySm1GaPRA', title: 'Jay-Z' },
    { id: 'sb_dHYwBIIASRyl3UeEm6BRNg', title: 'Jean Reno' },
    { id: 'sb_YM2BpR4TTAaRUWOpNdH8dw', title: 'Jean-Jacques Muyembe Tamfum' },
    { id: 'sb_Akqmc-AbSGuFWHB6O3R6XA', title: 'Jeff Bezos' },
    { id: 'sb_JAMR__HUREu24eoW4IOntQ', title: 'Jennifer Aniston' },
    { id: 'sb_NRKNP9xoQ4mnlUmsaMoeHw', title: 'Jennifer Garner' },
    { id: 'sb_INrz31HfT-m_qar28utqUw', title: 'Jennifer Hudson' },
    { id: 'sb_ZCR1ESKTQGy21751S5J5og', title: 'Jennifer Lopez' },
    { id: 'sb_WIv4RM6dQ8-r_UAI-EFJxw', title: 'Jerome Powell' },
    { id: 'sb_GsRI_X1fRticA2bf6VojGQ', title: 'Jerry Seinfeld' },
    { id: 'sb_OxDt5R7cTUCe0INskp0YOQ', title: 'Jesse Plemons' },
    { id: 'sb_fOdm5uLRRgWs-hdd0poQow', title: 'Jessica McNamee' },
    { id: 'sb_E49uwg5dQUaWI--7zj_N3g', title: 'Jim Beam' },
    { id: 'sb_Oc8WQ-PxTSmSAi8kmwF4iw', title: 'Jimmy Garoppolo' },
    { id: 'sb_Lx6BggQeS6Sd37Sq_x57rw', title: 'Joaquin Phoenix' },
    { id: 'sb_fyW6EXgZStSejv9pZ4WoCQ', title: 'Jodie Foster' },
    { id: 'sb_DxCgE_7NTpiui5mul8xDkw', title: 'Joe Biden' },
    { id: 'sb_VHFPB390RNWcHS2u75aClA', title: 'Joe Pesci' },
    { id: 'sb_XWrqVS1OScuspwOVhyXQHA', title: 'Johhny Depp' },
    { id: 'sb_YkbhI1NsTVO0ohfkTKFN9Q', title: 'John Cena' },
    { id: 'sb_Nzh_Ny2XRNm-EwWOwom-VQ', title: 'John Hurt' },
    { id: 'sb_Q5CEMVUPQNmOtuMJzJMoiw', title: 'John Krasinski' },
    { id: 'sb_f81a1zVEQECQLCRyEHaXLQ', title: 'John Legend' },
    { id: 'sb_TuDPGwyPR-KNSEzXz_VqXQ', title: 'John Oliver' },
    { id: 'sb_QPDGLowORCavQAqlE8Z8Rg', title: 'John Roberts' },
    { id: 'sb_Cy7uviaSTuuwzuAjiFnDOQ', title: 'John Stamos' },
    { id: 'sb_FuZyDdwRScqIi0tzyJX-qA', title: 'Johnnie Walker' },
    { id: 'sb_Dmtn-6JjTceAtEF2pxUOkw', title: 'Johnson & Johnson' },
    { id: 'sb_WTCVh7c8QiWUPoparADStg', title: 'Jordan Spieth' },
    { id: 'sb_TmW2Q4EOSzSO9MEB-iUshA', title: 'Jorja Smith' },
    { id: 'sb_RyBnM2zbRlmZaHBVDh9Tyg', title: 'Joseph Gordon-Levitt' },
    { id: 'sb_Q4n8VyYHTouP3yM_QpihzA', title: 'JP Morgan' },
    { id: 'sb_bMmwv9vFTzWAuzmwH5IraA', title: 'Julian Edelman' },
    { id: 'sb_ZqQi3bjgRS2xumm2jzlpig', title: 'Julie Bowen' },
    { id: 'sb_fCIKTuIJRam25C7VDRQw0g', title: 'Julie Harris' },
    { id: 'sb_MeM6Yoh5RZqEDn-dBm5BVQ', title: 'Julio Jones' },
    { id: 'sb_G1Cg1xoqQCuKjrDqrz63QQ', title: 'Justin Bieber' },
    { id: 'sb_bCBiHj-9TYCucEM4_gsT3g', title: 'Justin Roiland' },
    { id: 'sb_Y1_lr1lsQQq1kbJLcLiATA', title: 'Justin Timberlake' },
    { id: 'sb_e1KhBiOdRcmfs4aSXF3IHg', title: 'Kaley Cuoco' },
    { id: 'sb_ZTXyK6jWRRWA6XpJMGiY6w', title: 'Kamala Harris' },
    { id: 'sb_NrhBQqzRQeKkleMNoJ9O2w', title: 'Kane Brown' },
    { id: 'sb_HQ1lTDFLT_WPZzx5F7FhsQ', title: 'Kanye West' },
    { id: 'sb_KG_LrwaNQnuBVFVCjZBC1A', title: 'Karl Urban' },
    { id: 'sb_Dprxct8DQXqkOFwKNycuVw', title: 'Katharine McPhee' },
    { id: 'sb_ChCvM1nFQwKCr8b1lyxxiQ', title: 'Kathryn Hahn' },
    { id: 'sb_EuHiDEp-RSment-yMaQOhg', title: 'Katie Ledecky' },
    { id: 'sb_eNOjvFbuQ0a1VxdxOTkJAw', title: 'Katy Perry' },
    { id: 'sb_N9gSLU1sSJGMXIlSrzaAKg', title: 'Keanu Reeves' },
    { id: 'sb_T7nQqdLWR1ulRMUWbwredA', title: 'Kelly Clarkson' },
    { id: 'sb_X3crfDEHSquzmBFlvR1Xvw', title: 'Kelly Rowland' },
    { id: 'sb_ZS95BGp1Q6utW-PgbeMwEw', title: 'Kendall Jenner' },
    { id: 'sb_cya15R1OSVmThy0fP1MJgQ', title: 'Kevin Durant' },
    { id: 'sb_CXCZUlVNRBKQTf1Dh2KXhg', title: 'Kevin Hart' },
    { id: 'sb_FbwoMoCXRsmztRR4PsUwHQ', title: 'KFC' },
    { id: 'sb_Zn2k9Zf4TEWory_92NHVLw', title: 'Khalid' },
    { id: 'sb_EG0rwMi1QrqjyCNDMlt7hg', title: 'Khlo√© Kardashian' },
    { id: 'sb_BqC9vKJKQWGTtz6RcZdJEg', title: 'Kim Kardashian' },
    { id: 'sb_cTtqrHYHTqGKBxUGdnN0EA', title: 'Kobe Bryant' },
    { id: 'sb_MGFjY1c8QEeLJY73sSwNmw', title: 'Kourtney Kardashian' },
    { id: 'sb_MXx8C_KmTxWP550Xu4qNTg', title: 'Kraft' },
    { id: 'sb_cOXy3vl2RCKiIIBDe7ntWw', title: 'Kristalina Georgieva' },
    { id: 'sb_JsMl20BnRDG8O0BNYgRYIg', title: 'Kristen Schaal' },
    { id: 'sb_BM8y_qdZTZ2gBJho1_8ZwQ', title: 'Kristen Wiig' },
    { id: 'sb_Nts_WYkWQ1uUaU7og58f9A', title: 'Kristin Davis' },
    { id: 'sb_MuRZ1-5XQJaTJBMDwKVKLA', title: 'Kylie Jenner' },
    { id: 'sb_VVPdJCruTPe3F56JgdXRcg', title: 'Kylie Minogue' },
    { id: 'sb_bT0W6JWfTyS78oWAbFFUCw', title: 'Kyrie Irving' },
    { id: 'sb_R-0Oz8hISX-ZtuHjpZv0lA', title: 'LOreal' },
    { id: 'sb_X2KoHZ-xRfWcar59_KN6Ww', title: 'Lady Gaga' },
    { id: 'sb_KYT7Im0lSKaPVjC2F5F0hg', title: 'LaKeith Stanfield' },
    { id: 'sb_eXqyFubJSzKJSQP6TXbgUw', title: 'Lana Del Rey' },
    { id: 'sb_QsJTXqXIR5SHIOz5AaqvGQ', title: 'Larry Fitzgerald' },
    { id: 'sb_CwxRQAMaTNyU3KIeRRf8_g', title: 'Laura Dahlmeier' },
    { id: 'sb_GlOI1dWMTRa_oAe5EPDf4A', title: 'Lauren Cohan' },
    { id: 'sb_evdkyyLnRSKpPtreV5u6_w', title: 'Lauren Gardner' },
    { id: 'sb_CTW4eZ4-SaekrXhBZCwyWg', title: 'Lauren Graham' },
    { id: 'sb_IUBBju_rSjWbAt7FnreOmQ', title: 'Laurence Fishburne' },
    { id: 'sb_a6PC5qjNQg6dX9I529M-Yw', title: 'LeVeon Bell' },
    { id: 'sb_WWKGTAC0R7WIqHwzyDqMzg', title: 'Lea Thompson' },
    { id: 'sb_GmlqxZDLQyWCHhzFVq4tnA', title: 'LeBron James' },
    { id: 'sb_PR8hUnuIQ6m6gta7kXPn4A', title: 'Lena Headey' },
    { id: 'sb_ev5Ps6iqQYaklyNptvMPjA', title: 'Leonardo DiCaprio' },
    { id: 'sb_MG0ITwnTT3uHZPVQ6k5vUw', title: 'Levi Roots' },
    { id: 'sb_dwqkvPNWTxaT0W-dv7SMMQ', title: 'Lewis Capaldi' },
    { id: 'sb_SBVs5e1fQ724ODaTUgorwg', title: 'Lewis Hamilton' },
    { id: 'sb_Vf5IA50RSOarMMWEldOBxQ', title: 'LG' },
    { id: 'sb_JPTut0diSnWIAEUDzOnYWQ', title: 'Liam Neeson' },
    { id: 'sb_fv34kg0aQL-ye1mVuXVOnQ', title: 'Liberty Mutual' },
    { id: 'sb_AdT52VfkSX6P1Nv015sgGg', title: 'Lidia Bastianich' },
    { id: 'sb_E-9fosKrT0Kk1C_yiKRCxg', title: 'Lily Collins' },
    { id: 'sb_d9E2-jECSn6CaMNbuonR2g', title: 'Linda Hamilton' },
    { id: 'sb_dR8-DFpzS4uVyKq1XbayDQ', title: 'Lindsey Vonn' },
    { id: 'sb_YH6bgKq5QsW0FhCcOCX-6Q', title: 'LinkedIn' },
    { id: 'sb_VetcDvqJRm-enUfgyCLF0w', title: 'Lionel Messi' },
    { id: 'sb_MIZJDam3TfeSeYGmA1YOKg', title: 'Lisa Kudrow' },
    { id: 'sb_Hn78uqA4QIuijtKkgeKeZg', title: 'Lizzo' },
    { id: 'sb_crTOHV-4RaOg0AsivfYXdA', title: 'LLoyds Banking' },
    { id: 'sb_BWEnB40qSo6HehMTnKu_fw', title: 'Louis Theroux' },
    { id: 'sb_YLtEKK7uSm6SqrnsXdMtiA', title: 'Louis Vuitton' },
    { id: 'sb_VZ5C2aCPR-mhhI3S8UwwzA', title: 'Lowes' },
    { id: 'sb_TwxCKfXxTY-aGIOD1XmFBg', title: 'Luke Bryan' },
    { id: 'sb_cbVNp1xKSbu0zStu8ZSmkQ', title: 'Lululemon' },
    { id: 'sb_MSJnYyWAQ12aqC0gCpv3Ug', title: 'Luxury Goods' },
    { id: 'sb_MIMYEV3MRF-jXW99JgzHWw', title: 'LV Insurance' },
    { id: 'sb_JPFGo0XkSeWFh-f5cfpz9A', title: 'Mads Mikkelsen' },
    { id: 'sb_RpAffxN3QDagOx9YuXdI1g', title: 'Mah√© Drysdale' },
    { id: 'sb_TEzTToJ4Tu2UDFgJnCMUfQ', title: 'Mahershala Ali' },
    { id: 'sb_BYGUWYDYRgW9k7WB-uThcw', title: 'MAIF Insurance' },
    { id: 'sb_X8cmQGHBRBaydrRm1rE5eQ', title: 'Mandy Moore' },
    { id: 'sb_OnIaMWDBScep5MgLgsV-CQ', title: 'Manny Machado' },
    { id: 'sb_AHXhnbZQSNiIjKF_nfr_tQ', title: 'Manuel Neuer' },
    { id: 'sb_HzPTEWQaQIK8DMh-rOCj8Q', title: 'MAPFRE Insurance' },
    { id: 'sb_KTzxSAHATcyTIGxSgR270g', title: 'Marcel Hirscher' },
    { id: 'sb_JXIXM6LITtGvLV8KZENt8g', title: 'Marcus Samuelsson' },
    { id: 'sb_bdthL3tfRvmW-GFLTMrNiQ', title: 'Margot Robbie' },
    { id: 'sb_IY7F0jrWRke0Cl20BFEMAA', title: 'Maria Sharapova' },
    { id: 'sb_c-2ahrvkS7eRO57AyOB2PQ', title: 'Mario Batali' },
    { id: 'sb_NlCt4zQuQm6EVIDDVXuKcQ', title: 'Mariska Hargitay' },
    { id: 'sb_KR3zO8BBT2e5bm_XKP7Zhg', title: 'Mark Gatiss' },
    { id: 'sb_EJLaImJYQreiKC3R5b3V0g', title: 'Mark Hamill' },
    { id: 'sb_W9sxgtIYRyCdhkSzARFKrw', title: 'Mark Harmon' },
    { id: 'sb_OnCaF8TxSImZrgJgHY_Y4g', title: 'Mark Ruffalo' },
    { id: 'sb_EN4Rr0T1SH6p9S7WFWgbgg', title: 'Mark Zuckerberg' },
    { id: 'sb_FODsp960RKeBnELk_JP_2w', title: 'Marshon Lattimore' },
    { id: 'sb_ZZqihJKcRKGM6OP7Qr6Kdw', title: 'Martin Freeman' },
    { id: 'sb_QH9bBR03SbGvXwkML8LkoA', title: 'Martin Sheen' },
    { id: 'sb_AkSkFdkCSZCKkhDVDvYq9g', title: 'Martin Yan' },
    { id: 'sb_JIZTaewWSbyWmS626FhMrg', title: 'Mary J. Blige' },
    { id: 'sb_NktdicBMQEWHSk72oWLc2Q', title: 'Massey Services' },
    { id: 'sb_PwX2HpoOS7mxtmCGIGULpg', title: 'MassMutual' },
    { id: 'sb_Mb0oymhlQq-nAmPZT3brow', title: 'Matt Damon' },
    { id: 'sb_Ct8RfsMkTtS6ejKHAO-E8g', title: 'Matt Ryan' },
    { id: 'sb_LBFNJdIARlOJJc0DPMTlAg', title: 'Matthew Broderick' },
    { id: 'sb_OwbwVAHHRXmoGm-4Md35xw', title: 'Matthew McConaughey' },
    { id: 'sb_Ik6ENAMARNiB9_Vj0FIjwA', title: 'Maya Moore' },
    { id: 'sb_e9dFDhehSwSM3y1R9tKoyg', title: 'McDonalds' },
    { id: 'sb_Bw_PN910TpSg3oIpo8_56Q', title: 'Meat Loaf' },
    { id: 'sb_CCXie_0lTPS0J1Ac7vK7Eg', title: 'Medical Health' },
    { id: 'sb_N8Syo4wrTz6ZTFk_N1PUAg', title: 'Megan Rapinoe' },
    { id: 'sb_e5nR0bleRh-yq1BcQ_6a2Q', title: 'Megan Thee Stallion' },
    { id: 'sb_T92Df21uQrClZYhx4AemPg', title: 'Meghan Markle' },
    { id: 'sb_CC2hitgjSfW97koyV86-Xg', title: 'Melissa McBride' },
    { id: 'sb_dF5a99JSTXCXZyb3Os0EEg', title: 'Mens Clothing & Accessories' },
    { id: 'sb_Xk2iA6_qQjizrHBpO4cRyw', title: 'Mercedes-Benz' },
    { id: 'sb_WDv3Vkr2RMGVFtifw2qiXw', title: 'Merck' },
    { id: 'sb_MwNxkUJFTa6PaXtZtBAwtw', title: 'MetLife' },
    { id: 'sb_K3tZn_6ZQXm3BpXtNmTKig', title: 'Michael B. Jordan' },
    { id: 'sb_HEJE2MJ2TqqDpo2X4siHGA', title: 'Michael Caine' },
    { id: 'sb_MjkxHdw0S6GSm86gMmIASQ', title: 'Michael Imperioli' },
    { id: 'sb_RafxNOObRuG5qMXasm9SKg', title: 'Michael J. Fox' },
    { id: 'sb_ErEV8XbRRcGMkpOyYpSqdQ', title: 'Michael Jordan' },
    { id: 'sb_WX8YV2AzRZmX3iRgaUJ8sQ', title: 'Michael Pe√±a' },
    { id: 'sb_Snh-FlxzSLm0flMVDD-IKQ', title: 'Michaela Coel' },
    { id: 'sb_Y_eN8_7jTrOB86ugokYUxA', title: 'Michelle Obama' },
    { id: 'sb_Wqt5zCwmTXWI8fsL4gIv3A', title: 'Microsoft' },
    { id: 'sb_O60olG4tQICDKVWz-VgLzg', title: 'Mikaela Shiffrin' },
    { id: 'sb_Jt3lWl7WTTeFoHlNiqSb_Q', title: 'Mike Trout' },
    { id: 'sb_PXJk4jVIS4mJxEhfnopAXQ', title: 'Mila Kunis' },
    { id: 'sb_fxjcv0q3S3ab_w3XmylriQ', title: 'Miles Teller' },
    { id: 'sb_JgqzSRe3T6G0j09mMt6UMg', title: 'Miley Cyrus' },
    { id: 'sb_J506PwynSQyB9KlRgtcl7g', title: 'Miller' },
    { id: 'sb_VHehGvvZQ_WFfEEz7wQk9w', title: 'Millie Bobby Brown' },
    { id: 'sb_UY9TMbDxQSy8RBusLq2TKQ', title: 'Milo Ventimiglia' },
    { id: 'sb_ZBV5OU4vTMGXQxFcNYSkuQ', title: 'Ming Tsai' },
    { id: 'sb_K1aYpOHZTB6M4DxgFC-o3w', title: 'Miranda Lambert' },
    { id: 'sb_cYYlb1yTSJetsDIMXoWixw', title: 'Mitch McConnell' },
    { id: 'sb_L33_wCQfT2ODkibs5Un40Q', title: 'Mitsubishi Group' },
    { id: 'sb_I-yW13ZUTcazkh_S5RFMdQ', title: 'Mohamed Salah' },
    { id: 'sb_MjEgXB91Sg6SIUFxHydBTA', title: 'Moncler' },
    { id: 'sb_XqFf1WI9QWij7W2de5UsAQ', title: 'Mookie Betts' },
    { id: 'sb_Y2KfQYoLRkC5xAZWuWVzUQ', title: 'Morgan Freeman' },
    { id: 'sb_TV5D0DCbTyaFOqzq0lF-Aw', title: 'Motorbikes and Scooters' },
    { id: 'sb_HneusNifSNmbTTFxy50kwg', title: 'Museums & Galleries' },
    { id: 'sb_O8PYlVQiQru77ALS_M00Rg', title: 'Music & Audio' },
    { id: 'sb_HxOQQEHMRwCgOg0GgVjs7g', title: 'Nadia Giosia' },
    { id: 'sb_KcYcE_JhTfKG9CpnFVSjmQ', title: 'Nancy Pelosi' },
    { id: 'sb_PX6_TnodQZyMBfomPKK9Hw', title: 'Naomi Osaka' },
    { id: 'sb_DsfQoEJOTVa6KaZ95dMdoQ', title: 'Narendra Modi' },
    { id: 'sb_FuiC6Rx9S3aQDx1RVoK5_Q', title: 'Natalie Portman' },
    { id: 'sb_VH77sHhXTBW2xmTfmNycKA', title: 'Nathan Fillion' },
    { id: 'sb_NZcQ62oATTWlt0nFCu22hw', title: 'Nationwide Group' },
    { id: 'sb_f03rXf0SQDWCO_Ctmilolg', title: 'Neil deGrasse Tyson' },
    { id: 'sb_Gba2W-OFRxOTDXkALzUZnA', title: 'Nemonte Nenquimo' },
    { id: 'sb_ZTGkk8jqS6OlozlonKqe9w', title: 'Nestle' },
    { id: 'sb_cK2UYqDHTD2vNtCAOi5aJA', title: 'Netflix' },
    { id: 'sb_K0to5imDSpO1s1pgRdjamg', title: 'New Balance' },
    { id: 'sb_Zy534KOhSQetuOYwQssQVg', title: 'New York Life Insurance' },
    { id: 'sb_aDHpdCXTTwmTTxo3xoxevg', title: 'News & Politics' },
    { id: 'sb_c9bUjaneSlC0Q15Evn9_6g', title: 'Neymar' },
    { id: 'sb_ewKc7jRpSIe411j9zItP8w', title: 'NFU Mutual' },
    { id: 'sb_YUKQD2_7RvKZ4Ougt3ED5w', title: 'Niall Horan' },
    { id: 'sb_DjT7-ReaQYeKRrIM7DiC6Q', title: 'Nick Jonas' },
    { id: 'sb_HauTmwzzSbWcRhsaYKD6tA', title: 'Nicki Minaj' },
    { id: 'sb_We5XjNflS327_uVhM_jWhQ', title: 'Niel Armstrong' },
    { id: 'sb_aQSlvgEZQp24d-b_GR_60w', title: 'Niel Degrasse Tyson' },
    { id: 'sb_Wt1PeqNnTYSSlFX-FtQSWw', title: 'NIG' },
    { id: 'sb_M4Jrs0c5RM6Q7exOV8K2sQ', title: 'Nigella Lawson' },
    { id: 'sb_Tlmu1O-wSiy4zHtqTa3ZtQ', title: 'Nike' },
    { id: 'sb_Hrd3CJf2SnaM_HnD0XZABw', title: 'Nikon' },
    { id: 'sb_FNkDR09_Rre_5zx3ql2k4w', title: 'Nintendo' },
    { id: 'sb_BDwz_mvpSRmjRKqYB9LHaw', title: 'Non-alcoholic Drinks' },
    { id: 'sb_EiN8lFE-QTKLxg6tZlMDCQ', title: 'Norman Reedus' },
    { id: 'sb_SoltTCRhTri0mnRBkfWxww', title: 'Novak Djokovic' },
    { id: 'sb_YzST_QIuQhKqt0_j8PxBjw', title: 'Novartis' },
    { id: 'sb_AsRVjXG_QFmqL6nBtN5PZg', title: 'Nury Turkel' },
    { id: 'sb_NKsIgLrlTOaGyFbjVHbnUQ', title: 'O2' },
    { id: 'sb_ZZGtPmMeTuWZ0wFyIWaRvA', title: 'Odell Beckham Jr.' },
    { id: 'sb_J6GsmVYETCOqaIHiUSM-5g', title: 'Odessa Young' },
    { id: 'sb_bOxZzNtPR0yvNRijVaOXQw', title: 'Olivia Colman' },
    { id: 'sb_D_0TOlEES5m9RjOHcu5ooA', title: 'Olivia Cooke' },
    { id: 'sb_VYSdVgcwQGuDLXEwuEX68A', title: 'Oprah Winfrey' },
    { id: 'sb_VcQmTC0mTZGu-sZGBP4vpQ', title: 'Orkin' },
    { id: 'sb_eWwBXHu3SBG_fL6DbedrfQ', title: 'Ortho' },
    { id: 'sb_bDojNeu6QiCSdoWO5vdDsA', title: 'OUTsurance' },
    { id: 'sb_TEbBhY-qTfa6rs2OykTWMg', title: 'P!nk' },
    { id: 'sb_O-_aeYF7SUSTF1FKwAMeJA', title: 'Painting' },
    { id: 'sb_FNlH_WiiQsWCQGBuYGRMew', title: 'Panasonic' },
    { id: 'sb_GMSmdctuTOSJsmRdeXfbLQ', title: 'Patrick Mahomes' },
    { id: 'sb_EZvZ0NFLRrOmTIB0aD3L-A', title: 'Patron' },
    { id: 'sb_Sp8ONyBzSxSPg6i16o_xGw', title: 'Paul Anderson' },
    { id: 'sb_AxTBSmvQQwei1Whu4vt9uw', title: 'Paul Bates' },
    { id: 'sb_T5sU3sNsRQq0QPupESOs5w', title: 'Paul Bettany' },
    { id: 'sb_PFAS__9QRvKIzdPZu36JIA', title: 'Paul McCartney' },
    { id: 'sb_fTijF0EyQCaTWDIq54lwCA', title: 'Paul Reiser' },
    { id: 'sb_UzSaPI8kTeCRjaFCHHlzQQ', title: 'Paula Deen' },
    { id: 'sb_CUmHgUbjQKSRdZOnX2yt0Q', title: 'Pedro Pascal' },
    { id: 'sb_AnajPv_mR_WTZZY6o0D_Fw', title: 'Pepsi' },
    { id: 'sb_K8hIFYs0TQe73wYoiL9m6w', title: 'Personal Finance' },
    { id: 'sb_F17_lZpFQ7iVIHU_LI-h0w', title: 'Peter Dinklage' },
    { id: 'sb_X3QWQ9ViQPuNVGmuIgUdwg', title: 'Pets' },
    { id: 'sb_NPTlJoRxRzeD5dqP2u2hYw', title: 'Peyton Manning' },
    { id: 'sb_DIMAau1MR3-_Sqq50U35sA', title: 'Pfizer' },
    { id: 'sb_IiP6qJ0PQmOy21xzNzFQKA', title: 'Phil Jackson' },
    { id: 'sb_HhKgFusUSYeB3r90VHuIAw', title: 'Phil Mickelson' },
    { id: 'sb_RGJ-G27FQ5GRIOYQ_eIHKw', title: 'Phoebe Waller-Bridge' },
    { id: 'sb_f1rj-ExMRPGq70yq9qiKZw', title: 'Pierce Brosnan' },
    { id: 'sb_DpL8Bz0qTrCE-YjQaT00kA', title: 'Pinterest' },
    { id: 'sb_Fy6ViEBaRGqViXKgMLUAOQ', title: 'Playstation' },
    { id: 'sb_UweX0wigQQOb8TIUtevwVg', title: 'Plunketts Pest Control' },
    { id: 'sb_bgE5h9XAR1qCOHw4SkTGvQ', title: 'Porsche' },
    { id: 'sb_KBv02iOxQOiCXfF0w-SrIw', title: 'Post Malone' },
    { id: 'sb_KD4bglU1SAe9fev3WYLGWg', title: 'Pregnancy' },
    { id: 'sb_EFAMvea6Rw2asLK6-JkUxA', title: 'Prince Charles' },
    { id: 'sb_WTJgChIGTJu2b1k9s9wZpg', title: 'Prince Harry' },
    { id: 'sb_Vc-Lowj_Twy9bSy_Bi2sNg', title: 'Prince Philip' },
    { id: 'sb_PBur0ac_RhO3aG6HJDeImQ', title: 'Prince William' },
    { id: 'sb_fjw3VLfdRfqpN-dyl9FYrA', title: 'Principal Financial Group' },
    { id: 'sb_KryzbnGFT8iGhbKwI9oMvg', title: 'Progressive' },
    { id: 'sb_F8j9P_t5RiyPU9rvKZ9beA', title: 'Prudential' },
    { id: 'sb_dye8GYb3Skain6X846AHtw', title: 'Puma' },
    { id: 'sb_NV0BeW86TQKEPtmQiRHj0A', title: 'Purina' },
    { id: 'sb_WWRYn3hoQ8-0AJ65Ui3egw', title: 'Queen Elizabeth II' },
    { id: 'sb_dORrgylxSS244_frPxakJA', title: 'Rachael Ray' },
    { id: 'sb_RS813bMsTuuYF3ig5GSFGQ', title: 'Rachel Weisz' },
    { id: 'sb_Cbo1FflASjCAvSgnr639CA', title: 'Rafael Nadal' },
    { id: 'sb_d0V0eZgVQt6CVjsJYCE4Lw', title: 'Raid' },
    { id: 'sb_H3W30NPxSPaoQOEIXlA8cA', title: 'Rainn Wilson' },
    { id: 'sb_GLC_zoqESc2bh877AUrB2A', title: 'Ralph Fiennes' },
    { id: 'sb_cIoCmeBPTgudhu1IdGaSXQ', title: 'Rami Malek' },
    { id: 'sb_XfCs-P-YSoiG8olK2zsU7g', title: 'Ravindra Gupta' },
    { id: 'sb_J8NS8L_pR6yiuFsXI6-ftw', title: 'Ray Liotta' },
    { id: 'sb_HS4kBjEWQxe63BCjdQ73cA', title: 'Ray Nicholson' },
    { id: 'sb_XYERE6sTQSaYKFyI1Ur1rQ', title: 'Real Estate' },
    { id: 'sb_A0RVaTxCSBOwlaLUqLCU5A', title: 'Rebecca Hall' },
    { id: 'sb_Pb6PkSv0SSSYXktr4xuwIw', title: 'Red Bull' },
    { id: 'sb_N2OS-B3zR_CuLmjjUOy6qg', title: 'Reebok' },
    { id: 'sb_LcMYoT-8TrOgrelwItYE6Q', title: 'Reina Gossett' },
    { id: 'sb_CqhuA2tTQ3CWZOWG-Kv0_g', title: 'Richard Blais' },
    { id: 'sb_L4XUQCk9TVm2BJy6rffmXQ', title: 'Rick Stein' },
    { id: 'sb_Y5cvaO2xTdSA92d4d9dkOg', title: 'Rihanna' },
    { id: 'sb_G8ClddThQXGVc1ub-vkCOQ', title: 'Ringo Starr' },
    { id: 'sb_A3NVchkgSc-LFoXCqNrHwQ', title: 'Riz Ahmed' },
    { id: 'sb_SrD8op3DR761_dVPcvSOGQ', title: 'Rob Gronkowski' },
    { id: 'sb_fL8gxua_TJGVcWbydbQjLQ', title: 'Rob Lowe' },
    { id: 'sb_Tf1j6hKWQ6mtDdQ0oLfg7g', title: 'Robert De Niro' },
    { id: 'sb_c6OtWt5_SDiSaQdVT8JTcw', title: 'Robert Downey Jr.' },
    { id: 'sb_J-QWjfMyS2i-PA605U6V2A', title: 'Robert Duvall' },
    { id: 'sb_UIEnhEYUSGWzcXlaqOQkGw', title: 'Robert F. Smith' },
    { id: 'sb_CGV0_OaBSJGDxRNrkdtObg', title: 'Robert Pattinson' },
    { id: 'sb_GkuhrIjyTMSyUJOL9ql-Bw', title: 'Roche' },
    { id: 'sb_b_azcctIQfyPajhBEL0_rA', title: 'Roger Federer' },
    { id: 'sb_aPqSfKIAQHG7dkuNckWnyA', title: 'Ron Livingston' },
    { id: 'sb_OmBqCtshTtO4V0fzsIUVDw', title: 'Rory McIlroy' },
    { id: 'sb_XQ9SFdRqRJGIiR-6X2d0yQ', title: 'Rosamund Pike' },
    { id: 'sb_eqqyQOmcRJyfY1Omxv6P_A', title: 'Rowan Atkinson' },
    { id: 'sb_Llgc6SAPT2-0S3C8F_r-SA', title: 'Roy Choi' },
    { id: 'sb_EghrPlUsRP2pESj_9qac8g', title: 'Royal Bank of Scotland' },
    { id: 'sb_Qe9C_9NRSFiZzp9Y3qxBWQ', title: 'Rugby' },
    { id: 'sb_ZtdcQf94T2OiGI37RP9ZBg', title: 'Russell Crowe' },
    { id: 'sb_eKPHQJe_SKSYEP5HWvD9DQ', title: 'Russell Westbrook' },
    { id: 'sb_MsRw4prARISXsgn_SaSFwA', title: 'Russell Wilson' },
    { id: 'sb_Dljyt4DDRBKpAkhhl_K0ew', title: 'Sacha Baron Cohen' },
    { id: 'sb_LmWVFsJEQzKmjhohBp6UPA', title: 'Sally Field' },
    { id: 'sb_Ze-lmB3DTTall3W3ep6abQ', title: 'Sam Heughan' },
    { id: 'sb_LHEuaATeR9qZ-_3OIHF30g', title: 'Sam Riley' },
    { id: 'sb_V5ZL4LmySrmU5Nh5l7TVkA', title: 'Sam Smith' },
    { id: 'sb_V2Agce4zThaRxniWEbYnrQ', title: 'Sam Waterston' },
    { id: 'sb_ReL57adIRoqXoVaYl1FwrQ', title: 'Samsung' },
    { id: 'sb_UK10fwzHRO-_4CAyDbw4iA', title: 'Samuel L. Jackson' },
    { id: 'sb_P-X_fwRUQiKLhkTGFm4BNg', title: 'Sandra Oh' },
    { id: 'sb_P5QVu7ouR0-zTNWjcgzGmw', title: 'Sanofi' },
    { id: 'sb_ZcF3D1DRSbChL3n8fX6B1Q', title: 'SBS' },
    { id: 'sb_OuGGy8PcRh-KaCOUX9G1Kw', title: 'Scarlett Johansson' },
    { id: 'sb_NWV5ZpnwSsi8bi1T5c0tow', title: 'Sean Astin' },
    { id: 'sb_EGVH1K5fQQeiAKE407GBwg', title: 'Sean Hannity' },
    { id: 'sb_dtnjC7xURPexriBHY6dhjw', title: 'Sean Murray' },
    { id: 'sb_dyFc61zUQ9GPSVmiX4GiPQ', title: 'Selena Gomez' },
    { id: 'sb_Ev3xtAmeTxmNB_kANZu3-g', title: 'Selena Gomez' },
    { id: 'sb_Qs2P-OnATYOACkWIXiDL_A', title: 'Serena Williams' },
    { id: 'sb_OXpWYU0iT8-TCCGGJV4B5g', title: 'Shaquille O‚ÄôNeal' },
    { id: 'sb_aOoiE5hNTsWXC2eeT054aw', title: 'Shari Redstone' },
    { id: 'sb_LL-qv7AxSvG6296U6xG4KQ', title: 'Shaun White' },
    { id: 'sb_cTsMOcvFSVmZrR_LLkAEkw', title: 'Shawn Mendes' },
    { id: 'sb_HcSc1iBVSeyQPG5ZkTLFlQ', title: 'Shohei Ohtani' },
    { id: 'sb_E3VQFq2ISauTz7iWecudNA', title: 'Sia' },
    { id: 'sb_AaV35bFGSw2ItxJTTy4PiA', title: 'Sidney Crosby' },
    { id: 'sb_N8pXNQ0MSp-wj6qCjSuxqQ', title: 'Sigourney Weaver' },
    { id: 'sb_KaxTx-_FRM6zmlaT1oujhQ', title: 'Sigrid' },
    { id: 'sb_XUVTF_HSRNSuuGh0OBUFLQ', title: 'Sky' },
    { id: 'sb_Psvqyeb4Q_6EXFDunM0ckw', title: 'Snapchat' },
    { id: 'sb_WUbrmIWiT9uOhikbgA_FtA', title: 'Sof√≠a Vergara' },
    { id: 'sb_GKbzOVmzT2CNHG6YORZW7Q', title: 'Sony' },
    { id: 'sb_Pdy82DLoSsa8O7sxa9rlfA', title: 'Spectracide' },
    { id: 'sb_FvH5PnKaTqi3SFUx6R5x1A', title: 'Spencer Grammer' },
    { id: 'sb_Lh9J7aF2QRWEabG0A1vi6g', title: 'Sports' },
    { id: 'sb_AKdFcLTBQGGPrd5I02RArQ', title: 'Sports Betting' },
    { id: 'sb_Wvfpl6Y_SDuKm2s_LK74dg', title: 'Spotify' },
    { id: 'sb_WWlGTcxRRsuNho4TVU4Spw', title: 'Sprint' },
    { id: 'sb_SVIol8B3RmSmfk3tMY_uXw', title: 'Stacey Abrams' },
    { id: 'sb_aLNPbzGPS6iPMIBvYHZMvQ', title: 'Starbucks' },
    { id: 'sb_OPnggQm_TSy-jZTh61vgvw', title: 'State Farm' },
    { id: 'sb_Y2nC18xLTgGU1YQeEl2Gbg', title: 'Stella Artois' },
    { id: 'sb_RjwoA3WlSvio4hz6GDz-uw', title: 'Stellan Skarsg√•rd' },
    { id: 'sb_Bj5gkcyAS3ehlwNHVTIfbA', title: 'Stephen Baldwin' },
    { id: 'sb_LNT7iVArQ-WE-u6OyiTJdQ', title: 'Stephen Curry' },
    { id: 'sb_NT6gOINSSpS5wVLUiSIV9w', title: 'Sterling K. Brown' },
    { id: 'sb_SO32DWtDQ6Spt-L27r2b9g', title: 'Steven Yeun' },
    { id: 'sb_dd2mTJWRT0--MYAvjvXS5g', title: 'Style & Fashion' },
    { id: 'sb_IOBiLK1wR--R3tVCAqr6ew', title: 'Subway' },
    { id: 'sb_YTBR9KACSEKWsM8LfgI_5w', title: 'Sun Life Insurance' },
    { id: 'sb_XNucHcbpRI6ZyvlGWoInsw', title: 'Sun Yang' },
    { id: 'sb_Y2wqNAusSNOir_rtPZhv6Q', title: 'Sundar Pichai' },
    { id: 'sb_LMGaxfl1Sm-G6JMyaMFghQ', title: 'Swiss Re Insurance' },
    { id: 'sb_MlM3HeK1QvaJikR1-mEj_w', title: 'T-mobile' },
    { id: 'sb_E-jSXdHwS8aLek6j-Ye9jw', title: 'Taika Waititi' },
    { id: 'sb_VBwzm6dxRBOPneAb46B8Eg', title: 'Target' },
    { id: 'sb_B3K0ViTgRKCcUygMCUZKjg', title: 'Taylor Kitsch' },
    { id: 'sb_JA2K6_bgSz2Xjyiv0iQ5wg', title: 'Taylor Swift' },
    { id: 'sb_B5UwbswiSuqrV-jmrkBLAw', title: 'Tecate' },
    { id: 'sb_Jw-DNwqdQ_GFY3YK2f0nVQ', title: 'Technology & Computing' },
    { id: 'sb_V1uflU4nQG2HGm6RYSGfqQ', title: 'Tedros Adhanom Ghebreyesus' },
    { id: 'sb_GZJCdD49Rmai-QNbJ6ScjA', title: 'Terminix' },
    { id: 'sb_MTec--4kRku8Crs60e65mw', title: 'Terro' },
    { id: 'sb_WrpU5rCAQHGceUI5j5IsVg', title: 'Tesla' },
    { id: 'sb_MpM4c92MR3G1MXI2Ja1jJA', title: 'The Weeknd' },
    { id: 'sb_KbxEHXLBRL-Vrb2Sw63w8g', title: 'Theatre' },
    { id: 'sb_CsPk_dtxSaudx7FROtQSOQ', title: 'Tiger Woods' },
    { id: 'sb_WRQUfXt9QS6Df2c2pIrnig', title: 'TikTok' },
    { id: 'sb_LsZRfZHMRGO9jSoH4JsV6A', title: 'Tim Robbins' },
    { id: 'sb_Nva60fGVS76wS7ELX10rLg', title: 'Tim Roth' },
    { id: 'sb_bEYRKBWlRVO27fJqYVAcEQ', title: 'Tina Fey' },
    { id: 'sb_DhKUjL5dTky2zNxB_CO_hw', title: 'Titos Vodka' },
    { id: 'sb_eeU7ZxAiSmS3izOZ3CCSVA', title: 'Todd Gurley II' },
    { id: 'sb_HGs9zmITRhCnV5PrDRA0Fw', title: 'Tom Brady' },
    { id: 'sb_EqHF3ELBRpGOtmO1uxJ5UQ', title: 'Tom Colicchio' },
    { id: 'sb_LuBpd5SQRYytlTDsGBbEkA', title: 'Tom Hanks' },
    { id: 'sb_LABiC-JuTMWfpIvPaJhYCg', title: 'Tom Holland' },
    { id: 'sb_Hj3UlEn8Q1uD--d_Y8ThtQ', title: 'Tom Sizemore' },
    { id: 'sb_fW9QDsw6SQu4E2myeepJvg', title: 'Toni Collette' },
    { id: 'sb_D_UwpuKUQwmHl2N9ciAS8Q', title: 'Tony Elumelu' },
    { id: 'sb_NxXkDuS2RaWuEvm4xb8R8g', title: 'Tonya Harding' },
    { id: 'sb_X0P0aRcPR26InqN8byaYnQ', title: 'Tove Lo' },
    { id: 'sb_N_p9_lQ0QTSdWH70di0c1w', title: 'Toyota' },
    { id: 'sb_C1dEK5xbScylC3NU3c9KLg', title: 'Transport' },
    { id: 'sb_BfaTlqp_RzaByyCjm-I_Lw', title: 'Travelers' },
    { id: 'sb_Tj2a5TNbTWeUlwtUyo7t_Q', title: 'Truly Nolen' },
    { id: 'sb_QdUrPjuTRB6odDPEt3aPhQ', title: 'Tsai Ing-wen' },
    { id: 'sb_VdI2_Gj-Sbetvb8A56TLwA', title: 'TUI' },
    { id: 'sb_X8WlKrLMRRe94Vc0H-29EQ', title: 'Twitter' },
    { id: 'sb_TshAZU_RR56eeIdOj4p85g', title: 'Ty Dolla $ign' },
    { id: 'sb_RhN5pYdERjKf5a76jPQzOA', title: 'Tyler Florence' },
    { id: 'sb_cM1TIZ56QlG6euP4Y7nEAQ', title: 'Tyler Perry' },
    { id: 'sb_J-0eWsWRQwejZpETOdQ19Q', title: 'Uber' },
    { id: 'sb_YoKALcMwQe2gmFTiLDXFPg', title: 'Under Armour' },
    { id: 'sb_JVPPqeOwQ-2FpkJgqsv1VQ', title: 'Underwear Fashion' },
    { id: 'sb_b6ZmjDsmTuSwkqa6ktZorA', title: 'UNIQLO' },
    { id: 'sb_dPSvjwXPTv2KSoY0kHrGiA', title: 'United Airlines' },
    { id: 'sb_JOX7QqZHS0mrXyVsgiP8vw', title: 'Universal' },
    { id: 'sb_NATMSqyESgaSipIMsZO_uw', title: 'Ursula von der Leyen' },
    { id: 'sb_SvgRT-3sTGGqW-nNlhGxAg', title: 'USAA Insurance' },
    { id: 'sb_Bh3iESpZRQOYNQ3Gz9G6Ww', title: 'Usain Bolt' },
    { id: 'sb_NsI3NNqzQ3Kz1P2CdLHEVg', title: 'Venus Williams' },
    { id: 'sb_GYo3PCvXRb-2idnQwjGXng', title: 'Vera Miles' },
    { id: 'sb_UCaWTaagTCizPFla_Xdbdg', title: 'Verizon' },
    { id: 'sb_UTS_U8WYR2yLB3T2SCPecw', title: 'Vin Diesel' },
    { id: 'sb_PKVYS1G4RC-A0cEzQdjKbw', title: 'Virat Kohli' },
    { id: 'sb_f_vXkKG3TWOpbKmlZBFIXw', title: 'Virgin Atlantic' },
    { id: 'sb_fZh-DF5cTmOMI3-SNxCRyg', title: 'VISA' },
    { id: 'sb_RHScg9yNTHeaojYKT78O4g', title: 'Vitality Insurance' },
    { id: 'sb_Ns1TWgS5RlSJWvCZSklWLg', title: 'Vizio' },
    { id: 'sb_fBa6RKqtRn22spjPPhYj2Q', title: 'Vodafone' },
    { id: 'sb_FdRGmeA7TRisxCzOXPVNpg', title: 'Volkswagen' },
    { id: 'sb_f6mY6huXTCOhNcs2IXWiog', title: 'Waad al-Kateab' },
    { id: 'sb_Y78u8BjIQ6ONZ0o7GtdbOA', title: 'Walgreens' },
    { id: 'sb_I3ngqJooQIKOfki3GlawQA', title: 'Walmart' },
    { id: 'sb_RJC3z5CXQXCKiljPSItCcw', title: 'Warner Bros' },
    { id: 'sb_JtEf3HW6RkSg_Cs4tb9kmA', title: 'Wayne Rooney' },
    { id: 'sb_aY_YTREBQV-gUvJ244lQBQ', title: 'Weddings' },
    { id: 'sb_HyGGb0zgTAOdZPuzwxmTOQ', title: 'Weight Loss' },
    { id: 'sb_TkqFfkTkTSWVKjdcF23Bgg', title: 'Wells Fargo' },
    { id: 'sb_Am8iPnB2RzSgC0Tvc5ONKw', title: 'Western Exterminator' },
    { id: 'sb_MkKeQ1auTveL1NFhgV22sQ', title: 'Whatsapp' },
    { id: 'sb_VD2MppW2SjKnEHPVE3bXDg', title: 'Will Ferrell' },
    { id: 'sb_L72zcHRDR_WvpcTx0BCy4Q', title: 'Will Smith' },
    { id: 'sb_QJbCg69TS2uYbdmZI09a2w', title: 'Willem Dafoe' },
    { id: 'sb_FSzbSPuYSySc2xHaCUdULQ', title: 'William Barr' },
    { id: 'sb_QBJwziv8T0WVHWR0WPXoFA', title: 'William H. Macy' },
    { id: 'sb_ADBDyu7yT8-aqFkU-nnhNw', title: 'William Shatner' },
    { id: 'sb_UegHdfH9TNSGylkIgl1Kmw', title: 'Wolfgang Puck' },
    { id: 'sb_Gi5NG8xgSdOOZZHDCyzVQw', title: 'Womens Clothing & Accessories' },
    { id: 'sb_XvFdhZ0YSreRxYoopkrmzg', title: 'Wyatt Russell' },
    { id: 'sb_SrRqaLU_Tfiu7m6DwmrLUQ', title: 'Xbox' },
    { id: 'sb_cpb5uvx_RvKAdTmMSZB0_g', title: 'Xfinity' },
    { id: 'sb_G2RCci-OR3uQziOU_MOiLQ', title: 'Xi Jinping' },
    { id: 'sb_JwBlef3CTVazscBW9sKq9A', title: 'Yelena Isinbayeva' },
    { id: 'sb_CliGXKTSTN-NTQOR5v-JfA', title: 'Yousef Al Otaiba' },
    { id: 'sb_WTLwzxuzTvefuJe4hhA2dQ', title: 'Youtube' },
    { id: 'sb_f-iqeUnjScGwFdAhm7ExQA', title: 'ZARA' },
    { id: 'sb_ZogJt78NQ26k5AaYRrMm4Q', title: 'Zayn Malik' },
    { id: 'sb_Y5oMRWKgRv-ticYGYMAd6g', title: 'Zhang Yongzhen' },
    { id: 'sb_Anp4Bp0uStegy4VS7wzu4w', title: 'Zo√´ Kravitz' },
    { id: 'sb_Gxq7LaVzQ4mPGt2-PbKqrw', title: 'Zoe Saldana' },
    { id: 'sb_KThSGTsjSX6M2lEb-S4fIQ', title: 'Zosia Mamet' },
    { id: 'sb_I7syTozTQ7Kxf_VdTXKS5g', title: 'Zurich' },
  ];

  return generateIRISSegmentList(options, currentValues);
};

export const generatePreciseTVContextualSegmentList = (currentValues: string[]): IMenuList => {
  const options: IIRISOption[] = [
    { id: 'pt_1', title: 'Attractions' },
    { id: 'pt_1a', title: 'Attractions-Amusement & theme parks' },
    { id: 'pt_1b', title: 'Attractions-Historic sites & landmarks' },
    { id: 'pt_1c', title: 'Attractions-Holidays & travel' },
    { id: 'pt_1d', title: 'Attractions-Museums & galleries' },
    { id: 'pt_1e', title: 'Attractions-Shopping' },
    { id: 'pt_2', title: 'Business & finance' },
    { id: 'pt_2a', title: 'Business & finance-Big business' },
    { id: 'pt_2b', title: 'Business & finance-Careers' },
    { id: 'pt_2c', title: 'Business & finance-Economics' },
    { id: 'pt_2d', title: 'Business & finance-Offices, equipement & supplies' },
    { id: 'pt_2e', title: 'Business & finance-Personal finance' },
    { id: 'pt_2f', title: 'Business & finance-Starting & running a business' },
    { id: 'pt_3', title: 'Digital' },
    { id: 'pt_3a', title: 'Digital-Artificial intelligence' },
    { id: 'pt_3b', title: 'Digital-Crypto currency' },
    { id: 'pt_3c', title: 'Digital-Digital data & security' },
    { id: 'pt_3d', title: 'Digital-Digital software & applications' },
    { id: 'pt_3e', title: 'Digital-Internet' },
    { id: 'pt_3f', title: 'Digital-NFT' },
    { id: 'pt_3g', title: 'Digital-Programming' },
    { id: 'pt_3h', title: 'Digital-Social media' },
    { id: 'pt_3i', title: 'Digital-Virtual & augmented reality' },
    { id: 'pt_4', title: 'Electronics' },
    { id: 'pt_4a', title: 'Electronics-Cameras' },
    { id: 'pt_4b', title: 'Electronics-Gadgets' },
    { id: 'pt_4c', title: 'Electronics-GPS & navigation' },
    { id: 'pt_4d', title: 'Electronics-Laptops & PCs' },
    { id: 'pt_4e', title: 'Electronics-Media players' },
    { id: 'pt_4f', title: 'Electronics-Mobiles, tablets, & smart watches' },
    { id: 'pt_4g', title: 'Electronics-Print, copy, & fax machines' },
    { id: 'pt_4h', title: 'Electronics-Remote controlled' },
    { id: 'pt_4i', title: 'Electronics-Sound & audio' },
    { id: 'pt_4j', title: 'Electronics-TVs & projectors' },
    { id: 'pt_5', title: 'Entertainment' },
    { id: 'pt_5a', title: 'Entertainment-Books & literature' },
    { id: 'pt_5a1', title: 'Entertainment-Books & literature-Biographies' },
    { id: 'pt_5a2', title: 'Entertainment-Books & literature-Comics & graphic novels' },
    { id: 'pt_5a3', title: 'Entertainment-Books & literature-Fantasy' },
    { id: 'pt_5a4', title: 'Entertainment-Books & literature-Magazines' },
    { id: 'pt_5a5', title: 'Entertainment-Books & literature-Mystery & thrillers' },
    { id: 'pt_5a6', title: 'Entertainment-Books & literature-Poetry' },
    { id: 'pt_5a7', title: 'Entertainment-Books & literature-Romance' },
    { id: 'pt_5a8', title: 'Entertainment-Books & literature-Sci-fi' },
    { id: 'pt_5b', title: 'Entertainment-Celebrities & influencers' },
    { id: 'pt_5c', title: 'Entertainment-Comedy' },
    { id: 'pt_5d', title: 'Entertainment-Magic & illusion' },
    { id: 'pt_5e', title: 'Entertainment-Podcasts & radio' },
    { id: 'pt_5f', title: 'Entertainment-TV & Film' },
    { id: 'pt_5f1', title: 'Entertainment-TV & Film-Entertainment reviews & critic' },
    { id: 'pt_5f2', title: 'Entertainment-TV & Film-Film and TV creation' },
    { id: 'pt_5f3', title: 'Entertainment-TV & Film-Genre - Action & adventure' },
    { id: 'pt_5f4', title: 'Entertainment-TV & Film-Genre - Animation' },
    { id: 'pt_5f5', title: 'Entertainment-TV & Film-Genre - Bollywood' },
    { id: 'pt_5f6', title: 'Entertainment-TV & Film-Genre - Comedy' },
    { id: 'pt_5f7', title: 'Entertainment-TV & Film-Genre - Documentaries' },
    { id: 'pt_5f8', title: 'Entertainment-TV & Film-Genre - Drama' },
    { id: 'pt_5f9', title: 'Entertainment-TV & Film-Genre - Fantasy' },
    { id: 'pt_5f10', title: 'Entertainment-TV & Film-Genre - Holiday' },
    { id: 'pt_5f11', title: 'Entertainment-TV & Film-Genre - Horror & thrillers' },
    { id: 'pt_5f12', title: 'Entertainment-TV & Film-Genre - Romantic' },
    { id: 'pt_5f13', title: 'Entertainment-TV & Film-Genre - Sci-fi' },
    { id: 'pt_5f14', title: 'Entertainment-TV & Film-Genre - Sitcom' },
    { id: 'pt_5f15', title: 'Entertainment-TV & Film-Genre - Superhero' },
    { id: 'pt_6', title: 'Events' },
    { id: 'pt_6a', title: 'Events-Awards shows' },
    { id: 'pt_6b', title: 'Events-Cultural' },
    { id: 'pt_6b1', title: 'Events-Cultural-Burning Man' },
    { id: 'pt_6b2', title: 'Events-Cultural-Carnival' },
    { id: 'pt_6b3', title: 'Events-Cultural-Chinese New Year' },
    { id: 'pt_6b4', title: 'Events-Cultural-Christmas' },
    { id: 'pt_6b5', title: 'Events-Cultural-Easter' },
    { id: 'pt_6b6', title: 'Events-Cultural-Edinburgh Fringe Festival' },
    { id: 'pt_6b7', title: 'Events-Cultural-Film festivals' },
    { id: 'pt_6b8', title: 'Events-Cultural-Halloween' },
    { id: 'pt_6b9', title: 'Events-Cultural-Mardi Gras' },
    { id: 'pt_6b10', title: 'Events-Cultural-Music festivals' },
    { id: 'pt_6b11', title: 'Events-Cultural-Oktoberfest' },
    { id: 'pt_6b12', title: 'Events-Cultural-Thanksgiving' },
    { id: 'pt_6b13', title: 'Events-Cultural-Valentines day' },
    { id: 'pt_6c', title: 'Events-Fan conventions' },
    { id: 'pt_6d', title: 'Events-Personal events' },
    { id: 'pt_6d1', title: 'Events-Personal events-Bachelor / bachelorette party' },
    { id: 'pt_6d2', title: 'Events-Personal events-Birthday' },
    { id: 'pt_6d3', title: 'Events-Personal events-Funeral' },
    { id: 'pt_6d4', title: 'Events-Personal events-Honeymoon' },
    { id: 'pt_6d5', title: 'Events-Personal events-Wedding' },
    { id: 'pt_6e', title: 'Events-Sporting events' },
    { id: 'pt_6e1', title: 'Events-Sporting events-Cricket World Cup' },
    { id: 'pt_6e2', title: 'Events-Sporting events-Daytonna 500 (Racing)' },
    { id: 'pt_6e3', title: 'Events-Sporting events-Euros (Soccer)' },
    { id: 'pt_6e4', title: 'Events-Sporting events-Indianapolis 500 (Racing)' },
    { id: 'pt_6e5', title: 'Events-Sporting events-Kentucky Derby (Horse Racing)' },
    { id: 'pt_6e6', title: 'Events-Sporting events-March Madness (Basketball)' },
    { id: 'pt_6e7', title: 'Events-Sporting events-NBA Finals (Basketball)' },
    { id: 'pt_6e8', title: 'Events-Sporting events-Olympic Games' },
    { id: 'pt_6e9', title: 'Events-Sporting events-Paralympics' },
    { id: 'pt_6e10', title: 'Events-Sporting events-Rugby World Cup' },
    { id: 'pt_6e11', title: 'Events-Sporting events-Ryder Cup (Golf)' },
    { id: 'pt_6e12', title: 'Events-Sporting events-Stanley Cup Finals (Ice Hockey)' },
    { id: 'pt_6e13', title: 'Events-Sporting events-Superbowl (NFL)' },
    { id: 'pt_6e14', title: 'Events-Sporting events-The Ashes' },
    { id: 'pt_6e15', title: 'Events-Sporting events-The Grand National (Horse Racing)' },
    { id: 'pt_6e16', title: 'Events-Sporting events-The Masters (Golf)' },
    { id: 'pt_6e17', title: 'Events-Sporting events-Tour De France (Cycling)' },
    { id: 'pt_6e18', title: 'Events-Sporting events-Wimbledon (Tennis)' },
    { id: 'pt_6e19', title: 'Events-Sporting events-Winter Olympics' },
    { id: 'pt_6e20', title: 'Events-Sporting events-World Cup (Soccer)' },
    { id: 'pt_6e21', title: 'Events-Sporting events-World Series (Baseball)' },
    { id: 'pt_6e22', title: 'Events-Sporting events-X Games' },
    { id: 'pt_7', title: 'Family & relationships' },
    { id: 'pt_7a', title: 'Family & relationships-Child care & parenting' },
    { id: 'pt_7b', title: 'Family & relationships-Family' },
    { id: 'pt_7c', title: 'Family & relationships-Friendship' },
    { id: 'pt_7d', title: 'Family & relationships-Romantic relationships' },
    { id: 'pt_8', title: 'Fashion & beauty' },
    { id: 'pt_8a', title: 'Fashion & beauty-Beauty products' },
    { id: 'pt_8b', title: 'Fashion & beauty-Beauty services' },
    { id: 'pt_8c', title: 'Fashion & beauty-Body modification' },
    { id: 'pt_8d', title: 'Fashion & beauty-High fashion' },
    { id: 'pt_8e', title: 'Fashion & beauty-Mens fashion' },
    { id: 'pt_8e1', title: 'Fashion & beauty-Mens fashion-Mens - Accessories' },
    { id: 'pt_8e2', title: 'Fashion & beauty-Mens fashion-Mens - Business' },
    { id: 'pt_8e3', title: 'Fashion & beauty-Mens fashion-Mens - Clothing' },
    { id: 'pt_8e4', title: 'Fashion & beauty-Mens fashion-Mens - Shoes' },
    { id: 'pt_8e5', title: 'Fashion & beauty-Mens fashion-Mens - Sportswear' },
    { id: 'pt_8f', title: 'Fashion & beauty-Personal grooming' },
    { id: 'pt_8g', title: 'Fashion & beauty-Skin care' },
    { id: 'pt_8h', title: 'Fashion & beauty-Womens fashion' },
    { id: 'pt_8h1', title: 'Fashion & beauty-Womens fashion-Womens - Accessories' },
    { id: 'pt_8h2', title: 'Fashion & beauty-Womens fashion-Womens - Business' },
    { id: 'pt_8h3', title: 'Fashion & beauty-Womens fashion-Womens - Clothing' },
    { id: 'pt_8h4', title: 'Fashion & beauty-Womens fashion-Womens - Shoes' },
    { id: 'pt_8h5', title: 'Fashion & beauty-Womens fashion-Womens - Sportswear' },
    { id: 'pt_9', title: 'Food & drink' },
    { id: 'pt_9a', title: 'Food & drink-Baking & desserts' },
    { id: 'pt_9a1', title: 'Food & drink-Baking & desserts-Baking accessories' },
    { id: 'pt_9a2', title: 'Food & drink-Baking & desserts-Biscuits & cookies' },
    { id: 'pt_9a3', title: 'Food & drink-Baking & desserts-Bread' },
    { id: 'pt_9a4', title: 'Food & drink-Baking & desserts-Cakes' },
    { id: 'pt_9a5', title: 'Food & drink-Baking & desserts-Ice cream' },
    { id: 'pt_9a6', title: 'Food & drink-Baking & desserts-Mixers' },
    { id: 'pt_9a7', title: 'Food & drink-Baking & desserts-Pastries' },
    { id: 'pt_9b', title: 'Food & drink-Beverages' },
    { id: 'pt_9b1', title: 'Food & drink-Beverages-Alcohol - Beer' },
    { id: 'pt_9b2', title: 'Food & drink-Beverages-Alcohol - Cider' },
    { id: 'pt_9b3', title: 'Food & drink-Beverages-Alcohol - Cocktails' },
    { id: 'pt_9b4', title: 'Food & drink-Beverages-Alcohol - Gin' },
    { id: 'pt_9b5', title: 'Food & drink-Beverages-Alcohol - Liqueur' },
    { id: 'pt_9b6', title: 'Food & drink-Beverages-Alcohol - Tequila' },
    { id: 'pt_9b7', title: 'Food & drink-Beverages-Alcohol - Vodka' },
    { id: 'pt_9b8', title: 'Food & drink-Beverages-Alcohol - Whiskey' },
    { id: 'pt_9b9', title: 'Food & drink-Beverages-Alcohol - Wine' },
    { id: 'pt_9b10', title: 'Food & drink-Beverages-Beverage accessories' },
    { id: 'pt_9b11', title: 'Food & drink-Beverages-Coffee' },
    { id: 'pt_9b12', title: 'Food & drink-Beverages-Juice, smoothies, and shakes' },
    { id: 'pt_9b13', title: 'Food & drink-Beverages-Mocktails' },
    { id: 'pt_9b14', title: 'Food & drink-Beverages-Soda & pop' },
    { id: 'pt_9b15', title: 'Food & drink-Beverages-Sparkling water' },
    { id: 'pt_9b16', title: 'Food & drink-Beverages-Sports drinks' },
    { id: 'pt_9b17', title: 'Food & drink-Beverages-Tea' },
    { id: 'pt_9c', title: 'Food & drink-Cooking' },
    { id: 'pt_9c1', title: 'Food & drink-Cooking-Barbecue' },
    { id: 'pt_9c2', title: 'Food & drink-Cooking-Boil' },
    { id: 'pt_9c3', title: 'Food & drink-Cooking-Casserole' },
    { id: 'pt_9c4', title: 'Food & drink-Cooking-Compact ovens' },
    { id: 'pt_9c5', title: 'Food & drink-Cooking-Cookers, ovens, and stovetops' },
    { id: 'pt_9c6', title: 'Food & drink-Cooking-Cooking accessories' },
    { id: 'pt_9c7', title: 'Food & drink-Cooking-Cookware' },
    { id: 'pt_9c8', title: 'Food & drink-Cooking-Deep fry' },
    { id: 'pt_9c9', title: 'Food & drink-Cooking-Fry' },
    { id: 'pt_9c10', title: 'Food & drink-Cooking-Grill' },
    { id: 'pt_9c11', title: 'Food & drink-Cooking-Induction cooking' },
    { id: 'pt_9c12', title: 'Food & drink-Cooking-Microwave' },
    { id: 'pt_9c13', title: 'Food & drink-Cooking-Rice cooker' },
    { id: 'pt_9c14', title: 'Food & drink-Cooking-Roast' },
    { id: 'pt_9c15', title: 'Food & drink-Cooking-Slow cooker' },
    { id: 'pt_9c16', title: 'Food & drink-Cooking-Smoke' },
    { id: 'pt_9c17', title: 'Food & drink-Cooking-Steam' },
    { id: 'pt_9c18', title: 'Food & drink-Cooking-Stew & soup' },
    { id: 'pt_9c19', title: 'Food & drink-Cooking-Toast' },
    { id: 'pt_9d', title: 'Food & drink-Diets & lifestyles' },
    { id: 'pt_9d1', title: 'Food & drink-Diets & lifestyles-Carnivore' },
    { id: 'pt_9d2', title: 'Food & drink-Diets & lifestyles-Diabetes friendly' },
    { id: 'pt_9d3', title: 'Food & drink-Diets & lifestyles-Free from' },
    { id: 'pt_9d4', title: 'Food & drink-Diets & lifestyles-Halal' },
    { id: 'pt_9d5', title: 'Food & drink-Diets & lifestyles-Healthy' },
    { id: 'pt_9d6', title: 'Food & drink-Diets & lifestyles-Intermittent fasting' },
    { id: 'pt_9d7', title: 'Food & drink-Diets & lifestyles-Junk food' },
    { id: 'pt_9d8', title: 'Food & drink-Diets & lifestyles-Ketogenic' },
    { id: 'pt_9d9', title: 'Food & drink-Diets & lifestyles-Kosher' },
    { id: 'pt_9d10', title: 'Food & drink-Diets & lifestyles-Low carb' },
    { id: 'pt_9d11', title: 'Food & drink-Diets & lifestyles-Organic' },
    { id: 'pt_9d12', title: 'Food & drink-Diets & lifestyles-Paleo' },
    { id: 'pt_9d13', title: 'Food & drink-Diets & lifestyles-Pescatarian' },
    { id: 'pt_9d14', title: 'Food & drink-Diets & lifestyles-Vegan' },
    { id: 'pt_9d15', title: 'Food & drink-Diets & lifestyles-Vegetarian' },
    { id: 'pt_9e', title: 'Food & drink-Dining' },
    { id: 'pt_9e1', title: 'Food & drink-Dining-Cafe' },
    { id: 'pt_9e2', title: 'Food & drink-Dining-Casual dining' },
    { id: 'pt_9e3', title: 'Food & drink-Dining-Family dining' },
    { id: 'pt_9e4', title: 'Food & drink-Dining-Fast food' },
    { id: 'pt_9e5', title: 'Food & drink-Dining-Fine dining' },
    { id: 'pt_9e6', title: 'Food & drink-Dining-Food trucks & concession stands' },
    { id: 'pt_9e7', title: 'Food & drink-Dining-Pubs & bars' },
    { id: 'pt_9f', title: 'Food & drink-Food prep' },
    { id: 'pt_9f1', title: 'Food & drink-Food prep-Blender' },
    { id: 'pt_9f2', title: 'Food & drink-Food prep-Curing' },
    { id: 'pt_9f3', title: 'Food & drink-Food prep-Food maker' },
    { id: 'pt_9f4', title: 'Food & drink-Food prep-Food processors' },
    { id: 'pt_9f5', title: 'Food & drink-Food prep-Fridge, freezers, and coolers' },
    { id: 'pt_9f6', title: 'Food & drink-Food prep-Grinder' },
    { id: 'pt_9f7', title: 'Food & drink-Food prep-Pasta maker' },
    { id: 'pt_9f8', title: 'Food & drink-Food prep-Salad' },
    { id: 'pt_9f9', title: 'Food & drink-Food prep-Vaccuum packaging' },
    { id: 'pt_9g', title: 'Food & drink-Seasonal food & drink' },
    { id: 'pt_9h', title: 'Food & drink-Snacks' },
    { id: 'pt_9h1', title: 'Food & drink-Snacks-Candy' },
    { id: 'pt_9h2', title: 'Food & drink-Snacks-Fruit' },
    { id: 'pt_9h3', title: 'Food & drink-Snacks-Savory' },
    { id: 'pt_9i', title: 'Food & drink-World cuisine' },
    { id: 'pt_9i1', title: 'Food & drink-World cuisine-Cuisine - African' },
    { id: 'pt_9i2', title: 'Food & drink-World cuisine-Cuisine - American' },
    { id: 'pt_9i3', title: 'Food & drink-World cuisine-Cuisine - British' },
    { id: 'pt_9i4', title: 'Food & drink-World cuisine-Cuisine - Caribbean' },
    { id: 'pt_9i5', title: 'Food & drink-World cuisine-Cuisine - Chinese' },
    { id: 'pt_9i6', title: 'Food & drink-World cuisine-Cuisine - Eastern European' },
    { id: 'pt_9i7', title: 'Food & drink-World cuisine-Cuisine - European' },
    { id: 'pt_9i8', title: 'Food & drink-World cuisine-Cuisine - French' },
    { id: 'pt_9i9', title: 'Food & drink-World cuisine-Cuisine - Greek' },
    { id: 'pt_9i10', title: 'Food & drink-World cuisine-Cuisine - Indian' },
    { id: 'pt_9i11', title: 'Food & drink-World cuisine-Cuisine - Italian' },
    { id: 'pt_9i12', title: 'Food & drink-World cuisine-Cuisine - Japanese' },
    { id: 'pt_9i13', title: 'Food & drink-World cuisine-Cuisine - Korean' },
    { id: 'pt_9i14', title: 'Food & drink-World cuisine-Cuisine - Mexican' },
    { id: 'pt_9i15', title: 'Food & drink-World cuisine-Cuisine - Spanish' },
    { id: 'pt_9i16', title: 'Food & drink-World cuisine-Cuisine - Thai' },
    { id: 'pt_9i17', title: 'Food & drink-World cuisine-Cuisine - Vietnamese' },
    { id: 'pt_10', title: 'Games' },
    { id: 'pt_10a', title: 'Games-Board games' },
    { id: 'pt_10b', title: 'Games-Card games' },
    { id: 'pt_10c', title: 'Games-Gambling' },
    { id: 'pt_10d', title: 'Games-Physical games' },
    { id: 'pt_10e', title: 'Games-Puzzles & jigsaws' },
    { id: 'pt_10f', title: 'Games-Roleplay' },
    { id: 'pt_10g', title: 'Games-Video games' },
    { id: 'pt_10g1', title: 'Games-Video games-Among us' },
    { id: 'pt_10g2', title: 'Games-Video games-Animal crossing' },
    { id: 'pt_10g3', title: 'Games-Video games-Assassins creed' },
    { id: 'pt_10g4', title: 'Games-Video games-Call of duty' },
    { id: 'pt_10g5', title: 'Games-Video games-Console games' },
    { id: 'pt_10g6', title: 'Games-Video games-Dota' },
    { id: 'pt_10g7', title: 'Games-Video games-esports' },
    { id: 'pt_10g8', title: 'Games-Video games-Fifa' },
    { id: 'pt_10g9', title: 'Games-Video games-Final fantasy' },
    { id: 'pt_10g10', title: 'Games-Video games-Fortnite' },
    { id: 'pt_10g11', title: 'Games-Video games-GTA' },
    { id: 'pt_10g12', title: 'Games-Video games-Halo' },
    { id: 'pt_10g13', title: 'Games-Video games-Handheld games' },
    { id: 'pt_10g14', title: 'Games-Video games-League of legends' },
    { id: 'pt_10g15', title: 'Games-Video games-Madden nfl' },
    { id: 'pt_10g16', title: 'Games-Video games-Mario bros' },
    { id: 'pt_10g17', title: 'Games-Video games-Minecraft' },
    { id: 'pt_10g18', title: 'Games-Video games-Mobile games' },
    { id: 'pt_10g19', title: 'Games-Video games-Overwatch' },
    { id: 'pt_10g20', title: 'Games-Video games-PC games' },
    { id: 'pt_10g21', title: 'Games-Video games-Pokemon' },
    { id: 'pt_10g22', title: 'Games-Video games-Resident evil' },
    { id: 'pt_10g23', title: 'Games-Video games-Roblox' },
    { id: 'pt_10g24', title: 'Games-Video games-Tetris' },
    { id: 'pt_10g25', title: 'Games-Video games-The legend of zelda' },
    { id: 'pt_10g26', title: 'Games-Video games-The sims' },
    { id: 'pt_10g27', title: 'Games-Video games-World of warcraft' },
    { id: 'pt_11', title: 'Global events & news' },
    { id: 'pt_11a', title: 'Global events & news-Climate change' },
    { id: 'pt_11b', title: 'Global events & news-Coronavirus' },
    { id: 'pt_11c', title: 'Global events & news-Journalism & reporting' },
    { id: 'pt_11e4', title: 'Global events & news-Social movements' },
    { id: 'pt_11d', title: 'Global events & news-Social movements-Gender & sex' },
    { id: 'pt_11e1', title: 'Global events & news-Social movements-Human rights' },
    { id: 'pt_11e2', title: 'Global events & news-Social movements-Poverty' },
    { id: 'pt_11e3', title: 'Global events & news-Social movements-Race' },
    { id: 'pt_11e5', title: 'Global events & news-UK politics' },
    { id: 'pt_11f', title: 'Global events & news-UK royal family' },
    { id: 'pt_11g', title: 'Global events & news-US politics' },
    { id: 'pt_12', title: 'Government' },
    { id: 'pt_12a', title: 'Government-Emergency services' },
    { id: 'pt_12b', title: 'Government-Housing & community services' },
    { id: 'pt_12c', title: 'Government-Immigration' },
    { id: 'pt_12d', title: 'Government-Infrastructure' },
    { id: 'pt_12e', title: 'Government-Legal system' },
    { id: 'pt_12f', title: 'Government-Military' },
    { id: 'pt_12g', title: 'Government-Politics' },
    { id: 'pt_13', title: 'Health & wellbeing' },
    { id: 'pt_13a', title: 'Health & wellbeing-Disability' },
    { id: 'pt_13b', title: 'Health & wellbeing-Exercise & fitness' },
    { id: 'pt_13b1', title: 'Health & wellbeing-Exercise & fitness-Cardio workouts' },
    { id: 'pt_13b2', title: 'Health & wellbeing-Exercise & fitness-Resistance workouts' },
    { id: 'pt_13b3', title: 'Health & wellbeing-Exercise & fitness-Yoga & stretching' },
    { id: 'pt_13c', title: 'Health & wellbeing-Eye care' },
    { id: 'pt_13d', title: 'Health & wellbeing-Medical' },
    { id: 'pt_13d1', title: 'Health & wellbeing-Medical-Alternative medicine' },
    { id: 'pt_13d2', title: 'Health & wellbeing-Medical-Disease & illness' },
    { id: 'pt_13d3', title: 'Health & wellbeing-Medical-Genetics' },
    { id: 'pt_13d4', title: 'Health & wellbeing-Medical-Healthcare' },
    { id: 'pt_13d5', title: 'Health & wellbeing-Medical-Paediatrician' },
    { id: 'pt_13d6', title: 'Health & wellbeing-Medical-Pharmaceutical' },
    { id: 'pt_13d7', title: 'Health & wellbeing-Medical-Surgery' },
    { id: 'pt_13e', title: 'Health & wellbeing-Mental health' },
    { id: 'pt_13f', title: 'Health & wellbeing-Mindfullness' },
    { id: 'pt_13g', title: 'Health & wellbeing-Oral health' },
    { id: 'pt_13h', title: 'Health & wellbeing-Physical care services' },
    { id: 'pt_13h1', title: 'Health & wellbeing-Physical care services-Acupuncture' },
    { id: 'pt_13h2', title: 'Health & wellbeing-Physical care services-Massage' },
    {
      id: 'pt_13h3',
      title: 'Health & wellbeing-Physical care services-Physiotherapy & physical rehab',
    },
    { id: 'pt_13i', title: 'Health & wellbeing-Sexual health' },
    { id: 'pt_13j', title: 'Health & wellbeing-Sleep & tiredness' },
    { id: 'pt_13k', title: 'Health & wellbeing-Socializing' },
    { id: 'pt_14', title: 'Home & garden' },
    { id: 'pt_14a', title: 'Home & garden-Antiquing' },
    { id: 'pt_14b', title: 'Home & garden-Cleaning' },
    { id: 'pt_14c', title: 'Home & garden-Garden care & plants' },
    { id: 'pt_14d', title: 'Home & garden-Home construction & renovation' },
    { id: 'pt_14e', title: 'Home & garden-Home decor' },
    { id: 'pt_14f', title: 'Home & garden-Home utilities' },
    { id: 'pt_14g', title: 'Home & garden-Outdoor furniture' },
    { id: 'pt_14h', title: 'Home & garden-Outdoor structures & landscaping' },
    { id: 'pt_15', title: 'Industry' },
    { id: 'pt_15a', title: 'Industry-Construction' },
    { id: 'pt_15b', title: 'Industry-Electric' },
    { id: 'pt_15c', title: 'Industry-Energy' },
    { id: 'pt_15c1', title: 'Industry-Energy-Fossil fuels & oil' },
    { id: 'pt_15c2', title: 'Industry-Energy-Green' },
    { id: 'pt_15c3', title: 'Industry-Energy-Nuclear' },
    { id: 'pt_15d', title: 'Industry-Farming & agriculture' },
    { id: 'pt_15e', title: 'Industry-Fishing' },
    { id: 'pt_15f', title: 'Industry-Manufacturing' },
    { id: 'pt_15g', title: 'Industry-Mining' },
    { id: 'pt_15h', title: 'Industry-Plumbing' },
    { id: 'pt_15i', title: 'Industry-Transportation' },
    { id: 'pt_15j', title: 'Industry-Waste' },
    { id: 'pt_16', title: 'Kids - Activities & construction' },
    { id: 'pt_16a', title: 'Kids - Activities & construction-Arts & Crafts' },
    { id: 'pt_16b', title: 'Kids - Activities & construction-Building' },
    { id: 'pt_16c', title: 'Kids - Activities & construction-Clay putty and compounds' },
    { id: 'pt_16d', title: 'Kids - Activities & construction-Dress up & role play' },
    { id: 'pt_16e', title: 'Kids - Activities & construction-Easels & activity tables' },
    { id: 'pt_16f', title: 'Kids - Activities & construction-Food / cooking activity kits' },
    { id: 'pt_16g', title: 'Kids - Activities & construction-Jewelry & bead kits' },
    { id: 'pt_16h', title: 'Kids - Activities & construction-Magic kits' },
    { id: 'pt_17', title: 'Kids - Content' },
    { id: 'pt_17a', title: 'Kids - Content-Alvin and the Chipmunks' },
    { id: 'pt_17b', title: 'Kids - Content-Amazing World of Gumball' },
    { id: 'pt_17c', title: 'Kids - Content-Annedroids' },
    { id: 'pt_17d', title: 'Kids - Content-Apple and Onion' },
    { id: 'pt_17e', title: 'Kids - Content-Barbie' },
    { id: 'pt_17f', title: 'Kids - Content-Bella and the Bulldogs' },
    { id: 'pt_17g', title: 'Kids - Content-Ben and Hollys Little Kingdom' },
    { id: 'pt_17h', title: 'Kids - Content-Ben10' },
    { id: 'pt_17i', title: 'Kids - Content-Big Clive' },
    { id: 'pt_17j', title: 'Kids - Content-Bing' },
    { id: 'pt_17k', title: 'Kids - Content-Blaze and the Monster Machines' },
    { id: 'pt_17l', title: 'Kids - Content-Blippi' },
    { id: 'pt_17m', title: 'Kids - Content-Boss Baby' },
    { id: 'pt_17n', title: 'Kids - Content-Bunkd' },
    { id: 'pt_17o', title: 'Kids - Content-Craig of the Creek' },
    { id: 'pt_17p', title: 'Kids - Content-Curious George' },
    { id: 'pt_17q', title: 'Kids - Content-Daniel Tigers Neighbourhood' },
    { id: 'pt_17r', title: 'Kids - Content-DanTDM' },
    { id: 'pt_17s', title: 'Kids - Content-Dino Dana' },
    { id: 'pt_17t', title: 'Kids - Content-Disney princesses' },
    { id: 'pt_17u', title: 'Kids - Content-Dr Seuss' },
    { id: 'pt_17v', title: 'Kids - Content-Educational' },
    { id: 'pt_17w', title: 'Kids - Content-Elena of Avalor' },
    { id: 'pt_17x', title: 'Kids - Content-Fireman Sam' },
    { id: 'pt_17y', title: 'Kids - Content-Floogals' },
    { id: 'pt_17z', title: 'Kids - Content-Freaktown' },
    { id: 'pt_17aa', title: 'Kids - Content-Full House / Fuller House' },
    { id: 'pt_17ab', title: 'Kids - Content-Game Shakers' },
    { id: 'pt_17ac', title: 'Kids - Content-Graces World' },
    { id: 'pt_17ad', title: 'Kids - Content-Grizzy and the Lemmings' },
    { id: 'pt_17ae', title: 'Kids - Content-Henry Danger' },
    { id: 'pt_17af', title: 'Kids - Content-Hey Duggee' },
    { id: 'pt_17ag', title: 'Kids - Content-Horrid Henry' },
    { id: 'pt_17ah', title: 'Kids - Content-In the Night Garden' },
    { id: 'pt_17ai', title: 'Kids - Content-Jacksepticeye' },
    { id: 'pt_17aj', title: 'Kids - Content-Jessie' },
    { id: 'pt_17ak', title: 'Kids - Content-Kim Possible' },
    { id: 'pt_17al', title: 'Kids - Content-L.O.L surprise' },
    { id: 'pt_17am', title: 'Kids - Content-Larva Island' },
    { id: 'pt_17an', title: 'Kids - Content-LEGO' },
    { id: 'pt_17ao', title: 'Kids - Content-Little Baby Bum' },
    { id: 'pt_17ap', title: 'Kids - Content-Looney Tunes' },
    { id: 'pt_17aq', title: 'Kids - Content-Lost in Oz' },
    { id: 'pt_17ar', title: 'Kids - Content-Masha and the Bear' },
    { id: 'pt_17as', title: 'Kids - Content-Mia and Me' },
    { id: 'pt_17at', title: 'Kids - Content-Mickey Mouse & Friends' },
    { id: 'pt_17au', title: 'Kids - Content-Milo Murphys Law' },
    { id: 'pt_17av', title: 'Kids - Content-Miraculous: Tales Of Ladybug & Cat Noir' },
    { id: 'pt_17aw', title: 'Kids - Content-Mr Bean' },
    { id: 'pt_17ax', title: 'Kids - Content-My Little Pony' },
    { id: 'pt_17ay', title: 'Kids - Content-Nicky, Ricky, Dicky and Dawn' },
    { id: 'pt_17az', title: 'Kids - Content-Paw Patrol' },
    { id: 'pt_17ba', title: 'Kids - Content-Penny on Mars' },
    { id: 'pt_17bb', title: 'Kids - Content-Peppa Pig' },
    { id: 'pt_17bc', title: 'Kids - Content-Pete the Cat' },
    { id: 'pt_17bd', title: 'Kids - Content-PJ Masks' },
    { id: 'pt_17be', title: 'Kids - Content-Postman Pat' },
    { id: 'pt_17bf', title: 'Kids - Content-Power Rangers' },
    { id: 'pt_17bg', title: 'Kids - Content-Powerpuff Girls' },
    { id: 'pt_17bh', title: 'Kids - Content-Puffin Rock' },
    { id: 'pt_17bi', title: 'Kids - Content-Rusty Rivets' },
    { id: 'pt_17bj', title: 'Kids - Content-Sam and Cat' },
    { id: 'pt_17bk', title: 'Kids - Content-Scooby Doo' },
    { id: 'pt_17bl', title: 'Kids - Content-Seven and Me' },
    { id: 'pt_17bm', title: 'Kids - Content-Shane the Chef' },
    { id: 'pt_17bn', title: 'Kids - Content-Shaun the Sheep' },
    { id: 'pt_17bo', title: 'Kids - Content-Shimmer and Shine' },
    { id: 'pt_17bp', title: 'Kids - Content-Simon' },
    { id: 'pt_17bq', title: 'Kids - Content-Sonic Boom' },
    { id: 'pt_17br', title: 'Kids - Content-Spirit: Riding Free' },
    { id: 'pt_17bs', title: 'Kids - Content-Spongebob Squarepants' },
    { id: 'pt_17bt', title: 'Kids - Content-Stick Man' },
    { id: 'pt_17bu', title: 'Kids - Content-Super Monsters' },
    { id: 'pt_17bv', title: 'Kids - Content-Superwings' },
    { id: 'pt_17bw', title: 'Kids - Content-Teen Titans Go!' },
    { id: 'pt_17bx', title: 'Kids - Content-Teen Z' },
    { id: 'pt_17by', title: 'Kids - Content-The Dragon Prince' },
    { id: 'pt_17bz', title: 'Kids - Content-The Epic Tales of Captain Underpants' },
    { id: 'pt_17ca', title: 'Kids - Content-The Muppets' },
    { id: 'pt_17cb', title: 'Kids - Content-The Next Step' },
    { id: 'pt_17cc', title: 'Kids - Content-The Simpsons' },
    { id: 'pt_17cd', title: 'Kids - Content-The Thundermans' },
    { id: 'pt_17ce', title: 'Kids - Content-The Worst Witch' },
    { id: 'pt_17cf', title: 'Kids - Content-Thomas and Friends' },
    { id: 'pt_17cg', title: 'Kids - Content-ThunderBirds are Go' },
    { id: 'pt_17ch', title: 'Kids - Content-Tiana' },
    { id: 'pt_17ci', title: 'Kids - Content-Tom and Jerry' },
    { id: 'pt_17cj', title: 'Kids - Content-Trollhunters' },
    { id: 'pt_17ck', title: 'Kids - Content-Unikitty!' },
    { id: 'pt_17cl', title: 'Kids - Content-We Bare Bears' },
    { id: 'pt_17cm', title: 'Kids - Content-Winnie the Pooh' },
    { id: 'pt_17cn', title: 'Kids - Content-Zafari' },
    { id: 'pt_18', title: 'Kids - Electronics' },
    { id: 'pt_18a', title: 'Kids - Electronics-Cameras' },
    { id: 'pt_18b', title: 'Kids - Electronics-Headphones' },
    { id: 'pt_18c', title: 'Kids - Electronics-Phones' },
    { id: 'pt_18d', title: 'Kids - Electronics-Tablets' },
    { id: 'pt_18e', title: 'Kids - Electronics-Wearables' },
    { id: 'pt_19', title: 'Kids - Entertainment' },
    { id: 'pt_19a', title: 'Kids - Entertainment-Books' },
    { id: 'pt_19a1', title: 'Kids - Entertainment-Books-Boys books' },
    { id: 'pt_19a2', title: 'Kids - Entertainment-Books-Educational books' },
    { id: 'pt_19a3', title: 'Kids - Entertainment-Books-Girls books' },
    { id: 'pt_19a4', title: 'Kids - Entertainment-Books-Preschool books' },
    { id: 'pt_19a5', title: 'Kids - Entertainment-Books-Teens books' },
    { id: 'pt_19a6', title: 'Kids - Entertainment-Books-Tweens books' },
    { id: 'pt_19b', title: 'Kids - Entertainment-Comics & graphic novels' },
    { id: 'pt_19c', title: 'Kids - Entertainment-Games' },
    { id: 'pt_19d', title: 'Kids - Entertainment-Music' },
    { id: 'pt_19d1', title: 'Kids - Entertainment-Music-Boys music' },
    { id: 'pt_19d2', title: 'Kids - Entertainment-Music-Disney music' },
    { id: 'pt_19d3', title: 'Kids - Entertainment-Music-Girls music' },
    { id: 'pt_19d4', title: 'Kids - Entertainment-Music-Preschool music' },
    { id: 'pt_19d5', title: 'Kids - Entertainment-Music-Sing-a-long' },
    { id: 'pt_19d6', title: 'Kids - Entertainment-Music-Teens music' },
    { id: 'pt_19d7', title: 'Kids - Entertainment-Music-Tweens music' },
    { id: 'pt_19e', title: 'Kids - Entertainment-TV & FIlm' },
    { id: 'pt_19f', title: 'Kids - Entertainment-Video games' },
    { id: 'pt_20', title: 'Kids - Fashion & style' },
    { id: 'pt_20a', title: 'Kids - Fashion & style-Backpacks' },
    { id: 'pt_20b', title: 'Kids - Fashion & style-Boys clothing' },
    { id: 'pt_20c', title: 'Kids - Fashion & style-Girls clothing' },
    { id: 'pt_20d', title: 'Kids - Fashion & style-Nursery clothing' },
    { id: 'pt_20e', title: 'Kids - Fashion & style-Teens clothing' },
    { id: 'pt_20f', title: 'Kids - Fashion & style-Tween clothing' },
    { id: 'pt_21', title: 'Kids - Outdoors' },
    { id: 'pt_21a', title: 'Kids - Outdoors-Beach toys' },
    { id: 'pt_21b', title: 'Kids - Outdoors-Bikes' },
    { id: 'pt_21c', title: 'Kids - Outdoors-Bouncy castle' },
    { id: 'pt_21d', title: 'Kids - Outdoors-Climbing sets, slides, and swings' },
    { id: 'pt_21e', title: 'Kids - Outdoors-Flying toys' },
    { id: 'pt_21f', title: 'Kids - Outdoors-Hoops, pogos, jump rumps' },
    { id: 'pt_21g', title: 'Kids - Outdoors-Hoverboard' },
    { id: 'pt_21h', title: 'Kids - Outdoors-Lawn games' },
    { id: 'pt_21i', title: 'Kids - Outdoors-Ride on toy vehicles' },
    { id: 'pt_21j', title: 'Kids - Outdoors-Scooters' },
    { id: 'pt_21k', title: 'Kids - Outdoors-Snow toys' },
    { id: 'pt_21l', title: 'Kids - Outdoors-Trampolines' },
    { id: 'pt_21m', title: 'Kids - Outdoors-Water slides, pools, and inflatables' },
    { id: 'pt_22', title: 'Kids - Toys' },
    { id: 'pt_22a', title: 'Kids - Toys-Cars & vehicles' },
    { id: 'pt_22b', title: 'Kids - Toys-Dinosaurs' },
    { id: 'pt_22c', title: 'Kids - Toys-Electronic' },
    { id: 'pt_22c1', title: 'Kids - Toys-Electronic-Karaoke machine' },
    { id: 'pt_22c2', title: 'Kids - Toys-Electronic-Remote controlled vehicles' },
    { id: 'pt_22c3', title: 'Kids - Toys-Electronic-Robots' },
    { id: 'pt_22d', title: 'Kids - Toys-Fidget toys' },
    { id: 'pt_22e', title: 'Kids - Toys-Figures & dolls' },
    { id: 'pt_22e1', title: 'Kids - Toys-Figures & dolls-Action figures' },
    { id: 'pt_22e2', title: 'Kids - Toys-Figures & dolls-Dolls' },
    { id: 'pt_22e3', title: 'Kids - Toys-Figures & dolls-Plush' },
    { id: 'pt_22e4', title: 'Kids - Toys-Figures & dolls-Puppets' },
    { id: 'pt_22f', title: 'Kids - Toys-Gag toys & practical jokes' },
    { id: 'pt_22g', title: 'Kids - Toys-Learning & development toys' },
    { id: 'pt_22h', title: 'Kids - Toys-Play make up & hair' },
    { id: 'pt_22i', title: 'Kids - Toys-Slime' },
    { id: 'pt_22j', title: 'Kids - Toys-Spy gear' },
    { id: 'pt_22k', title: 'Kids - Toys-Squishies' },
    { id: 'pt_22l', title: 'Kids - Toys-Toy tools and workbenches' },
    { id: 'pt_22m', title: 'Kids - Toys-Toy weapons' },
    { id: 'pt_22m1', title: 'Kids - Toys-Toy weapons-Nerf guns' },
    { id: 'pt_22m2', title: 'Kids - Toys-Toy weapons-Water guns' },
    { id: 'pt_22n', title: 'Kids - Toys-Trading cards' },
    { id: 'pt_23', title: 'Locations' },
    { id: 'pt_23a', title: 'Locations-Africa' },
    { id: 'pt_23b', title: 'Locations-Arctic' },
    { id: 'pt_23c', title: 'Locations-Asia' },
    { id: 'pt_23c1', title: 'Locations-Asia-China' },
    { id: 'pt_23c2', title: 'Locations-Asia-India' },
    { id: 'pt_23c3', title: 'Locations-Asia-Israel' },
    { id: 'pt_23c4', title: 'Locations-Asia-Japan' },
    { id: 'pt_23c5', title: 'Locations-Asia-Korea' },
    { id: 'pt_23c6', title: 'Locations-Asia-UAE' },
    { id: 'pt_23d', title: 'Locations-Europe' },
    { id: 'pt_23d1', title: 'Locations-Europe-France' },
    { id: 'pt_23d2', title: 'Locations-Europe-Germany' },
    { id: 'pt_23d3', title: 'Locations-Europe-Greece' },
    { id: 'pt_23d4', title: 'Locations-Europe-Italy' },
    { id: 'pt_23d5', title: 'Locations-Europe-Russia' },
    { id: 'pt_23d6', title: 'Locations-Europe-Scandanavia' },
    { id: 'pt_23d7', title: 'Locations-Europe-Spain' },
    { id: 'pt_23d8', title: 'Locations-Europe-UK' },
    { id: 'pt_23e', title: 'Locations-North America' },
    { id: 'pt_23e1', title: 'Locations-North America-USA' },
    { id: 'pt_23f', title: 'Locations-Oceania' },
    { id: 'pt_23g', title: 'Locations-South America' },
    { id: 'pt_24', title: 'Nature' },
    { id: 'pt_24a', title: 'Nature-Beaches' },
    { id: 'pt_24b', title: 'Nature-Desert' },
    { id: 'pt_24c', title: 'Nature-Fields' },
    { id: 'pt_24d', title: 'Nature-Flowers' },
    { id: 'pt_24e', title: 'Nature-Forests' },
    { id: 'pt_24f', title: 'Nature-Lakes & rivers' },
    { id: 'pt_24g', title: 'Nature-Mountains' },
    { id: 'pt_24h', title: 'Nature-Ocean' },
    { id: 'pt_24i', title: 'Nature-Parks' },
    { id: 'pt_24j', title: 'Nature-Weather & environmental events' },
    { id: 'pt_25', title: 'Non-profits & activism' },
    { id: 'pt_26', title: 'Performance arts' },
    { id: 'pt_26a', title: 'Performance arts-Circus' },
    { id: 'pt_26b', title: 'Performance arts-Dance' },
    { id: 'pt_26b1', title: 'Performance arts-Dance-Genre - Ballet' },
    { id: 'pt_26b2', title: 'Performance arts-Dance-Genre - Ballroom' },
    { id: 'pt_26b3', title: 'Performance arts-Dance-Genre - Ceremonial & folk dance' },
    { id: 'pt_26b4', title: 'Performance arts-Dance-Genre - Latin & rhythm dance' },
    { id: 'pt_26b5', title: 'Performance arts-Dance-Genre - Performance / interprative dance' },
    { id: 'pt_26b6', title: 'Performance arts-Dance-Genre - Street dance' },
    { id: 'pt_26b7', title: 'Performance arts-Dance-Genre - Tap dance' },
    { id: 'pt_26b8', title: 'Performance arts-Dance-Learn dance' },
    { id: 'pt_26c', title: 'Performance arts-Music' },
    { id: 'pt_26c1', title: 'Performance arts-Music-Genre - Ambient' },
    { id: 'pt_26c2', title: 'Performance arts-Music-Genre - Blues' },
    { id: 'pt_26c3', title: 'Performance arts-Music-Genre - Classical' },
    { id: 'pt_26c4', title: 'Performance arts-Music-Genre - Country' },
    { id: 'pt_26c5', title: 'Performance arts-Music-Genre - Electronic' },
    { id: 'pt_26c6', title: 'Performance arts-Music-Genre - Folk' },
    { id: 'pt_26c7', title: 'Performance arts-Music-Genre - Hip-hop' },
    { id: 'pt_26c8', title: 'Performance arts-Music-Genre - Jazz' },
    { id: 'pt_26c9', title: 'Performance arts-Music-Genre - Pop' },
    { id: 'pt_26c10', title: 'Performance arts-Music-Genre - Punk' },
    { id: 'pt_26c11', title: 'Performance arts-Music-Genre - Rhythm & blues' },
    { id: 'pt_26c12', title: 'Performance arts-Music-Genre - Rock' },
    { id: 'pt_26c13', title: 'Performance arts-Music-Learn music' },
    { id: 'pt_26c14', title: 'Performance arts-Music-Music equipment' },
    { id: 'pt_26d', title: 'Performance arts-Opera' },
    { id: 'pt_26e', title: 'Performance arts-Theatre' },
    { id: 'pt_27', title: 'Pets & animals' },
    { id: 'pt_27a', title: 'Pets & animals-Amphibians' },
    { id: 'pt_27b', title: 'Pets & animals-Birds' },
    { id: 'pt_27c', title: 'Pets & animals-Cats (house)' },
    { id: 'pt_27d', title: 'Pets & animals-Dinosaurs' },
    { id: 'pt_27e', title: 'Pets & animals-Dogs' },
    { id: 'pt_27f', title: 'Pets & animals-Fish' },
    { id: 'pt_27g', title: 'Pets & animals-Mammals' },
    { id: 'pt_27h', title: 'Pets & animals-Pest control' },
    { id: 'pt_27i', title: 'Pets & animals-Reptiles' },
    { id: 'pt_27j', title: 'Pets & animals-Vetinary' },
    { id: 'pt_27k', title: 'Pets & animals-Zoos & aquariums' },
    { id: 'pt_28', title: 'Religion & belief systems' },
    { id: 'pt_28a', title: 'Religion & belief systems-Aetheism & Agnosticism' },
    { id: 'pt_28b', title: 'Religion & belief systems-Astrology' },
    { id: 'pt_28c', title: 'Religion & belief systems-Buddhism' },
    { id: 'pt_28d', title: 'Religion & belief systems-Christianity' },
    { id: 'pt_28e', title: 'Religion & belief systems-Hinduism' },
    { id: 'pt_28f', title: 'Religion & belief systems-Islam' },
    { id: 'pt_28g', title: 'Religion & belief systems-Judaism' },
    { id: 'pt_28h', title: 'Religion & belief systems-Mythology' },
    { id: 'pt_28i', title: 'Religion & belief systems-Philosophy' },
    { id: 'pt_29', title: 'Science & education' },
    { id: 'pt_29a', title: 'Science & education-Applied sciences' },
    { id: 'pt_29b', title: 'Science & education-Biology' },
    { id: 'pt_29c', title: 'Science & education-Chemistry' },
    { id: 'pt_29d', title: 'Science & education-Education systems' },
    { id: 'pt_29d2', title: 'Science & education-Education systems-Preschool' },
    { id: 'pt_29d1', title: 'Science & education-Education systems-School' },
    { id: 'pt_29d3', title: 'Science & education-Education systems-University' },
    { id: 'pt_29e', title: 'Science & education-History' },
    { id: 'pt_29e1', title: 'Science & education-History-Ancient - Egyptian' },
    { id: 'pt_29e2', title: 'Science & education-History-Ancient - Greek' },
    { id: 'pt_29e3', title: 'Science & education-History-Ancient - Roman' },
    { id: 'pt_29e4', title: 'Science & education-History-UK History' },
    { id: 'pt_29e5', title: 'Science & education-History-US History' },
    { id: 'pt_29e6', title: 'Science & education-History-Vietnam war' },
    { id: 'pt_29e7', title: 'Science & education-History-WW1 & WW2' },
    { id: 'pt_29f', title: 'Science & education-Language' },
    { id: 'pt_29g', title: 'Science & education-Mathematics' },
    { id: 'pt_29h', title: 'Science & education-Physics' },
    { id: 'pt_29i', title: 'Science & education-Psychology' },
    { id: 'pt_29j', title: 'Science & education-Social science' },
    { id: 'pt_29k', title: 'Science & education-Space & astronomy' },
    { id: 'pt_30', title: 'Sports & activities' },
    { id: 'pt_30a', title: 'Sports & activities-Air sports' },
    { id: 'pt_30a1', title: 'Sports & activities-Air sports-Air racing & stunts' },
    { id: 'pt_30a2', title: 'Sports & activities-Air sports-Bunjee jumping' },
    { id: 'pt_30a3', title: 'Sports & activities-Air sports-Gliding' },
    { id: 'pt_30a4', title: 'Sports & activities-Air sports-Sky diving' },
    { id: 'pt_30b', title: 'Sports & activities-American football' },
    { id: 'pt_30c', title: 'Sports & activities-Archery' },
    { id: 'pt_30d', title: 'Sports & activities-Athletics - Track and field' },
    { id: 'pt_30e', title: 'Sports & activities-Baseball' },
    { id: 'pt_30f', title: 'Sports & activities-Basketball' },
    { id: 'pt_30g', title: 'Sports & activities-Bodybuilding' },
    { id: 'pt_30h', title: 'Sports & activities-Bowling' },
    { id: 'pt_30i', title: 'Sports & activities-Camping & survival' },
    { id: 'pt_30j', title: 'Sports & activities-Climbing' },
    { id: 'pt_30k', title: 'Sports & activities-Combat sports' },
    { id: 'pt_30k1', title: 'Sports & activities-Combat sports-Boxing' },
    { id: 'pt_30k2', title: 'Sports & activities-Combat sports-Fencing' },
    { id: 'pt_30k3', title: 'Sports & activities-Combat sports-Judo' },
    { id: 'pt_30k4', title: 'Sports & activities-Combat sports-Jujutsu' },
    { id: 'pt_30k5', title: 'Sports & activities-Combat sports-Karate' },
    { id: 'pt_30k6', title: 'Sports & activities-Combat sports-Kickboxing' },
    { id: 'pt_30k7', title: 'Sports & activities-Combat sports-MMA' },
    { id: 'pt_30k8', title: 'Sports & activities-Combat sports-Muay Thai' },
    { id: 'pt_30k9', title: 'Sports & activities-Combat sports-Sumo' },
    { id: 'pt_30k10', title: 'Sports & activities-Combat sports-Taekwondo' },
    { id: 'pt_30k11', title: 'Sports & activities-Combat sports-Wrestling' },
    { id: 'pt_30l', title: 'Sports & activities-Cricket' },
    { id: 'pt_30m', title: 'Sports & activities-Crossfit' },
    { id: 'pt_30n', title: 'Sports & activities-Cue sports' },
    { id: 'pt_30o', title: 'Sports & activities-Cycling' },
    { id: 'pt_30p', title: 'Sports & activities-Darts' },
    { id: 'pt_30q', title: 'Sports & activities-Equestrianism' },
    { id: 'pt_30r', title: 'Sports & activities-Field hockey' },
    { id: 'pt_30s', title: 'Sports & activities-Fishing' },
    { id: 'pt_30t', title: 'Sports & activities-Golf' },
    { id: 'pt_30u', title: 'Sports & activities-Gymnastics' },
    { id: 'pt_30v', title: 'Sports & activities-Hiking & mountaineering' },
    { id: 'pt_30w', title: 'Sports & activities-Horse racing' },
    { id: 'pt_30x', title: 'Sports & activities-Hunting' },
    { id: 'pt_30y', title: 'Sports & activities-Ice hockey' },
    { id: 'pt_30z', title: 'Sports & activities-Lacrosse' },
    { id: 'pt_30za', title: 'Sports & activities-Motorsports' },
    { id: 'pt_30za1', title: 'Sports & activities-Motorsports-Drag racing' },
    { id: 'pt_30za2', title: 'Sports & activities-Motorsports-Formula 1' },
    { id: 'pt_30za3', title: 'Sports & activities-Motorsports-Motocross' },
    { id: 'pt_30za4', title: 'Sports & activities-Motorsports-Motorbike racing' },
    { id: 'pt_30za5', title: 'Sports & activities-Motorsports-Nascar' },
    { id: 'pt_30za6', title: 'Sports & activities-Motorsports-Rally' },
    { id: 'pt_30zb', title: 'Sports & activities-Professional wrestling' },
    { id: 'pt_30zc', title: 'Sports & activities-Racket sports' },
    { id: 'pt_30zc1', title: 'Sports & activities-Racket sports-Badminton' },
    { id: 'pt_30zc2', title: 'Sports & activities-Racket sports-Squash' },
    { id: 'pt_30zc3', title: 'Sports & activities-Racket sports-Table tennis' },
    { id: 'pt_30zc4', title: 'Sports & activities-Racket sports-Tennis' },
    { id: 'pt_30zd', title: 'Sports & activities-Rugby' },
    { id: 'pt_30ze', title: 'Sports & activities-Running' },
    { id: 'pt_30zf', title: 'Sports & activities-Soccer' },
    { id: 'pt_30zg', title: 'Sports & activities-Softball' },
    { id: 'pt_30zh', title: 'Sports & activities-Strongman' },
    { id: 'pt_30zi', title: 'Sports & activities-Volleyball' },
    { id: 'pt_30zj', title: 'Sports & activities-Water sports' },
    { id: 'pt_30zj1', title: 'Sports & activities-Water sports-Canoeing & kayaking' },
    { id: 'pt_30zj2', title: 'Sports & activities-Water sports-Diving' },
    { id: 'pt_30zj3', title: 'Sports & activities-Water sports-Jet skiing' },
    { id: 'pt_30zj4', title: 'Sports & activities-Water sports-Rafting' },
    { id: 'pt_30zj5', title: 'Sports & activities-Water sports-Rowing' },
    { id: 'pt_30zj6', title: 'Sports & activities-Water sports-Sailing' },
    { id: 'pt_30zj7', title: 'Sports & activities-Water sports-Scuba diving' },
    { id: 'pt_30zj8', title: 'Sports & activities-Water sports-Surfing' },
    { id: 'pt_30zj9', title: 'Sports & activities-Water sports-Swimming' },
    { id: 'pt_30zk', title: 'Sports & activities-Weightlifting' },
    { id: 'pt_30zl', title: 'Sports & activities-Winter sports' },
    { id: 'pt_30zl1', title: 'Sports & activities-Winter sports-Curling' },
    { id: 'pt_30zl2', title: 'Sports & activities-Winter sports-Ice skating' },
    { id: 'pt_30zl3', title: 'Sports & activities-Winter sports-Skiing' },
    { id: 'pt_30zl4', title: 'Sports & activities-Winter sports-Sledding' },
    { id: 'pt_30zl5', title: 'Sports & activities-Winter sports-Snowboarding' },
    { id: 'pt_30zl6', title: 'Sports & activities-Winter sports-Snowmobiling' },
    { id: 'pt_31', title: 'Vehicles' },
    { id: 'pt_31a', title: 'Vehicles-Classic cars' },
    { id: 'pt_31b', title: 'Vehicles-Consumer cars' },
    { id: 'pt_31c', title: 'Vehicles-Driving education' },
    { id: 'pt_31d', title: 'Vehicles-Electric & hybrid' },
    { id: 'pt_31e', title: 'Vehicles-Flying vehicles' },
    { id: 'pt_31f', title: 'Vehicles-Heavy vehicles' },
    { id: 'pt_31g', title: 'Vehicles-Luxury & high-end cars' },
    { id: 'pt_31h', title: 'Vehicles-Motorbikes' },
    { id: 'pt_31i', title: 'Vehicles-Off road vehicles' },
    { id: 'pt_31j', title: 'Vehicles-Trains' },
    { id: 'pt_31k', title: 'Vehicles-Unique vehicles' },
    { id: 'pt_31l', title: 'Vehicles-Vehicle maintenance & repair' },
    { id: 'pt_31m', title: 'Vehicles-Water vehicles' },
    { id: 'pt_32', title: 'Visual arts and crafts' },
    { id: 'pt_32a', title: 'Visual arts and crafts-Collectibles' },
    { id: 'pt_32b', title: 'Visual arts and crafts-Crafts & hacks' },
    { id: 'pt_32c', title: 'Visual arts and crafts-Digital arts' },
    { id: 'pt_32d', title: 'Visual arts and crafts-Drawing' },
    { id: 'pt_32e', title: 'Visual arts and crafts-Metalworking' },
    { id: 'pt_32f', title: 'Visual arts and crafts-Models & figurines' },
    { id: 'pt_32g', title: 'Visual arts and crafts-Painting' },
    { id: 'pt_32h', title: 'Visual arts and crafts-Photography' },
    { id: 'pt_32i', title: 'Visual arts and crafts-Sculpting' },
    { id: 'pt_32j', title: 'Visual arts and crafts-Woodworking' },
  ];

  return generateIRISSegmentList(options, currentValues);
};

export const generateHotSpexContextualSegmentList = (currentValues: string[]): IMenuList => {
  const options: IIRISOption[] = [
    { id: 'hs_active', title: 'Active' },
    { id: 'hs_attractive', title: 'Attractive' },
    { id: 'hs_capable', title: 'Capable' },
    { id: 'hs_conventional', title: 'Conventional' },
    { id: 'hs_courageous', title: 'Courageous' },
    { id: 'hs_creative', title: 'Creative' },
    { id: 'hs_friendly', title: 'Friendly' },
    { id: 'hs_funny', title: 'Funny' },
    { id: 'hs_interesting', title: 'Interesting' },
    { id: 'hs_nurturing', title: 'Nurturing' },
    { id: 'hs_open_minded', title: 'Open_Minded' },
    { id: 'hs_reliable', title: 'Reliable' },
    { id: 'hs_respectful', title: 'Respectful' },
    { id: 'hs_safety', title: 'Safety' },
    { id: 'hs_smart', title: 'Smart' },
    { id: 'hs_sophisticated', title: 'Sophisticated' },
    { id: 'hs_successful', title: 'Successful' },
    { id: 'hs_trendy', title: 'Trendy' },
    { id: 'hs_uplifting', title: 'Uplifting' },
  ];

  return generateIRISSegmentList(options, currentValues);
};

export const generateSilverpushContextualSegmentList = (currentValues: string[]): IMenuList => {
  const options: IIRISOption[] = [
    { id: 'sv_automotive', title: 'Automotive' },
    { id: 'sv_automotive_autobodystyles', title: 'Automotive-Auto Body Styles' },
    {
      id: 'sv_automotive_autobodystyles_commercialtrucks',
      title: 'Automotive-Auto Body Styles-Commercial Trucks',
    },
    { id: 'sv_automotive_autobodystyles_sedan', title: 'Automotive-Auto Body Styles-Sedan' },
    {
      id: 'sv_automotive_autobodystyles_stationwagon',
      title: 'Automotive-Auto Body Styles-Station Wagon',
    },
    { id: 'sv_automotive_autobodystyles_suv', title: 'Automotive-Auto Body Styles-SUV' },
    { id: 'sv_automotive_autobodystyles_van', title: 'Automotive-Auto Body Styles-Van' },
    {
      id: 'sv_automotive_autobodystyles_convertible',
      title: 'Automotive-Auto Body Styles-Convertible',
    },
    { id: 'sv_automotive_autobodystyles_coupe', title: 'Automotive-Auto Body Styles-Coupe' },
    {
      id: 'sv_automotive_autobodystyles_crossover',
      title: 'Automotive-Auto Body Styles-Crossover',
    },
    {
      id: 'sv_automotive_autobodystyles_hatchback',
      title: 'Automotive-Auto Body Styles-Hatchback',
    },
    { id: 'sv_automotive_autobodystyles_microcar', title: 'Automotive-Auto Body Styles-Microcar' },
    { id: 'sv_automotive_autobodystyles_minivan', title: 'Automotive-Auto Body Styles-Minivan' },
    {
      id: 'sv_automotive_autobodystyles_offroadvehicles',
      title: 'Automotive-Auto Body Styles-Off-Road Vehicles',
    },
    {
      id: 'sv_automotive_autobodystyles_pickuptrucks',
      title: 'Automotive-Auto Body Styles-Pickup Trucks',
    },
    { id: 'sv_automotive_autotype', title: 'Automotive-Auto Type' },
    { id: 'sv_automotive_autotype_budgetcars', title: 'Automotive-Auto Type-Budget Cars' },
    {
      id: 'sv_automotive_autotype_certifiedpreownedcars',
      title: 'Automotive-Auto Type-Certified Pre-Owned Cars',
    },
    { id: 'sv_automotive_autotype_classiccars', title: 'Automotive-Auto Type-Classic Cars' },
    { id: 'sv_automotive_autotype_conceptcars', title: 'Automotive-Auto Type-Concept Cars' },
    { id: 'sv_automotive_autotype_driverlesscars', title: 'Automotive-Auto Type-Driverless Cars' },
    { id: 'sv_automotive_autotype_greenvehicles', title: 'Automotive-Auto Type-Green Vehicles' },
    { id: 'sv_automotive_autotype_luxurycars', title: 'Automotive-Auto Type-Luxury Cars' },
    {
      id: 'sv_automotive_autotype_performancecars',
      title: 'Automotive-Auto Type-Performance Cars',
    },
    { id: 'sv_automotive_carculture', title: 'Automotive-Car Culture' },
    { id: 'sv_automotive_dashcamvideos', title: 'Automotive-Dash Cam Videos' },
    { id: 'sv_automotive_motorcycles', title: 'Automotive-Motorcycles' },
    { id: 'sv_automotive_roadsideassistance', title: 'Automotive-Road-Side Assistance' },
    { id: 'sv_automotive_scooters', title: 'Automotive-Scooters' },
    { id: 'sv_automotive_autobuyingandselling', title: 'Automotive-Auto Buying and Selling' },
    { id: 'sv_automotive_autoinsurance', title: 'Automotive-Auto Insurance' },
    { id: 'sv_automotive_autoparts', title: 'Automotive-Auto Parts' },
    { id: 'sv_automotive_autorecalls', title: 'Automotive-Auto Recalls' },
    { id: 'sv_automotive_autorepair', title: 'Automotive-Auto Repair' },
    { id: 'sv_automotive_autosafety', title: 'Automotive-Auto Safety' },
    { id: 'sv_automotive_autoshows', title: 'Automotive-Auto Shows' },
    { id: 'sv_automotive_autotechnology', title: 'Automotive-Auto Technology' },
    {
      id: 'sv_automotive_autotechnology_autoinfotainmenttechnologies',
      title: 'Automotive-Auto Technology-Auto Infotainment Technologies',
    },
    {
      id: 'sv_automotive_autotechnology_autonavigationsystems',
      title: 'Automotive-Auto Technology-Auto Navigation Systems',
    },
    {
      id: 'sv_automotive_autotechnology_autosafetytechnologies',
      title: 'Automotive-Auto Technology-Auto Safety Technologies',
    },
    { id: 'sv_automotive_autorentals', title: 'Automotive-Auto Rentals' },
    { id: 'sv_booksandliterature', title: 'Books and Literature' },
    {
      id: 'sv_booksandliterature_artandphotographybooks',
      title: 'Books and Literature-Art and Photography Books',
    },
    { id: 'sv_booksandliterature_biographies', title: 'Books and Literature-Biographies' },
    {
      id: 'sv_booksandliterature_childrensliterature',
      title: 'Books and Literature-Childrens Literature',
    },
    {
      id: 'sv_booksandliterature_comicsandgraphicnovels',
      title: 'Books and Literature-Comics and Graphic Novels',
    },
    { id: 'sv_booksandliterature_cookbooks', title: 'Books and Literature-Cookbooks' },
    { id: 'sv_booksandliterature_fiction', title: 'Books and Literature-Fiction' },
    { id: 'sv_booksandliterature_poetry', title: 'Books and Literature-Poetry' },
    { id: 'sv_booksandliterature_travelbooks', title: 'Books and Literature-Travel Books' },
    {
      id: 'sv_booksandliterature_youngadultliterature',
      title: 'Books and Literature-Young Adult Literature',
    },
    { id: 'sv_businessandfinance', title: 'Business and Finance' },
    { id: 'sv_businessandfinance_business', title: 'Business and Finance-Business' },
    {
      id: 'sv_businessandfinance_business_businessaccountingfinance',
      title: 'Business and Finance-Business-Business Accounting & Finance',
    },
    {
      id: 'sv_businessandfinance_business_humanresources',
      title: 'Business and Finance-Business-Human Resources',
    },
    {
      id: 'sv_businessandfinance_business_largebusiness',
      title: 'Business and Finance-Business-Large Business',
    },
    {
      id: 'sv_businessandfinance_business_logistics',
      title: 'Business and Finance-Business-Logistics',
    },
    {
      id: 'sv_businessandfinance_business_marketingandadvertising',
      title: 'Business and Finance-Business-Marketing and Advertising',
    },
    { id: 'sv_businessandfinance_business_sales', title: 'Business and Finance-Business-Sales' },
    {
      id: 'sv_businessandfinance_business_smallandmediumsizedbusiness',
      title: 'Business and Finance-Business-Small and Medium-sized Business',
    },
    {
      id: 'sv_businessandfinance_business_startups',
      title: 'Business and Finance-Business-Startups',
    },
    {
      id: 'sv_businessandfinance_business_businessadministration',
      title: 'Business and Finance-Business-Business Administration',
    },
    {
      id: 'sv_businessandfinance_business_businessbankingfinance',
      title: 'Business and Finance-Business-Business Banking & Finance',
    },
    {
      id: 'sv_businessandfinance_business_businessit',
      title: 'Business and Finance-Business-Business I.T.',
    },
    {
      id: 'sv_businessandfinance_business_businessoperations',
      title: 'Business and Finance-Business-Business Operations',
    },
    {
      id: 'sv_businessandfinance_business_consumerissues',
      title: 'Business and Finance-Business-Consumer Issues',
    },
    {
      id: 'sv_businessandfinance_business_executiveleadershipmanagement',
      title: 'Business and Finance-Business-Executive Leadership & Management',
    },
    {
      id: 'sv_businessandfinance_business_governmentbusiness',
      title: 'Business and Finance-Business-Government Business',
    },
    {
      id: 'sv_businessandfinance_business_greensolutions',
      title: 'Business and Finance-Business-Green Solutions',
    },
    {
      id: 'sv_businessandfinance_business_businessutilities',
      title: 'Business and Finance-Business-Business Utilities',
    },
    { id: 'sv_businessandfinance_economy', title: 'Business and Finance-Economy' },
    {
      id: 'sv_businessandfinance_economy_commodities',
      title: 'Business and Finance-Economy-Commodities',
    },
    {
      id: 'sv_businessandfinance_economy_currencies',
      title: 'Business and Finance-Economy-Currencies',
    },
    {
      id: 'sv_businessandfinance_economy_financialcrisis',
      title: 'Business and Finance-Economy-Financial Crisis',
    },
    {
      id: 'sv_businessandfinance_economy_financialreform',
      title: 'Business and Finance-Economy-Financial Reform',
    },
    {
      id: 'sv_businessandfinance_economy_financialregulation',
      title: 'Business and Finance-Economy-Financial Regulation',
    },
    {
      id: 'sv_businessandfinance_economy_gasolineprices',
      title: 'Business and Finance-Economy-Gasoline Prices',
    },
    {
      id: 'sv_businessandfinance_economy_housingmarket',
      title: 'Business and Finance-Economy-Housing Market',
    },
    {
      id: 'sv_businessandfinance_economy_interestrates',
      title: 'Business and Finance-Economy-Interest Rates',
    },
    {
      id: 'sv_businessandfinance_economy_jobmarket',
      title: 'Business and Finance-Economy-Job Market',
    },
    { id: 'sv_businessandfinance_industries', title: 'Business and Finance-Industries' },
    {
      id: 'sv_businessandfinance_industries_advertisingindustry',
      title: 'Business and Finance-Industries-Advertising Industry',
    },
    {
      id: 'sv_businessandfinance_industries_educationindustry',
      title: 'Business and Finance-Industries-Education industry',
    },
    {
      id: 'sv_businessandfinance_industries_entertainmentindustry',
      title: 'Business and Finance-Industries-Entertainment Industry',
    },
    {
      id: 'sv_businessandfinance_industries_environmentalservicesindustry',
      title: 'Business and Finance-Industries-Environmental Services Industry',
    },
    {
      id: 'sv_businessandfinance_industries_financialindustry',
      title: 'Business and Finance-Industries-Financial Industry',
    },
    {
      id: 'sv_businessandfinance_industries_foodindustry',
      title: 'Business and Finance-Industries-Food Industry',
    },
    {
      id: 'sv_businessandfinance_industries_healthcareindustry',
      title: 'Business and Finance-Industries-Healthcare Industry',
    },
    {
      id: 'sv_businessandfinance_industries_hospitalityindustry',
      title: 'Business and Finance-Industries-Hospitality Industry',
    },
    {
      id: 'sv_businessandfinance_industries_informationservicesindustry',
      title: 'Business and Finance-Industries-Information Services Industry',
    },
    {
      id: 'sv_businessandfinance_industries_legalservicesindustry',
      title: 'Business and Finance-Industries-Legal Services Industry',
    },
    {
      id: 'sv_businessandfinance_industries_logisticsandtransportationindustry',
      title: 'Business and Finance-Industries-Logistics and Transportation Industry',
    },
    {
      id: 'sv_businessandfinance_industries_agriculture',
      title: 'Business and Finance-Industries-Agriculture',
    },
    {
      id: 'sv_businessandfinance_industries_managementconsultingindustry',
      title: 'Business and Finance-Industries-Management Consulting Industry',
    },
    {
      id: 'sv_businessandfinance_industries_manufacturingindustry',
      title: 'Business and Finance-Industries-Manufacturing Industry',
    },
    {
      id: 'sv_businessandfinance_industries_mechanicalandindustrialengineeringindustry',
      title: 'Business and Finance-Industries-Mechanical and Industrial Engineering Industry',
    },
    {
      id: 'sv_businessandfinance_industries_mediaindustry',
      title: 'Business and Finance-Industries-Media Industry',
    },
    {
      id: 'sv_businessandfinance_industries_metalsindustry',
      title: 'Business and Finance-Industries-Metals Industry',
    },
    {
      id: 'sv_businessandfinance_industries_nonprofitorganizations',
      title: 'Business and Finance-Industries-Non-Profit Organizations',
    },
    {
      id: 'sv_businessandfinance_industries_pharmaceuticalindustry',
      title: 'Business and Finance-Industries-Pharmaceutical Industry',
    },
    {
      id: 'sv_businessandfinance_industries_powerandenergyindustry',
      title: 'Business and Finance-Industries-Power and Energy Industry',
    },
    {
      id: 'sv_businessandfinance_industries_publishingindustry',
      title: 'Business and Finance-Industries-Publishing Industry',
    },
    {
      id: 'sv_businessandfinance_industries_realestateindustry',
      title: 'Business and Finance-Industries-Real Estate Industry',
    },
    {
      id: 'sv_businessandfinance_industries_apparelindustry',
      title: 'Business and Finance-Industries-Apparel Industry',
    },
    {
      id: 'sv_businessandfinance_industries_retailindustry',
      title: 'Business and Finance-Industries-Retail Industry',
    },
    {
      id: 'sv_businessandfinance_industries_technologyindustry',
      title: 'Business and Finance-Industries-Technology Industry',
    },
    {
      id: 'sv_businessandfinance_industries_telecommunicationsindustry',
      title: 'Business and Finance-Industries-Telecommunications Industry',
    },
    {
      id: 'sv_businessandfinance_industries_automotiveindustry',
      title: 'Business and Finance-Industries-Automotive Industry',
    },
    {
      id: 'sv_businessandfinance_industries_aviationindustry',
      title: 'Business and Finance-Industries-Aviation Industry',
    },
    {
      id: 'sv_businessandfinance_industries_biotechandbiomedicalindustry',
      title: 'Business and Finance-Industries-Biotech and Biomedical Industry',
    },
    {
      id: 'sv_businessandfinance_industries_civilengineeringindustry',
      title: 'Business and Finance-Industries-Civil Engineering Industry',
    },
    {
      id: 'sv_businessandfinance_industries_constructionindustry',
      title: 'Business and Finance-Industries-Construction Industry',
    },
    {
      id: 'sv_businessandfinance_industries_defenseindustry',
      title: 'Business and Finance-Industries-Defense Industry',
    },
    { id: 'sv_careers', title: 'Careers' },
    { id: 'sv_careers_apprenticeships', title: 'Careers-Apprenticeships' },
    { id: 'sv_careers_careeradvice', title: 'Careers-Career Advice' },
    { id: 'sv_careers_careerplanning', title: 'Careers-Career Planning' },
    { id: 'sv_careers_jobsearch', title: 'Careers-Job Search' },
    { id: 'sv_careers_jobsearch_jobfairs', title: 'Careers-Job Search-Job Fairs' },
    {
      id: 'sv_careers_jobsearch_resumewritingandadvice',
      title: 'Careers-Job Search-Resume Writing and Advice',
    },
    { id: 'sv_careers_remoteworking', title: 'Careers-Remote Working' },
    { id: 'sv_careers_vocationaltraining', title: 'Careers-Vocational Training' },
    { id: 'sv_education', title: 'Education' },
    { id: 'sv_education_adulteducation', title: 'Education-Adult Education' },
    { id: 'sv_education_privateschool', title: 'Education-Private School' },
    { id: 'sv_education_secondaryeducation', title: 'Education-Secondary Education' },
    { id: 'sv_education_specialeducation', title: 'Education-Special Education' },
    { id: 'sv_education_collegeeducation', title: 'Education-College Education' },
    {
      id: 'sv_education_collegeeducation_collegeplanning',
      title: 'Education-College Education-College Planning',
    },
    {
      id: 'sv_education_collegeeducation_postgraduateeducation',
      title: 'Education-College Education-Postgraduate Education',
    },
    {
      id: 'sv_education_collegeeducation_undergraduateeducation',
      title: 'Education-College Education-Undergraduate Education',
    },
    { id: 'sv_education_earlychildhoodeducation', title: 'Education-Early Childhood Education' },
    { id: 'sv_education_educationalassessment', title: 'Education-Educational Assessment' },
    {
      id: 'sv_education_educationalassessment_standardizedtesting',
      title: 'Education-Educational Assessment-Standardized Testing',
    },
    { id: 'sv_education_homeschooling', title: 'Education-Homeschooling' },
    { id: 'sv_education_homeworkandstudy', title: 'Education-Homework and Study' },
    { id: 'sv_education_languagelearning', title: 'Education-Language Learning' },
    { id: 'sv_education_onlineeducation', title: 'Education-Online Education' },
    { id: 'sv_education_primaryeducation', title: 'Education-Primary Education' },
    { id: 'sv_eventsandattractions', title: 'Events and Attractions' },
    {
      id: 'sv_eventsandattractions_amusementandthemeparks',
      title: 'Events and Attractions-Amusement and Theme Parks',
    },
    { id: 'sv_eventsandattractions_fashionevents', title: 'Events and Attractions-Fashion Events' },
    {
      id: 'sv_eventsandattractions_historicsiteandlandmarktours',
      title: 'Events and Attractions-Historic Site and Landmark Tours',
    },
    {
      id: 'sv_eventsandattractions_mallsshoppingcenters',
      title: 'Events and Attractions-Malls & Shopping Centers',
    },
    {
      id: 'sv_eventsandattractions_museumsgalleries',
      title: 'Events and Attractions-Museums & Galleries',
    },
    { id: 'sv_eventsandattractions_musicals', title: 'Events and Attractions-Musicals' },
    {
      id: 'sv_eventsandattractions_nationalcivicholidays',
      title: 'Events and Attractions-National & Civic Holidays',
    },
    { id: 'sv_eventsandattractions_nightclubs', title: 'Events and Attractions-Nightclubs' },
    {
      id: 'sv_eventsandattractions_outdooractivities',
      title: 'Events and Attractions-Outdoor Activities',
    },
    { id: 'sv_eventsandattractions_parksnature', title: 'Events and Attractions-Parks & Nature' },
    {
      id: 'sv_eventsandattractions_partysuppliesanddecorations',
      title: 'Events and Attractions-Party Supplies and Decorations',
    },
    { id: 'sv_eventsandattractions_awardsshows', title: 'Events and Attractions-Awards Shows' },
    {
      id: 'sv_eventsandattractions_personalcelebrationslifeevents',
      title: 'Events and Attractions-Personal Celebrations & Life Events',
    },
    {
      id: 'sv_eventsandattractions_personalcelebrationslifeevents_anniversary',
      title: 'Events and Attractions-Personal Celebrations & Life Events-Anniversary',
    },
    {
      id: 'sv_eventsandattractions_personalcelebrationslifeevents_wedding',
      title: 'Events and Attractions-Personal Celebrations & Life Events-Wedding',
    },
    {
      id: 'sv_eventsandattractions_personalcelebrationslifeevents_babyshower',
      title: 'Events and Attractions-Personal Celebrations & Life Events-Baby Shower',
    },
    {
      id: 'sv_eventsandattractions_personalcelebrationslifeevents_bachelorparty',
      title: 'Events and Attractions-Personal Celebrations & Life Events-Bachelor Party',
    },
    {
      id: 'sv_eventsandattractions_personalcelebrationslifeevents_bacheloretteparty',
      title: 'Events and Attractions-Personal Celebrations & Life Events-Bachelorette Party',
    },
    {
      id: 'sv_eventsandattractions_personalcelebrationslifeevents_birth',
      title: 'Events and Attractions-Personal Celebrations & Life Events-Birth',
    },
    {
      id: 'sv_eventsandattractions_personalcelebrationslifeevents_birthday',
      title: 'Events and Attractions-Personal Celebrations & Life Events-Birthday',
    },
    {
      id: 'sv_eventsandattractions_personalcelebrationslifeevents_funeral',
      title: 'Events and Attractions-Personal Celebrations & Life Events-Funeral',
    },
    {
      id: 'sv_eventsandattractions_personalcelebrationslifeevents_graduation',
      title: 'Events and Attractions-Personal Celebrations & Life Events-Graduation',
    },
    {
      id: 'sv_eventsandattractions_personalcelebrationslifeevents_prom',
      title: 'Events and Attractions-Personal Celebrations & Life Events-Prom',
    },
    {
      id: 'sv_eventsandattractions_politicalevent',
      title: 'Events and Attractions-Political Event',
    },
    {
      id: 'sv_eventsandattractions_religiousevents',
      title: 'Events and Attractions-Religious Events',
    },
    {
      id: 'sv_eventsandattractions_sportingevents',
      title: 'Events and Attractions-Sporting Events',
    },
    {
      id: 'sv_eventsandattractions_theatervenuesandevents',
      title: 'Events and Attractions-Theater Venues and Events',
    },
    {
      id: 'sv_eventsandattractions_zoosaquariums',
      title: 'Events and Attractions-Zoos & Aquariums',
    },
    {
      id: 'sv_eventsandattractions_barsrestaurants',
      title: 'Events and Attractions-Bars & Restaurants',
    },
    {
      id: 'sv_eventsandattractions_businessexposconferences',
      title: 'Events and Attractions-Business Expos & Conferences',
    },
    {
      id: 'sv_eventsandattractions_casinosgambling',
      title: 'Events and Attractions-Casinos & Gambling',
    },
    {
      id: 'sv_eventsandattractions_cinemasandevents',
      title: 'Events and Attractions-Cinemas and Events',
    },
    { id: 'sv_eventsandattractions_comedyevents', title: 'Events and Attractions-Comedy Events' },
    {
      id: 'sv_eventsandattractions_concertsmusicevents',
      title: 'Events and Attractions-Concerts & Music Events',
    },
    {
      id: 'sv_eventsandattractions_fanconventions',
      title: 'Events and Attractions-Fan Conventions',
    },
    { id: 'sv_familyandrelationships', title: 'Family and Relationships' },
    { id: 'sv_familyandrelationships_bereavement', title: 'Family and Relationships-Bereavement' },
    { id: 'sv_familyandrelationships_dating', title: 'Family and Relationships-Dating' },
    { id: 'sv_familyandrelationships_divorce', title: 'Family and Relationships-Divorce' },
    { id: 'sv_familyandrelationships_eldercare', title: 'Family and Relationships-Eldercare' },
    {
      id: 'sv_familyandrelationships_marriageandcivilunions',
      title: 'Family and Relationships-Marriage and Civil Unions',
    },
    { id: 'sv_familyandrelationships_parenting', title: 'Family and Relationships-Parenting' },
    {
      id: 'sv_familyandrelationships_parenting_adoptionandfostering',
      title: 'Family and Relationships-Parenting-Adoption and Fostering',
    },
    {
      id: 'sv_familyandrelationships_parenting_daycareandpreschool',
      title: 'Family and Relationships-Parenting-Daycare and Pre-School',
    },
    {
      id: 'sv_familyandrelationships_parenting_internetsafety',
      title: 'Family and Relationships-Parenting-Internet Safety',
    },
    {
      id: 'sv_familyandrelationships_parenting_parentingbabiesandtoddlers',
      title: 'Family and Relationships-Parenting-Parenting Babies and Toddlers',
    },
    {
      id: 'sv_familyandrelationships_parenting_parentingchildrenaged411',
      title: 'Family and Relationships-Parenting-Parenting Children Aged 4-11',
    },
    {
      id: 'sv_familyandrelationships_parenting_parentingteens',
      title: 'Family and Relationships-Parenting-Parenting Teens',
    },
    {
      id: 'sv_familyandrelationships_parenting_specialneedskids',
      title: 'Family and Relationships-Parenting-Special Needs Kids',
    },
    { id: 'sv_familyandrelationships_singlelife', title: 'Family and Relationships-Single Life' },
    { id: 'sv_fineart', title: 'Fine Art' },
    { id: 'sv_fineart_costume', title: 'Fine Art-Costume' },
    { id: 'sv_fineart_dance', title: 'Fine Art-Dance' },
    { id: 'sv_fineart_design', title: 'Fine Art-Design' },
    { id: 'sv_fineart_digitalarts', title: 'Fine Art-Digital Arts' },
    { id: 'sv_fineart_fineartphotography', title: 'Fine Art-Fine Art Photography' },
    { id: 'sv_fineart_modernart', title: 'Fine Art-Modern Art' },
    { id: 'sv_fineart_opera', title: 'Fine Art-Opera' },
    { id: 'sv_fineart_theater', title: 'Fine Art-Theater' },
    { id: 'sv_fooddrink', title: 'Food & Drink' },
    { id: 'sv_fooddrink_alcoholicbeverages', title: 'Food & Drink-Alcoholic Beverages' },
    { id: 'sv_fooddrink_vegandiets', title: 'Food & Drink-Vegan Diets' },
    { id: 'sv_fooddrink_vegetariandiets', title: 'Food & Drink-Vegetarian Diets' },
    { id: 'sv_fooddrink_worldcuisines', title: 'Food & Drink-World Cuisines' },
    { id: 'sv_fooddrink_barbecuesandgrilling', title: 'Food & Drink-Barbecues and Grilling' },
    { id: 'sv_fooddrink_cooking', title: 'Food & Drink-Cooking' },
    { id: 'sv_fooddrink_dessertsandbaking', title: 'Food & Drink-Desserts and Baking' },
    { id: 'sv_fooddrink_diningout', title: 'Food & Drink-Dining Out' },
    { id: 'sv_fooddrink_foodallergies', title: 'Food & Drink-Food Allergies' },
    { id: 'sv_fooddrink_foodmovements', title: 'Food & Drink-Food Movements' },
    {
      id: 'sv_fooddrink_healthycookingandeating',
      title: 'Food & Drink-Healthy Cooking and Eating',
    },
    { id: 'sv_fooddrink_nonalcoholicbeverages', title: 'Food & Drink-Non-Alcoholic Beverages' },
    { id: 'sv_healthyliving', title: 'Healthy Living' },
    { id: 'sv_healthyliving_childrenshealth', title: 'Healthy Living-Childrens Health' },
    { id: 'sv_healthyliving_fitnessandexercise', title: 'Healthy Living-Fitness and Exercise' },
    {
      id: 'sv_healthyliving_fitnessandexercise_participantsports',
      title: 'Healthy Living-Fitness and Exercise-Participant Sports',
    },
    {
      id: 'sv_healthyliving_fitnessandexercise_runningandjogging',
      title: 'Healthy Living-Fitness and Exercise-Running and Jogging',
    },
    { id: 'sv_healthyliving_menshealth', title: 'Healthy Living-Mens Health' },
    { id: 'sv_healthyliving_nutrition', title: 'Healthy Living-Nutrition' },
    { id: 'sv_healthyliving_seniorhealth', title: 'Healthy Living-Senior Health' },
    { id: 'sv_healthyliving_weightloss', title: 'Healthy Living-Weight Loss' },
    { id: 'sv_healthyliving_wellness', title: 'Healthy Living-Wellness' },
    {
      id: 'sv_healthyliving_wellness_alternativemedicine',
      title: 'Healthy Living-Wellness-Alternative Medicine',
    },
    {
      id: 'sv_healthyliving_wellness_physicaltherapy',
      title: 'Healthy Living-Wellness-Physical Therapy',
    },
    {
      id: 'sv_healthyliving_wellness_smokingcessation',
      title: 'Healthy Living-Wellness-Smoking Cessation',
    },
    { id: 'sv_healthyliving_womenshealth', title: 'Healthy Living-Womens Health' },
    { id: 'sv_hobbiesinterests', title: 'Hobbies & Interests' },
    {
      id: 'sv_hobbiesinterests_antiquingandantiques',
      title: 'Hobbies & Interests-Antiquing and Antiques',
    },
    { id: 'sv_hobbiesinterests_magicandillusion', title: 'Hobbies & Interests-Magic and Illusion' },
    { id: 'sv_hobbiesinterests_modeltoys', title: 'Hobbies & Interests-Model Toys' },
    {
      id: 'sv_hobbiesinterests_musicalinstruments',
      title: 'Hobbies & Interests-Musical Instruments',
    },
    {
      id: 'sv_hobbiesinterests_paranormalphenomena',
      title: 'Hobbies & Interests-Paranormal Phenomena',
    },
    { id: 'sv_hobbiesinterests_radiocontrol', title: 'Hobbies & Interests-Radio Control' },
    { id: 'sv_hobbiesinterests_scifiandfantasy', title: 'Hobbies & Interests-Sci-fi and Fantasy' },
    {
      id: 'sv_hobbiesinterests_workshopsandclasses',
      title: 'Hobbies & Interests-Workshops and Classes',
    },
    { id: 'sv_hobbiesinterests_artsandcrafts', title: 'Hobbies & Interests-Arts and Crafts' },
    {
      id: 'sv_hobbiesinterests_artsandcrafts_beadwork',
      title: 'Hobbies & Interests-Arts and Crafts-Beadwork',
    },
    {
      id: 'sv_hobbiesinterests_artsandcrafts_candleandsoapmaking',
      title: 'Hobbies & Interests-Arts and Crafts-Candle and Soap Making',
    },
    {
      id: 'sv_hobbiesinterests_artsandcrafts_drawingandsketching',
      title: 'Hobbies & Interests-Arts and Crafts-Drawing and Sketching',
    },
    {
      id: 'sv_hobbiesinterests_artsandcrafts_jewelrymaking',
      title: 'Hobbies & Interests-Arts and Crafts-Jewelry Making',
    },
    {
      id: 'sv_hobbiesinterests_artsandcrafts_needlework',
      title: 'Hobbies & Interests-Arts and Crafts-Needlework',
    },
    {
      id: 'sv_hobbiesinterests_artsandcrafts_painting',
      title: 'Hobbies & Interests-Arts and Crafts-Painting',
    },
    {
      id: 'sv_hobbiesinterests_artsandcrafts_photography',
      title: 'Hobbies & Interests-Arts and Crafts-Photography',
    },
    {
      id: 'sv_hobbiesinterests_artsandcrafts_scrapbooking',
      title: 'Hobbies & Interests-Arts and Crafts-Scrapbooking',
    },
    {
      id: 'sv_hobbiesinterests_artsandcrafts_woodworking',
      title: 'Hobbies & Interests-Arts and Crafts-Woodworking',
    },
    { id: 'sv_hobbiesinterests_beekeeping', title: 'Hobbies & Interests-Beekeeping' },
    { id: 'sv_hobbiesinterests_birdwatching', title: 'Hobbies & Interests-Birdwatching' },
    { id: 'sv_hobbiesinterests_cigars', title: 'Hobbies & Interests-Cigars' },
    { id: 'sv_hobbiesinterests_collecting', title: 'Hobbies & Interests-Collecting' },
    {
      id: 'sv_hobbiesinterests_collecting_comicbooks',
      title: 'Hobbies & Interests-Collecting-Comic Books',
    },
    {
      id: 'sv_hobbiesinterests_collecting_stampsandcoins',
      title: 'Hobbies & Interests-Collecting-Stamps and Coins',
    },
    {
      id: 'sv_hobbiesinterests_contentproduction',
      title: 'Hobbies & Interests-Content Production',
    },
    {
      id: 'sv_hobbiesinterests_contentproduction_audioproduction',
      title: 'Hobbies & Interests-Content Production-Audio Production',
    },
    {
      id: 'sv_hobbiesinterests_contentproduction_freelancewriting',
      title: 'Hobbies & Interests-Content Production-Freelance Writing',
    },
    {
      id: 'sv_hobbiesinterests_contentproduction_screenwriting',
      title: 'Hobbies & Interests-Content Production-Screenwriting',
    },
    {
      id: 'sv_hobbiesinterests_contentproduction_videoproduction',
      title: 'Hobbies & Interests-Content Production-Video Production',
    },
    { id: 'sv_hobbiesinterests_gamesandpuzzles', title: 'Hobbies & Interests-Games and Puzzles' },
    {
      id: 'sv_hobbiesinterests_gamesandpuzzles_boardgamesandpuzzles',
      title: 'Hobbies & Interests-Games and Puzzles-Board Games and Puzzles',
    },
    {
      id: 'sv_hobbiesinterests_gamesandpuzzles_cardgames',
      title: 'Hobbies & Interests-Games and Puzzles-Card Games',
    },
    {
      id: 'sv_hobbiesinterests_gamesandpuzzles_roleplayinggames',
      title: 'Hobbies & Interests-Games and Puzzles-Roleplaying Games',
    },
    {
      id: 'sv_hobbiesinterests_genealogyandancestry',
      title: 'Hobbies & Interests-Genealogy and Ancestry',
    },
    { id: 'sv_homegarden', title: 'Home & Garden' },
    { id: 'sv_homegarden_gardening', title: 'Home & Garden-Gardening' },
    {
      id: 'sv_homegarden_remodelingconstruction',
      title: 'Home & Garden-Remodeling & Construction',
    },
    { id: 'sv_homegarden_smarthome', title: 'Home & Garden-Smart Home' },
    { id: 'sv_homegarden_homeappliances', title: 'Home & Garden-Home Appliances' },
    { id: 'sv_homegarden_homeentertaining', title: 'Home & Garden-Home Entertaining' },
    { id: 'sv_homegarden_homeimprovement', title: 'Home & Garden-Home Improvement' },
    { id: 'sv_homegarden_homesecurity', title: 'Home & Garden-Home Security' },
    {
      id: 'sv_homegarden_indoorenvironmentalquality',
      title: 'Home & Garden-Indoor Environmental Quality',
    },
    { id: 'sv_homegarden_interiordecorating', title: 'Home & Garden-Interior Decorating' },
    { id: 'sv_homegarden_landscaping', title: 'Home & Garden-Landscaping' },
    { id: 'sv_homegarden_outdoordecorating', title: 'Home & Garden-Outdoor Decorating' },
    { id: 'sv_medicalhealth', title: 'Medical Health' },
    {
      id: 'sv_medicalhealth_diseasesandconditions',
      title: 'Medical Health-Diseases and Conditions',
    },
    {
      id: 'sv_medicalhealth_diseasesandconditions_allergies',
      title: 'Medical Health-Diseases and Conditions-Allergies',
    },
    {
      id: 'sv_medicalhealth_diseasesandconditions_ear,noseandthroatconditions',
      title: 'Medical Health-Diseases and Conditions-Ear, Nose and Throat Conditions',
    },
    {
      id: 'sv_medicalhealth_diseasesandconditions_endocrineandmetabolicdiseases',
      title: 'Medical Health-Diseases and Conditions-Endocrine and Metabolic Diseases',
    },
    {
      id: 'sv_medicalhealth_diseasesandconditions_eyeandvisionconditions',
      title: 'Medical Health-Diseases and Conditions-Eye and Vision Conditions',
    },
    {
      id: 'sv_medicalhealth_diseasesandconditions_foothealth',
      title: 'Medical Health-Diseases and Conditions-Foot Health',
    },
    {
      id: 'sv_medicalhealth_diseasesandconditions_heartandcardiovasculardiseases',
      title: 'Medical Health-Diseases and Conditions-Heart and Cardiovascular Diseases',
    },
    {
      id: 'sv_medicalhealth_diseasesandconditions_infectiousdiseases',
      title: 'Medical Health-Diseases and Conditions-Infectious Diseases',
    },
    {
      id: 'sv_medicalhealth_diseasesandconditions_injuries',
      title: 'Medical Health-Diseases and Conditions-Injuries',
    },
    {
      id: 'sv_medicalhealth_diseasesandconditions_injuries',
      title: 'Medical Health-Diseases and Conditions-Injuries',
    },
    {
      id: 'sv_medicalhealth_diseasesandconditions_lungandrespiratoryhealth',
      title: 'Medical Health-Diseases and Conditions-Lung and Respiratory Health',
    },
    {
      id: 'sv_medicalhealth_diseasesandconditions_mentalhealth',
      title: 'Medical Health-Diseases and Conditions-Mental Health',
    },
    {
      id: 'sv_medicalhealth_diseasesandconditions_reproductivehealth',
      title: 'Medical Health-Diseases and Conditions-Reproductive Health',
    },
    {
      id: 'sv_medicalhealth_diseasesandconditions_blooddisorders',
      title: 'Medical Health-Diseases and Conditions-Blood Disorders',
    },
    {
      id: 'sv_medicalhealth_diseasesandconditions_sexualhealth',
      title: 'Medical Health-Diseases and Conditions-Sexual Health',
    },
    {
      id: 'sv_medicalhealth_diseasesandconditions_skinanddermatology',
      title: 'Medical Health-Diseases and Conditions-Skin and Dermatology',
    },
    {
      id: 'sv_medicalhealth_diseasesandconditions_sleepdisorders',
      title: 'Medical Health-Diseases and Conditions-Sleep Disorders',
    },
    {
      id: 'sv_medicalhealth_diseasesandconditions_substanceabuse',
      title: 'Medical Health-Diseases and Conditions-Substance Abuse',
    },
    {
      id: 'sv_medicalhealth_diseasesandconditions_boneandjointconditions',
      title: 'Medical Health-Diseases and Conditions-Bone and Joint Conditions',
    },
    {
      id: 'sv_medicalhealth_diseasesandconditions_brainandnervoussystemdisorders',
      title: 'Medical Health-Diseases and Conditions-Brain and Nervous System Disorders',
    },
    {
      id: 'sv_medicalhealth_diseasesandconditions_cancer',
      title: 'Medical Health-Diseases and Conditions-Cancer',
    },
    {
      id: 'sv_medicalhealth_diseasesandconditions_coldandflu',
      title: 'Medical Health-Diseases and Conditions-Cold and Flu',
    },
    {
      id: 'sv_medicalhealth_diseasesandconditions_dentalhealth',
      title: 'Medical Health-Diseases and Conditions-Dental Health',
    },
    {
      id: 'sv_medicalhealth_diseasesandconditions_diabetes',
      title: 'Medical Health-Diseases and Conditions-Diabetes',
    },
    {
      id: 'sv_medicalhealth_diseasesandconditions_digestivedisorders',
      title: 'Medical Health-Diseases and Conditions-Digestive Disorders',
    },
    { id: 'sv_medicalhealth_medicaltests', title: 'Medical Health-Medical Tests' },
    { id: 'sv_medicalhealth_pharmaceuticaldrugs', title: 'Medical Health-Pharmaceutical Drugs' },
    { id: 'sv_medicalhealth_surgery', title: 'Medical Health-Surgery' },
    { id: 'sv_medicalhealth_vaccines', title: 'Medical Health-Vaccines' },
    {
      id: 'sv_medicalhealth_cosmeticmedicalservices',
      title: 'Medical Health-Cosmetic Medical Services',
    },
    { id: 'sv_movies', title: 'Movies' },
    { id: 'sv_movies_actionandadventuremovies', title: 'Movies-Action and Adventure Movies' },
    { id: 'sv_movies_romancemovies', title: 'Movies-Romance Movies' },
    { id: 'sv_movies_sciencefictionmovies', title: 'Movies-Science Fiction Movies' },
    { id: 'sv_movies_indieandarthousemovies', title: 'Movies-Indie and Arthouse Movies' },
    { id: 'sv_movies_animationmovies', title: 'Movies-Animation Movies' },
    { id: 'sv_movies_comedymovies', title: 'Movies-Comedy Movies' },
    { id: 'sv_movies_crimeandmysterymovies', title: 'Movies-Crime and Mystery Movies' },
    { id: 'sv_movies_documentarymovies', title: 'Movies-Documentary Movies' },
    { id: 'sv_movies_dramamovies', title: 'Movies-Drama Movies' },
    { id: 'sv_movies_familyandchildrenmovies', title: 'Movies-Family and Children Movies' },
    { id: 'sv_movies_fantasymovies', title: 'Movies-Fantasy Movies' },
    { id: 'sv_movies_horrormovies', title: 'Movies-Horror Movies' },
    { id: 'sv_movies_worldmovies', title: 'Movies-World Movies' },
    { id: 'sv_musicandaudio', title: 'Music and Audio' },
    {
      id: 'sv_musicandaudio_adultcontemporarymusic',
      title: 'Music and Audio-Adult Contemporary Music',
    },
    {
      id: 'sv_musicandaudio_adultcontemporarymusic_softacmusic',
      title: 'Music and Audio-Adult Contemporary Music-Soft AC Music',
    },
    {
      id: 'sv_musicandaudio_adultcontemporarymusic_urbanacmusic',
      title: 'Music and Audio-Adult Contemporary Music-Urban AC Music',
    },
    {
      id: 'sv_musicandaudio_adultalbumalternative',
      title: 'Music and Audio-Adult Album Alternative',
    },
    { id: 'sv_musicandaudio_alternativemusic', title: 'Music and Audio-Alternative Music' },
    { id: 'sv_musicandaudio_childrensmusic', title: 'Music and Audio-Childrens Music' },
    { id: 'sv_musicandaudio_classichits', title: 'Music and Audio-Classic Hits' },
    { id: 'sv_musicandaudio_classicalmusic', title: 'Music and Audio-Classical Music' },
    { id: 'sv_musicandaudio_collegeradio', title: 'Music and Audio-College Radio' },
    {
      id: 'sv_musicandaudio_comedy(musicandaudio)',
      title: 'Music and Audio-Comedy (Music and Audio)',
    },
    {
      id: 'sv_musicandaudio_contemporaryhits/pop/top40',
      title: 'Music and Audio-Contemporary Hits/Pop/Top 40',
    },
    { id: 'sv_musicandaudio_countrymusic', title: 'Music and Audio-Country Music' },
    {
      id: 'sv_musicandaudio_danceandelectronicmusic',
      title: 'Music and Audio-Dance and Electronic Music',
    },
    {
      id: 'sv_musicandaudio_world/internationalmusic',
      title: 'Music and Audio-World/International Music',
    },
    { id: 'sv_musicandaudio_songwriters/folk', title: 'Music and Audio-Songwriters/Folk' },
    { id: 'sv_musicandaudio_gospelmusic', title: 'Music and Audio-Gospel Music' },
    { id: 'sv_musicandaudio_hiphopmusic', title: 'Music and Audio-Hip Hop Music' },
    {
      id: 'sv_musicandaudio_inspirational/newagemusic',
      title: 'Music and Audio-Inspirational/New Age Music',
    },
    { id: 'sv_musicandaudio_jazz', title: 'Music and Audio-Jazz' },
    {
      id: 'sv_musicandaudio_oldies/adultstandards',
      title: 'Music and Audio-Oldies/Adult Standards',
    },
    { id: 'sv_musicandaudio_reggae', title: 'Music and Audio-Reggae' },
    { id: 'sv_musicandaudio_blues', title: 'Music and Audio-Blues' },
    {
      id: 'sv_musicandaudio_religiousmusicandaudio',
      title: 'Music and Audio-Religious (Music and Audio)',
    },
    { id: 'sv_musicandaudio_rbsoulfunk', title: 'Music and Audio-R&B/Soul/Funk' },
    { id: 'sv_musicandaudio_rockmusic', title: 'Music and Audio-Rock Music' },
    {
      id: 'sv_musicandaudio_rockmusic_albumorientedrock',
      title: 'Music and Audio-Rock Music-Album-oriented Rock',
    },
    {
      id: 'sv_musicandaudio_rockmusic_alternativerock',
      title: 'Music and Audio-Rock Music-Alternative Rock',
    },
    {
      id: 'sv_musicandaudio_rockmusic_classicrock',
      title: 'Music and Audio-Rock Music-Classic Rock',
    },
    { id: 'sv_musicandaudio_rockmusic_hardrock', title: 'Music and Audio-Rock Music-Hard Rock' },
    { id: 'sv_musicandaudio_rockmusic_softrock', title: 'Music and Audio-Rock Music-Soft Rock' },
    {
      id: 'sv_musicandaudio_soundtrackstvandshowtunes',
      title: 'Music and Audio-Soundtracks, TV and Showtunes',
    },
    { id: 'sv_musicandaudio_sportsradio', title: 'Music and Audio-Sports Radio' },
    { id: 'sv_musicandaudio_talkradio', title: 'Music and Audio-Talk Radio' },
    {
      id: 'sv_musicandaudio_talkradio_businessnewsradio',
      title: 'Music and Audio-Talk Radio-Business News Radio',
    },
    {
      id: 'sv_musicandaudio_talkradio_educationalradio',
      title: 'Music and Audio-Talk Radio-Educational Radio',
    },
    { id: 'sv_musicandaudio_talkradio_newsradio', title: 'Music and Audio-Talk Radio-News Radio' },
    {
      id: 'sv_musicandaudio_talkradio_news/talkradio',
      title: 'Music and Audio-Talk Radio-News/Talk Radio',
    },
    {
      id: 'sv_musicandaudio_talkradio_publicradio',
      title: 'Music and Audio-Talk Radio-Public Radio',
    },
    {
      id: 'sv_musicandaudio_urbancontemporarymusic',
      title: 'Music and Audio-Urban Contemporary Music',
    },
    {
      id: 'sv_musicandaudio_varietymusicandaudio',
      title: 'Music and Audio-Variety (Music and Audio)',
    },
    { id: 'sv_newsandpolitics', title: 'News and Politics' },
    { id: 'sv_newsandpolitics_crime', title: 'News and Politics-Crime' },
    { id: 'sv_newsandpolitics_disasters', title: 'News and Politics-Disasters' },
    { id: 'sv_newsandpolitics_internationalnews', title: 'News and Politics-International News' },
    { id: 'sv_newsandpolitics_law', title: 'News and Politics-Law' },
    { id: 'sv_newsandpolitics_localnews', title: 'News and Politics-Local News' },
    { id: 'sv_newsandpolitics_nationalnews', title: 'News and Politics-National News' },
    { id: 'sv_newsandpolitics_politics', title: 'News and Politics-Politics' },
    { id: 'sv_newsandpolitics_politics_elections', title: 'News and Politics-Politics-Elections' },
    {
      id: 'sv_newsandpolitics_politics_politicalissues',
      title: 'News and Politics-Politics-Political Issues',
    },
    {
      id: 'sv_newsandpolitics_politics_warandconflicts',
      title: 'News and Politics-Politics-War and Conflicts',
    },
    { id: 'sv_newsandpolitics_weather', title: 'News and Politics-Weather' },
    { id: 'sv_personalfinance', title: 'Personal Finance' },
    { id: 'sv_personalfinance_consumerbanking', title: 'Personal Finance-Consumer Banking' },
    {
      id: 'sv_personalfinance_financialassistance',
      title: 'Personal Finance-Financial Assistance',
    },
    {
      id: 'sv_personalfinance_financialassistance_governmentsupportandwelfare',
      title: 'Personal Finance-Financial Assistance-Government Support and Welfare',
    },
    {
      id: 'sv_personalfinance_financialassistance_studentfinancialaid',
      title: 'Personal Finance-Financial Assistance-Student Financial Aid',
    },
    { id: 'sv_personalfinance_financialplanning', title: 'Personal Finance-Financial Planning' },
    { id: 'sv_personalfinance_frugalliving', title: 'Personal Finance-Frugal Living' },
    { id: 'sv_personalfinance_insurance', title: 'Personal Finance-Insurance' },
    {
      id: 'sv_personalfinance_insurance_healthinsurance',
      title: 'Personal Finance-Insurance-Health Insurance',
    },
    {
      id: 'sv_personalfinance_insurance_homeinsurance',
      title: 'Personal Finance-Insurance-Home Insurance',
    },
    {
      id: 'sv_personalfinance_insurance_lifeinsurance',
      title: 'Personal Finance-Insurance-Life Insurance',
    },
    {
      id: 'sv_personalfinance_insurance_motorinsurance',
      title: 'Personal Finance-Insurance-Motor Insurance',
    },
    {
      id: 'sv_personalfinance_insurance_petinsurance',
      title: 'Personal Finance-Insurance-Pet Insurance',
    },
    {
      id: 'sv_personalfinance_insurance_travelinsurance',
      title: 'Personal Finance-Insurance-Travel Insurance',
    },
    { id: 'sv_personalfinance_personaldebt', title: 'Personal Finance-Personal Debt' },
    {
      id: 'sv_personalfinance_personaldebt_creditcards',
      title: 'Personal Finance-Personal Debt-Credit Cards',
    },
    {
      id: 'sv_personalfinance_personaldebt_homefinancing',
      title: 'Personal Finance-Personal Debt-Home Financing',
    },
    {
      id: 'sv_personalfinance_personaldebt_personalloans',
      title: 'Personal Finance-Personal Debt-Personal Loans',
    },
    {
      id: 'sv_personalfinance_personaldebt_studentloans',
      title: 'Personal Finance-Personal Debt-Student Loans',
    },
    { id: 'sv_personalfinance_personalinvesting', title: 'Personal Finance-Personal Investing' },
    {
      id: 'sv_personalfinance_personalinvesting_hedgefunds',
      title: 'Personal Finance-Personal Investing-Hedge Funds',
    },
    {
      id: 'sv_personalfinance_personalinvesting_mutualfunds',
      title: 'Personal Finance-Personal Investing-Mutual Funds',
    },
    {
      id: 'sv_personalfinance_personalinvesting_options',
      title: 'Personal Finance-Personal Investing-Options',
    },
    {
      id: 'sv_personalfinance_personalinvesting_stocksandbonds',
      title: 'Personal Finance-Personal Investing-Stocks and Bonds',
    },
    { id: 'sv_personalfinance_personaltaxes', title: 'Personal Finance-Personal Taxes' },
    { id: 'sv_personalfinance_retirementplanning', title: 'Personal Finance-Retirement Planning' },
    { id: 'sv_personalfinance_homeutilities', title: 'Personal Finance-Home Utilities' },
    {
      id: 'sv_personalfinance_homeutilities_gasandelectric',
      title: 'Personal Finance-Home Utilities-Gas and Electric',
    },
    {
      id: 'sv_personalfinance_homeutilities_internetserviceproviders',
      title: 'Personal Finance-Home Utilities-Internet Service Providers',
    },
    {
      id: 'sv_personalfinance_homeutilities_phoneservices',
      title: 'Personal Finance-Home Utilities-Phone Services',
    },
    {
      id: 'sv_personalfinance_homeutilities_waterservices',
      title: 'Personal Finance-Home Utilities-Water Services',
    },
    { id: 'sv_pets', title: 'Pets' },
    { id: 'sv_pets_birds', title: 'Pets-Birds' },
    { id: 'sv_pets_cats', title: 'Pets-Cats' },
    { id: 'sv_pets_dogs', title: 'Pets-Dogs' },
    { id: 'sv_pets_fishandaquariums', title: 'Pets-Fish and Aquariums' },
    { id: 'sv_pets_largeanimals', title: 'Pets-Large Animals' },
    { id: 'sv_pets_petadoptions', title: 'Pets-Pet Adoptions' },
    { id: 'sv_pets_reptiles', title: 'Pets-Reptiles' },
    { id: 'sv_pets_veterinarymedicine', title: 'Pets-Veterinary Medicine' },
    { id: 'sv_pets_petsupplies', title: 'Pets-Pet Supplies' },
    { id: 'sv_popculture', title: 'Pop Culture' },
    { id: 'sv_popculture_celebritydeaths', title: 'Pop Culture-Celebrity Deaths' },
    { id: 'sv_popculture_celebrityfamilies', title: 'Pop Culture-Celebrity Families' },
    { id: 'sv_popculture_celebrityhomes', title: 'Pop Culture-Celebrity Homes' },
    { id: 'sv_popculture_celebritypregnancy', title: 'Pop Culture-Celebrity Pregnancy' },
    { id: 'sv_popculture_celebrityrelationships', title: 'Pop Culture-Celebrity Relationships' },
    { id: 'sv_popculture_celebrityscandal', title: 'Pop Culture-Celebrity Scandal' },
    { id: 'sv_popculture_celebritystyle', title: 'Pop Culture-Celebrity Style' },
    { id: 'sv_popculture_humorandsatire', title: 'Pop Culture-Humor and Satire' },
    { id: 'sv_realestate', title: 'Real Estate' },
    { id: 'sv_realestate_apartments', title: 'Real Estate-Apartments' },
    { id: 'sv_realestate_retailproperty', title: 'Real Estate-Retail Property' },
    { id: 'sv_realestate_vacationproperties', title: 'Real Estate-Vacation Properties' },
    { id: 'sv_realestate_developmentalsites', title: 'Real Estate-Developmental Sites' },
    { id: 'sv_realestate_hotelproperties', title: 'Real Estate-Hotel Properties' },
    { id: 'sv_realestate_houses', title: 'Real Estate-Houses' },
    { id: 'sv_realestate_industrialproperty', title: 'Real Estate-Industrial Property' },
    { id: 'sv_realestate_landandfarms', title: 'Real Estate-Land and Farms' },
    { id: 'sv_realestate_officeproperty', title: 'Real Estate-Office Property' },
    {
      id: 'sv_realestate_realestatebuyingandselling',
      title: 'Real Estate-Real Estate Buying and Selling',
    },
    {
      id: 'sv_realestate_realestaterentingandleasing',
      title: 'Real Estate-Real Estate Renting and Leasing',
    },
    { id: 'sv_religionspirituality', title: 'Religion & Spirituality' },
    { id: 'sv_religionspirituality_agnosticism', title: 'Religion & Spirituality-Agnosticism' },
    { id: 'sv_religionspirituality_spirituality', title: 'Religion & Spirituality-Spirituality' },
    { id: 'sv_religionspirituality_astrology', title: 'Religion & Spirituality-Astrology' },
    { id: 'sv_religionspirituality_atheism', title: 'Religion & Spirituality-Atheism' },
    { id: 'sv_religionspirituality_buddhism', title: 'Religion & Spirituality-Buddhism' },
    { id: 'sv_religionspirituality_christianity', title: 'Religion & Spirituality-Christianity' },
    { id: 'sv_religionspirituality_hinduism', title: 'Religion & Spirituality-Hinduism' },
    { id: 'sv_religionspirituality_islam', title: 'Religion & Spirituality-Islam' },
    { id: 'sv_religionspirituality_judaism', title: 'Religion & Spirituality-Judaism' },
    { id: 'sv_religionspirituality_sikhism', title: 'Religion & Spirituality-Sikhism' },
    { id: 'sv_science', title: 'Science' },
    { id: 'sv_science_biologicalsciences', title: 'Science-Biological Sciences' },
    { id: 'sv_science_chemistry', title: 'Science-Chemistry' },
    { id: 'sv_science_environment', title: 'Science-Environment' },
    { id: 'sv_science_genetics', title: 'Science-Genetics' },
    { id: 'sv_science_geography', title: 'Science-Geography' },
    { id: 'sv_science_geology', title: 'Science-Geology' },
    { id: 'sv_science_physics', title: 'Science-Physics' },
    { id: 'sv_science_spaceandastronomy', title: 'Science-Space and Astronomy' },
    { id: 'sv_shopping', title: 'Shopping' },
    { id: 'sv_shopping_couponsanddiscounts', title: 'Shopping-Coupons and Discounts' },
    { id: 'sv_shopping_flowershopping', title: 'Shopping-Flower Shopping' },
    { id: 'sv_shopping_giftsandgreetingscards', title: 'Shopping-Gifts and Greetings Cards' },
    { id: 'sv_shopping_groceryshopping', title: 'Shopping-Grocery Shopping' },
    { id: 'sv_shopping_holidayshopping', title: 'Shopping-Holiday Shopping' },
    { id: 'sv_shopping_householdsupplies', title: 'Shopping-Household Supplies' },
    { id: 'sv_shopping_lotteriesandscratchcards', title: 'Shopping-Lotteries and Scratchcards' },
    { id: 'sv_shopping_salesandpromotions', title: 'Shopping-Sales and Promotions' },
    { id: 'sv_shopping_childrensgamesandtoys', title: 'Shopping-Childrens Games and Toys' },
    { id: 'sv_sports', title: 'Sports' },
    { id: 'sv_sports_americanfootball', title: 'Sports-American Football' },
    { id: 'sv_sports_boxing', title: 'Sports-Boxing' },
    { id: 'sv_sports_cheerleading', title: 'Sports-Cheerleading' },
    { id: 'sv_sports_collegesports', title: 'Sports-College Sports' },
    {
      id: 'sv_sports_collegesports_collegefootball',
      title: 'Sports-College Sports-College Football',
    },
    {
      id: 'sv_sports_collegesports_collegebasketball',
      title: 'Sports-College Sports-College Basketball',
    },
    {
      id: 'sv_sports_collegesports_collegebaseball',
      title: 'Sports-College Sports-College Baseball',
    },
    { id: 'sv_sports_cricket', title: 'Sports-Cricket' },
    { id: 'sv_sports_cycling', title: 'Sports-Cycling' },
    { id: 'sv_sports_darts', title: 'Sports-Darts' },
    { id: 'sv_sports_disabledsports', title: 'Sports-Disabled Sports' },
    { id: 'sv_sports_diving', title: 'Sports-Diving' },
    { id: 'sv_sports_equinesports', title: 'Sports-Equine Sports' },
    { id: 'sv_sports_equinesports_horseracing', title: 'Sports-Equine Sports-Horse Racing' },
    { id: 'sv_sports_extremesports', title: 'Sports-Extreme Sports' },
    {
      id: 'sv_sports_extremesports_canoeingandkayaking',
      title: 'Sports-Extreme Sports-Canoeing and Kayaking',
    },
    { id: 'sv_sports_extremesports_climbing', title: 'Sports-Extreme Sports-Climbing' },
    { id: 'sv_sports_extremesports_paintball', title: 'Sports-Extreme Sports-Paintball' },
    { id: 'sv_sports_extremesports_scubadiving', title: 'Sports-Extreme Sports-Scuba Diving' },
    { id: 'sv_sports_extremesports_skateboarding', title: 'Sports-Extreme Sports-Skateboarding' },
    { id: 'sv_sports_extremesports_snowboarding', title: 'Sports-Extreme Sports-Snowboarding' },
    {
      id: 'sv_sports_extremesports_surfingandbodyboarding',
      title: 'Sports-Extreme Sports-Surfing and Bodyboarding',
    },
    {
      id: 'sv_sports_extremesports_waterskiingandwakeboarding',
      title: 'Sports-Extreme Sports-Waterskiing and Wakeboarding',
    },
    { id: 'sv_sports_australianrulesfootball', title: 'Sports-Australian Rules Football' },
    { id: 'sv_sports_fantasysports', title: 'Sports-Fantasy Sports' },
    { id: 'sv_sports_fieldhockey', title: 'Sports-Field Hockey' },
    { id: 'sv_sports_figureskating', title: 'Sports-Figure Skating' },
    { id: 'sv_sports_fishingsports', title: 'Sports-Fishing Sports' },
    { id: 'sv_sports_golf', title: 'Sports-Golf' },
    { id: 'sv_sports_gymnastics', title: 'Sports-Gymnastics' },
    { id: 'sv_sports_huntingandshooting', title: 'Sports-Hunting and Shooting' },
    { id: 'sv_sports_icehockey', title: 'Sports-Ice Hockey' },
    { id: 'sv_sports_inlineskating', title: 'Sports-Inline Skating' },
    { id: 'sv_sports_lacrosse', title: 'Sports-Lacrosse' },
    { id: 'sv_sports_autoracing', title: 'Sports-Auto Racing' },
    { id: 'sv_sports_autoracing_motorcyclesports', title: 'Sports-Auto Racing-Motorcycle Sports' },
    { id: 'sv_sports_martialarts', title: 'Sports-Martial Arts' },
    { id: 'sv_sports_olympicsports', title: 'Sports-Olympic Sports' },
    {
      id: 'sv_sports_olympicsports_summerolympicsports',
      title: 'Sports-Olympic Sports-Summer Olympic Sports',
    },
    {
      id: 'sv_sports_olympicsports_winterolympicsports',
      title: 'Sports-Olympic Sports-Winter Olympic Sports',
    },
    {
      id: 'sv_sports_pokerandprofessionalgambling',
      title: 'Sports-Poker and Professional Gambling',
    },
    { id: 'sv_sports_rodeo', title: 'Sports-Rodeo' },
    { id: 'sv_sports_rowing', title: 'Sports-Rowing' },
    { id: 'sv_sports_rugby', title: 'Sports-Rugby' },
    { id: 'sv_sports_rugby_rugbyleague', title: 'Sports-Rugby-Rugby League' },
    { id: 'sv_sports_rugby_rugbyunion', title: 'Sports-Rugby-Rugby Union' },
    { id: 'sv_sports_sailing', title: 'Sports-Sailing' },
    { id: 'sv_sports_skiing', title: 'Sports-Skiing' },
    { id: 'sv_sports_snooker/pool/billiards', title: 'Sports-Snooker/Pool/Billiards' },
    { id: 'sv_sports_soccer', title: 'Sports-Soccer' },
    { id: 'sv_sports_badminton', title: 'Sports-Badminton' },
    { id: 'sv_sports_softball', title: 'Sports-Softball' },
    { id: 'sv_sports_squash', title: 'Sports-Squash' },
    { id: 'sv_sports_swimming', title: 'Sports-Swimming' },
    { id: 'sv_sports_tabletennis', title: 'Sports-Table Tennis' },
    { id: 'sv_sports_tennis', title: 'Sports-Tennis' },
    { id: 'sv_sports_trackandfield', title: 'Sports-Track and Field' },
    { id: 'sv_sports_volleyball', title: 'Sports-Volleyball' },
    { id: 'sv_sports_walking', title: 'Sports-Walking' },
    { id: 'sv_sports_waterpolo', title: 'Sports-Water Polo' },
    { id: 'sv_sports_weightlifting', title: 'Sports-Weightlifting' },
    { id: 'sv_sports_baseball', title: 'Sports-Baseball' },
    { id: 'sv_sports_wrestling', title: 'Sports-Wrestling' },
    { id: 'sv_sports_basketball', title: 'Sports-Basketball' },
    { id: 'sv_sports_beachvolleyball', title: 'Sports-Beach Volleyball' },
    { id: 'sv_sports_bodybuilding', title: 'Sports-Bodybuilding' },
    { id: 'sv_sports_bowling', title: 'Sports-Bowling' },
    { id: 'sv_sports_sportsequipment', title: 'Sports-Sports Equipment' },
    { id: 'sv_stylefashion', title: 'Style & Fashion' },
    { id: 'sv_stylefashion_beauty', title: 'Style & Fashion-Beauty' },
    { id: 'sv_stylefashion_beauty_haircare', title: 'Style & Fashion-Beauty-Hair Care' },
    {
      id: 'sv_stylefashion_beauty_makeupandaccessories',
      title: 'Style & Fashion-Beauty-Makeup and Accessories',
    },
    { id: 'sv_stylefashion_beauty_nailcare', title: 'Style & Fashion-Beauty-Nail Care' },
    {
      id: 'sv_stylefashion_beauty_naturalandorganicbeauty',
      title: 'Style & Fashion-Beauty-Natural and Organic Beauty',
    },
    {
      id: 'sv_stylefashion_beauty_perfumeandfragrance',
      title: 'Style & Fashion-Beauty-Perfume and Fragrance',
    },
    { id: 'sv_stylefashion_beauty_skincare', title: 'Style & Fashion-Beauty-Skin Care' },
    { id: 'sv_stylefashion_womensfashion', title: 'Style & Fashion-Womens Fashion' },
    {
      id: 'sv_stylefashion_womensfashion_womensaccessories',
      title: 'Style & Fashion-Womens Fashion-Womens Accessories',
    },
    {
      id: 'sv_stylefashion_womensfashion_womensclothing',
      title: 'Style & Fashion-Womens Fashion-Womens Clothing',
    },
    {
      id: 'sv_stylefashion_womensfashion_womensshoesandfootwear',
      title: 'Style & Fashion-Womens Fashion-Womens Shoes and Footwear',
    },
    { id: 'sv_stylefashion_bodyart', title: 'Style & Fashion-Body Art' },
    { id: 'sv_stylefashion_childrensclothing', title: 'Style & Fashion-Childrens Clothing' },
    { id: 'sv_stylefashion_designerclothing', title: 'Style & Fashion-Designer Clothing' },
    { id: 'sv_stylefashion_fashiontrends', title: 'Style & Fashion-Fashion Trends' },
    { id: 'sv_stylefashion_highfashion', title: 'Style & Fashion-High Fashion' },
    { id: 'sv_stylefashion_mensfashion', title: 'Style & Fashion-Mens Fashion' },
    {
      id: 'sv_stylefashion_mensfashion_mensaccessories',
      title: 'Style & Fashion-Mens Fashion-Mens Accessories',
    },
    {
      id: 'sv_stylefashion_mensfashion_mensclothing',
      title: 'Style & Fashion-Mens Fashion-Mens Clothing',
    },
    {
      id: 'sv_stylefashion_mensfashion_mensshoesandfootwear',
      title: 'Style & Fashion-Mens Fashion-Mens Shoes and Footwear',
    },
    { id: 'sv_stylefashion_personalcare', title: 'Style & Fashion-Personal Care' },
    {
      id: 'sv_stylefashion_personalcare_bathandshower',
      title: 'Style & Fashion-Personal Care-Bath and Shower',
    },
    {
      id: 'sv_stylefashion_personalcare_deodorantandantiperspirant',
      title: 'Style & Fashion-Personal Care-Deodorant and Antiperspirant',
    },
    {
      id: 'sv_stylefashion_personalcare_oralcare',
      title: 'Style & Fashion-Personal Care-Oral care',
    },
    { id: 'sv_stylefashion_personalcare_shaving', title: 'Style & Fashion-Personal Care-Shaving' },
    { id: 'sv_stylefashion_streetstyle', title: 'Style & Fashion-Street Style' },
    { id: 'sv_technologycomputing', title: 'Technology & Computing' },
    {
      id: 'sv_technologycomputing_artificialintelligence',
      title: 'Technology & Computing-Artificial Intelligence',
    },
    {
      id: 'sv_technologycomputing_augmentedreality',
      title: 'Technology & Computing-Augmented Reality',
    },
    { id: 'sv_technologycomputing_computing', title: 'Technology & Computing-Computing' },
    {
      id: 'sv_technologycomputing_computing_computernetworking',
      title: 'Technology & Computing-Computing-Computer Networking',
    },
    {
      id: 'sv_technologycomputing_computing_computerperipherals',
      title: 'Technology & Computing-Computing-Computer Peripherals',
    },
    {
      id: 'sv_technologycomputing_computing_computersoftwareandapplications',
      title: 'Technology & Computing-Computing-Computer Software and Applications',
    },
    {
      id: 'sv_technologycomputing_computing_datastorageandwarehousing',
      title: 'Technology & Computing-Computing-Data Storage and Warehousing',
    },
    {
      id: 'sv_technologycomputing_computing_desktops',
      title: 'Technology & Computing-Computing-Desktops',
    },
    {
      id: 'sv_technologycomputing_computing_informationandnetworksecurity',
      title: 'Technology & Computing-Computing-Information and Network Security',
    },
    {
      id: 'sv_technologycomputing_computing_internet',
      title: 'Technology & Computing-Computing-Internet',
    },
    {
      id: 'sv_technologycomputing_computing_laptops',
      title: 'Technology & Computing-Computing-Laptops',
    },
    {
      id: 'sv_technologycomputing_computing_programminglanguages',
      title: 'Technology & Computing-Computing-Programming Languages',
    },
    {
      id: 'sv_technologycomputing_consumerelectronics',
      title: 'Technology & Computing-Consumer Electronics',
    },
    {
      id: 'sv_technologycomputing_consumerelectronics_camerasandcamcorders',
      title: 'Technology & Computing-Consumer Electronics-Cameras and Camcorders',
    },
    {
      id: 'sv_technologycomputing_consumerelectronics_homeentertainmentsystems',
      title: 'Technology & Computing-Consumer Electronics-Home Entertainment Systems',
    },
    {
      id: 'sv_technologycomputing_consumerelectronics_smartphones',
      title: 'Technology & Computing-Consumer Electronics-Smartphones',
    },
    {
      id: 'sv_technologycomputing_consumerelectronics_tabletsandereaders',
      title: 'Technology & Computing-Consumer Electronics-Tablets and E-readers',
    },
    {
      id: 'sv_technologycomputing_consumerelectronics_wearabletechnology',
      title: 'Technology & Computing-Consumer Electronics-Wearable Technology',
    },
    { id: 'sv_technologycomputing_robotics', title: 'Technology & Computing-Robotics' },
    {
      id: 'sv_technologycomputing_virtualreality',
      title: 'Technology & Computing-Virtual Reality',
    },
    { id: 'sv_television', title: 'Television' },
    { id: 'sv_television_animationtv', title: 'Television-Animation TV' },
    { id: 'sv_television_soapoperatv', title: 'Television-Soap Opera TV' },
    { id: 'sv_television_specialinteresttv', title: 'Television-Special Interest TV' },
    { id: 'sv_television_sportstv', title: 'Television-Sports TV' },
    { id: 'sv_television_childrenstv', title: 'Television-Childrens TV' },
    { id: 'sv_television_comedytv', title: 'Television-Comedy TV' },
    { id: 'sv_television_dramatv', title: 'Television-Drama TV' },
    { id: 'sv_television_factualtv', title: 'Television-Factual TV' },
    { id: 'sv_television_holidaytv', title: 'Television-Holiday TV' },
    { id: 'sv_television_musictv', title: 'Television-Music TV' },
    { id: 'sv_television_realitytv', title: 'Television-Reality TV' },
    { id: 'sv_television_sciencefictiontv', title: 'Television-Science Fiction TV' },
    { id: 'sv_travel', title: 'Travel' },
    { id: 'sv_travel_travelaccessories', title: 'Travel-Travel Accessories' },
    { id: 'sv_travel_travellocations', title: 'Travel-Travel Locations' },
    {
      id: 'sv_travel_travellocations_africatravel',
      title: 'Travel-Travel Locations-Africa Travel',
    },
    { id: 'sv_travel_travellocations_asiatravel', title: 'Travel-Travel Locations-Asia Travel' },
    {
      id: 'sv_travel_travellocations_australiaandoceaniatravel',
      title: 'Travel-Travel Locations-Australia and Oceania Travel',
    },
    {
      id: 'sv_travel_travellocations_europetravel',
      title: 'Travel-Travel Locations-Europe Travel',
    },
    {
      id: 'sv_travel_travellocations_northamericatravel',
      title: 'Travel-Travel Locations-North America Travel',
    },
    { id: 'sv_travel_travellocations_polartravel', title: 'Travel-Travel Locations-Polar Travel' },
    {
      id: 'sv_travel_travellocations_southamericatravel',
      title: 'Travel-Travel Locations-South America Travel',
    },
    { id: 'sv_travel_travelpreparationandadvice', title: 'Travel-Travel Preparation and Advice' },
    { id: 'sv_travel_traveltype', title: 'Travel-Travel Type' },
    { id: 'sv_travel_traveltype_adventuretravel', title: 'Travel-Travel Type-Adventure Travel' },
    { id: 'sv_travel_traveltype_familytravel', title: 'Travel-Travel Type-Family Travel' },
    {
      id: 'sv_travel_traveltype_honeymoonsandgetaways',
      title: 'Travel-Travel Type-Honeymoons and Getaways',
    },
    { id: 'sv_travel_traveltype_hotelsandmotels', title: 'Travel-Travel Type-Hotels and Motels' },
    { id: 'sv_travel_traveltype_railtravel', title: 'Travel-Travel Type-Rail Travel' },
    { id: 'sv_travel_traveltype_roadtrips', title: 'Travel-Travel Type-Road Trips' },
    { id: 'sv_travel_traveltype_spas', title: 'Travel-Travel Type-Spas' },
    { id: 'sv_travel_traveltype_airtravel', title: 'Travel-Travel Type-Air Travel' },
    { id: 'sv_travel_traveltype_beachtravel', title: 'Travel-Travel Type-Beach Travel' },
    { id: 'sv_travel_traveltype_bedbreakfasts', title: 'Travel-Travel Type-Bed & Breakfasts' },
    { id: 'sv_travel_traveltype_budgettravel', title: 'Travel-Travel Type-Budget Travel' },
    { id: 'sv_travel_traveltype_businesstravel', title: 'Travel-Travel Type-Business Travel' },
    { id: 'sv_travel_traveltype_camping', title: 'Travel-Travel Type-Camping' },
    { id: 'sv_travel_traveltype_cruises', title: 'Travel-Travel Type-Cruises' },
    { id: 'sv_travel_traveltype_daytrips', title: 'Travel-Travel Type-Day Trips' },
    { id: 'sv_videogaming', title: 'Video Gaming' },
    { id: 'sv_videogaming_consolegames', title: 'Video Gaming-Console Games' },
    { id: 'sv_videogaming_esports', title: 'Video Gaming-eSports' },
    { id: 'sv_videogaming_mobilegames', title: 'Video Gaming-Mobile Games' },
    { id: 'sv_videogaming_pcgames', title: 'Video Gaming-PC Games' },
    { id: 'sv_videogaming_videogamegenres', title: 'Video Gaming-Video Game Genres' },
    {
      id: 'sv_videogaming_videogamegenres_actionvideogames',
      title: 'Video Gaming-Video Game Genres-Action Video Games',
    },
    {
      id: 'sv_videogaming_videogamegenres_roleplayingvideogames',
      title: 'Video Gaming-Video Game Genres-Role-Playing Video Games',
    },
    {
      id: 'sv_videogaming_videogamegenres_simulationvideogames',
      title: 'Video Gaming-Video Game Genres-Simulation Video Games',
    },
    {
      id: 'sv_videogaming_videogamegenres_sportsvideogames',
      title: 'Video Gaming-Video Game Genres-Sports Video Games',
    },
    {
      id: 'sv_videogaming_videogamegenres_strategyvideogames',
      title: 'Video Gaming-Video Game Genres-Strategy Video Games',
    },
    {
      id: 'sv_videogaming_videogamegenres_actionadventurevideogames',
      title: 'Video Gaming-Video Game Genres-Action-Adventure Video Games',
    },
    {
      id: 'sv_videogaming_videogamegenres_adventurevideogames',
      title: 'Video Gaming-Video Game Genres-Adventure Video Games',
    },
    {
      id: 'sv_videogaming_videogamegenres_casualgames',
      title: 'Video Gaming-Video Game Genres-Casual Games',
    },
    {
      id: 'sv_videogaming_videogamegenres_educationalvideogames',
      title: 'Video Gaming-Video Game Genres-Educational Video Games',
    },
    {
      id: 'sv_videogaming_videogamegenres_exerciseandfitnessvideogames',
      title: 'Video Gaming-Video Game Genres-Exercise and Fitness Video Games',
    },
    { id: 'sv_videogaming_videogamegenres_mmos', title: 'Video Gaming-Video Game Genres-MMOs' },
    {
      id: 'sv_videogaming_videogamegenres_musicandpartyvideogames',
      title: 'Video Gaming-Video Game Genres-Music and Party Video Games',
    },
    {
      id: 'sv_videogaming_videogamegenres_puzzlevideogames',
      title: 'Video Gaming-Video Game Genres-Puzzle Video Games',
    },
    { id: 'sv_sensitivetopics', title: 'Sensitive Topics' },
  ];

  return generateIRISSegmentList(options, currentValues);
};

export const generateGrapeshotContextualSegmentList = (currentValues: string[]): IMenuList => {
  const options: IIRISOption[] = [
    { id: 'gs_auto', title: 'auto' },
    { id: 'gs_auto_4wd', title: 'auto  4wd' },
    { id: 'gs_auto_buying_selling', title: 'auto  buying  selling' },
    { id: 'gs_auto_commercial', title: 'auto  commercial' },
    { id: 'gs_auto_components', title: 'auto  components' },
    { id: 'gs_auto_family', title: 'auto  family' },
    { id: 'gs_auto_hybrid', title: 'auto  hybrid' },
    { id: 'gs_auto_interiors', title: 'auto  interiors' },
    { id: 'gs_auto_luxury', title: 'auto  luxury' },
    { id: 'gs_auto_luxury_convertible', title: 'auto  luxury  convertible' },
    { id: 'gs_auto_luxury_misc', title: 'auto  luxury  misc' },
    { id: 'gs_auto_maintenance', title: 'auto  maintenance' },
    { id: 'gs_auto_manufacturers', title: 'auto  manufacturers' },
    { id: 'gs_auto_media', title: 'auto  media' },
    { id: 'gs_auto_misc', title: 'auto  misc' },
    { id: 'gs_auto_motorbikes', title: 'auto  motorbikes' },
    { id: 'gs_auto_roadtransport', title: 'auto  roadtransport' },
    { id: 'gs_auto_valeting', title: 'auto  valeting' },
    { id: 'gs_auto_vintage', title: 'auto  vintage' },
    { id: 'gs_business', title: 'business' },
    { id: 'gs_business_agri', title: 'business  agri' },
    { id: 'gs_business_biotech', title: 'business  biotech' },
    { id: 'gs_business_careers', title: 'business  careers' },
    { id: 'gs_business_construction', title: 'business  construction' },
    { id: 'gs_business_energy', title: 'business  energy' },
    { id: 'gs_business_health', title: 'business  health' },
    { id: 'gs_business_management', title: 'business  management' },
    { id: 'gs_business_marketing', title: 'business  marketing' },
    { id: 'gs_business_misc', title: 'business  misc' },
    { id: 'gs_business_sme', title: 'business  sme' },
    { id: 'gs_economy', title: 'economy' },
    { id: 'gs_economy_markets', title: 'economy  markets' },
    { id: 'gs_economy_misc', title: 'economy  misc' },
    { id: 'gs_education', title: 'education' },
    { id: 'gs_education_language', title: 'education  language' },
    { id: 'gs_education_misc', title: 'education  misc' },
    { id: 'gs_education_preschool', title: 'education  preschool' },
    { id: 'gs_education_university', title: 'education  university' },
    { id: 'gs_entertain', title: 'entertain' },
    { id: 'gs_entertain_arts', title: 'entertain  arts' },
    { id: 'gs_entertain_books', title: 'entertain  books' },
    { id: 'gs_entertain_celeb', title: 'entertain  celeb' },
    { id: 'gs_entertain_comedy', title: 'entertain  comedy' },
    { id: 'gs_entertain_comicbooks', title: 'entertain  comicbooks' },
    { id: 'gs_entertain_gambling', title: 'entertain  gambling' },
    { id: 'gs_entertain_games', title: 'entertain  games' },
    { id: 'gs_entertain_hobbies', title: 'entertain  hobbies' },
    { id: 'gs_entertain_hobbies_arts_and_crafts', title: 'entertain  hobbies  arts  and  crafts' },
    { id: 'gs_entertain_hobbies_birdwatching', title: 'entertain  hobbies  birdwatching' },
    { id: 'gs_entertain_hobbies_candle_and_soap', title: 'entertain  hobbies  candle  and  soap' },
    { id: 'gs_entertain_hobbies_collecting', title: 'entertain  hobbies  collecting' },
    { id: 'gs_entertain_hobbies_drawing_painting', title: 'entertain  hobbies  drawing  painting' },
    { id: 'gs_entertain_hobbies_genealogy', title: 'entertain  hobbies  genealogy' },
    { id: 'gs_entertain_hobbies_inventions', title: 'entertain  hobbies  inventions' },
    { id: 'gs_entertain_hobbies_jewelry_making', title: 'entertain  hobbies  jewelry  making' },
    { id: 'gs_entertain_hobbies_knitting_needlew', title: 'entertain  hobbies  knitting  needlew' },
    {
      id: 'gs_entertain_hobbies_magic_and_illusion',
      title: 'entertain  hobbies  magic  and  illusion',
    },
    { id: 'gs_entertain_hobbies_music', title: 'entertain  hobbies  music' },
    {
      id: 'gs_entertain_hobbies_stamps_and_coins',
      title: 'entertain  hobbies  stamps  and  coins',
    },
    { id: 'gs_entertain_hobbies_woodworking', title: 'entertain  hobbies  woodworking' },
    { id: 'gs_entertain_movies', title: 'entertain  movies' },
    { id: 'gs_entertain_music', title: 'entertain  music' },
    { id: 'gs_entertain_perfarts', title: 'entertain  perfarts' },
    { id: 'gs_entertain_podcasts', title: 'entertain  podcasts' },
    { id: 'gs_entertain_radio', title: 'entertain  radio' },
    { id: 'gs_entertain_satire', title: 'entertain  satire' },
    { id: 'gs_entertain_tv', title: 'entertain  tv' },
    { id: 'gs_entertain_vidgames', title: 'entertain  vidgames' },
    { id: 'gs_event', title: 'event' },
    { id: 'gs_event_8march', title: 'event  8march' },
    { id: 'gs_event_back_to_school', title: 'event  back  to  school' },
    { id: 'gs_event_backtoschool', title: 'event  backtoschool' },
    { id: 'gs_event_blackfriday', title: 'event  blackfriday' },
    { id: 'gs_event_blackfriday_specific', title: 'event  blackfriday  specific' },
    { id: 'gs_event_christmas', title: 'event  christmas' },
    { id: 'gs_event_cny', title: 'event  cny' },
    { id: 'gs_event_copa_america', title: 'event  copa  america' },
    { id: 'gs_event_easter', title: 'event  easter' },
    { id: 'gs_event_ecommerce', title: 'event  ecommerce' },
    { id: 'gs_event_eid', title: 'event  eid' },
    { id: 'gs_event_euro_championship', title: 'event  euro  championship' },
    { id: 'gs_event_fathers_day', title: 'event  fathers  day' },
    { id: 'gs_event_fourth_july', title: 'event  fourth  july' },
    { id: 'gs_event_graduation', title: 'event  graduation' },
    { id: 'gs_event_halloween', title: 'event  halloween' },
    { id: 'gs_event_marchmadness', title: 'event  marchmadness' },
    { id: 'gs_event_martisor', title: 'event  martisor' },
    { id: 'gs_event_mid_autumn', title: 'event  mid  autumn' },
    { id: 'gs_event_mothers_day', title: 'event  mothers  day' },
    { id: 'gs_event_music_festival', title: 'event  music  festival' },
    { id: 'gs_event_newyear', title: 'event  newyear' },
    { id: 'gs_event_olympics', title: 'event  olympics' },
    { id: 'gs_event_oscars', title: 'event  oscars' },
    { id: 'gs_event_paralympics', title: 'event  paralympics' },
    { id: 'gs_event_singles_day', title: 'event  singles  day' },
    { id: 'gs_event_singlesday', title: 'event  singlesday' },
    { id: 'gs_event_summer_fun', title: 'event  summer  fun' },
    { id: 'gs_event_superbowl', title: 'event  superbowl' },
    { id: 'gs_event_tennis_championship', title: 'event  tennis  championship' },
    { id: 'gs_event_thanksgiving', title: 'event  thanksgiving' },
    { id: 'gs_event_valentine', title: 'event  valentine' },
    { id: 'gs_family', title: 'family' },
    { id: 'gs_family_children', title: 'family  children' },
    { id: 'gs_family_elderly', title: 'family  elderly' },
    { id: 'gs_family_marriage', title: 'family  marriage' },
    { id: 'gs_fashion', title: 'fashion' },
    { id: 'gs_fashion_accessories', title: 'fashion  accessories' },
    { id: 'gs_fashion_beauty', title: 'fashion  beauty' },
    { id: 'gs_fashion_body_art', title: 'fashion  body  art' },
    { id: 'gs_fashion_jewellery', title: 'fashion  jewellery' },
    { id: 'gs_fashion_men', title: 'fashion  men' },
    { id: 'gs_fashion_men_specific', title: 'fashion  men  specific' },
    { id: 'gs_fashion_misc', title: 'fashion  misc' },
    { id: 'gs_fashion_women', title: 'fashion  women' },
    { id: 'gs_fashion_women_specific', title: 'fashion  women  specific' },
    { id: 'gs_finance', title: 'finance' },
    { id: 'gs_finance_banking', title: 'finance  banking' },
    { id: 'gs_finance_credit', title: 'finance  credit' },
    { id: 'gs_finance_insurance', title: 'finance  insurance' },
    { id: 'gs_finance_loans', title: 'finance  loans' },
    { id: 'gs_finance_pensions', title: 'finance  pensions' },
    { id: 'gs_finance_tax', title: 'finance  tax' },
    { id: 'gs_food', title: 'food' },
    { id: 'gs_food_coffee_and_tea', title: 'food  coffee  and  tea' },
    { id: 'gs_food_cuisine', title: 'food  cuisine' },
    { id: 'gs_food_diet', title: 'food  diet' },
    { id: 'gs_food_drink', title: 'food  drink' },
    { id: 'gs_food_drink_beer', title: 'food  drink  beer' },
    { id: 'gs_food_drink_cocktails', title: 'food  drink  cocktails' },
    { id: 'gs_food_drink_misc', title: 'food  drink  misc' },
    { id: 'gs_food_drink_spirits', title: 'food  drink  spirits' },
    { id: 'gs_food_drink_wine', title: 'food  drink  wine' },
    { id: 'gs_food_estab', title: 'food  estab' },
    { id: 'gs_food_ingredients', title: 'food  ingredients' },
    { id: 'gs_food_ingredients_animal-products', title: 'food  ingredients  animal-products' },
    { id: 'gs_food_ingredients_fruits', title: 'food  ingredients  fruits' },
    { id: 'gs_food_ingredients_herbs-spices', title: 'food  ingredients  herbs-spices' },
    { id: 'gs_food_ingredients_meat', title: 'food  ingredients  meat' },
    { id: 'gs_food_ingredients_seafood', title: 'food  ingredients  seafood' },
    { id: 'gs_food_ingredients_vegetables', title: 'food  ingredients  vegetables' },
    { id: 'gs_food_kitchen', title: 'food  kitchen' },
    { id: 'gs_food_misc', title: 'food  misc' },
    { id: 'gs_health', title: 'health' },
    { id: 'gs_health_allergy', title: 'health  allergy' },
    { id: 'gs_health_alt', title: 'health  alt' },
    { id: 'gs_health_dentistry', title: 'health  dentistry' },
    { id: 'gs_health_diet', title: 'health  diet' },
    { id: 'gs_health_disability', title: 'health  disability' },
    { id: 'gs_health_disability_deafness', title: 'health  disability  deafness' },
    { id: 'gs_health_disability_misc', title: 'health  disability  misc' },
    { id: 'gs_health_exercise', title: 'health  exercise' },
    { id: 'gs_health_heart', title: 'health  heart' },
    { id: 'gs_health_incontinence', title: 'health  incontinence' },
    { id: 'gs_health_misc', title: 'health  misc' },
    { id: 'gs_health_psychology', title: 'health  psychology' },
    { id: 'gs_health_sexuality', title: 'health  sexuality' },
    { id: 'gs_health_spas', title: 'health  spas' },
    { id: 'gs_health_women', title: 'health  women' },
    { id: 'gs_home', title: 'home' },
    { id: 'gs_home_appliances', title: 'home  appliances' },
    { id: 'gs_home_entertaining', title: 'home  entertaining' },
    { id: 'gs_home_gardening', title: 'home  gardening' },
    { id: 'gs_home_interiors', title: 'home  interiors' },
    { id: 'gs_home_interiors_misc', title: 'home  interiors  misc' },
    { id: 'gs_home_interiors_repair', title: 'home  interiors  repair' },
    { id: 'gs_home_pets', title: 'home  pets' },
    { id: 'gs_home_pets_aquarium', title: 'home  pets  aquarium' },
    { id: 'gs_home_pets_aquariums', title: 'home  pets  aquariums' },
    { id: 'gs_home_pets_birds', title: 'home  pets  birds' },
    { id: 'gs_home_pets_cats', title: 'home  pets  cats' },
    { id: 'gs_home_pets_dogs', title: 'home  pets  dogs' },
    { id: 'gs_home_pets_large', title: 'home  pets  large' },
    { id: 'gs_home_pets_misc', title: 'home  pets  misc' },
    { id: 'gs_home_pets_reptiles', title: 'home  pets  reptiles' },
    { id: 'gs_home_pets_veterinary', title: 'home  pets  veterinary' },
    { id: 'gs_home_property', title: 'home  property' },
    { id: 'gs_home_safety', title: 'home  safety' },
    { id: 'gs_law', title: 'law' },
    { id: 'gs_law_misc', title: 'law  misc' },
    { id: 'gs_mobileapp', title: 'mobileapp' },
    { id: 'gs_mobileapp_art', title: 'mobileapp  art' },
    { id: 'gs_mobileapp_auto', title: 'mobileapp  auto' },
    { id: 'gs_mobileapp_beauty', title: 'mobileapp  beauty' },
    { id: 'gs_mobileapp_business', title: 'mobileapp  business' },
    { id: 'gs_mobileapp_comics', title: 'mobileapp  comics' },
    { id: 'gs_mobileapp_communication', title: 'mobileapp  communication' },
    { id: 'gs_mobileapp_dating', title: 'mobileapp  dating' },
    { id: 'gs_mobileapp_education', title: 'mobileapp  education' },
    { id: 'gs_mobileapp_entertainment', title: 'mobileapp  entertainment' },
    { id: 'gs_mobileapp_events', title: 'mobileapp  events' },
    { id: 'gs_mobileapp_finance', title: 'mobileapp  finance' },
    { id: 'gs_mobileapp_food', title: 'mobileapp  food' },
    { id: 'gs_mobileapp_games', title: 'mobileapp  games' },
    { id: 'gs_mobileapp_health', title: 'mobileapp  health' },
    { id: 'gs_mobileapp_home', title: 'mobileapp  home' },
    { id: 'gs_mobileapp_lifestyle', title: 'mobileapp  lifestyle' },
    { id: 'gs_mobileapp_medical', title: 'mobileapp  medical' },
    { id: 'gs_mobileapp_music', title: 'mobileapp  music' },
    { id: 'gs_mobileapp_navigation', title: 'mobileapp  navigation' },
    { id: 'gs_mobileapp_news', title: 'mobileapp  news' },
    { id: 'gs_mobileapp_parenting', title: 'mobileapp  parenting' },
    { id: 'gs_mobileapp_personalisation', title: 'mobileapp  personalisation' },
    { id: 'gs_mobileapp_photography', title: 'mobileapp  photography' },
    { id: 'gs_mobileapp_productivity', title: 'mobileapp  productivity' },
    { id: 'gs_mobileapp_reference', title: 'mobileapp  reference' },
    { id: 'gs_mobileapp_shopping', title: 'mobileapp  shopping' },
    { id: 'gs_mobileapp_social', title: 'mobileapp  social' },
    { id: 'gs_mobileapp_sports', title: 'mobileapp  sports' },
    { id: 'gs_mobileapp_tools', title: 'mobileapp  tools' },
    { id: 'gs_mobileapp_travel', title: 'mobileapp  travel' },
    { id: 'gs_mobileapp_trivia', title: 'mobileapp  trivia' },
    { id: 'gs_mobileapp_userrating', title: 'mobileapp  userrating' },
    { id: 'gs_mobileapp_userrating_1', title: 'mobileapp  userrating  1' },
    { id: 'gs_mobileapp_userrating_2', title: 'mobileapp  userrating  2' },
    { id: 'gs_mobileapp_userrating_3', title: 'mobileapp  userrating  3' },
    { id: 'gs_mobileapp_userrating_4', title: 'mobileapp  userrating  4' },
    { id: 'gs_mobileapp_userrating_5', title: 'mobileapp  userrating  5' },
    { id: 'gs_mobileapp_video', title: 'mobileapp  video' },
    { id: 'gs_mobileapp_weather', title: 'mobileapp  weather' },
    { id: 'gs_news', title: 'news' },
    { id: 'gs_news_and_weather', title: 'news  and  weather' },
    { id: 'gs_politics', title: 'politics' },
    { id: 'gs_politics_american', title: 'politics  american' },
    { id: 'gs_politics_arabic', title: 'politics  arabic' },
    { id: 'gs_politics_australian', title: 'politics  australian' },
    { id: 'gs_politics_brazilian', title: 'politics  brazilian' },
    { id: 'gs_politics_british', title: 'politics  british' },
    { id: 'gs_politics_bulgarian', title: 'politics  bulgarian' },
    { id: 'gs_politics_canadian', title: 'politics  canadian' },
    { id: 'gs_politics_chinese', title: 'politics  chinese' },
    { id: 'gs_politics_czech', title: 'politics  czech' },
    { id: 'gs_politics_danish', title: 'politics  danish' },
    { id: 'gs_politics_dutch', title: 'politics  dutch' },
    { id: 'gs_politics_finnish', title: 'politics  finnish' },
    { id: 'gs_politics_french', title: 'politics  french' },
    { id: 'gs_politics_german', title: 'politics  german' },
    { id: 'gs_politics_greek', title: 'politics  greek' },
    { id: 'gs_politics_hindi', title: 'politics  hindi' },
    { id: 'gs_politics_hungarian', title: 'politics  hungarian' },
    { id: 'gs_politics_indonesian', title: 'politics  indonesian' },
    { id: 'gs_politics_italian', title: 'politics  italian' },
    { id: 'gs_politics_japanese', title: 'politics  japanese' },
    { id: 'gs_politics_korean', title: 'politics  korean' },
    { id: 'gs_politics_malaysia', title: 'politics  malaysia' },
    { id: 'gs_politics_mexican', title: 'politics  mexican' },
    { id: 'gs_politics_misc', title: 'politics  misc' },
    { id: 'gs_politics_newzealand', title: 'politics  newzealand' },
    { id: 'gs_politics_norwegian', title: 'politics  norwegian' },
    { id: 'gs_politics_philippines', title: 'politics  philippines' },
    { id: 'gs_politics_polish', title: 'politics  polish' },
    { id: 'gs_politics_portuguese', title: 'politics  portuguese' },
    { id: 'gs_politics_romanian', title: 'politics  romanian' },
    { id: 'gs_politics_russian', title: 'politics  russian' },
    { id: 'gs_politics_slovakian', title: 'politics  slovakian' },
    { id: 'gs_politics_spanish', title: 'politics  spanish' },
    { id: 'gs_politics_swedish', title: 'politics  swedish' },
    { id: 'gs_politics_swiss', title: 'politics  swiss' },
    { id: 'gs_politics_thai', title: 'politics  thai' },
    { id: 'gs_politics_turkish', title: 'politics  turkish' },
    { id: 'gs_politics_vietnamese', title: 'politics  vietnamese' },
    { id: 'gs_science', title: 'science' },
    { id: 'gs_science_biology', title: 'science  biology' },
    { id: 'gs_science_environ', title: 'science  environ' },
    { id: 'gs_science_geology', title: 'science  geology' },
    { id: 'gs_science_misc', title: 'science  misc' },
    { id: 'gs_science_paranormal', title: 'science  paranormal' },
    { id: 'gs_science_physics', title: 'science  physics' },
    { id: 'gs_science_space', title: 'science  space' },
    { id: 'gs_science_weather', title: 'science  weather' },
    { id: 'gs_shopping', title: 'shopping' },
    { id: 'gs_shopping_brands', title: 'shopping  brands' },
    { id: 'gs_shopping_deals', title: 'shopping  deals' },
    { id: 'gs_shopping_misc', title: 'shopping  misc' },
    { id: 'gs_society', title: 'society' },
    { id: 'gs_society_charity', title: 'society  charity' },
    { id: 'gs_society_dating', title: 'society  dating' },
    { id: 'gs_society_divorce', title: 'society  divorce' },
    { id: 'gs_society_lgbt', title: 'society  lgbt' },
    { id: 'gs_society_marriage', title: 'society  marriage' },
    { id: 'gs_society_misc', title: 'society  misc' },
    { id: 'gs_society_religion', title: 'society  religion' },
    { id: 'gs_society_religion_alt', title: 'society  religion  alt' },
    { id: 'gs_society_religion_buddism', title: 'society  religion  buddism' },
    { id: 'gs_society_religion_judaism', title: 'society  religion  judaism' },
    { id: 'gs_society_religion_judasim', title: 'society  religion  judasim' },
    { id: 'gs_society_religion_misc', title: 'society  religion  misc' },
    { id: 'gs_sport', title: 'sport' },
    { id: 'gs_sport_angling', title: 'sport  angling' },
    { id: 'gs_sport_athletics', title: 'sport  athletics' },
    { id: 'gs_sport_ausfootball', title: 'sport  ausfootball' },
    { id: 'gs_sport_baseball', title: 'sport  baseball' },
    { id: 'gs_sport_basketball', title: 'sport  basketball' },
    { id: 'gs_sport_boxing', title: 'sport  boxing' },
    { id: 'gs_sport_canoe', title: 'sport  canoe' },
    { id: 'gs_sport_cheerleading', title: 'sport  cheerleading' },
    { id: 'gs_sport_climbing', title: 'sport  climbing' },
    { id: 'gs_sport_cricket', title: 'sport  cricket' },
    { id: 'gs_sport_cycling', title: 'sport  cycling' },
    { id: 'gs_sport_diving', title: 'sport  diving' },
    { id: 'gs_sport_equestrian', title: 'sport  equestrian' },
    { id: 'gs_sport_extreme', title: 'sport  extreme' },
    { id: 'gs_sport_figure_skating', title: 'sport  figure  skating' },
    { id: 'gs_sport_golf', title: 'sport  golf' },
    { id: 'gs_sport_handball', title: 'sport  handball' },
    { id: 'gs_sport_horseracing', title: 'sport  horseracing' },
    { id: 'gs_sport_hunting', title: 'sport  hunting' },
    { id: 'gs_sport_icehockey', title: 'sport  icehockey' },
    { id: 'gs_sport_inlineskating', title: 'sport  inlineskating' },
    { id: 'gs_sport_martial', title: 'sport  martial' },
    { id: 'gs_sport_misc', title: 'sport  misc' },
    { id: 'gs_sport_motor', title: 'sport  motor' },
    { id: 'gs_sport_motor_f1', title: 'sport  motor  f1' },
    { id: 'gs_sport_motor_misc', title: 'sport  motor  misc' },
    { id: 'gs_sport_motor_motorbikes', title: 'sport  motor  motorbikes' },
    { id: 'gs_sport_motor_nascar', title: 'sport  motor  nascar' },
    { id: 'gs_sport_motor_rally', title: 'sport  motor  rally' },
    { id: 'gs_sport_paintball', title: 'sport  paintball' },
    { id: 'gs_sport_powerboats', title: 'sport  powerboats' },
    { id: 'gs_sport_rugby', title: 'sport  rugby' },
    { id: 'gs_sport_running', title: 'sport  running' },
    { id: 'gs_sport_sailing', title: 'sport  sailing' },
    { id: 'gs_sport_soccer', title: 'sport  soccer' },
    { id: 'gs_sport_surfing_bodyboarding', title: 'sport  surfing  bodyboarding' },
    { id: 'gs_sport_swimming', title: 'sport  swimming' },
    { id: 'gs_sport_tabletennis', title: 'sport  tabletennis' },
    { id: 'gs_sport_tennis', title: 'sport  tennis' },
    { id: 'gs_sport_usfootball', title: 'sport  usfootball' },
    { id: 'gs_sport_volleyball', title: 'sport  volleyball' },
    { id: 'gs_sport_waterpolo', title: 'sport  waterpolo' },
    { id: 'gs_sport_waterski_wakeboard', title: 'sport  waterski  wakeboard' },
    { id: 'gs_sport_winter', title: 'sport  winter' },
    { id: 'gs_tech', title: 'tech' },
    { id: 'gs_tech_cameras', title: 'tech  cameras' },
    { id: 'gs_tech_computing', title: 'tech  computing' },
    { id: 'gs_tech_computing_beginners', title: 'tech  computing  beginners' },
    { id: 'gs_tech_computing_business', title: 'tech  computing  business' },
    { id: 'gs_tech_computing_datacenter', title: 'tech  computing  datacenter' },
    { id: 'gs_tech_computing_db', title: 'tech  computing  db' },
    { id: 'gs_tech_computing_internet', title: 'tech  computing  internet' },
    { id: 'gs_tech_computing_laptops', title: 'tech  computing  laptops' },
    { id: 'gs_tech_computing_misc', title: 'tech  computing  misc' },
    { id: 'gs_tech_computing_peripherals', title: 'tech  computing  peripherals' },
    { id: 'gs_tech_computing_software', title: 'tech  computing  software' },
    { id: 'gs_tech_computing_software_3d', title: 'tech  computing  software  3d' },
    { id: 'gs_tech_computing_software_antivirus', title: 'tech  computing  software  antivirus' },
    { id: 'gs_tech_computing_software_dtp', title: 'tech  computing  software  dtp' },
    { id: 'gs_tech_computing_software_graphics', title: 'tech  computing  software  graphics' },
    { id: 'gs_tech_computing_software_mac', title: 'tech  computing  software  mac' },
    { id: 'gs_tech_computing_software_misc', title: 'tech  computing  software  misc' },
    { id: 'gs_tech_computing_software_shareware', title: 'tech  computing  software  shareware' },
    { id: 'gs_tech_computing_support', title: 'tech  computing  support' },
    { id: 'gs_tech_computing_tablets', title: 'tech  computing  tablets' },
    { id: 'gs_tech_gadgets', title: 'tech  gadgets' },
    { id: 'gs_tech_phones', title: 'tech  phones' },
    { id: 'gs_tech_social', title: 'tech  social' },
    { id: 'gs_travel', title: 'travel' },
    { id: 'gs_travel_air', title: 'travel  air' },
    { id: 'gs_travel_business', title: 'travel  business' },
    { id: 'gs_travel_cruise', title: 'travel  cruise' },
    { id: 'gs_travel_family', title: 'travel  family' },
    { id: 'gs_travel_holidays', title: 'travel  holidays' },
    { id: 'gs_travel_hotels', title: 'travel  hotels' },
    { id: 'gs_travel_misc', title: 'travel  misc' },
    { id: 'gs_travel_parks', title: 'travel  parks' },
    { id: 'gs_travel_rail', title: 'travel  rail' },
    { id: 'gs_webmail', title: 'webmail' },
    { id: '', title: '' },
    { id: 'Brand-Safety Segments', title: 'Brand-Safety Segments' },
    { id: 'gv_adult', title: 'adult' },
    { id: 'gv_arms', title: 'arms' },
    { id: 'gv_crime', title: 'crime' },
    { id: 'gv_crime_computing', title: 'crime computing' },
    { id: 'gv_crime_other', title: 'crime other' },
    { id: 'gv_crime_serious', title: 'crime serious' },
    { id: 'gv_crime_sex', title: 'crime sex' },
    { id: 'gv_crime_violent', title: 'crime violent' },
    { id: 'gv_death_injury', title: 'death injury' },
    { id: 'gv_death_injury_air', title: 'death injury air' },
    { id: 'gv_adult', title: 'death injury fire' },
    { id: 'gv_arms', title: 'death injury misc' },
    { id: 'gv_crime', title: 'death injury rail' },
    { id: 'gv_crime_computing', title: 'death injury road' },
    { id: 'gv_crime_other', title: 'death injury sea' },
    { id: 'gv_crime_serious', title: 'download' },
    { id: 'gv_crime_sex', title: 'drugs' },
    { id: 'gv_crime_violent', title: 'fakenews' },
    { id: 'gv_death_injury', title: 'hatespeech' },
    { id: 'gv_death_injury_air', title: 'military' },
    { id: 'gv_obscenity', title: 'obscenity' },
    { id: 'gv_safe_for_mobile_app', title: 'safe for mobile app' },
    { id: 'gv_spam_or_harmful_site', title: 'spam or harmful site' },
    { id: 'gv_terrorism', title: 'terrorism' },
    { id: 'gv_tobacco', title: 'tobacco' },
  ];

  return generateIRISSegmentList(options, currentValues);
};

export const generateIllumaContextualSegmentList = (currentValues: string[]): IMenuList => {
  const options: IIRISOption[] = [
    { id: 'il_automotive', title: 'Automotive' },
    { id: 'il_business_and_finance', title: 'Business And Finance' },
    { id: 'il_education', title: 'Education' },
    { id: 'il_entertainment', title: 'Entertainment' },
    { id: 'il_family_life', title: 'Family Life' },
    { id: 'il_food_and_drink', title: 'Food And Drink' },
    { id: 'il_gaming', title: 'Gaming' },
    { id: 'il_health', title: 'Health' },
    { id: 'il_hobbies_and_interests', title: 'Hobbies And Interests' },
    { id: 'il_news_and_politics', title: 'News And Politics' },
    { id: 'il_personal_finance', title: 'Personal Finance' },
    { id: 'il_shopping', title: 'Shopping' },
    { id: 'il_sports', title: 'Sports' },
    { id: 'il_technology', title: 'Technology' },
    { id: 'il_travel', title: 'Travel' },
  ];

  return generateIRISSegmentList(options, currentValues);
};

export const generateComScoreContextualSegmentList = (currentValues: string[]): IMenuList => {
  const options: IIRISOption[] = [
    { id: 'cs_110000', title: 'Contextual::IAB 1.0::Arts & Entertainment' },
    { id: 'cs_110001', title: 'Contextual::IAB 1.0::Arts & Entertainment::Books & Literature' },
    { id: 'cs_110002', title: 'Contextual::IAB 1.0::Arts & Entertainment::Celebrity Fan/Gossip' },
    { id: 'cs_110003', title: 'Contextual::IAB 1.0::Arts & Entertainment::Humor' },
    { id: 'cs_110004', title: 'Contextual::IAB 1.0::Arts & Entertainment::Movies' },
    { id: 'cs_110005', title: 'Contextual::IAB 1.0::Arts & Entertainment::Music' },
    { id: 'cs_110006', title: 'Contextual::IAB 1.0::Arts & Entertainment::Television' },
    { id: 'cs_110007', title: 'Contextual::IAB 1.0::Arts & Entertainment::Fine Art' },
    { id: 'cs_111100', title: 'Contextual::IAB 1.0::Automotive' },
    { id: 'cs_111101', title: 'Contextual::IAB 1.0::Automotive::4-Wheel Drive/SUVs' },
    { id: 'cs_111102', title: 'Contextual::IAB 1.0::Automotive::Auto Parts' },
    { id: 'cs_111103', title: 'Contextual::IAB 1.0::Automotive::Auto Repair' },
    { id: 'cs_111104', title: 'Contextual::IAB 1.0::Automotive::Buying/Selling Cars' },
    { id: 'cs_111105', title: 'Contextual::IAB 1.0::Automotive::Convertible' },
    { id: 'cs_111106', title: 'Contextual::IAB 1.0::Automotive::Coupe' },
    { id: 'cs_111107', title: 'Contextual::IAB 1.0::Automotive::Crossover' },
    { id: 'cs_111108', title: 'Contextual::IAB 1.0::Automotive::Diesel' },
    { id: 'cs_111109', title: 'Contextual::IAB 1.0::Automotive::Electric Vehicles' },
    { id: 'cs_111110', title: 'Contextual::IAB 1.0::Automotive::Hatchback' },
    { id: 'cs_111111', title: 'Contextual::IAB 1.0::Automotive::Hybrid' },
    { id: 'cs_111112', title: 'Contextual::IAB 1.0::Automotive::Luxury' },
    { id: 'cs_111113', title: 'Contextual::IAB 1.0::Automotive::MiniVan' },
    { id: 'cs_111114', title: 'Contextual::IAB 1.0::Automotive::Motorcycles' },
    { id: 'cs_111115', title: 'Contextual::IAB 1.0::Automotive::Off-Road Vehicles' },
    { id: 'cs_111116', title: 'Contextual::IAB 1.0::Automotive::Performance Vehicles' },
    { id: 'cs_111117', title: 'Contextual::IAB 1.0::Automotive::Pickup' },
    { id: 'cs_111118', title: 'Contextual::IAB 1.0::Automotive::Road-Side Assistance' },
    { id: 'cs_111119', title: 'Contextual::IAB 1.0::Automotive::Sedan' },
    { id: 'cs_111120', title: 'Contextual::IAB 1.0::Automotive::Trucks & Accessories' },
    { id: 'cs_111121', title: 'Contextual::IAB 1.0::Automotive::Vintage Cars' },
    { id: 'cs_111122', title: 'Contextual::IAB 1.0::Automotive::Wagon' },
    { id: 'cs_111123', title: 'Contextual::IAB 1.0::Automotive::Car Culture' },
    { id: 'cs_601307', title: 'Contextual::IAB 1.0::Automotive::Certified Pre-Owned' },
    { id: 'cs_112200', title: 'Contextual::IAB 1.0::Business' },
    { id: 'cs_112201', title: 'Contextual::IAB 1.0::Business::Agriculture' },
    { id: 'cs_112202', title: 'Contextual::IAB 1.0::Business::Business Software' },
    { id: 'cs_112203', title: 'Contextual::IAB 1.0::Business::Construction' },
    { id: 'cs_112204', title: 'Contextual::IAB 1.0::Business::E-Commerce' },
    { id: 'cs_112205', title: 'Contextual::IAB 1.0::Business::Entrepreneurs' },
    { id: 'cs_112206', title: 'Contextual::IAB 1.0::Business::Financial Services' },
    { id: 'cs_112207', title: 'Contextual::IAB 1.0::Business::Forestry' },
    { id: 'cs_112208', title: 'Contextual::IAB 1.0::Business::Government' },
    { id: 'cs_112209', title: 'Contextual::IAB 1.0::Business::Graphic Design' },
    { id: 'cs_112210', title: 'Contextual::IAB 1.0::Business::Human Resources' },
    { id: 'cs_112211', title: 'Contextual::IAB 1.0::Business::Logistics' },
    { id: 'cs_112212', title: 'Contextual::IAB 1.0::Business::Management' },
    { id: 'cs_112213', title: 'Contextual::IAB 1.0::Business::Marketing' },
    { id: 'cs_112214', title: 'Contextual::IAB 1.0::Business::Metals' },
    { id: 'cs_112215', title: 'Contextual::IAB 1.0::Business::Non-Profit Organizations' },
    { id: 'cs_112216', title: 'Contextual::IAB 1.0::Business::Power Industry' },
    { id: 'cs_112217', title: 'Contextual::IAB 1.0::Business::Public Services' },
    { id: 'cs_112218', title: 'Contextual::IAB 1.0::Business::Purchasing' },
    { id: 'cs_112219', title: 'Contextual::IAB 1.0::Business::Retail Industry' },
    { id: 'cs_112220', title: 'Contextual::IAB 1.0::Business::SOHO' },
    { id: 'cs_112221', title: 'Contextual::IAB 1.0::Business::Telecom' },
    { id: 'cs_112222', title: 'Contextual::IAB 1.0::Business::Advertising' },
    { id: 'cs_112223', title: 'Contextual::IAB 1.0::Business::Biotech/Biomedical' },
    { id: 'cs_112224', title: 'Contextual::IAB 1.0::Business::Green Solutions' },
    { id: 'cs_113300', title: 'Contextual::IAB 1.0::Careers' },
    { id: 'cs_113301', title: 'Contextual::IAB 1.0::Careers::Career Planning' },
    { id: 'cs_113302', title: 'Contextual::IAB 1.0::Careers::Carreer Advice' },
    { id: 'cs_113303', title: 'Contextual::IAB 1.0::Careers::College' },
    { id: 'cs_113304', title: 'Contextual::IAB 1.0::Careers::Financial Aid' },
    { id: 'cs_113305', title: 'Contextual::IAB 1.0::Careers::Job Fairs' },
    { id: 'cs_113306', title: 'Contextual::IAB 1.0::Careers::Job Search' },
    { id: 'cs_113307', title: 'Contextual::IAB 1.0::Careers::Nursing' },
    { id: 'cs_113308', title: 'Contextual::IAB 1.0::Careers::Resume Writing/Advice' },
    { id: 'cs_113309', title: 'Contextual::IAB 1.0::Careers::Scholarships' },
    { id: 'cs_113310', title: 'Contextual::IAB 1.0::Careers::Telecommuting' },
    { id: 'cs_113311', title: 'Contextual::IAB 1.0::Careers::U.S. Military' },
    { id: 'cs_114400', title: 'Contextual::IAB 1.0::Education' },
    { id: 'cs_114401', title: 'Contextual::IAB 1.0::Education::7-12 Educators' },
    { id: 'cs_114402', title: 'Contextual::IAB 1.0::Education::Adult Education' },
    { id: 'cs_114403', title: 'Contextual::IAB 1.0::Education::Art History' },
    { id: 'cs_114404', title: 'Contextual::IAB 1.0::Education::College Admissions' },
    { id: 'cs_114405', title: 'Contextual::IAB 1.0::Education::College Life' },
    { id: 'cs_114406', title: 'Contextual::IAB 1.0::Education::Distance Learning' },
    { id: 'cs_114407', title: 'Contextual::IAB 1.0::Education::English as a 2nd Language' },
    { id: 'cs_114408', title: 'Contextual::IAB 1.0::Education::Graduate School' },
    { id: 'cs_114409', title: 'Contextual::IAB 1.0::Education::Homeschooling' },
    { id: 'cs_114410', title: 'Contextual::IAB 1.0::Education::Homework/Study Tips' },
    { id: 'cs_114411', title: 'Contextual::IAB 1.0::Education::K-6 Educators' },
    { id: 'cs_114412', title: 'Contextual::IAB 1.0::Education::Language Learning' },
    { id: 'cs_114413', title: 'Contextual::IAB 1.0::Education::Private School' },
    { id: 'cs_114414', title: 'Contextual::IAB 1.0::Education::Special Education' },
    { id: 'cs_114415', title: 'Contextual::IAB 1.0::Education::Studying Business' },
    { id: 'cs_115500', title: 'Contextual::IAB 1.0::Family & Parenting' },
    { id: 'cs_115501', title: 'Contextual::IAB 1.0::Family & Parenting::Adoption' },
    { id: 'cs_115502', title: 'Contextual::IAB 1.0::Family & Parenting::Babies & Toddlers' },
    { id: 'cs_115503', title: 'Contextual::IAB 1.0::Family & Parenting::Daycare/Pre-School' },
    { id: 'cs_115504', title: 'Contextual::IAB 1.0::Family & Parenting::Eldercare' },
    { id: 'cs_115505', title: 'Contextual::IAB 1.0::Family & Parenting::Family Internet' },
    { id: 'cs_115506', title: 'Contextual::IAB 1.0::Family & Parenting::Parenting - K-6 Kids' },
    { id: 'cs_115507', title: 'Contextual::IAB 1.0::Family & Parenting::Parenting Teens' },
    { id: 'cs_115508', title: 'Contextual::IAB 1.0::Family & Parenting::Pregnancy' },
    { id: 'cs_115509', title: 'Contextual::IAB 1.0::Family & Parenting::Special Needs Kids' },
    { id: 'cs_116600', title: 'Contextual::IAB 1.0::Food & Drink' },
    { id: 'cs_116601', title: 'Contextual::IAB 1.0::Food & Drink::American Cusine' },
    { id: 'cs_116602', title: 'Contextual::IAB 1.0::Food & Drink::Barbecues & Grilling' },
    { id: 'cs_116603', title: 'Contextual::IAB 1.0::Food & Drink::Cajun/Creole' },
    { id: 'cs_116604', title: 'Contextual::IAB 1.0::Food & Drink::Chinese Cuisine' },
    { id: 'cs_116605', title: 'Contextual::IAB 1.0::Food & Drink::Cocktails/Beer' },
    { id: 'cs_116606', title: 'Contextual::IAB 1.0::Food & Drink::Coffee/Tea' },
    { id: 'cs_116607', title: 'Contextual::IAB 1.0::Food & Drink::Cuisine-specific' },
    { id: 'cs_116608', title: 'Contextual::IAB 1.0::Food & Drink::Desserts & Baking' },
    { id: 'cs_116609', title: 'Contextual::IAB 1.0::Food & Drink::Food Allergies' },
    { id: 'cs_116610', title: 'Contextual::IAB 1.0::Food & Drink::Food Issues' },
    { id: 'cs_116611', title: 'Contextual::IAB 1.0::Food & Drink::French Cuisine' },
    { id: 'cs_116612', title: 'Contextual::IAB 1.0::Food & Drink::Health/LowFat Cooking' },
    { id: 'cs_116613', title: 'Contextual::IAB 1.0::Food & Drink::Italian Cuisine' },
    { id: 'cs_116614', title: 'Contextual::IAB 1.0::Food & Drink::Japanese Cuisine' },
    { id: 'cs_116615', title: 'Contextual::IAB 1.0::Food & Drink::Mexican Cuisine' },
    { id: 'cs_116616', title: 'Contextual::IAB 1.0::Food & Drink::Vegan' },
    { id: 'cs_116617', title: 'Contextual::IAB 1.0::Food & Drink::Vegetarian' },
    { id: 'cs_116618', title: 'Contextual::IAB 1.0::Food & Drink::Wine' },
    { id: 'cs_116619', title: 'Contextual::IAB 1.0::Food & Drink::Dining Out' },
    { id: 'cs_117700', title: 'Contextual::IAB 1.0::Health & Fitness' },
    { id: 'cs_117701', title: 'Contextual::IAB 1.0::Health & Fitness::AIDS/HIV' },
    { id: 'cs_117702', title: 'Contextual::IAB 1.0::Health & Fitness::Allergies' },
    { id: 'cs_117703', title: 'Contextual::IAB 1.0::Health & Fitness::Alternative Medicine' },
    { id: 'cs_117704', title: 'Contextual::IAB 1.0::Health & Fitness::Arthritis' },
    { id: 'cs_117705', title: 'Contextual::IAB 1.0::Health & Fitness::Asthma' },
    { id: 'cs_117706', title: 'Contextual::IAB 1.0::Health & Fitness::Autism/PDD' },
    { id: 'cs_117707', title: 'Contextual::IAB 1.0::Health & Fitness::Bipolar Disorder' },
    { id: 'cs_117708', title: 'Contextual::IAB 1.0::Health & Fitness::Brain Tumor' },
    { id: 'cs_117709', title: 'Contextual::IAB 1.0::Health & Fitness::Cancer' },
    { id: 'cs_117710', title: 'Contextual::IAB 1.0::Health & Fitness::Cholesterol' },
    { id: 'cs_117711', title: 'Contextual::IAB 1.0::Health & Fitness::Chronic Fatigue Syndrome' },
    { id: 'cs_117712', title: 'Contextual::IAB 1.0::Health & Fitness::Chronic Pain' },
    { id: 'cs_117713', title: 'Contextual::IAB 1.0::Health & Fitness::Cold & Flu' },
    { id: 'cs_117714', title: 'Contextual::IAB 1.0::Health & Fitness::Deafness' },
    { id: 'cs_117715', title: 'Contextual::IAB 1.0::Health & Fitness::Dental Care' },
    { id: 'cs_117716', title: 'Contextual::IAB 1.0::Health & Fitness::Depression' },
    { id: 'cs_117717', title: 'Contextual::IAB 1.0::Health & Fitness::Dermatology' },
    { id: 'cs_117718', title: 'Contextual::IAB 1.0::Health & Fitness::Diabetes' },
    { id: 'cs_117719', title: 'Contextual::IAB 1.0::Health & Fitness::Epilepsy' },
    { id: 'cs_117720', title: 'Contextual::IAB 1.0::Health & Fitness::Exercise' },
    { id: 'cs_117721', title: 'Contextual::IAB 1.0::Health & Fitness::GERD/Acid Reflux' },
    { id: 'cs_117722', title: 'Contextual::IAB 1.0::Health & Fitness::Headaches/Migraines' },
    { id: 'cs_117723', title: 'Contextual::IAB 1.0::Health & Fitness::Heart Disease' },
    { id: 'cs_117724', title: 'Contextual::IAB 1.0::Health & Fitness::Herbs for Health' },
    { id: 'cs_117725', title: 'Contextual::IAB 1.0::Health & Fitness::Holistic Healing' },
    { id: 'cs_117726', title: 'Contextual::IAB 1.0::Health & Fitness::IBS/Crohns Disease' },
    { id: 'cs_117727', title: 'Contextual::IAB 1.0::Health & Fitness::Incest/Abuse Support' },
    { id: 'cs_117728', title: 'Contextual::IAB 1.0::Health & Fitness::Incontinence' },
    { id: 'cs_117729', title: 'Contextual::IAB 1.0::Health & Fitness::Infertility' },
    { id: 'cs_117730', title: 'Contextual::IAB 1.0::Health & Fitness::Mens Health' },
    { id: 'cs_117731', title: 'Contextual::IAB 1.0::Health & Fitness::Nutrition' },
    { id: 'cs_117732', title: 'Contextual::IAB 1.0::Health & Fitness::Orthopedics' },
    { id: 'cs_117733', title: 'Contextual::IAB 1.0::Health & Fitness::Panic/Anxiety Disorders' },
    { id: 'cs_117734', title: 'Contextual::IAB 1.0::Health & Fitness::Pediatrics' },
    { id: 'cs_117735', title: 'Contextual::IAB 1.0::Health & Fitness::Physical Therapy' },
    { id: 'cs_117736', title: 'Contextual::IAB 1.0::Health & Fitness::Psychology/Psychiatry' },
    { id: 'cs_117737', title: 'Contextual::IAB 1.0::Health & Fitness::Senior Health' },
    { id: 'cs_117738', title: 'Contextual::IAB 1.0::Health & Fitness::Sexuality' },
    { id: 'cs_117739', title: 'Contextual::IAB 1.0::Health & Fitness::Sleep Disorders' },
    { id: 'cs_117740', title: 'Contextual::IAB 1.0::Health & Fitness::Smoking Cessation' },
    { id: 'cs_117741', title: 'Contextual::IAB 1.0::Health & Fitness::Substance Abuse' },
    { id: 'cs_117742', title: 'Contextual::IAB 1.0::Health & Fitness::Thyroid Disease' },
    { id: 'cs_117743', title: 'Contextual::IAB 1.0::Health & Fitness::Weight Loss' },
    { id: 'cs_117744', title: 'Contextual::IAB 1.0::Health & Fitness::Womens Health' },
    { id: 'cs_117745', title: 'Contextual::IAB 1.0::Health & Fitness::A.D.D.' },
    { id: 'cs_118800', title: 'Contextual::IAB 1.0::Hobbies & Interests' },
    { id: 'cs_118801', title: 'Contextual::IAB 1.0::Hobbies & Interests::Art/Technology' },
    { id: 'cs_118802', title: 'Contextual::IAB 1.0::Hobbies & Interests::Arts & Crafts' },
    { id: 'cs_118803', title: 'Contextual::IAB 1.0::Hobbies & Interests::Beadwork' },
    { id: 'cs_118804', title: 'Contextual::IAB 1.0::Hobbies & Interests::Birdwatching' },
    { id: 'cs_118805', title: 'Contextual::IAB 1.0::Hobbies & Interests::BoardGames/Puzzles' },
    { id: 'cs_118806', title: 'Contextual::IAB 1.0::Hobbies & Interests::Candle & Soap Making' },
    { id: 'cs_118807', title: 'Contextual::IAB 1.0::Hobbies & Interests::Card Games' },
    { id: 'cs_118808', title: 'Contextual::IAB 1.0::Hobbies & Interests::Chess' },
    { id: 'cs_118809', title: 'Contextual::IAB 1.0::Hobbies & Interests::Cigars' },
    { id: 'cs_118810', title: 'Contextual::IAB 1.0::Hobbies & Interests::Collecting' },
    { id: 'cs_118811', title: 'Contextual::IAB 1.0::Hobbies & Interests::Comic Books' },
    { id: 'cs_118812', title: 'Contextual::IAB 1.0::Hobbies & Interests::Drawing/Sketching' },
    { id: 'cs_118813', title: 'Contextual::IAB 1.0::Hobbies & Interests::Freelance Writing' },
    { id: 'cs_118814', title: 'Contextual::IAB 1.0::Hobbies & Interests::Genealogy' },
    { id: 'cs_118815', title: 'Contextual::IAB 1.0::Hobbies & Interests::Getting Published' },
    { id: 'cs_118816', title: 'Contextual::IAB 1.0::Hobbies & Interests::Guitar' },
    { id: 'cs_118817', title: 'Contextual::IAB 1.0::Hobbies & Interests::Home Recording' },
    { id: 'cs_118818', title: 'Contextual::IAB 1.0::Hobbies & Interests::Inventors & Patents' },
    { id: 'cs_118819', title: 'Contextual::IAB 1.0::Hobbies & Interests::Jewelry Making' },
    { id: 'cs_118820', title: 'Contextual::IAB 1.0::Hobbies & Interests::Magic & Illusion' },
    { id: 'cs_118821', title: 'Contextual::IAB 1.0::Hobbies & Interests::Needlework' },
    { id: 'cs_118822', title: 'Contextual::IAB 1.0::Hobbies & Interests::Painting' },
    { id: 'cs_118823', title: 'Contextual::IAB 1.0::Hobbies & Interests::Photography' },
    { id: 'cs_118824', title: 'Contextual::IAB 1.0::Hobbies & Interests::Radio' },
    { id: 'cs_118825', title: 'Contextual::IAB 1.0::Hobbies & Interests::Roleplaying Games' },
    { id: 'cs_118826', title: 'Contextual::IAB 1.0::Hobbies & Interests::Sci-Fi & Fantasy' },
    { id: 'cs_118827', title: 'Contextual::IAB 1.0::Hobbies & Interests::Scrapbooking' },
    { id: 'cs_118828', title: 'Contextual::IAB 1.0::Hobbies & Interests::Screenwriting' },
    { id: 'cs_118829', title: 'Contextual::IAB 1.0::Hobbies & Interests::Stamps & Coins' },
    { id: 'cs_118830', title: 'Contextual::IAB 1.0::Hobbies & Interests::Video & Computer Games' },
    { id: 'cs_118831', title: 'Contextual::IAB 1.0::Hobbies & Interests::Woodworking' },
    { id: 'cs_119900', title: 'Contextual::IAB 1.0::Home & Garden' },
    { id: 'cs_119901', title: 'Contextual::IAB 1.0::Home & Garden::Appliances' },
    { id: 'cs_119902', title: 'Contextual::IAB 1.0::Home & Garden::Entertaining' },
    { id: 'cs_119903', title: 'Contextual::IAB 1.0::Home & Garden::Environmental Safety' },
    { id: 'cs_119904', title: 'Contextual::IAB 1.0::Home & Garden::Gardening' },
    { id: 'cs_119905', title: 'Contextual::IAB 1.0::Home & Garden::Home Repair' },
    { id: 'cs_119906', title: 'Contextual::IAB 1.0::Home & Garden::Home Theater' },
    { id: 'cs_119907', title: 'Contextual::IAB 1.0::Home & Garden::Interior Decorating' },
    { id: 'cs_119908', title: 'Contextual::IAB 1.0::Home & Garden::Landscaping' },
    { id: 'cs_119909', title: 'Contextual::IAB 1.0::Home & Garden::Remodeling & Construction' },
    { id: 'cs_120000', title: 'Contextual::IAB 1.0::Law, Govt & Politics' },
    { id: 'cs_120001', title: 'Contextual::IAB 1.0::Law, Govt & Politics::Immigration' },
    { id: 'cs_120002', title: 'Contextual::IAB 1.0::Law, Govt & Politics::Legal Issues' },
    { id: 'cs_120003', title: 'Contextual::IAB 1.0::Law, Govt & Politics::Politics' },
    {
      id: 'cs_120004',
      title: 'Contextual::IAB 1.0::Law, Govt & Politics::U.S. Government Resources',
    },
    { id: 'cs_120005', title: 'Contextual::IAB 1.0::Law, Govt & Politics::Commentary' },
    { id: 'cs_121100', title: 'Contextual::IAB 1.0::News' },
    { id: 'cs_121101', title: 'Contextual::IAB 1.0::News::International News' },
    { id: 'cs_121102', title: 'Contextual::IAB 1.0::News::Local News' },
    { id: 'cs_121103', title: 'Contextual::IAB 1.0::News::National News' },
    { id: 'cs_122200', title: 'Contextual::IAB 1.0::Personal Finance' },
    { id: 'cs_122201', title: 'Contextual::IAB 1.0::Personal Finance::Beginning Investing' },
    { id: 'cs_122202', title: 'Contextual::IAB 1.0::Personal Finance::Credit/Debt & Loans' },
    { id: 'cs_122203', title: 'Contextual::IAB 1.0::Personal Finance::Financial News' },
    { id: 'cs_122204', title: 'Contextual::IAB 1.0::Personal Finance::Financial Planning' },
    { id: 'cs_122205', title: 'Contextual::IAB 1.0::Personal Finance::Frugal Living' },
    { id: 'cs_122206', title: 'Contextual::IAB 1.0::Personal Finance::Hedge Fund' },
    { id: 'cs_122207', title: 'Contextual::IAB 1.0::Personal Finance::Insurance' },
    { id: 'cs_122208', title: 'Contextual::IAB 1.0::Personal Finance::Investing' },
    { id: 'cs_122209', title: 'Contextual::IAB 1.0::Personal Finance::Mutual Funds' },
    { id: 'cs_122210', title: 'Contextual::IAB 1.0::Personal Finance::Options' },
    { id: 'cs_122211', title: 'Contextual::IAB 1.0::Personal Finance::Retirement Planning' },
    { id: 'cs_122212', title: 'Contextual::IAB 1.0::Personal Finance::Stocks' },
    { id: 'cs_122213', title: 'Contextual::IAB 1.0::Personal Finance::Tax Planning' },
    { id: 'cs_123300', title: 'Contextual::IAB 1.0::Pets' },
    { id: 'cs_123301', title: 'Contextual::IAB 1.0::Pets::Aquariums' },
    { id: 'cs_123302', title: 'Contextual::IAB 1.0::Pets::Birds' },
    { id: 'cs_123303', title: 'Contextual::IAB 1.0::Pets::Cats' },
    { id: 'cs_123304', title: 'Contextual::IAB 1.0::Pets::Dogs' },
    { id: 'cs_123305', title: 'Contextual::IAB 1.0::Pets::Large Animals' },
    { id: 'cs_123306', title: 'Contextual::IAB 1.0::Pets::Reptiles' },
    { id: 'cs_123307', title: 'Contextual::IAB 1.0::Pets::Veterinary Medicine' },
    { id: 'cs_124400', title: 'Contextual::IAB 1.0::Real Estate' },
    { id: 'cs_124401', title: 'Contextual::IAB 1.0::Real Estate::Apartments' },
    { id: 'cs_124402', title: 'Contextual::IAB 1.0::Real Estate::Architects' },
    { id: 'cs_124403', title: 'Contextual::IAB 1.0::Real Estate::Buying/Selling Homes' },
    { id: 'cs_125500', title: 'Contextual::IAB 1.0::Religion and Spirituality' },
    {
      id: 'cs_125501',
      title: 'Contextual::IAB 1.0::Religion and Spirituality::Alternative Religions',
    },
    {
      id: 'cs_125502',
      title: 'Contextual::IAB 1.0::Religion and Spirituality::Atheism/Agnosticism',
    },
    { id: 'cs_125503', title: 'Contextual::IAB 1.0::Religion and Spirituality::Buddhism' },
    { id: 'cs_125504', title: 'Contextual::IAB 1.0::Religion and Spirituality::Catholicism' },
    { id: 'cs_125505', title: 'Contextual::IAB 1.0::Religion and Spirituality::Christianity' },
    { id: 'cs_125506', title: 'Contextual::IAB 1.0::Religion and Spirituality::Hinduism' },
    { id: 'cs_125507', title: 'Contextual::IAB 1.0::Religion and Spirituality::Islam' },
    { id: 'cs_125508', title: 'Contextual::IAB 1.0::Religion and Spirituality::Judaism' },
    { id: 'cs_125509', title: 'Contextual::IAB 1.0::Religion and Spirituality::Latter-Day Saints' },
    { id: 'cs_125510', title: 'Contextual::IAB 1.0::Religion and Spirituality::Pagan/Wiccan' },
    { id: 'cs_126600', title: 'Contextual::IAB 1.0::Science' },
    { id: 'cs_126601', title: 'Contextual::IAB 1.0::Science::Astrology' },
    { id: 'cs_126602', title: 'Contextual::IAB 1.0::Science::Biology' },
    { id: 'cs_126603', title: 'Contextual::IAB 1.0::Science::Botany' },
    { id: 'cs_126604', title: 'Contextual::IAB 1.0::Science::Chemistry' },
    { id: 'cs_126605', title: 'Contextual::IAB 1.0::Science::Geography' },
    { id: 'cs_126606', title: 'Contextual::IAB 1.0::Science::Geology' },
    { id: 'cs_126607', title: 'Contextual::IAB 1.0::Science::Paranormal Phenomena' },
    { id: 'cs_126608', title: 'Contextual::IAB 1.0::Science::Physics' },
    { id: 'cs_126609', title: 'Contextual::IAB 1.0::Science::Space/Astronomy' },
    { id: 'cs_126610', title: 'Contextual::IAB 1.0::Science::Weather' },
    { id: 'cs_127700', title: 'Contextual::IAB 1.0::Shopping' },
    { id: 'cs_127701', title: 'Contextual::IAB 1.0::Shopping::Comparison Engines' },
    { id: 'cs_127702', title: 'Contextual::IAB 1.0::Shopping::Contests & Freebies' },
    { id: 'cs_127703', title: 'Contextual::IAB 1.0::Shopping::Couponing' },
    { id: 'cs_128800', title: 'Contextual::IAB 1.0::Society' },
    { id: 'cs_128801', title: 'Contextual::IAB 1.0::Society::Dating' },
    { id: 'cs_128802', title: 'Contextual::IAB 1.0::Society::Divorce Support' },
    { id: 'cs_128803', title: 'Contextual::IAB 1.0::Society::Ethnic-specific' },
    { id: 'cs_128804', title: 'Contextual::IAB 1.0::Society::Gay Life' },
    { id: 'cs_128805', title: 'Contextual::IAB 1.0::Society::Marriage' },
    { id: 'cs_128806', title: 'Contextual::IAB 1.0::Society::Senior Living' },
    { id: 'cs_128807', title: 'Contextual::IAB 1.0::Society::Teens' },
    { id: 'cs_128808', title: 'Contextual::IAB 1.0::Society::Weddings' },
    { id: 'cs_129900', title: 'Contextual::IAB 1.0::Sports' },
    { id: 'cs_129901', title: 'Contextual::IAB 1.0::Sports::Baseball' },
    { id: 'cs_129902', title: 'Contextual::IAB 1.0::Sports::Bicycling' },
    { id: 'cs_129903', title: 'Contextual::IAB 1.0::Sports::Bodybuilding' },
    { id: 'cs_129904', title: 'Contextual::IAB 1.0::Sports::Boxing' },
    { id: 'cs_129905', title: 'Contextual::IAB 1.0::Sports::Canoeing/Kayaking' },
    { id: 'cs_129906', title: 'Contextual::IAB 1.0::Sports::Cheerleading' },
    { id: 'cs_129907', title: 'Contextual::IAB 1.0::Sports::Climbing' },
    { id: 'cs_129908', title: 'Contextual::IAB 1.0::Sports::Cricket' },
    { id: 'cs_129909', title: 'Contextual::IAB 1.0::Sports::Figure Skating' },
    { id: 'cs_129910', title: 'Contextual::IAB 1.0::Sports::Fly Fishing' },
    { id: 'cs_129911', title: 'Contextual::IAB 1.0::Sports::Football' },
    { id: 'cs_129912', title: 'Contextual::IAB 1.0::Sports::Freshwater Fishing' },
    { id: 'cs_129913', title: 'Contextual::IAB 1.0::Sports::Game & Fish' },
    { id: 'cs_129914', title: 'Contextual::IAB 1.0::Sports::Golf' },
    { id: 'cs_129915', title: 'Contextual::IAB 1.0::Sports::Horse Racing' },
    { id: 'cs_129916', title: 'Contextual::IAB 1.0::Sports::Horses' },
    { id: 'cs_129917', title: 'Contextual::IAB 1.0::Sports::Hunting/Shooting' },
    { id: 'cs_129918', title: 'Contextual::IAB 1.0::Sports::Inline Skating' },
    { id: 'cs_129919', title: 'Contextual::IAB 1.0::Sports::Martial Arts' },
    { id: 'cs_129920', title: 'Contextual::IAB 1.0::Sports::Mountain Biking' },
    { id: 'cs_129921', title: 'Contextual::IAB 1.0::Sports::NASCAR Racing' },
    { id: 'cs_129922', title: 'Contextual::IAB 1.0::Sports::Olympics' },
    { id: 'cs_129923', title: 'Contextual::IAB 1.0::Sports::Paintball' },
    { id: 'cs_129924', title: 'Contextual::IAB 1.0::Sports::Power & Motoryachts' },
    { id: 'cs_129925', title: 'Contextual::IAB 1.0::Sports::Pro Basketball' },
    { id: 'cs_129926', title: 'Contextual::IAB 1.0::Sports::Pro Ice Hockey' },
    { id: 'cs_129927', title: 'Contextual::IAB 1.0::Sports::Rodeo' },
    { id: 'cs_129928', title: 'Contextual::IAB 1.0::Sports::Rugby' },
    { id: 'cs_129929', title: 'Contextual::IAB 1.0::Sports::Running/Jogging' },
    { id: 'cs_129930', title: 'Contextual::IAB 1.0::Sports::Sailing' },
    { id: 'cs_129931', title: 'Contextual::IAB 1.0::Sports::Saltwater Fishing' },
    { id: 'cs_129932', title: 'Contextual::IAB 1.0::Sports::Scuba Diving' },
    { id: 'cs_129933', title: 'Contextual::IAB 1.0::Sports::Skateboarding' },
    { id: 'cs_129934', title: 'Contextual::IAB 1.0::Sports::Skiing' },
    { id: 'cs_129935', title: 'Contextual::IAB 1.0::Sports::Snowboarding' },
    { id: 'cs_129936', title: 'Contextual::IAB 1.0::Sports::Surfing/Bodyboarding' },
    { id: 'cs_129937', title: 'Contextual::IAB 1.0::Sports::Swimming' },
    { id: 'cs_129938', title: 'Contextual::IAB 1.0::Sports::Table Tennis/Ping-Pong' },
    { id: 'cs_129939', title: 'Contextual::IAB 1.0::Sports::Tennis' },
    { id: 'cs_129940', title: 'Contextual::IAB 1.0::Sports::Volleyball' },
    { id: 'cs_129941', title: 'Contextual::IAB 1.0::Sports::Walking' },
    { id: 'cs_129942', title: 'Contextual::IAB 1.0::Sports::Waterski/Wakeboard' },
    { id: 'cs_129943', title: 'Contextual::IAB 1.0::Sports::World Soccer' },
    { id: 'cs_129944', title: 'Contextual::IAB 1.0::Sports::Auto Racing' },
    { id: 'cs_130000', title: 'Contextual::IAB 1.0::Style & Fashion' },
    { id: 'cs_130001', title: 'Contextual::IAB 1.0::Style & Fashion::Accessories' },
    { id: 'cs_130002', title: 'Contextual::IAB 1.0::Style & Fashion::Beauty' },
    { id: 'cs_130003', title: 'Contextual::IAB 1.0::Style & Fashion::Body Art' },
    { id: 'cs_130004', title: 'Contextual::IAB 1.0::Style & Fashion::Clothing' },
    { id: 'cs_130005', title: 'Contextual::IAB 1.0::Style & Fashion::Fashion' },
    { id: 'cs_130006', title: 'Contextual::IAB 1.0::Style & Fashion::Jewelry' },
    { id: 'cs_131100', title: 'Contextual::IAB 1.0::Technology & Computing' },
    { id: 'cs_131101', title: 'Contextual::IAB 1.0::Technology & Computing::3-D Graphics' },
    { id: 'cs_131102', title: 'Contextual::IAB 1.0::Technology & Computing::Animation' },
    { id: 'cs_131103', title: 'Contextual::IAB 1.0::Technology & Computing::Antivirus Software' },
    { id: 'cs_131104', title: 'Contextual::IAB 1.0::Technology & Computing::C/C++' },
    { id: 'cs_131105', title: 'Contextual::IAB 1.0::Technology & Computing::Cameras & Camcorders' },
    { id: 'cs_131106', title: 'Contextual::IAB 1.0::Technology & Computing::Cell Phones' },
    {
      id: 'cs_131107',
      title: 'Contextual::IAB 1.0::Technology & Computing::Computer Certification',
    },
    { id: 'cs_131108', title: 'Contextual::IAB 1.0::Technology & Computing::Computer Networking' },
    { id: 'cs_131109', title: 'Contextual::IAB 1.0::Technology & Computing::Computer Peripherals' },
    { id: 'cs_131110', title: 'Contextual::IAB 1.0::Technology & Computing::Computer Reviews' },
    { id: 'cs_131111', title: 'Contextual::IAB 1.0::Technology & Computing::Data Centers' },
    { id: 'cs_131112', title: 'Contextual::IAB 1.0::Technology & Computing::Databases' },
    { id: 'cs_131113', title: 'Contextual::IAB 1.0::Technology & Computing::Desktop Publishing' },
    { id: 'cs_131114', title: 'Contextual::IAB 1.0::Technology & Computing::Desktop Video' },
    { id: 'cs_131115', title: 'Contextual::IAB 1.0::Technology & Computing::Email' },
    { id: 'cs_131116', title: 'Contextual::IAB 1.0::Technology & Computing::Graphics Software' },
    { id: 'cs_131117', title: 'Contextual::IAB 1.0::Technology & Computing::Home Video/DVD' },
    { id: 'cs_131118', title: 'Contextual::IAB 1.0::Technology & Computing::Internet Technology' },
    { id: 'cs_131119', title: 'Contextual::IAB 1.0::Technology & Computing::Java' },
    { id: 'cs_131120', title: 'Contextual::IAB 1.0::Technology & Computing::JavaScript' },
    { id: 'cs_131121', title: 'Contextual::IAB 1.0::Technology & Computing::Linux' },
    { id: 'cs_131122', title: 'Contextual::IAB 1.0::Technology & Computing::MP3/MIDI' },
    { id: 'cs_131123', title: 'Contextual::IAB 1.0::Technology & Computing::Mac OS' },
    { id: 'cs_131124', title: 'Contextual::IAB 1.0::Technology & Computing::Mac Support' },
    { id: 'cs_131125', title: 'Contextual::IAB 1.0::Technology & Computing::Net Conferencing' },
    { id: 'cs_131126', title: 'Contextual::IAB 1.0::Technology & Computing::Net for Beginners' },
    { id: 'cs_131127', title: 'Contextual::IAB 1.0::Technology & Computing::Network Security' },
    { id: 'cs_131128', title: 'Contextual::IAB 1.0::Technology & Computing::PC Support' },
    { id: 'cs_131129', title: 'Contextual::IAB 1.0::Technology & Computing::Palmtops/PDAs' },
    {
      id: 'cs_131130',
      title: 'Contextual::IAB 1.0::Technology & Computing::Portable Entertainment',
    },
    { id: 'cs_131131', title: 'Contextual::IAB 1.0::Technology & Computing::Shareware/Freeware' },
    { id: 'cs_131132', title: 'Contextual::IAB 1.0::Technology & Computing::Unix' },
    { id: 'cs_131133', title: 'Contextual::IAB 1.0::Technology & Computing::Visual Basic' },
    { id: 'cs_131134', title: 'Contextual::IAB 1.0::Technology & Computing::Web Clip Art' },
    { id: 'cs_131135', title: 'Contextual::IAB 1.0::Technology & Computing::Web Design/HTML' },
    { id: 'cs_131136', title: 'Contextual::IAB 1.0::Technology & Computing::Web Search' },
    { id: 'cs_131137', title: 'Contextual::IAB 1.0::Technology & Computing::Windows' },
    { id: 'cs_132200', title: 'Contextual::IAB 1.0::Travel' },
    { id: 'cs_132201', title: 'Contextual::IAB 1.0::Travel::Adventure Travel' },
    { id: 'cs_132202', title: 'Contextual::IAB 1.0::Travel::Africa' },
    { id: 'cs_132203', title: 'Contextual::IAB 1.0::Travel::Air Travel' },
    { id: 'cs_132204', title: 'Contextual::IAB 1.0::Travel::Asia' },
    { id: 'cs_132205', title: 'Contextual::IAB 1.0::Travel::Australia & New Zealand' },
    { id: 'cs_132206', title: 'Contextual::IAB 1.0::Travel::Bed & Breakfasts' },
    { id: 'cs_132207', title: 'Contextual::IAB 1.0::Travel::Budget Travel' },
    { id: 'cs_132208', title: 'Contextual::IAB 1.0::Travel::Business Travel' },
    { id: 'cs_132209', title: 'Contextual::IAB 1.0::Travel::By US Locale' },
    { id: 'cs_132210', title: 'Contextual::IAB 1.0::Travel::Camping' },
    { id: 'cs_132211', title: 'Contextual::IAB 1.0::Travel::Canada' },
    { id: 'cs_132212', title: 'Contextual::IAB 1.0::Travel::Caribbean' },
    { id: 'cs_132213', title: 'Contextual::IAB 1.0::Travel::Cruises' },
    { id: 'cs_132214', title: 'Contextual::IAB 1.0::Travel::Eastern Europe' },
    { id: 'cs_132215', title: 'Contextual::IAB 1.0::Travel::Europe' },
    { id: 'cs_132216', title: 'Contextual::IAB 1.0::Travel::France' },
    { id: 'cs_132217', title: 'Contextual::IAB 1.0::Travel::Greece' },
    { id: 'cs_132218', title: 'Contextual::IAB 1.0::Travel::Honeymoons/Getaways' },
    { id: 'cs_132219', title: 'Contextual::IAB 1.0::Travel::Hotels' },
    { id: 'cs_132220', title: 'Contextual::IAB 1.0::Travel::Italy' },
    { id: 'cs_132221', title: 'Contextual::IAB 1.0::Travel::Japan' },
    { id: 'cs_132222', title: 'Contextual::IAB 1.0::Travel::Mexico & Central America' },
    { id: 'cs_132223', title: 'Contextual::IAB 1.0::Travel::National Parks' },
    { id: 'cs_132224', title: 'Contextual::IAB 1.0::Travel::South America' },
    { id: 'cs_132225', title: 'Contextual::IAB 1.0::Travel::Spas' },
    { id: 'cs_132226', title: 'Contextual::IAB 1.0::Travel::Theme Parks' },
    { id: 'cs_132227', title: 'Contextual::IAB 1.0::Travel::Traveling with Kids' },
    { id: 'cs_132228', title: 'Contextual::IAB 1.0::Travel::United Kingdom' },
    { id: 'cs_', title: 'Contextual::IAB 2.0' },
    { id: 'cs_iab2_00001', title: 'Contextual::IAB 2.0::Automotive' },
    { id: 'cs_iab2_00002', title: 'Contextual::IAB 2.0::Automotive::Auto Body Styles' },
    {
      id: 'cs_iab2_00003',
      title: 'Contextual::IAB 2.0::Automotive::Auto Body Styles::Commercial Trucks',
    },
    {
      id: 'cs_iab2_00008',
      title: 'Contextual::IAB 2.0::Automotive::Auto Body Styles::Convertible',
    },
    { id: 'cs_iab2_00009', title: 'Contextual::IAB 2.0::Automotive::Auto Body Styles::Coupe' },
    { id: 'cs_iab2_00010', title: 'Contextual::IAB 2.0::Automotive::Auto Body Styles::Crossover' },
    { id: 'cs_iab2_00011', title: 'Contextual::IAB 2.0::Automotive::Auto Body Styles::Hatchback' },
    { id: 'cs_iab2_00012', title: 'Contextual::IAB 2.0::Automotive::Auto Body Styles::Microcar' },
    { id: 'cs_iab2_00013', title: 'Contextual::IAB 2.0::Automotive::Auto Body Styles::Minivan' },
    {
      id: 'cs_iab2_00014',
      title: 'Contextual::IAB 2.0::Automotive::Auto Body Styles::Off-Road Vehicles',
    },
    {
      id: 'cs_iab2_00015',
      title: 'Contextual::IAB 2.0::Automotive::Auto Body Styles::Pickup Trucks',
    },
    { id: 'cs_iab2_00004', title: 'Contextual::IAB 2.0::Automotive::Auto Body Styles::Sedan' },
    {
      id: 'cs_iab2_00005',
      title: 'Contextual::IAB 2.0::Automotive::Auto Body Styles::Station Wagon',
    },
    { id: 'cs_iab2_00006', title: 'Contextual::IAB 2.0::Automotive::Auto Body Styles::SUV' },
    { id: 'cs_iab2_00007', title: 'Contextual::IAB 2.0::Automotive::Auto Body Styles::Van' },
    { id: 'cs_iab2_00030', title: 'Contextual::IAB 2.0::Automotive::Auto Buying and Selling' },
    { id: 'cs_iab2_00031', title: 'Contextual::IAB 2.0::Automotive::Auto Insurance' },
    { id: 'cs_iab2_00032', title: 'Contextual::IAB 2.0::Automotive::Auto Parts' },
    { id: 'cs_iab2_00033', title: 'Contextual::IAB 2.0::Automotive::Auto Recalls' },
    { id: 'cs_iab2_00034', title: 'Contextual::IAB 2.0::Automotive::Auto Repair' },
    { id: 'cs_iab2_00035', title: 'Contextual::IAB 2.0::Automotive::Auto Safety' },
    { id: 'cs_iab2_00036', title: 'Contextual::IAB 2.0::Automotive::Auto Shows' },
    { id: 'cs_iab2_00037', title: 'Contextual::IAB 2.0::Automotive::Auto Technology' },
    {
      id: 'cs_iab2_00038',
      title: 'Contextual::IAB 2.0::Automotive::Auto Technology::Auto Infotainment Technologies',
    },
    {
      id: 'cs_iab2_00039',
      title: 'Contextual::IAB 2.0::Automotive::Auto Technology::Auto Navigation Systems',
    },
    {
      id: 'cs_iab2_00040',
      title: 'Contextual::IAB 2.0::Automotive::Auto Technology::Auto Safety Technologies',
    },
    { id: 'cs_iab2_00016', title: 'Contextual::IAB 2.0::Automotive::Auto Type' },
    { id: 'cs_iab2_00017', title: 'Contextual::IAB 2.0::Automotive::Auto Type::Budget Cars' },
    {
      id: 'cs_iab2_00018',
      title: 'Contextual::IAB 2.0::Automotive::Auto Type::Certified Pre-Owned Cars',
    },
    { id: 'cs_iab2_00019', title: 'Contextual::IAB 2.0::Automotive::Auto Type::Classic Cars' },
    { id: 'cs_iab2_00020', title: 'Contextual::IAB 2.0::Automotive::Auto Type::Concept Cars' },
    { id: 'cs_iab2_00021', title: 'Contextual::IAB 2.0::Automotive::Auto Type::Driverless Cars' },
    { id: 'cs_iab2_00022', title: 'Contextual::IAB 2.0::Automotive::Auto Type::Green Vehicles' },
    { id: 'cs_iab2_00023', title: 'Contextual::IAB 2.0::Automotive::Auto Type::Luxury Cars' },
    { id: 'cs_iab2_00024', title: 'Contextual::IAB 2.0::Automotive::Auto Type::Performance Cars' },
    { id: 'cs_iab2_00025', title: 'Contextual::IAB 2.0::Automotive::Car Culture' },
    { id: 'cs_iab2_00026', title: 'Contextual::IAB 2.0::Automotive::Dash Cam Videos' },
    { id: 'cs_iab2_00027', title: 'Contextual::IAB 2.0::Automotive::Motorcycles' },
    { id: 'cs_iab2_00028', title: 'Contextual::IAB 2.0::Automotive::Road-Side Assistance' },
    { id: 'cs_iab2_00029', title: 'Contextual::IAB 2.0::Automotive::Scooters' },
    { id: 'cs_iab2_00042', title: 'Contextual::IAB 2.0::Books and Literature' },
    {
      id: 'cs_iab2_00043',
      title: 'Contextual::IAB 2.0::Books and Literature::Art and Photography Books',
    },
    { id: 'cs_iab2_00044', title: 'Contextual::IAB 2.0::Books and Literature::Biographies' },
    {
      id: 'cs_iab2_00045',
      title: 'Contextual::IAB 2.0::Books and Literature::Childrens Literature',
    },
    {
      id: 'cs_iab2_00046',
      title: 'Contextual::IAB 2.0::Books and Literature::Comics and Graphic Novels',
    },
    { id: 'cs_iab2_00047', title: 'Contextual::IAB 2.0::Books and Literature::Cookbooks' },
    { id: 'cs_iab2_00048', title: 'Contextual::IAB 2.0::Books and Literature::Fiction' },
    { id: 'cs_iab2_00049', title: 'Contextual::IAB 2.0::Books and Literature::Poetry' },
    { id: 'cs_iab2_00050', title: 'Contextual::IAB 2.0::Books and Literature::Travel Books' },
    {
      id: 'cs_iab2_00051',
      title: 'Contextual::IAB 2.0::Books and Literature::Young Adult Literature',
    },
    { id: 'cs_iab2_00052', title: 'Contextual::IAB 2.0::Business and Finance' },
    { id: 'cs_iab2_00053', title: 'Contextual::IAB 2.0::Business and Finance::Business' },
    {
      id: 'cs_iab2_00054',
      title: 'Contextual::IAB 2.0::Business and Finance::Business::Business Accounting and Finance',
    },
    {
      id: 'cs_iab2_00062',
      title: 'Contextual::IAB 2.0::Business and Finance::Business::Business Administration',
    },
    {
      id: 'cs_iab2_00063',
      title: 'Contextual::IAB 2.0::Business and Finance::Business::Business Banking and Finance',
    },
    {
      id: 'cs_iab2_00064',
      title:
        'Contextual::IAB 2.0::Business and Finance::Business::Business Banking and Finance::Angel Investment',
    },
    {
      id: 'cs_iab2_00065',
      title:
        'Contextual::IAB 2.0::Business and Finance::Business::Business Banking and Finance::Bankruptcy',
    },
    {
      id: 'cs_iab2_00066',
      title:
        'Contextual::IAB 2.0::Business and Finance::Business::Business Banking and Finance::Business Loans',
    },
    {
      id: 'cs_iab2_00067',
      title:
        'Contextual::IAB 2.0::Business and Finance::Business::Business Banking and Finance::Debt Factoring and Invoice Discounting',
    },
    {
      id: 'cs_iab2_00068',
      title:
        'Contextual::IAB 2.0::Business and Finance::Business::Business Banking and Finance::Mergers and Acquisitions',
    },
    {
      id: 'cs_iab2_00069',
      title:
        'Contextual::IAB 2.0::Business and Finance::Business::Business Banking and Finance::Private Equity',
    },
    {
      id: 'cs_iab2_00070',
      title:
        'Contextual::IAB 2.0::Business and Finance::Business::Business Banking and Finance::Sale and Lease Back',
    },
    {
      id: 'cs_iab2_00071',
      title:
        'Contextual::IAB 2.0::Business and Finance::Business::Business Banking and Finance::Venture Capital',
    },
    {
      id: 'cs_iab2_00072',
      title: 'Contextual::IAB 2.0::Business and Finance::Business::Business I.T.',
    },
    {
      id: 'cs_iab2_00073',
      title: 'Contextual::IAB 2.0::Business and Finance::Business::Business Operations',
    },
    {
      id: 'cs_iab2_00074',
      title: 'Contextual::IAB 2.0::Business and Finance::Business::Consumer Issues',
    },
    {
      id: 'cs_iab2_00075',
      title: 'Contextual::IAB 2.0::Business and Finance::Business::Consumer Issues::Recalls',
    },
    {
      id: 'cs_iab2_00076',
      title:
        'Contextual::IAB 2.0::Business and Finance::Business::Executive Leadership and Management',
    },
    {
      id: 'cs_iab2_00077',
      title: 'Contextual::IAB 2.0::Business and Finance::Business::Government Business',
    },
    {
      id: 'cs_iab2_00078',
      title: 'Contextual::IAB 2.0::Business and Finance::Business::Green Solutions',
    },
    {
      id: 'cs_iab2_00055',
      title: 'Contextual::IAB 2.0::Business and Finance::Business::Human Resources',
    },
    {
      id: 'cs_iab2_00056',
      title: 'Contextual::IAB 2.0::Business and Finance::Business::Large Business',
    },
    {
      id: 'cs_iab2_00057',
      title: 'Contextual::IAB 2.0::Business and Finance::Business::Logistics',
    },
    {
      id: 'cs_iab2_00058',
      title: 'Contextual::IAB 2.0::Business and Finance::Business::Marketing and Advertising',
    },
    { id: 'cs_iab2_00059', title: 'Contextual::IAB 2.0::Business and Finance::Business::Sales' },
    {
      id: 'cs_iab2_00060',
      title: 'Contextual::IAB 2.0::Business and Finance::Business::Small and Medium-sized Business',
    },
    { id: 'cs_iab2_00061', title: 'Contextual::IAB 2.0::Business and Finance::Business::Startups' },
    { id: 'cs_iab2_00080', title: 'Contextual::IAB 2.0::Business and Finance::Economy' },
    {
      id: 'cs_iab2_00081',
      title: 'Contextual::IAB 2.0::Business and Finance::Economy::Commodities',
    },
    {
      id: 'cs_iab2_00082',
      title: 'Contextual::IAB 2.0::Business and Finance::Economy::Currencies',
    },
    {
      id: 'cs_iab2_00083',
      title: 'Contextual::IAB 2.0::Business and Finance::Economy::Financial Crisis',
    },
    {
      id: 'cs_iab2_00084',
      title: 'Contextual::IAB 2.0::Business and Finance::Economy::Financial Reform',
    },
    {
      id: 'cs_iab2_00085',
      title: 'Contextual::IAB 2.0::Business and Finance::Economy::Financial Regulation',
    },
    {
      id: 'cs_iab2_00086',
      title: 'Contextual::IAB 2.0::Business and Finance::Economy::Gasoline Prices',
    },
    {
      id: 'cs_iab2_00087',
      title: 'Contextual::IAB 2.0::Business and Finance::Economy::Housing Market',
    },
    {
      id: 'cs_iab2_00088',
      title: 'Contextual::IAB 2.0::Business and Finance::Economy::Interest Rates',
    },
    {
      id: 'cs_iab2_00089',
      title: 'Contextual::IAB 2.0::Business and Finance::Economy::Job Market',
    },
    { id: 'cs_iab2_00090', title: 'Contextual::IAB 2.0::Business and Finance::Industries' },
    {
      id: 'cs_iab2_00091',
      title: 'Contextual::IAB 2.0::Business and Finance::Industries::Advertising Industry',
    },
    {
      id: 'cs_iab2_00102',
      title: 'Contextual::IAB 2.0::Business and Finance::Industries::Agriculture',
    },
    {
      id: 'cs_iab2_00113',
      title: 'Contextual::IAB 2.0::Business and Finance::Industries::Apparel Industry',
    },
    {
      id: 'cs_iab2_00117',
      title: 'Contextual::IAB 2.0::Business and Finance::Industries::Automotive Industry',
    },
    {
      id: 'cs_iab2_00118',
      title: 'Contextual::IAB 2.0::Business and Finance::Industries::Aviation Industry',
    },
    {
      id: 'cs_iab2_00119',
      title:
        'Contextual::IAB 2.0::Business and Finance::Industries::Biotech and Biomedical Industry',
    },
    {
      id: 'cs_iab2_00120',
      title: 'Contextual::IAB 2.0::Business and Finance::Industries::Civil Engineering Industry',
    },
    {
      id: 'cs_iab2_00121',
      title: 'Contextual::IAB 2.0::Business and Finance::Industries::Construction Industry',
    },
    {
      id: 'cs_iab2_00122',
      title: 'Contextual::IAB 2.0::Business and Finance::Industries::Defense Industry',
    },
    {
      id: 'cs_iab2_00092',
      title: 'Contextual::IAB 2.0::Business and Finance::Industries::Education industry',
    },
    {
      id: 'cs_iab2_00093',
      title: 'Contextual::IAB 2.0::Business and Finance::Industries::Entertainment Industry',
    },
    {
      id: 'cs_iab2_00094',
      title:
        'Contextual::IAB 2.0::Business and Finance::Industries::Environmental Services Industry',
    },
    {
      id: 'cs_iab2_00095',
      title: 'Contextual::IAB 2.0::Business and Finance::Industries::Financial Industry',
    },
    {
      id: 'cs_iab2_00096',
      title: 'Contextual::IAB 2.0::Business and Finance::Industries::Food Industry',
    },
    {
      id: 'cs_iab2_00097',
      title: 'Contextual::IAB 2.0::Business and Finance::Industries::Healthcare Industry',
    },
    {
      id: 'cs_iab2_00098',
      title: 'Contextual::IAB 2.0::Business and Finance::Industries::Hospitality Industry',
    },
    {
      id: 'cs_iab2_00099',
      title: 'Contextual::IAB 2.0::Business and Finance::Industries::Information Services Industry',
    },
    {
      id: 'cs_iab2_00100',
      title: 'Contextual::IAB 2.0::Business and Finance::Industries::Legal Services Industry',
    },
    {
      id: 'cs_iab2_00101',
      title:
        'Contextual::IAB 2.0::Business and Finance::Industries::Logistics and Transportation Industry',
    },
    {
      id: 'cs_iab2_00103',
      title:
        'Contextual::IAB 2.0::Business and Finance::Industries::Management Consulting Industry',
    },
    {
      id: 'cs_iab2_00104',
      title: 'Contextual::IAB 2.0::Business and Finance::Industries::Manufacturing Industry',
    },
    {
      id: 'cs_iab2_00105',
      title:
        'Contextual::IAB 2.0::Business and Finance::Industries::Mechanical and Industrial Engineering Industry',
    },
    {
      id: 'cs_iab2_00106',
      title: 'Contextual::IAB 2.0::Business and Finance::Industries::Media Industry',
    },
    {
      id: 'cs_iab2_00107',
      title: 'Contextual::IAB 2.0::Business and Finance::Industries::Metals Industry',
    },
    {
      id: 'cs_iab2_00108',
      title: 'Contextual::IAB 2.0::Business and Finance::Industries::Non-Profit Organizations',
    },
    {
      id: 'cs_iab2_00109',
      title: 'Contextual::IAB 2.0::Business and Finance::Industries::Pharmaceutical Industry',
    },
    {
      id: 'cs_iab2_00110',
      title: 'Contextual::IAB 2.0::Business and Finance::Industries::Power and Energy Industry',
    },
    {
      id: 'cs_iab2_00111',
      title: 'Contextual::IAB 2.0::Business and Finance::Industries::Publishing Industry',
    },
    {
      id: 'cs_iab2_00112',
      title: 'Contextual::IAB 2.0::Business and Finance::Industries::Real Estate Industry',
    },
    {
      id: 'cs_iab2_00114',
      title: 'Contextual::IAB 2.0::Business and Finance::Industries::Retail Industry',
    },
    {
      id: 'cs_iab2_00115',
      title: 'Contextual::IAB 2.0::Business and Finance::Industries::Technology Industry',
    },
    {
      id: 'cs_iab2_00116',
      title: 'Contextual::IAB 2.0::Business and Finance::Industries::Telecommunications Industry',
    },
    { id: 'cs_iab2_00123', title: 'Contextual::IAB 2.0::Careers' },
    { id: 'cs_iab2_00124', title: 'Contextual::IAB 2.0::Careers::Apprenticeships' },
    { id: 'cs_iab2_00125', title: 'Contextual::IAB 2.0::Careers::Career Advice' },
    { id: 'cs_iab2_00126', title: 'Contextual::IAB 2.0::Careers::Career Planning' },
    { id: 'cs_iab2_00127', title: 'Contextual::IAB 2.0::Careers::Job Search' },
    { id: 'cs_iab2_00128', title: 'Contextual::IAB 2.0::Careers::Job Search::Job Fairs' },
    {
      id: 'cs_iab2_00129',
      title: 'Contextual::IAB 2.0::Careers::Job Search::Resume Writing and Advice',
    },
    { id: 'cs_iab2_00130', title: 'Contextual::IAB 2.0::Careers::Telecommuting' },
    { id: 'cs_iab2_00131', title: 'Contextual::IAB 2.0::Careers::Vocational Training' },
    { id: 'cs_iab2_01000', title: 'Contextual::IAB 2.0::Content Channel' },
    { id: 'cs_iab2_01001', title: 'Contextual::IAB 2.0::Content Channel::Editorial/Professional' },
    { id: 'cs_iab2_01002', title: 'Contextual::IAB 2.0::Content Channel::Email' },
    { id: 'cs_iab2_01003', title: 'Contextual::IAB 2.0::Content Channel::Forum/Community' },
    { id: 'cs_iab2_01004', title: 'Contextual::IAB 2.0::Content Channel::Game' },
    { id: 'cs_iab2_01005', title: 'Contextual::IAB 2.0::Content Channel::Marketplace/eCommerce' },
    { id: 'cs_iab2_01006', title: 'Contextual::IAB 2.0::Content Channel::Search Engine/Listings' },
    { id: 'cs_iab2_01007', title: 'Contextual::IAB 2.0::Content Channel::Social' },
    { id: 'cs_iab2_01008', title: 'Contextual::IAB 2.0::Content Channel::Utility/Online Tool' },
    {
      id: 'cs_iab2_01009',
      title: 'Contextual::IAB 2.0::Content Channel::Voice Activated Channels',
    },
    { id: 'cs_iab2_01030', title: 'Contextual::IAB 2.0::Content Language' },
    { id: 'cs_iab2_01032', title: 'Contextual::IAB 2.0::Content Language::Abkhaz' },
    { id: 'cs_iab2_01031', title: 'Contextual::IAB 2.0::Content Language::Afar' },
    { id: 'cs_iab2_01034', title: 'Contextual::IAB 2.0::Content Language::Afrikaans' },
    { id: 'cs_iab2_01035', title: 'Contextual::IAB 2.0::Content Language::Akan' },
    { id: 'cs_iab2_01179', title: 'Contextual::IAB 2.0::Content Language::Albanian' },
    { id: 'cs_iab2_01036', title: 'Contextual::IAB 2.0::Content Language::Amharic' },
    { id: 'cs_iab2_01038', title: 'Contextual::IAB 2.0::Content Language::Arabic' },
    { id: 'cs_iab2_01037', title: 'Contextual::IAB 2.0::Content Language::Aragonese' },
    { id: 'cs_iab2_01093', title: 'Contextual::IAB 2.0::Content Language::Armenian' },
    { id: 'cs_iab2_01039', title: 'Contextual::IAB 2.0::Content Language::Assamese' },
    { id: 'cs_iab2_01040', title: 'Contextual::IAB 2.0::Content Language::Avaric' },
    { id: 'cs_iab2_01033', title: 'Contextual::IAB 2.0::Content Language::Avestan' },
    { id: 'cs_iab2_01041', title: 'Contextual::IAB 2.0::Content Language::Aymara' },
    { id: 'cs_iab2_01042', title: 'Contextual::IAB 2.0::Content Language::Azerbaijani' },
    { id: 'cs_iab2_01048', title: 'Contextual::IAB 2.0::Content Language::Bambara' },
    { id: 'cs_iab2_01043', title: 'Contextual::IAB 2.0::Content Language::Bashkir' },
    { id: 'cs_iab2_01072', title: 'Contextual::IAB 2.0::Content Language::Basque' },
    { id: 'cs_iab2_01044', title: 'Contextual::IAB 2.0::Content Language::Belarusian' },
    { id: 'cs_iab2_01049', title: 'Contextual::IAB 2.0::Content Language::Bengali' },
    { id: 'cs_iab2_01046', title: 'Contextual::IAB 2.0::Content Language::Bihari' },
    { id: 'cs_iab2_01047', title: 'Contextual::IAB 2.0::Content Language::Bislama' },
    { id: 'cs_iab2_01052', title: 'Contextual::IAB 2.0::Content Language::Bosnian' },
    { id: 'cs_iab2_01051', title: 'Contextual::IAB 2.0::Content Language::Breton' },
    { id: 'cs_iab2_01045', title: 'Contextual::IAB 2.0::Content Language::Bulgarian' },
    { id: 'cs_iab2_01140', title: 'Contextual::IAB 2.0::Content Language::Burmese' },
    { id: 'cs_iab2_01053', title: 'Contextual::IAB 2.0::Content Language::Catalan' },
    { id: 'cs_iab2_01055', title: 'Contextual::IAB 2.0::Content Language::Chamorro' },
    { id: 'cs_iab2_01054', title: 'Contextual::IAB 2.0::Content Language::Chechen' },
    { id: 'cs_iab2_01151', title: 'Contextual::IAB 2.0::Content Language::Chichewa' },
    { id: 'cs_iab2_01213', title: 'Contextual::IAB 2.0::Content Language::Chinese' },
    { id: 'cs_iab2_01060', title: 'Contextual::IAB 2.0::Content Language::Chuvash' },
    { id: 'cs_iab2_01120', title: 'Contextual::IAB 2.0::Content Language::Cornish' },
    { id: 'cs_iab2_01056', title: 'Contextual::IAB 2.0::Content Language::Corsican' },
    { id: 'cs_iab2_01057', title: 'Contextual::IAB 2.0::Content Language::Cree' },
    { id: 'cs_iab2_01090', title: 'Contextual::IAB 2.0::Content Language::Croatian' },
    { id: 'cs_iab2_01058', title: 'Contextual::IAB 2.0::Content Language::Czech' },
    { id: 'cs_iab2_01062', title: 'Contextual::IAB 2.0::Content Language::Danish' },
    { id: 'cs_iab2_01064', title: 'Contextual::IAB 2.0::Content Language::Divehi' },
    { id: 'cs_iab2_01146', title: 'Contextual::IAB 2.0::Content Language::Dutch' },
    { id: 'cs_iab2_01065', title: 'Contextual::IAB 2.0::Content Language::Dzongkha' },
    { id: 'cs_iab2_01068', title: 'Contextual::IAB 2.0::Content Language::English' },
    { id: 'cs_iab2_01069', title: 'Contextual::IAB 2.0::Content Language::Esperanto' },
    { id: 'cs_iab2_01071', title: 'Contextual::IAB 2.0::Content Language::Estonian' },
    { id: 'cs_iab2_01066', title: 'Contextual::IAB 2.0::Content Language::Ewe' },
    { id: 'cs_iab2_01077', title: 'Contextual::IAB 2.0::Content Language::Faroese' },
    { id: 'cs_iab2_01073', title: 'Contextual::IAB 2.0::Content Language::Farsi' },
    { id: 'cs_iab2_01076', title: 'Contextual::IAB 2.0::Content Language::Fijian' },
    { id: 'cs_iab2_01075', title: 'Contextual::IAB 2.0::Content Language::Finnish' },
    { id: 'cs_iab2_01078', title: 'Contextual::IAB 2.0::Content Language::French' },
    { id: 'cs_iab2_01074', title: 'Contextual::IAB 2.0::Content Language::Fula' },
    { id: 'cs_iab2_01082', title: 'Contextual::IAB 2.0::Content Language::Galician' },
    { id: 'cs_iab2_01124', title: 'Contextual::IAB 2.0::Content Language::Ganda' },
    { id: 'cs_iab2_01107', title: 'Contextual::IAB 2.0::Content Language::Georgian' },
    { id: 'cs_iab2_01063', title: 'Contextual::IAB 2.0::Content Language::German' },
    { id: 'cs_iab2_01067', title: 'Contextual::IAB 2.0::Content Language::Greek' },
    { id: 'cs_iab2_01083', title: 'Contextual::IAB 2.0::Content Language::Guarani' },
    { id: 'cs_iab2_01084', title: 'Contextual::IAB 2.0::Content Language::Gujarati' },
    { id: 'cs_iab2_01091', title: 'Contextual::IAB 2.0::Content Language::Haitian' },
    { id: 'cs_iab2_01086', title: 'Contextual::IAB 2.0::Content Language::Hausa' },
    { id: 'cs_iab2_01087', title: 'Contextual::IAB 2.0::Content Language::Hebrew' },
    { id: 'cs_iab2_01094', title: 'Contextual::IAB 2.0::Content Language::Herero' },
    { id: 'cs_iab2_01088', title: 'Contextual::IAB 2.0::Content Language::Hindi' },
    { id: 'cs_iab2_01089', title: 'Contextual::IAB 2.0::Content Language::Hiri Motu' },
    { id: 'cs_iab2_01092', title: 'Contextual::IAB 2.0::Content Language::Hungarian' },
    { id: 'cs_iab2_01102', title: 'Contextual::IAB 2.0::Content Language::Icelandic' },
    { id: 'cs_iab2_01101', title: 'Contextual::IAB 2.0::Content Language::Ido' },
    { id: 'cs_iab2_01098', title: 'Contextual::IAB 2.0::Content Language::Igbo' },
    { id: 'cs_iab2_01096', title: 'Contextual::IAB 2.0::Content Language::Indonesian' },
    { id: 'cs_iab2_01095', title: 'Contextual::IAB 2.0::Content Language::Interlingua' },
    { id: 'cs_iab2_01097', title: 'Contextual::IAB 2.0::Content Language::Interlingue' },
    { id: 'cs_iab2_01104', title: 'Contextual::IAB 2.0::Content Language::Inuktitut' },
    { id: 'cs_iab2_01100', title: 'Contextual::IAB 2.0::Content Language::Inupiaq' },
    { id: 'cs_iab2_01080', title: 'Contextual::IAB 2.0::Content Language::Irish' },
    { id: 'cs_iab2_01103', title: 'Contextual::IAB 2.0::Content Language::Italian' },
    { id: 'cs_iab2_01105', title: 'Contextual::IAB 2.0::Content Language::Japanese' },
    { id: 'cs_iab2_01106', title: 'Contextual::IAB 2.0::Content Language::Javanese' },
    { id: 'cs_iab2_01112', title: 'Contextual::IAB 2.0::Content Language::Kalaallisut' },
    { id: 'cs_iab2_01114', title: 'Contextual::IAB 2.0::Content Language::Kannada' },
    { id: 'cs_iab2_01116', title: 'Contextual::IAB 2.0::Content Language::Kanuri' },
    { id: 'cs_iab2_01117', title: 'Contextual::IAB 2.0::Content Language::Kashmiri' },
    { id: 'cs_iab2_01111', title: 'Contextual::IAB 2.0::Content Language::Kazakh' },
    { id: 'cs_iab2_01113', title: 'Contextual::IAB 2.0::Content Language::Khmer' },
    { id: 'cs_iab2_01109', title: 'Contextual::IAB 2.0::Content Language::Kikuyu' },
    { id: 'cs_iab2_01167', title: 'Contextual::IAB 2.0::Content Language::Kinyarwanda' },
    { id: 'cs_iab2_01164', title: 'Contextual::IAB 2.0::Content Language::Kirundi' },
    { id: 'cs_iab2_01119', title: 'Contextual::IAB 2.0::Content Language::Komi' },
    { id: 'cs_iab2_01108', title: 'Contextual::IAB 2.0::Content Language::Kongo' },
    { id: 'cs_iab2_01115', title: 'Contextual::IAB 2.0::Content Language::Korean' },
    { id: 'cs_iab2_01118', title: 'Contextual::IAB 2.0::Content Language::Kurdish' },
    { id: 'cs_iab2_01110', title: 'Contextual::IAB 2.0::Content Language::Kwanyama' },
    { id: 'cs_iab2_01121', title: 'Contextual::IAB 2.0::Content Language::Kyrgyz' },
    { id: 'cs_iab2_01127', title: 'Contextual::IAB 2.0::Content Language::Lao' },
    { id: 'cs_iab2_01122', title: 'Contextual::IAB 2.0::Content Language::Latin' },
    { id: 'cs_iab2_01130', title: 'Contextual::IAB 2.0::Content Language::Latvian' },
    { id: 'cs_iab2_01125', title: 'Contextual::IAB 2.0::Content Language::Limburgish' },
    { id: 'cs_iab2_01126', title: 'Contextual::IAB 2.0::Content Language::Lingala' },
    { id: 'cs_iab2_01128', title: 'Contextual::IAB 2.0::Content Language::Lithuanian' },
    { id: 'cs_iab2_01129', title: 'Contextual::IAB 2.0::Content Language::Luba-Katanga' },
    { id: 'cs_iab2_01123', title: 'Contextual::IAB 2.0::Content Language::Luxembourgish' },
    { id: 'cs_iab2_01134', title: 'Contextual::IAB 2.0::Content Language::Macedonian' },
    { id: 'cs_iab2_01131', title: 'Contextual::IAB 2.0::Content Language::Malagasy' },
    { id: 'cs_iab2_01138', title: 'Contextual::IAB 2.0::Content Language::Malay' },
    { id: 'cs_iab2_01135', title: 'Contextual::IAB 2.0::Content Language::Malayalam' },
    { id: 'cs_iab2_01139', title: 'Contextual::IAB 2.0::Content Language::Maltese' },
    { id: 'cs_iab2_01085', title: 'Contextual::IAB 2.0::Content Language::Manx' },
    { id: 'cs_iab2_01133', title: 'Contextual::IAB 2.0::Content Language::Maori' },
    { id: 'cs_iab2_01137', title: 'Contextual::IAB 2.0::Content Language::Marathi' },
    { id: 'cs_iab2_01132', title: 'Contextual::IAB 2.0::Content Language::Marshallese' },
    { id: 'cs_iab2_01136', title: 'Contextual::IAB 2.0::Content Language::Mongolian' },
    { id: 'cs_iab2_01141', title: 'Contextual::IAB 2.0::Content Language::Nauruan' },
    { id: 'cs_iab2_01150', title: 'Contextual::IAB 2.0::Content Language::Navajo' },
    { id: 'cs_iab2_01145', title: 'Contextual::IAB 2.0::Content Language::Ndonga' },
    { id: 'cs_iab2_01144', title: 'Contextual::IAB 2.0::Content Language::Nepali' },
    { id: 'cs_iab2_01143', title: 'Contextual::IAB 2.0::Content Language::Northern Ndebele' },
    { id: 'cs_iab2_01171', title: 'Contextual::IAB 2.0::Content Language::Northern Sami' },
    { id: 'cs_iab2_01148', title: 'Contextual::IAB 2.0::Content Language::Norwegian' },
    { id: 'cs_iab2_01142', title: 'Contextual::IAB 2.0::Content Language::Norwegian Bokmal' },
    { id: 'cs_iab2_01147', title: 'Contextual::IAB 2.0::Content Language::Norwegian Nynorsk' },
    { id: 'cs_iab2_01099', title: 'Contextual::IAB 2.0::Content Language::Nuosu' },
    { id: 'cs_iab2_01152', title: 'Contextual::IAB 2.0::Content Language::Occitan' },
    { id: 'cs_iab2_01153', title: 'Contextual::IAB 2.0::Content Language::Ojibwe' },
    { id: 'cs_iab2_01059', title: 'Contextual::IAB 2.0::Content Language::Old Church Slavonic' },
    { id: 'cs_iab2_01155', title: 'Contextual::IAB 2.0::Content Language::Oriya' },
    { id: 'cs_iab2_01154', title: 'Contextual::IAB 2.0::Content Language::Oromo' },
    { id: 'cs_iab2_01156', title: 'Contextual::IAB 2.0::Content Language::Ossetian' },
    { id: 'cs_iab2_01158', title: 'Contextual::IAB 2.0::Content Language::Pali' },
    { id: 'cs_iab2_01160', title: 'Contextual::IAB 2.0::Content Language::Pashto' },
    { id: 'cs_iab2_01159', title: 'Contextual::IAB 2.0::Content Language::Polish' },
    { id: 'cs_iab2_01161', title: 'Contextual::IAB 2.0::Content Language::Portuguese' },
    { id: 'cs_iab2_01157', title: 'Contextual::IAB 2.0::Content Language::Punjabi' },
    { id: 'cs_iab2_01162', title: 'Contextual::IAB 2.0::Content Language::Quechua' },
    { id: 'cs_iab2_01165', title: 'Contextual::IAB 2.0::Content Language::Romanian' },
    { id: 'cs_iab2_01163', title: 'Contextual::IAB 2.0::Content Language::Romansh' },
    { id: 'cs_iab2_01166', title: 'Contextual::IAB 2.0::Content Language::Russian' },
    { id: 'cs_iab2_01176', title: 'Contextual::IAB 2.0::Content Language::Samoan' },
    { id: 'cs_iab2_01172', title: 'Contextual::IAB 2.0::Content Language::Sango' },
    { id: 'cs_iab2_01168', title: 'Contextual::IAB 2.0::Content Language::Sanskrit' },
    { id: 'cs_iab2_01169', title: 'Contextual::IAB 2.0::Content Language::Sardinian' },
    { id: 'cs_iab2_01081', title: 'Contextual::IAB 2.0::Content Language::Scottish Gaelic' },
    { id: 'cs_iab2_01180', title: 'Contextual::IAB 2.0::Content Language::Serbian' },
    { id: 'cs_iab2_01177', title: 'Contextual::IAB 2.0::Content Language::Shona' },
    { id: 'cs_iab2_01170', title: 'Contextual::IAB 2.0::Content Language::Sindhi' },
    { id: 'cs_iab2_01173', title: 'Contextual::IAB 2.0::Content Language::Sinhala' },
    { id: 'cs_iab2_01174', title: 'Contextual::IAB 2.0::Content Language::Slovak' },
    { id: 'cs_iab2_01175', title: 'Contextual::IAB 2.0::Content Language::Slovene' },
    { id: 'cs_iab2_01178', title: 'Contextual::IAB 2.0::Content Language::Somali' },
    { id: 'cs_iab2_01149', title: 'Contextual::IAB 2.0::Content Language::Southern Ndebele' },
    { id: 'cs_iab2_01182', title: 'Contextual::IAB 2.0::Content Language::Southern Sotho' },
    { id: 'cs_iab2_01070', title: 'Contextual::IAB 2.0::Content Language::Spanish' },
    { id: 'cs_iab2_01183', title: 'Contextual::IAB 2.0::Content Language::Sundanese' },
    { id: 'cs_iab2_01185', title: 'Contextual::IAB 2.0::Content Language::Swahili' },
    { id: 'cs_iab2_01181', title: 'Contextual::IAB 2.0::Content Language::Swati' },
    { id: 'cs_iab2_01184', title: 'Contextual::IAB 2.0::Content Language::Swedish' },
    { id: 'cs_iab2_01192', title: 'Contextual::IAB 2.0::Content Language::Tagalog' },
    { id: 'cs_iab2_01199', title: 'Contextual::IAB 2.0::Content Language::Tahitian' },
    { id: 'cs_iab2_01188', title: 'Contextual::IAB 2.0::Content Language::Tajik' },
    { id: 'cs_iab2_01186', title: 'Contextual::IAB 2.0::Content Language::Tamil' },
    { id: 'cs_iab2_01197', title: 'Contextual::IAB 2.0::Content Language::Tatar' },
    { id: 'cs_iab2_01187', title: 'Contextual::IAB 2.0::Content Language::Telugu' },
    { id: 'cs_iab2_01189', title: 'Contextual::IAB 2.0::Content Language::Thai' },
    { id: 'cs_iab2_01050', title: 'Contextual::IAB 2.0::Content Language::Tibetan' },
    { id: 'cs_iab2_01190', title: 'Contextual::IAB 2.0::Content Language::Tigrinya' },
    { id: 'cs_iab2_01194', title: 'Contextual::IAB 2.0::Content Language::Tonga' },
    { id: 'cs_iab2_01196', title: 'Contextual::IAB 2.0::Content Language::Tsonga' },
    { id: 'cs_iab2_01193', title: 'Contextual::IAB 2.0::Content Language::Tswana' },
    { id: 'cs_iab2_01195', title: 'Contextual::IAB 2.0::Content Language::Turkish' },
    { id: 'cs_iab2_01191', title: 'Contextual::IAB 2.0::Content Language::Turkmen' },
    { id: 'cs_iab2_01198', title: 'Contextual::IAB 2.0::Content Language::Twi' },
    { id: 'cs_iab2_01201', title: 'Contextual::IAB 2.0::Content Language::Ukrainian' },
    { id: 'cs_iab2_01202', title: 'Contextual::IAB 2.0::Content Language::Urdu' },
    { id: 'cs_iab2_01200', title: 'Contextual::IAB 2.0::Content Language::Uyghur' },
    { id: 'cs_iab2_01203', title: 'Contextual::IAB 2.0::Content Language::Uzbek' },
    { id: 'cs_iab2_01204', title: 'Contextual::IAB 2.0::Content Language::Venda' },
    { id: 'cs_iab2_01205', title: 'Contextual::IAB 2.0::Content Language::Vietnamese' },
    { id: 'cs_iab2_01206', title: 'Contextual::IAB 2.0::Content Language::Volapuk' },
    { id: 'cs_iab2_01207', title: 'Contextual::IAB 2.0::Content Language::Walloon' },
    { id: 'cs_iab2_01061', title: 'Contextual::IAB 2.0::Content Language::Welsh' },
    { id: 'cs_iab2_01079', title: 'Contextual::IAB 2.0::Content Language::Western Frisian' },
    { id: 'cs_iab2_01208', title: 'Contextual::IAB 2.0::Content Language::Wolof' },
    { id: 'cs_iab2_01209', title: 'Contextual::IAB 2.0::Content Language::Xhosa' },
    { id: 'cs_iab2_01210', title: 'Contextual::IAB 2.0::Content Language::Yiddish' },
    { id: 'cs_iab2_01211', title: 'Contextual::IAB 2.0::Content Language::Yoruba' },
    { id: 'cs_iab2_01212', title: 'Contextual::IAB 2.0::Content Language::Zhuang' },
    { id: 'cs_iab2_01214', title: 'Contextual::IAB 2.0::Content Language::Zulu' },
    { id: 'cs_iab2_01022', title: 'Contextual::IAB 2.0::Content Media Format' },
    { id: 'cs_iab2_01023', title: 'Contextual::IAB 2.0::Content Media Format::Audio' },
    { id: 'cs_iab2_01024', title: 'Contextual::IAB 2.0::Content Media Format::Images/Galleries' },
    { id: 'cs_iab2_01025', title: 'Contextual::IAB 2.0::Content Media Format::Interactive' },
    { id: 'cs_iab2_01026', title: 'Contextual::IAB 2.0::Content Media Format::Mixed' },
    { id: 'cs_iab2_01027', title: 'Contextual::IAB 2.0::Content Media Format::Textual' },
    { id: 'cs_iab2_01028', title: 'Contextual::IAB 2.0::Content Media Format::Video' },
    { id: 'cs_iab2_01029', title: 'Contextual::IAB 2.0::Content Media Format::VR/AR' },
    { id: 'cs_iab2_01215', title: 'Contextual::IAB 2.0::Content Source' },
    { id: 'cs_iab2_01218', title: 'Contextual::IAB 2.0::Content Source::Aggregated/Curated' },
    { id: 'cs_iab2_01216', title: 'Contextual::IAB 2.0::Content Source::Professionally Produced' },
    { id: 'cs_iab2_01217', title: 'Contextual::IAB 2.0::Content Source::User Generated' },
    { id: 'cs_iab2_01219', title: 'Contextual::IAB 2.0::Content Source Geo' },
    { id: 'cs_iab2_01480', title: 'Contextual::IAB 2.0::Content Source Geo::City' },
    { id: 'cs_iab2_01220', title: 'Contextual::IAB 2.0::Content Source Geo::Continent' },
    { id: 'cs_iab2_01222', title: 'Contextual::IAB 2.0::Content Source Geo::Continent::Africa' },
    {
      id: 'cs_iab2_01225',
      title: 'Contextual::IAB 2.0::Content Source Geo::Continent::Antarctica',
    },
    { id: 'cs_iab2_01221', title: 'Contextual::IAB 2.0::Content Source Geo::Continent::Asia' },
    { id: 'cs_iab2_01226', title: 'Contextual::IAB 2.0::Content Source Geo::Continent::Europe' },
    {
      id: 'cs_iab2_01223',
      title: 'Contextual::IAB 2.0::Content Source Geo::Continent::North America',
    },
    {
      id: 'cs_iab2_01224',
      title: 'Contextual::IAB 2.0::Content Source Geo::Continent::South America',
    },
    { id: 'cs_iab2_01228', title: 'Contextual::IAB 2.0::Content Source Geo::Country' },
    { id: 'cs_iab2_01229', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Afghanistan' },
    {
      id: 'cs_iab2_01230',
      title: 'Contextual::IAB 2.0::Content Source Geo::Country::Aland Islands',
    },
    { id: 'cs_iab2_01231', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Albania' },
    { id: 'cs_iab2_01232', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Algeria' },
    {
      id: 'cs_iab2_01233',
      title: 'Contextual::IAB 2.0::Content Source Geo::Country::American Samoa',
    },
    { id: 'cs_iab2_01234', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Andorra' },
    { id: 'cs_iab2_01235', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Angola' },
    { id: 'cs_iab2_01236', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Anguilla' },
    { id: 'cs_iab2_01237', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Antarctica' },
    {
      id: 'cs_iab2_01238',
      title: 'Contextual::IAB 2.0::Content Source Geo::Country::Antigua and Barbuda',
    },
    { id: 'cs_iab2_01239', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Argentina' },
    { id: 'cs_iab2_01240', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Armenia' },
    { id: 'cs_iab2_01241', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Aruba' },
    { id: 'cs_iab2_01242', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Australia' },
    { id: 'cs_iab2_01243', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Austria' },
    { id: 'cs_iab2_01244', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Azerbaijan' },
    { id: 'cs_iab2_01245', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Bahrain' },
    { id: 'cs_iab2_01246', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Bangladesh' },
    { id: 'cs_iab2_01247', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Barbados' },
    { id: 'cs_iab2_01248', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Belarus' },
    { id: 'cs_iab2_01249', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Belgium' },
    { id: 'cs_iab2_01250', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Belize' },
    { id: 'cs_iab2_01251', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Benin' },
    { id: 'cs_iab2_01252', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Bermuda' },
    { id: 'cs_iab2_01253', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Bhutan' },
    {
      id: 'cs_iab2_01254',
      title: 'Contextual::IAB 2.0::Content Source Geo::Country::Bolivarian Republic of Venezuela',
    },
    { id: 'cs_iab2_01255', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Bolivia' },
    {
      id: 'cs_iab2_01256',
      title: 'Contextual::IAB 2.0::Content Source Geo::Country::Bonaire, Sint Eustatius and Saba',
    },
    {
      id: 'cs_iab2_01257',
      title: 'Contextual::IAB 2.0::Content Source Geo::Country::Bosnia and Herzegovina',
    },
    { id: 'cs_iab2_01258', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Botswana' },
    {
      id: 'cs_iab2_01259',
      title: 'Contextual::IAB 2.0::Content Source Geo::Country::Bouvet Island',
    },
    { id: 'cs_iab2_01260', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Brazil' },
    {
      id: 'cs_iab2_01261',
      title: 'Contextual::IAB 2.0::Content Source Geo::Country::British Virgin Islands',
    },
    {
      id: 'cs_iab2_01262',
      title: 'Contextual::IAB 2.0::Content Source Geo::Country::Brunei Darussalam',
    },
    { id: 'cs_iab2_01263', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Bulgaria' },
    {
      id: 'cs_iab2_01264',
      title: 'Contextual::IAB 2.0::Content Source Geo::Country::Burkina Faso',
    },
    { id: 'cs_iab2_01265', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Burundi' },
    { id: 'cs_iab2_01266', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Cabo Verde' },
    { id: 'cs_iab2_01267', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Cambodia' },
    { id: 'cs_iab2_01268', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Cameroon' },
    { id: 'cs_iab2_01269', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Canada' },
    { id: 'cs_iab2_01270', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Chad' },
    { id: 'cs_iab2_01271', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Chile' },
    { id: 'cs_iab2_01272', title: 'Contextual::IAB 2.0::Content Source Geo::Country::China' },
    {
      id: 'cs_iab2_01273',
      title: 'Contextual::IAB 2.0::Content Source Geo::Country::Christmas Island',
    },
    { id: 'cs_iab2_01274', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Colombia' },
    { id: 'cs_iab2_01275', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Costa Rica' },
    {
      id: 'cs_iab2_01276',
      title: 'Contextual::IAB 2.0::Content Source Geo::Country::Cote dIvoire',
    },
    { id: 'cs_iab2_01277', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Croatia' },
    { id: 'cs_iab2_01278', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Cuba' },
    { id: 'cs_iab2_01279', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Curacao' },
    { id: 'cs_iab2_01280', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Cyprus' },
    { id: 'cs_iab2_01281', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Czechia' },
    { id: 'cs_iab2_01282', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Denmark' },
    { id: 'cs_iab2_01283', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Djibouti' },
    { id: 'cs_iab2_01284', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Dominica' },
    { id: 'cs_iab2_01285', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Ecuador' },
    { id: 'cs_iab2_01286', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Egypt' },
    { id: 'cs_iab2_01287', title: 'Contextual::IAB 2.0::Content Source Geo::Country::El Salvador' },
    {
      id: 'cs_iab2_01288',
      title: 'Contextual::IAB 2.0::Content Source Geo::Country::Equatorial Guinea',
    },
    { id: 'cs_iab2_01289', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Eritrea' },
    { id: 'cs_iab2_01290', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Estonia' },
    { id: 'cs_iab2_01291', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Ethiopia' },
    {
      id: 'cs_iab2_01292',
      title: 'Contextual::IAB 2.0::Content Source Geo::Country::Federated States of Micronesia',
    },
    { id: 'cs_iab2_01293', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Fiji' },
    { id: 'cs_iab2_01294', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Finland' },
    { id: 'cs_iab2_01295', title: 'Contextual::IAB 2.0::Content Source Geo::Country::France' },
    {
      id: 'cs_iab2_01296',
      title: 'Contextual::IAB 2.0::Content Source Geo::Country::French Guiana',
    },
    {
      id: 'cs_iab2_01297',
      title: 'Contextual::IAB 2.0::Content Source Geo::Country::French Polynesia',
    },
    { id: 'cs_iab2_01298', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Gabon' },
    { id: 'cs_iab2_01299', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Georgia' },
    { id: 'cs_iab2_01300', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Germany' },
    { id: 'cs_iab2_01301', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Ghana' },
    { id: 'cs_iab2_01302', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Gibraltar' },
    { id: 'cs_iab2_01303', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Greece' },
    { id: 'cs_iab2_01304', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Greenland' },
    { id: 'cs_iab2_01305', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Grenada' },
    { id: 'cs_iab2_01306', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Guadeloupe' },
    { id: 'cs_iab2_01307', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Guam' },
    { id: 'cs_iab2_01308', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Guatemala' },
    { id: 'cs_iab2_01309', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Guernsey' },
    { id: 'cs_iab2_01310', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Guinea' },
    {
      id: 'cs_iab2_01311',
      title: 'Contextual::IAB 2.0::Content Source Geo::Country::Guinea-Bissau',
    },
    { id: 'cs_iab2_01312', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Guyana' },
    { id: 'cs_iab2_01313', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Haiti' },
    {
      id: 'cs_iab2_01314',
      title: 'Contextual::IAB 2.0::Content Source Geo::Country::Heard Island and McDonald Islands',
    },
    { id: 'cs_iab2_01315', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Honduras' },
    { id: 'cs_iab2_01316', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Hong Kong' },
    { id: 'cs_iab2_01317', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Hungary' },
    { id: 'cs_iab2_01318', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Iceland' },
    { id: 'cs_iab2_01319', title: 'Contextual::IAB 2.0::Content Source Geo::Country::India' },
    { id: 'cs_iab2_01320', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Indonesia' },
    { id: 'cs_iab2_01321', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Iraq' },
    { id: 'cs_iab2_01322', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Ireland' },
    {
      id: 'cs_iab2_01323',
      title: 'Contextual::IAB 2.0::Content Source Geo::Country::Islamic Republic of Iran',
    },
    { id: 'cs_iab2_01324', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Isle of Man' },
    { id: 'cs_iab2_01325', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Israel' },
    { id: 'cs_iab2_01326', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Italy' },
    { id: 'cs_iab2_01327', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Jamaica' },
    { id: 'cs_iab2_01328', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Japan' },
    { id: 'cs_iab2_01329', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Jersey' },
    { id: 'cs_iab2_01330', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Jordan' },
    { id: 'cs_iab2_01331', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Kazakhstan' },
    { id: 'cs_iab2_01332', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Kenya' },
    { id: 'cs_iab2_01333', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Kiribati' },
    { id: 'cs_iab2_01334', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Kuwait' },
    { id: 'cs_iab2_01335', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Kyrgyzstan' },
    { id: 'cs_iab2_01336', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Latvia' },
    { id: 'cs_iab2_01337', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Lebanon' },
    { id: 'cs_iab2_01338', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Lesotho' },
    { id: 'cs_iab2_01339', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Liberia' },
    { id: 'cs_iab2_01340', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Libya' },
    {
      id: 'cs_iab2_01341',
      title: 'Contextual::IAB 2.0::Content Source Geo::Country::Liechtenstein',
    },
    { id: 'cs_iab2_01342', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Lithuania' },
    { id: 'cs_iab2_01343', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Luxembourg' },
    { id: 'cs_iab2_01344', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Macao' },
    { id: 'cs_iab2_01345', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Madagascar' },
    { id: 'cs_iab2_01346', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Malawi' },
    { id: 'cs_iab2_01347', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Malaysia' },
    { id: 'cs_iab2_01348', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Maldives' },
    { id: 'cs_iab2_01349', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Mali' },
    { id: 'cs_iab2_01350', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Malta' },
    { id: 'cs_iab2_01351', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Martinique' },
    { id: 'cs_iab2_01352', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Mauritania' },
    { id: 'cs_iab2_01353', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Mauritius' },
    { id: 'cs_iab2_01354', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Mayotte' },
    { id: 'cs_iab2_01355', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Mexico' },
    { id: 'cs_iab2_01356', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Monaco' },
    { id: 'cs_iab2_01357', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Mongolia' },
    { id: 'cs_iab2_01358', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Montenegro' },
    { id: 'cs_iab2_01359', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Montserrat' },
    { id: 'cs_iab2_01360', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Morocco' },
    { id: 'cs_iab2_01361', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Mozambique' },
    { id: 'cs_iab2_01362', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Myanmar' },
    { id: 'cs_iab2_01363', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Namibia' },
    { id: 'cs_iab2_01364', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Nauru' },
    { id: 'cs_iab2_01365', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Nepal' },
    {
      id: 'cs_iab2_01366',
      title: 'Contextual::IAB 2.0::Content Source Geo::Country::New Caledonia',
    },
    { id: 'cs_iab2_01367', title: 'Contextual::IAB 2.0::Content Source Geo::Country::New Zealand' },
    { id: 'cs_iab2_01368', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Nicaragua' },
    { id: 'cs_iab2_01369', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Nigeria' },
    { id: 'cs_iab2_01370', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Niue' },
    {
      id: 'cs_iab2_01371',
      title: 'Contextual::IAB 2.0::Content Source Geo::Country::Norfolk Island',
    },
    { id: 'cs_iab2_01372', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Norway' },
    { id: 'cs_iab2_01373', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Oman' },
    { id: 'cs_iab2_01374', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Pakistan' },
    { id: 'cs_iab2_01375', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Palau' },
    { id: 'cs_iab2_01376', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Panama' },
    {
      id: 'cs_iab2_01377',
      title: 'Contextual::IAB 2.0::Content Source Geo::Country::Papua New Guinea',
    },
    { id: 'cs_iab2_01378', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Paraguay' },
    { id: 'cs_iab2_01379', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Peru' },
    { id: 'cs_iab2_01380', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Pitcairn' },
    { id: 'cs_iab2_01381', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Poland' },
    { id: 'cs_iab2_01382', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Portugal' },
    { id: 'cs_iab2_01383', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Puerto Rico' },
    { id: 'cs_iab2_01384', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Qatar' },
    { id: 'cs_iab2_01385', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Reunion' },
    { id: 'cs_iab2_01386', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Romania' },
    { id: 'cs_iab2_01387', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Rwanda' },
    {
      id: 'cs_iab2_01388',
      title: 'Contextual::IAB 2.0::Content Source Geo::Country::Saint Barthelemy',
    },
    {
      id: 'cs_iab2_01389',
      title:
        'Contextual::IAB 2.0::Content Source Geo::Country::Saint Helena, Ascension and Tristan da Cunha',
    },
    {
      id: 'cs_iab2_01390',
      title: 'Contextual::IAB 2.0::Content Source Geo::Country::Saint Kitts and Nevis',
    },
    { id: 'cs_iab2_01391', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Saint Lucia' },
    {
      id: 'cs_iab2_01392',
      title: 'Contextual::IAB 2.0::Content Source Geo::Country::Saint Martin',
    },
    {
      id: 'cs_iab2_01393',
      title: 'Contextual::IAB 2.0::Content Source Geo::Country::Saint Pierre and Miquelon',
    },
    {
      id: 'cs_iab2_01394',
      title: 'Contextual::IAB 2.0::Content Source Geo::Country::Saint Vincent and the Grenadines',
    },
    { id: 'cs_iab2_01395', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Samoa' },
    { id: 'cs_iab2_01396', title: 'Contextual::IAB 2.0::Content Source Geo::Country::San Marino' },
    {
      id: 'cs_iab2_01397',
      title: 'Contextual::IAB 2.0::Content Source Geo::Country::Sao Tome and Principe',
    },
    {
      id: 'cs_iab2_01398',
      title: 'Contextual::IAB 2.0::Content Source Geo::Country::Saudi Arabia',
    },
    { id: 'cs_iab2_01399', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Senegal' },
    { id: 'cs_iab2_01400', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Serbia' },
    { id: 'cs_iab2_01401', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Seychelles' },
    {
      id: 'cs_iab2_01402',
      title: 'Contextual::IAB 2.0::Content Source Geo::Country::Sierra Leone',
    },
    { id: 'cs_iab2_01403', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Singapore' },
    {
      id: 'cs_iab2_01404',
      title: 'Contextual::IAB 2.0::Content Source Geo::Country::Sint Maarten',
    },
    { id: 'cs_iab2_01405', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Slovakia' },
    { id: 'cs_iab2_01406', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Slovenia' },
    {
      id: 'cs_iab2_01407',
      title: 'Contextual::IAB 2.0::Content Source Geo::Country::Solomon Islands',
    },
    { id: 'cs_iab2_01408', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Somalia' },
    {
      id: 'cs_iab2_01409',
      title: 'Contextual::IAB 2.0::Content Source Geo::Country::South Africa',
    },
    {
      id: 'cs_iab2_01410',
      title:
        'Contextual::IAB 2.0::Content Source Geo::Country::South Georgia and the South Sandwich Islands',
    },
    { id: 'cs_iab2_01411', title: 'Contextual::IAB 2.0::Content Source Geo::Country::South Sudan' },
    { id: 'cs_iab2_01412', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Spain' },
    { id: 'cs_iab2_01413', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Sri Lanka' },
    {
      id: 'cs_iab2_01414',
      title: 'Contextual::IAB 2.0::Content Source Geo::Country::State of Palestine',
    },
    { id: 'cs_iab2_01415', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Suriname' },
    {
      id: 'cs_iab2_01416',
      title: 'Contextual::IAB 2.0::Content Source Geo::Country::Svalbard and Jan Mayen',
    },
    { id: 'cs_iab2_01417', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Swaziland' },
    { id: 'cs_iab2_01418', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Sweden' },
    { id: 'cs_iab2_01419', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Switzerland' },
    {
      id: 'cs_iab2_01420',
      title: 'Contextual::IAB 2.0::Content Source Geo::Country::Syrian Arab Republic',
    },
    {
      id: 'cs_iab2_01421',
      title: 'Contextual::IAB 2.0::Content Source Geo::Country::Taiwan (Province of China)',
    },
    { id: 'cs_iab2_01422', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Tajikistan' },
    { id: 'cs_iab2_01423', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Thailand' },
    { id: 'cs_iab2_01424', title: 'Contextual::IAB 2.0::Content Source Geo::Country::The Bahamas' },
    {
      id: 'cs_iab2_01425',
      title: 'Contextual::IAB 2.0::Content Source Geo::Country::The British Indian Ocean Territory',
    },
    {
      id: 'cs_iab2_01426',
      title: 'Contextual::IAB 2.0::Content Source Geo::Country::The Cayman Islands',
    },
    {
      id: 'cs_iab2_01427',
      title: 'Contextual::IAB 2.0::Content Source Geo::Country::The Central African Republic',
    },
    {
      id: 'cs_iab2_01428',
      title: 'Contextual::IAB 2.0::Content Source Geo::Country::The Cocos Islands',
    },
    { id: 'cs_iab2_01429', title: 'Contextual::IAB 2.0::Content Source Geo::Country::The Comoros' },
    { id: 'cs_iab2_01430', title: 'Contextual::IAB 2.0::Content Source Geo::Country::The Congo' },
    {
      id: 'cs_iab2_01431',
      title: 'Contextual::IAB 2.0::Content Source Geo::Country::The Cook Islands',
    },
    {
      id: 'cs_iab2_01432',
      title:
        'Contextual::IAB 2.0::Content Source Geo::Country::The Democratic Peoples Republic of Korea',
    },
    {
      id: 'cs_iab2_01433',
      title:
        'Contextual::IAB 2.0::Content Source Geo::Country::the Democratic Republic of the Congo',
    },
    {
      id: 'cs_iab2_01434',
      title: 'Contextual::IAB 2.0::Content Source Geo::Country::The Dominican Republic',
    },
    {
      id: 'cs_iab2_01435',
      title: 'Contextual::IAB 2.0::Content Source Geo::Country::The Falkland Islands',
    },
    {
      id: 'cs_iab2_01436',
      title: 'Contextual::IAB 2.0::Content Source Geo::Country::The Faroe Islands',
    },
    {
      id: 'cs_iab2_01437',
      title:
        'Contextual::IAB 2.0::Content Source Geo::Country::The former Yugoslav Republic of Macedonia',
    },
    {
      id: 'cs_iab2_01438',
      title: 'Contextual::IAB 2.0::Content Source Geo::Country::The French Southern Territories',
    },
    { id: 'cs_iab2_01439', title: 'Contextual::IAB 2.0::Content Source Geo::Country::The Gambia' },
    {
      id: 'cs_iab2_01440',
      title: 'Contextual::IAB 2.0::Content Source Geo::Country::The Holy See',
    },
    {
      id: 'cs_iab2_01441',
      title:
        'Contextual::IAB 2.0::Content Source Geo::Country::The Lao Peoples Democratic Republic',
    },
    {
      id: 'cs_iab2_01442',
      title: 'Contextual::IAB 2.0::Content Source Geo::Country::The Marshall Islands',
    },
    {
      id: 'cs_iab2_01443',
      title: 'Contextual::IAB 2.0::Content Source Geo::Country::The Netherlands',
    },
    { id: 'cs_iab2_01444', title: 'Contextual::IAB 2.0::Content Source Geo::Country::The Niger' },
    {
      id: 'cs_iab2_01445',
      title: 'Contextual::IAB 2.0::Content Source Geo::Country::The Northern Mariana Islands',
    },
    {
      id: 'cs_iab2_01446',
      title: 'Contextual::IAB 2.0::Content Source Geo::Country::The Philippines',
    },
    {
      id: 'cs_iab2_01447',
      title: 'Contextual::IAB 2.0::Content Source Geo::Country::The Republic of Korea',
    },
    {
      id: 'cs_iab2_01448',
      title: 'Contextual::IAB 2.0::Content Source Geo::Country::The Republic of Moldova',
    },
    {
      id: 'cs_iab2_01449',
      title: 'Contextual::IAB 2.0::Content Source Geo::Country::The Russian Federation',
    },
    { id: 'cs_iab2_01450', title: 'Contextual::IAB 2.0::Content Source Geo::Country::The Sudan' },
    {
      id: 'cs_iab2_01451',
      title: 'Contextual::IAB 2.0::Content Source Geo::Country::The Turks and Caicos Islands',
    },
    {
      id: 'cs_iab2_01452',
      title: 'Contextual::IAB 2.0::Content Source Geo::Country::The United Arab Emirates',
    },
    {
      id: 'cs_iab2_01453',
      title:
        'Contextual::IAB 2.0::Content Source Geo::Country::The United Kingdom of Great Britain and Northern Ireland',
    },
    {
      id: 'cs_iab2_01454',
      title:
        'Contextual::IAB 2.0::Content Source Geo::Country::The United States Minor Outlying Islands',
    },
    {
      id: 'cs_iab2_01455',
      title: 'Contextual::IAB 2.0::Content Source Geo::Country::The United States of America',
    },
    { id: 'cs_iab2_01456', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Timor-Leste' },
    { id: 'cs_iab2_01457', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Togo' },
    { id: 'cs_iab2_01458', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Tokelau' },
    { id: 'cs_iab2_01459', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Tonga' },
    {
      id: 'cs_iab2_01460',
      title: 'Contextual::IAB 2.0::Content Source Geo::Country::Trinidad and Tobago',
    },
    { id: 'cs_iab2_01461', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Tunisia' },
    { id: 'cs_iab2_01462', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Turkey' },
    {
      id: 'cs_iab2_01463',
      title: 'Contextual::IAB 2.0::Content Source Geo::Country::Turkmenistan',
    },
    { id: 'cs_iab2_01464', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Tuvalu' },
    {
      id: 'cs_iab2_01465',
      title: 'Contextual::IAB 2.0::Content Source Geo::Country::U.S. Virgin Islands',
    },
    { id: 'cs_iab2_01466', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Uganda' },
    { id: 'cs_iab2_01467', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Ukraine' },
    {
      id: 'cs_iab2_01468',
      title: 'Contextual::IAB 2.0::Content Source Geo::Country::United Republic of Tanzania',
    },
    { id: 'cs_iab2_01469', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Uruguay' },
    { id: 'cs_iab2_01470', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Uzbekistan' },
    { id: 'cs_iab2_01471', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Vanuatu' },
    { id: 'cs_iab2_01472', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Viet Nam' },
    {
      id: 'cs_iab2_01473',
      title: 'Contextual::IAB 2.0::Content Source Geo::Country::Wallis and Futuna',
    },
    {
      id: 'cs_iab2_01474',
      title: 'Contextual::IAB 2.0::Content Source Geo::Country::Western Sahara',
    },
    { id: 'cs_iab2_01475', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Yemen' },
    { id: 'cs_iab2_01476', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Zambia' },
    { id: 'cs_iab2_01477', title: 'Contextual::IAB 2.0::Content Source Geo::Country::Zimbabwe' },
    { id: 'cs_iab2_01479', title: 'Contextual::IAB 2.0::Content Source Geo::Metro' },
    { id: 'cs_iab2_01478', title: 'Contextual::IAB 2.0::Content Source Geo::Region/State' },
    { id: 'cs_iab2_01010', title: 'Contextual::IAB 2.0::Content Type' },
    { id: 'cs_iab2_01011', title: 'Contextual::IAB 2.0::Content Type::Academic/Research Content' },
    { id: 'cs_iab2_01012', title: 'Contextual::IAB 2.0::Content Type::Chat/IM/Comments' },
    { id: 'cs_iab2_01013', title: 'Contextual::IAB 2.0::Content Type::Educational Content' },
    { id: 'cs_iab2_01014', title: 'Contextual::IAB 2.0::Content Type::Entertainment Content' },
    { id: 'cs_iab2_01015', title: 'Contextual::IAB 2.0::Content Type::Feature' },
    { id: 'cs_iab2_01016', title: 'Contextual::IAB 2.0::Content Type::General Social' },
    { id: 'cs_iab2_01017', title: 'Contextual::IAB 2.0::Content Type::Instructional Content' },
    { id: 'cs_iab2_01018', title: 'Contextual::IAB 2.0::Content Type::Interactive Content' },
    {
      id: 'cs_iab2_01019',
      title:
        'Contextual::IAB 2.0::Content Type::Listings/Classifieds/Product Listings/Search Results',
    },
    { id: 'cs_iab2_01020', title: 'Contextual::IAB 2.0::Content Type::News' },
    { id: 'cs_iab2_01021', title: 'Contextual::IAB 2.0::Content Type::Review' },
    { id: 'cs_iab2_00132', title: 'Contextual::IAB 2.0::Education' },
    { id: 'cs_iab2_00133', title: 'Contextual::IAB 2.0::Education::Adult Education' },
    { id: 'cs_iab2_00137', title: 'Contextual::IAB 2.0::Education::College Education' },
    {
      id: 'cs_iab2_00138',
      title: 'Contextual::IAB 2.0::Education::College Education::College Planning',
    },
    {
      id: 'cs_iab2_00139',
      title: 'Contextual::IAB 2.0::Education::College Education::Postgraduate Education',
    },
    {
      id: 'cs_iab2_00140',
      title:
        'Contextual::IAB 2.0::Education::College Education::Postgraduate Education::Professional School',
    },
    {
      id: 'cs_iab2_00141',
      title: 'Contextual::IAB 2.0::Education::College Education::Undergraduate Education',
    },
    { id: 'cs_iab2_00142', title: 'Contextual::IAB 2.0::Education::Early Childhood Education' },
    { id: 'cs_iab2_00143', title: 'Contextual::IAB 2.0::Education::Educational Assessment' },
    {
      id: 'cs_iab2_00144',
      title: 'Contextual::IAB 2.0::Education::Educational Assessment::Standardized Testing',
    },
    { id: 'cs_iab2_00145', title: 'Contextual::IAB 2.0::Education::Homeschooling' },
    { id: 'cs_iab2_00146', title: 'Contextual::IAB 2.0::Education::Homework and Study' },
    { id: 'cs_iab2_00147', title: 'Contextual::IAB 2.0::Education::Language Learning' },
    { id: 'cs_iab2_00148', title: 'Contextual::IAB 2.0::Education::Online Education' },
    { id: 'cs_iab2_00149', title: 'Contextual::IAB 2.0::Education::Primary Education' },
    { id: 'cs_iab2_00134', title: 'Contextual::IAB 2.0::Education::Private School' },
    { id: 'cs_iab2_00135', title: 'Contextual::IAB 2.0::Education::Secondary Education' },
    { id: 'cs_iab2_00136', title: 'Contextual::IAB 2.0::Education::Special Education' },
    { id: 'cs_iab2_00150', title: 'Contextual::IAB 2.0::Events and Attractions' },
    {
      id: 'cs_iab2_00151',
      title: 'Contextual::IAB 2.0::Events and Attractions::Amusement and Theme Parks',
    },
    { id: 'cs_iab2_00162', title: 'Contextual::IAB 2.0::Events and Attractions::Awards Shows' },
    {
      id: 'cs_iab2_00179',
      title: 'Contextual::IAB 2.0::Events and Attractions::Bars and Restaurants',
    },
    {
      id: 'cs_iab2_00180',
      title: 'Contextual::IAB 2.0::Events and Attractions::Business Expos and Conferences',
    },
    {
      id: 'cs_iab2_00181',
      title: 'Contextual::IAB 2.0::Events and Attractions::Casinos and Gambling',
    },
    {
      id: 'cs_iab2_00182',
      title: 'Contextual::IAB 2.0::Events and Attractions::Cinemas and Events',
    },
    { id: 'cs_iab2_00183', title: 'Contextual::IAB 2.0::Events and Attractions::Comedy Events' },
    {
      id: 'cs_iab2_00184',
      title: 'Contextual::IAB 2.0::Events and Attractions::Concerts and Music Events',
    },
    { id: 'cs_iab2_00185', title: 'Contextual::IAB 2.0::Events and Attractions::Fan Conventions' },
    { id: 'cs_iab2_00152', title: 'Contextual::IAB 2.0::Events and Attractions::Fashion Events' },
    {
      id: 'cs_iab2_00153',
      title: 'Contextual::IAB 2.0::Events and Attractions::Historic Site and Landmark Tours',
    },
    {
      id: 'cs_iab2_00154',
      title: 'Contextual::IAB 2.0::Events and Attractions::Malls and Shopping Centers',
    },
    {
      id: 'cs_iab2_00155',
      title: 'Contextual::IAB 2.0::Events and Attractions::Museums and Galleries',
    },
    { id: 'cs_iab2_00156', title: 'Contextual::IAB 2.0::Events and Attractions::Musicals' },
    {
      id: 'cs_iab2_00157',
      title: 'Contextual::IAB 2.0::Events and Attractions::National and Civic Holidays',
    },
    { id: 'cs_iab2_00158', title: 'Contextual::IAB 2.0::Events and Attractions::Nightclubs' },
    {
      id: 'cs_iab2_00159',
      title: 'Contextual::IAB 2.0::Events and Attractions::Outdoor Activities',
    },
    { id: 'cs_iab2_00160', title: 'Contextual::IAB 2.0::Events and Attractions::Parks and Nature' },
    {
      id: 'cs_iab2_00161',
      title: 'Contextual::IAB 2.0::Events and Attractions::Party Supplies and Decorations',
    },
    {
      id: 'cs_iab2_00163',
      title: 'Contextual::IAB 2.0::Events and Attractions::Personal Celebrations and Life Events',
    },
    {
      id: 'cs_iab2_00164',
      title:
        'Contextual::IAB 2.0::Events and Attractions::Personal Celebrations and Life Events::Anniversary',
    },
    {
      id: 'cs_iab2_00166',
      title:
        'Contextual::IAB 2.0::Events and Attractions::Personal Celebrations and Life Events::Baby Shower',
    },
    {
      id: 'cs_iab2_00167',
      title:
        'Contextual::IAB 2.0::Events and Attractions::Personal Celebrations and Life Events::Bachelor Party',
    },
    {
      id: 'cs_iab2_00168',
      title:
        'Contextual::IAB 2.0::Events and Attractions::Personal Celebrations and Life Events::Bachelorette Party',
    },
    {
      id: 'cs_iab2_00169',
      title:
        'Contextual::IAB 2.0::Events and Attractions::Personal Celebrations and Life Events::Birth',
    },
    {
      id: 'cs_iab2_00170',
      title:
        'Contextual::IAB 2.0::Events and Attractions::Personal Celebrations and Life Events::Birthday',
    },
    {
      id: 'cs_iab2_00171',
      title:
        'Contextual::IAB 2.0::Events and Attractions::Personal Celebrations and Life Events::Funeral',
    },
    {
      id: 'cs_iab2_00172',
      title:
        'Contextual::IAB 2.0::Events and Attractions::Personal Celebrations and Life Events::Graduation',
    },
    {
      id: 'cs_iab2_00173',
      title:
        'Contextual::IAB 2.0::Events and Attractions::Personal Celebrations and Life Events::Prom',
    },
    {
      id: 'cs_iab2_00165',
      title:
        'Contextual::IAB 2.0::Events and Attractions::Personal Celebrations and Life Events::Wedding',
    },
    { id: 'cs_iab2_00174', title: 'Contextual::IAB 2.0::Events and Attractions::Political Event' },
    { id: 'cs_iab2_00175', title: 'Contextual::IAB 2.0::Events and Attractions::Religious Events' },
    { id: 'cs_iab2_00176', title: 'Contextual::IAB 2.0::Events and Attractions::Sporting Events' },
    {
      id: 'cs_iab2_00177',
      title: 'Contextual::IAB 2.0::Events and Attractions::Theater Venues and Events',
    },
    {
      id: 'cs_iab2_00178',
      title: 'Contextual::IAB 2.0::Events and Attractions::Zoos and Aquariums',
    },
    { id: 'cs_iab2_00186', title: 'Contextual::IAB 2.0::Family and Relationships' },
    { id: 'cs_iab2_00187', title: 'Contextual::IAB 2.0::Family and Relationships::Bereavement' },
    { id: 'cs_iab2_00188', title: 'Contextual::IAB 2.0::Family and Relationships::Dating' },
    { id: 'cs_iab2_00189', title: 'Contextual::IAB 2.0::Family and Relationships::Divorce' },
    { id: 'cs_iab2_00190', title: 'Contextual::IAB 2.0::Family and Relationships::Eldercare' },
    {
      id: 'cs_iab2_00191',
      title: 'Contextual::IAB 2.0::Family and Relationships::Marriage and Civil Unions',
    },
    { id: 'cs_iab2_00192', title: 'Contextual::IAB 2.0::Family and Relationships::Parenting' },
    {
      id: 'cs_iab2_00193',
      title: 'Contextual::IAB 2.0::Family and Relationships::Parenting::Adoption and Fostering',
    },
    {
      id: 'cs_iab2_00194',
      title: 'Contextual::IAB 2.0::Family and Relationships::Parenting::Daycare and Pre-School',
    },
    {
      id: 'cs_iab2_00195',
      title: 'Contextual::IAB 2.0::Family and Relationships::Parenting::Internet Safety',
    },
    {
      id: 'cs_iab2_00196',
      title:
        'Contextual::IAB 2.0::Family and Relationships::Parenting::Parenting Babies and Toddlers',
    },
    {
      id: 'cs_iab2_00197',
      title:
        'Contextual::IAB 2.0::Family and Relationships::Parenting::Parenting Children Aged 4-11',
    },
    {
      id: 'cs_iab2_00198',
      title: 'Contextual::IAB 2.0::Family and Relationships::Parenting::Parenting Teens',
    },
    {
      id: 'cs_133077',
      title: 'Contextual::IAB 2.0::Family and Relationships::Parenting::Pregnancy',
    },
    {
      id: 'cs_iab2_00199',
      title: 'Contextual::IAB 2.0::Family and Relationships::Parenting::Special Needs Kids',
    },
    { id: 'cs_iab2_00200', title: 'Contextual::IAB 2.0::Family and Relationships::Single Life' },
    { id: 'cs_iab2_00201', title: 'Contextual::IAB 2.0::Fine Art' },
    { id: 'cs_iab2_00202', title: 'Contextual::IAB 2.0::Fine Art::Costume' },
    { id: 'cs_iab2_00203', title: 'Contextual::IAB 2.0::Fine Art::Dance' },
    { id: 'cs_iab2_00204', title: 'Contextual::IAB 2.0::Fine Art::Design' },
    { id: 'cs_iab2_00205', title: 'Contextual::IAB 2.0::Fine Art::Digital Arts' },
    { id: 'cs_iab2_00206', title: 'Contextual::IAB 2.0::Fine Art::Fine Art Photography' },
    { id: 'cs_iab2_00207', title: 'Contextual::IAB 2.0::Fine Art::Modern Art' },
    { id: 'cs_iab2_00208', title: 'Contextual::IAB 2.0::Fine Art::Opera' },
    { id: 'cs_iab2_00209', title: 'Contextual::IAB 2.0::Fine Art::Theater' },
    { id: 'cs_iab2_00210', title: 'Contextual::IAB 2.0::Food and Drink' },
    { id: 'cs_iab2_00211', title: 'Contextual::IAB 2.0::Food and Drink::Alcoholic Beverages' },
    { id: 'cs_iab2_00215', title: 'Contextual::IAB 2.0::Food and Drink::Barbecues and Grilling' },
    { id: 'cs_iab2_00216', title: 'Contextual::IAB 2.0::Food and Drink::Cooking' },
    { id: 'cs_iab2_00217', title: 'Contextual::IAB 2.0::Food and Drink::Desserts and Baking' },
    { id: 'cs_iab2_00218', title: 'Contextual::IAB 2.0::Food and Drink::Dining Out' },
    { id: 'cs_iab2_00219', title: 'Contextual::IAB 2.0::Food and Drink::Food Allergies' },
    { id: 'cs_iab2_00220', title: 'Contextual::IAB 2.0::Food and Drink::Food Movements' },
    {
      id: 'cs_iab2_00221',
      title: 'Contextual::IAB 2.0::Food and Drink::Healthy Cooking and Eating',
    },
    { id: 'cs_iab2_00222', title: 'Contextual::IAB 2.0::Food and Drink::Non-Alcoholic Beverages' },
    { id: 'cs_iab2_00212', title: 'Contextual::IAB 2.0::Food and Drink::Vegan Diets' },
    { id: 'cs_iab2_00213', title: 'Contextual::IAB 2.0::Food and Drink::Vegetarian Diets' },
    { id: 'cs_iab2_00214', title: 'Contextual::IAB 2.0::Food and Drink::World Cuisines' },
    { id: 'cs_iab2_00223', title: 'Contextual::IAB 2.0::Healthy Living' },
    { id: 'cs_iab2_00224', title: 'Contextual::IAB 2.0::Healthy Living::Childrens Health' },
    { id: 'cs_iab2_00225', title: 'Contextual::IAB 2.0::Healthy Living::Fitness and Exercise' },
    {
      id: 'cs_iab2_00226',
      title: 'Contextual::IAB 2.0::Healthy Living::Fitness and Exercise::Participant Sports',
    },
    {
      id: 'cs_iab2_00227',
      title: 'Contextual::IAB 2.0::Healthy Living::Fitness and Exercise::Running and Jogging',
    },
    { id: 'cs_iab2_00228', title: 'Contextual::IAB 2.0::Healthy Living::Mens Health' },
    { id: 'cs_iab2_00229', title: 'Contextual::IAB 2.0::Healthy Living::Nutrition' },
    { id: 'cs_iab2_00230', title: 'Contextual::IAB 2.0::Healthy Living::Senior Health' },
    { id: 'cs_iab2_00231', title: 'Contextual::IAB 2.0::Healthy Living::Weight Loss' },
    { id: 'cs_iab2_00232', title: 'Contextual::IAB 2.0::Healthy Living::Wellness' },
    {
      id: 'cs_iab2_00233',
      title: 'Contextual::IAB 2.0::Healthy Living::Wellness::Alternative Medicine',
    },
    {
      id: 'cs_iab2_00236',
      title: 'Contextual::IAB 2.0::Healthy Living::Wellness::Physical Therapy',
    },
    {
      id: 'cs_iab2_00237',
      title: 'Contextual::IAB 2.0::Healthy Living::Wellness::Smoking Cessation',
    },
    { id: 'cs_iab2_00238', title: 'Contextual::IAB 2.0::Healthy Living::Womens Health' },
    { id: 'cs_iab2_00239', title: 'Contextual::IAB 2.0::Hobbies and Interests' },
    {
      id: 'cs_iab2_00240',
      title: 'Contextual::IAB 2.0::Hobbies and Interests::Antiquing and Antiques',
    },
    { id: 'cs_iab2_00248', title: 'Contextual::IAB 2.0::Hobbies and Interests::Arts and Crafts' },
    {
      id: 'cs_iab2_00249',
      title: 'Contextual::IAB 2.0::Hobbies and Interests::Arts and Crafts::Beadwork',
    },
    {
      id: 'cs_iab2_00250',
      title: 'Contextual::IAB 2.0::Hobbies and Interests::Arts and Crafts::Candle and Soap Making',
    },
    {
      id: 'cs_iab2_00251',
      title: 'Contextual::IAB 2.0::Hobbies and Interests::Arts and Crafts::Drawing and Sketching',
    },
    {
      id: 'cs_iab2_00252',
      title: 'Contextual::IAB 2.0::Hobbies and Interests::Arts and Crafts::Jewelry Making',
    },
    {
      id: 'cs_iab2_00253',
      title: 'Contextual::IAB 2.0::Hobbies and Interests::Arts and Crafts::Needlework',
    },
    {
      id: 'cs_iab2_00254',
      title: 'Contextual::IAB 2.0::Hobbies and Interests::Arts and Crafts::Painting',
    },
    {
      id: 'cs_iab2_00255',
      title: 'Contextual::IAB 2.0::Hobbies and Interests::Arts and Crafts::Photography',
    },
    {
      id: 'cs_iab2_00256',
      title: 'Contextual::IAB 2.0::Hobbies and Interests::Arts and Crafts::Scrapbooking',
    },
    {
      id: 'cs_iab2_00257',
      title: 'Contextual::IAB 2.0::Hobbies and Interests::Arts and Crafts::Woodworking',
    },
    { id: 'cs_iab2_00258', title: 'Contextual::IAB 2.0::Hobbies and Interests::Beekeeping' },
    { id: 'cs_iab2_00259', title: 'Contextual::IAB 2.0::Hobbies and Interests::Birdwatching' },
    { id: 'cs_iab2_00260', title: 'Contextual::IAB 2.0::Hobbies and Interests::Cigars' },
    { id: 'cs_iab2_00261', title: 'Contextual::IAB 2.0::Hobbies and Interests::Collecting' },
    {
      id: 'cs_iab2_00262',
      title: 'Contextual::IAB 2.0::Hobbies and Interests::Collecting::Comic Books',
    },
    {
      id: 'cs_iab2_00263',
      title: 'Contextual::IAB 2.0::Hobbies and Interests::Collecting::Stamps and Coins',
    },
    {
      id: 'cs_iab2_00264',
      title: 'Contextual::IAB 2.0::Hobbies and Interests::Content Production',
    },
    {
      id: 'cs_iab2_00265',
      title: 'Contextual::IAB 2.0::Hobbies and Interests::Content Production::Audio Production',
    },
    {
      id: 'cs_iab2_00266',
      title: 'Contextual::IAB 2.0::Hobbies and Interests::Content Production::Freelance Writing',
    },
    {
      id: 'cs_iab2_00267',
      title: 'Contextual::IAB 2.0::Hobbies and Interests::Content Production::Screenwriting',
    },
    {
      id: 'cs_iab2_00268',
      title: 'Contextual::IAB 2.0::Hobbies and Interests::Content Production::Video Production',
    },
    { id: 'cs_iab2_00269', title: 'Contextual::IAB 2.0::Hobbies and Interests::Games and Puzzles' },
    {
      id: 'cs_iab2_00270',
      title:
        'Contextual::IAB 2.0::Hobbies and Interests::Games and Puzzles::Board Games and Puzzles',
    },
    {
      id: 'cs_iab2_00271',
      title: 'Contextual::IAB 2.0::Hobbies and Interests::Games and Puzzles::Card Games',
    },
    {
      id: 'cs_iab2_00272',
      title: 'Contextual::IAB 2.0::Hobbies and Interests::Games and Puzzles::Roleplaying Games',
    },
    {
      id: 'cs_iab2_00273',
      title: 'Contextual::IAB 2.0::Hobbies and Interests::Genealogy and Ancestry',
    },
    {
      id: 'cs_iab2_00241',
      title: 'Contextual::IAB 2.0::Hobbies and Interests::Magic and Illusion',
    },
    { id: 'cs_iab2_00242', title: 'Contextual::IAB 2.0::Hobbies and Interests::Model Toys' },
    {
      id: 'cs_iab2_00243',
      title: 'Contextual::IAB 2.0::Hobbies and Interests::Musical Instruments',
    },
    {
      id: 'cs_iab2_00244',
      title: 'Contextual::IAB 2.0::Hobbies and Interests::Paranormal Phenomena',
    },
    { id: 'cs_iab2_00245', title: 'Contextual::IAB 2.0::Hobbies and Interests::Radio Control' },
    {
      id: 'cs_iab2_00246',
      title: 'Contextual::IAB 2.0::Hobbies and Interests::Sci-fi and Fantasy',
    },
    {
      id: 'cs_iab2_00247',
      title: 'Contextual::IAB 2.0::Hobbies and Interests::Workshops and Classes',
    },
    { id: 'cs_iab2_00274', title: 'Contextual::IAB 2.0::Home and Garden' },
    { id: 'cs_iab2_00275', title: 'Contextual::IAB 2.0::Home and Garden::Gardening' },
    { id: 'cs_iab2_00278', title: 'Contextual::IAB 2.0::Home and Garden::Home Appliances' },
    { id: 'cs_iab2_00279', title: 'Contextual::IAB 2.0::Home and Garden::Home Entertaining' },
    { id: 'cs_iab2_00280', title: 'Contextual::IAB 2.0::Home and Garden::Home Improvement' },
    { id: 'cs_iab2_00281', title: 'Contextual::IAB 2.0::Home and Garden::Home Security' },
    {
      id: 'cs_iab2_00282',
      title: 'Contextual::IAB 2.0::Home and Garden::Indoor Environmental Quality',
    },
    { id: 'cs_iab2_00283', title: 'Contextual::IAB 2.0::Home and Garden::Interior Decorating' },
    { id: 'cs_iab2_00284', title: 'Contextual::IAB 2.0::Home and Garden::Landscaping' },
    { id: 'cs_iab2_00285', title: 'Contextual::IAB 2.0::Home and Garden::Outdoor Decorating' },
    {
      id: 'cs_iab2_00276',
      title: 'Contextual::IAB 2.0::Home and Garden::Remodeling and Construction',
    },
    { id: 'cs_iab2_00277', title: 'Contextual::IAB 2.0::Home and Garden::Smart Home' },
    { id: 'cs_iab2_00286', title: 'Contextual::IAB 2.0::Medical Health' },
    { id: 'cs_iab2_00287', title: 'Contextual::IAB 2.0::Medical Health::Diseases and Conditions' },
    {
      id: 'cs_iab2_00288',
      title: 'Contextual::IAB 2.0::Medical Health::Diseases and Conditions::Allergies',
    },
    {
      id: 'cs_iab2_00306',
      title: 'Contextual::IAB 2.0::Medical Health::Diseases and Conditions::Blood Disorders',
    },
    {
      id: 'cs_iab2_00312',
      title:
        'Contextual::IAB 2.0::Medical Health::Diseases and Conditions::Bone and Joint Conditions',
    },
    {
      id: 'cs_iab2_00313',
      title:
        'Contextual::IAB 2.0::Medical Health::Diseases and Conditions::Brain and Nervous System Disorders',
    },
    {
      id: 'cs_iab2_00314',
      title: 'Contextual::IAB 2.0::Medical Health::Diseases and Conditions::Cancer',
    },
    {
      id: 'cs_iab2_00315',
      title: 'Contextual::IAB 2.0::Medical Health::Diseases and Conditions::Cold and Flu',
    },
    {
      id: 'cs_iab2_00316',
      title: 'Contextual::IAB 2.0::Medical Health::Diseases and Conditions::Dental Health',
    },
    {
      id: 'cs_iab2_00317',
      title: 'Contextual::IAB 2.0::Medical Health::Diseases and Conditions::Diabetes',
    },
    {
      id: 'cs_iab2_00318',
      title: 'Contextual::IAB 2.0::Medical Health::Diseases and Conditions::Digestive Disorders',
    },
    {
      id: 'cs_iab2_00289',
      title:
        'Contextual::IAB 2.0::Medical Health::Diseases and Conditions::Ear, Nose and Throat Conditions',
    },
    {
      id: 'cs_iab2_00290',
      title:
        'Contextual::IAB 2.0::Medical Health::Diseases and Conditions::Endocrine and Metabolic Diseases',
    },
    {
      id: 'cs_iab2_00291',
      title:
        'Contextual::IAB 2.0::Medical Health::Diseases and Conditions::Endocrine and Metabolic Diseases::Hormonal Disorders',
    },
    {
      id: 'cs_iab2_00292',
      title:
        'Contextual::IAB 2.0::Medical Health::Diseases and Conditions::Endocrine and Metabolic Diseases::Menopause',
    },
    {
      id: 'cs_iab2_00293',
      title:
        'Contextual::IAB 2.0::Medical Health::Diseases and Conditions::Endocrine and Metabolic Diseases::Thyroid Disorders',
    },
    {
      id: 'cs_iab2_00294',
      title:
        'Contextual::IAB 2.0::Medical Health::Diseases and Conditions::Eye and Vision Conditions',
    },
    {
      id: 'cs_iab2_00295',
      title: 'Contextual::IAB 2.0::Medical Health::Diseases and Conditions::Foot Health',
    },
    {
      id: 'cs_iab2_00296',
      title:
        'Contextual::IAB 2.0::Medical Health::Diseases and Conditions::Heart and Cardiovascular Diseases',
    },
    {
      id: 'cs_iab2_00297',
      title: 'Contextual::IAB 2.0::Medical Health::Diseases and Conditions::Infectious Diseases',
    },
    {
      id: 'cs_iab2_00298',
      title: 'Contextual::IAB 2.0::Medical Health::Diseases and Conditions::Injuries',
    },
    {
      id: 'cs_iab2_00299',
      title: 'Contextual::IAB 2.0::Medical Health::Diseases and Conditions::Injuries::First Aid',
    },
    {
      id: 'cs_iab2_00300',
      title:
        'Contextual::IAB 2.0::Medical Health::Diseases and Conditions::Lung and Respiratory Health',
    },
    {
      id: 'cs_iab2_00301',
      title: 'Contextual::IAB 2.0::Medical Health::Diseases and Conditions::Mental Health',
    },
    {
      id: 'cs_iab2_00302',
      title: 'Contextual::IAB 2.0::Medical Health::Diseases and Conditions::Reproductive Health',
    },
    {
      id: 'cs_iab2_00303',
      title:
        'Contextual::IAB 2.0::Medical Health::Diseases and Conditions::Reproductive Health::Birth Control',
    },
    {
      id: 'cs_iab2_00304',
      title:
        'Contextual::IAB 2.0::Medical Health::Diseases and Conditions::Reproductive Health::Infertility',
    },
    {
      id: 'cs_iab2_00305',
      title:
        'Contextual::IAB 2.0::Medical Health::Diseases and Conditions::Reproductive Health::Pregnancy',
    },
    {
      id: 'cs_iab2_00307',
      title: 'Contextual::IAB 2.0::Medical Health::Diseases and Conditions::Sexual Health',
    },
    {
      id: 'cs_iab2_00308',
      title:
        'Contextual::IAB 2.0::Medical Health::Diseases and Conditions::Sexual Health::Sexual Conditions',
    },
    {
      id: 'cs_iab2_00309',
      title: 'Contextual::IAB 2.0::Medical Health::Diseases and Conditions::Skin and Dermatology',
    },
    {
      id: 'cs_iab2_00310',
      title: 'Contextual::IAB 2.0::Medical Health::Diseases and Conditions::Sleep Disorders',
    },
    {
      id: 'cs_iab2_00311',
      title: 'Contextual::IAB 2.0::Medical Health::Diseases and Conditions::Substance Abuse',
    },
    { id: 'cs_iab2_00319', title: 'Contextual::IAB 2.0::Medical Health::Medical Tests' },
    { id: 'cs_iab2_00320', title: 'Contextual::IAB 2.0::Medical Health::Pharmaceutical Drugs' },
    { id: 'cs_iab2_00321', title: 'Contextual::IAB 2.0::Medical Health::Surgery' },
    { id: 'cs_iab2_00322', title: 'Contextual::IAB 2.0::Medical Health::Vaccines' },
    { id: 'cs_iab2_00324', title: 'Contextual::IAB 2.0::Movies' },
    { id: 'cs_iab2_00325', title: 'Contextual::IAB 2.0::Movies::Action and Adventure Movies' },
    { id: 'cs_iab2_00329', title: 'Contextual::IAB 2.0::Movies::Animation Movies' },
    { id: 'cs_iab2_00330', title: 'Contextual::IAB 2.0::Movies::Comedy Movies' },
    { id: 'cs_iab2_00331', title: 'Contextual::IAB 2.0::Movies::Crime and Mystery Movies' },
    { id: 'cs_iab2_00332', title: 'Contextual::IAB 2.0::Movies::Documentary Movies' },
    { id: 'cs_iab2_00333', title: 'Contextual::IAB 2.0::Movies::Drama Movies' },
    { id: 'cs_iab2_00334', title: 'Contextual::IAB 2.0::Movies::Family and Children Movies' },
    { id: 'cs_iab2_00335', title: 'Contextual::IAB 2.0::Movies::Fantasy Movies' },
    { id: 'cs_iab2_00336', title: 'Contextual::IAB 2.0::Movies::Horror Movies' },
    { id: 'cs_iab2_00326', title: 'Contextual::IAB 2.0::Movies::Romance Movies' },
    { id: 'cs_iab2_00327', title: 'Contextual::IAB 2.0::Movies::Science Fiction Movies' },
    { id: 'cs_133213', title: 'Contextual::IAB 2.0::Movies::Special Interest Movies' },
    { id: 'cs_iab2_00338', title: 'Contextual::IAB 2.0::Music and Audio' },
    {
      id: 'cs_iab2_00339',
      title: 'Contextual::IAB 2.0::Music and Audio::Adult Contemporary Music',
    },
    { id: 'cs_133216', title: 'Contextual::IAB 2.0::Music and Audio::Arts Podcasts' },
    { id: 'cs_iab2_00360', title: 'Contextual::IAB 2.0::Music and Audio::Blues' },
    { id: 'cs_iab2_00372', title: 'Contextual::IAB 2.0::Music and Audio::Business Podcasts' },
    { id: 'cs_iab2_00344', title: 'Contextual::IAB 2.0::Music and Audio::Childrens Music' },
    { id: 'cs_iab2_00345', title: 'Contextual::IAB 2.0::Music and Audio::Classic Hits' },
    { id: 'cs_iab2_00346', title: 'Contextual::IAB 2.0::Music and Audio::Classical Music' },
    { id: 'cs_iab2_00347', title: 'Contextual::IAB 2.0::Music and Audio::College Radio' },
    { id: 'cs_iab2_00348', title: 'Contextual::IAB 2.0::Music and Audio::Comedy Podcasts' },
    { id: 'cs_iab2_00349', title: 'Contextual::IAB 2.0::Music and Audio::Contemporary Hits' },
    { id: 'cs_iab2_00350', title: 'Contextual::IAB 2.0::Music and Audio::Country Music' },
    {
      id: 'cs_iab2_00351',
      title: 'Contextual::IAB 2.0::Music and Audio::Dance and Electronic Music',
    },
    { id: 'cs_133229', title: 'Contextual::IAB 2.0::Music and Audio::Education Podcasts' },
    { id: 'cs_iab2_00353', title: 'Contextual::IAB 2.0::Music and Audio::Folk Music' },
    { id: 'cs_133231', title: 'Contextual::IAB 2.0::Music and Audio::Games and Hobbies Podcasts' },
    { id: 'cs_iab2_00354', title: 'Contextual::IAB 2.0::Music and Audio::Gospel Music' },
    { id: 'cs_133233', title: 'Contextual::IAB 2.0::Music and Audio::Health Podcasts' },
    { id: 'cs_iab2_00355', title: 'Contextual::IAB 2.0::Music and Audio::Hip Hop Music' },
    { id: 'cs_iab2_00356', title: 'Contextual::IAB 2.0::Music and Audio::Inspirational Music' },
    { id: 'cs_iab2_00352', title: 'Contextual::IAB 2.0::Music and Audio::International Music' },
    { id: 'cs_iab2_00357', title: 'Contextual::IAB 2.0::Music and Audio::Jazz' },
    { id: 'cs_133238', title: 'Contextual::IAB 2.0::Music and Audio::Kids and Family Podcasts' },
    { id: 'cs_133239', title: 'Contextual::IAB 2.0::Music and Audio::News and Politics Podcasts' },
    { id: 'cs_iab2_00358', title: 'Contextual::IAB 2.0::Music and Audio::Oldies Music' },
    { id: 'cs_iab2_00376', title: 'Contextual::IAB 2.0::Music and Audio::Public Radio' },
    { id: 'cs_iab2_00359', title: 'Contextual::IAB 2.0::Music and Audio::Reggae' },
    {
      id: 'cs_133243',
      title: 'Contextual::IAB 2.0::Music and Audio::Religion and Spirituality Podcasts',
    },
    { id: 'cs_iab2_00361', title: 'Contextual::IAB 2.0::Music and Audio::Religious Music' },
    { id: 'cs_iab2_00362', title: 'Contextual::IAB 2.0::Music and Audio::Rhythm and Blues' },
    { id: 'cs_iab2_00363', title: 'Contextual::IAB 2.0::Music and Audio::Rock Music' },
    {
      id: 'cs_iab2_00364',
      title: 'Contextual::IAB 2.0::Music and Audio::Rock Music::Album-oriented Rock',
    },
    {
      id: 'cs_iab2_00365',
      title: 'Contextual::IAB 2.0::Music and Audio::Rock Music::Alternative Rock',
    },
    {
      id: 'cs_iab2_00366',
      title: 'Contextual::IAB 2.0::Music and Audio::Rock Music::Classic Rock',
    },
    { id: 'cs_iab2_00367', title: 'Contextual::IAB 2.0::Music and Audio::Rock Music::Hard Rock' },
    {
      id: 'cs_133251',
      title: 'Contextual::IAB 2.0::Music and Audio::Science and Medicine Podcasts',
    },
    {
      id: 'cs_133252',
      title: 'Contextual::IAB 2.0::Music and Audio::Society and Culture Podcasts',
    },
    { id: 'cs_iab2_00369', title: 'Contextual::IAB 2.0::Music and Audio::Soundtracks' },
    {
      id: 'cs_133254',
      title: 'Contextual::IAB 2.0::Music and Audio::Sports and Recreation Podcasts',
    },
    { id: 'cs_iab2_00370', title: 'Contextual::IAB 2.0::Music and Audio::Sports Play-by-Play' },
    { id: 'cs_133256', title: 'Contextual::IAB 2.0::Music and Audio::Sports Talk Radio' },
    { id: 'cs_iab2_00371', title: 'Contextual::IAB 2.0::Music and Audio::Talk Radio' },
    {
      id: 'cs_iab2_00374',
      title: 'Contextual::IAB 2.0::Music and Audio::Talk Radio::All-news radio',
    },
    {
      id: 'cs_iab2_00373',
      title: 'Contextual::IAB 2.0::Music and Audio::Talk Radio::Educational Radio',
    },
    { id: 'cs_iab2_00375', title: 'Contextual::IAB 2.0::Music and Audio::Talk Radio::News/Talk' },
    { id: 'cs_133261', title: 'Contextual::IAB 2.0::Music and Audio::Technology Podcasts' },
    { id: 'cs_133262', title: 'Contextual::IAB 2.0::Music and Audio::TV and Film Podcasts' },
    {
      id: 'cs_133263',
      title: 'Contextual::IAB 2.0::Music and Audio::Urban Adult Contemporary Music',
    },
    {
      id: 'cs_iab2_00377',
      title: 'Contextual::IAB 2.0::Music and Audio::Urban Contemporary Music',
    },
    { id: 'cs_iab2_00379', title: 'Contextual::IAB 2.0::News and Politics' },
    { id: 'cs_iab2_00380', title: 'Contextual::IAB 2.0::News and Politics::Crime' },
    { id: 'cs_iab2_00381', title: 'Contextual::IAB 2.0::News and Politics::Disasters' },
    { id: 'cs_iab2_00382', title: 'Contextual::IAB 2.0::News and Politics::International News' },
    { id: 'cs_iab2_00383', title: 'Contextual::IAB 2.0::News and Politics::Law' },
    { id: 'cs_iab2_00384', title: 'Contextual::IAB 2.0::News and Politics::Local News' },
    { id: 'cs_iab2_00385', title: 'Contextual::IAB 2.0::News and Politics::National News' },
    { id: 'cs_iab2_00386', title: 'Contextual::IAB 2.0::News and Politics::Politics' },
    { id: 'cs_iab2_00387', title: 'Contextual::IAB 2.0::News and Politics::Politics::Elections' },
    {
      id: 'cs_iab2_00388',
      title: 'Contextual::IAB 2.0::News and Politics::Politics::Political Issues',
    },
    {
      id: 'cs_iab2_00389',
      title: 'Contextual::IAB 2.0::News and Politics::Politics::War and Conflicts',
    },
    { id: 'cs_iab2_00390', title: 'Contextual::IAB 2.0::News and Politics::Weather' },
    { id: 'cs_iab2_00391', title: 'Contextual::IAB 2.0::Personal Finance' },
    { id: 'cs_iab2_00392', title: 'Contextual::IAB 2.0::Personal Finance::Consumer Banking' },
    { id: 'cs_iab2_00393', title: 'Contextual::IAB 2.0::Personal Finance::Financial Assistance' },
    {
      id: 'cs_iab2_00394',
      title:
        'Contextual::IAB 2.0::Personal Finance::Financial Assistance::Government Support and Welfare',
    },
    {
      id: 'cs_iab2_00395',
      title: 'Contextual::IAB 2.0::Personal Finance::Financial Assistance::Student Financial Aid',
    },
    { id: 'cs_iab2_00396', title: 'Contextual::IAB 2.0::Personal Finance::Financial Planning' },
    { id: 'cs_iab2_00397', title: 'Contextual::IAB 2.0::Personal Finance::Frugal Living' },
    { id: 'cs_iab2_00398', title: 'Contextual::IAB 2.0::Personal Finance::Insurance' },
    {
      id: 'cs_iab2_00399',
      title: 'Contextual::IAB 2.0::Personal Finance::Insurance::Health Insurance',
    },
    {
      id: 'cs_iab2_00400',
      title: 'Contextual::IAB 2.0::Personal Finance::Insurance::Home Insurance',
    },
    {
      id: 'cs_iab2_00401',
      title: 'Contextual::IAB 2.0::Personal Finance::Insurance::Life Insurance',
    },
    {
      id: 'cs_iab2_00402',
      title: 'Contextual::IAB 2.0::Personal Finance::Insurance::Motor Insurance',
    },
    {
      id: 'cs_iab2_00403',
      title: 'Contextual::IAB 2.0::Personal Finance::Insurance::Pet Insurance',
    },
    {
      id: 'cs_iab2_00404',
      title: 'Contextual::IAB 2.0::Personal Finance::Insurance::Travel Insurance',
    },
    { id: 'cs_iab2_00405', title: 'Contextual::IAB 2.0::Personal Finance::Personal Debt' },
    {
      id: 'cs_iab2_00406',
      title: 'Contextual::IAB 2.0::Personal Finance::Personal Debt::Credit Cards',
    },
    {
      id: 'cs_iab2_00407',
      title: 'Contextual::IAB 2.0::Personal Finance::Personal Debt::Home Financing',
    },
    {
      id: 'cs_iab2_00408',
      title: 'Contextual::IAB 2.0::Personal Finance::Personal Debt::Personal Loans',
    },
    {
      id: 'cs_iab2_00409',
      title: 'Contextual::IAB 2.0::Personal Finance::Personal Debt::Student Loans',
    },
    { id: 'cs_iab2_00410', title: 'Contextual::IAB 2.0::Personal Finance::Personal Investing' },
    {
      id: 'cs_iab2_00411',
      title: 'Contextual::IAB 2.0::Personal Finance::Personal Investing::Hedge Funds',
    },
    {
      id: 'cs_iab2_00412',
      title: 'Contextual::IAB 2.0::Personal Finance::Personal Investing::Mutual Funds',
    },
    {
      id: 'cs_iab2_00413',
      title: 'Contextual::IAB 2.0::Personal Finance::Personal Investing::Options',
    },
    {
      id: 'cs_iab2_00414',
      title: 'Contextual::IAB 2.0::Personal Finance::Personal Investing::Stocks and Bonds',
    },
    { id: 'cs_iab2_00415', title: 'Contextual::IAB 2.0::Personal Finance::Personal Taxes' },
    { id: 'cs_iab2_00416', title: 'Contextual::IAB 2.0::Personal Finance::Retirement Planning' },
    { id: 'cs_iab2_00422', title: 'Contextual::IAB 2.0::Pets' },
    { id: 'cs_iab2_00423', title: 'Contextual::IAB 2.0::Pets::Birds' },
    { id: 'cs_iab2_00424', title: 'Contextual::IAB 2.0::Pets::Cats' },
    { id: 'cs_iab2_00425', title: 'Contextual::IAB 2.0::Pets::Dogs' },
    { id: 'cs_iab2_00426', title: 'Contextual::IAB 2.0::Pets::Fish and Aquariums' },
    { id: 'cs_iab2_00427', title: 'Contextual::IAB 2.0::Pets::Large Animals' },
    { id: 'cs_iab2_00428', title: 'Contextual::IAB 2.0::Pets::Pet Adoptions' },
    { id: 'cs_iab2_00429', title: 'Contextual::IAB 2.0::Pets::Reptiles' },
    { id: 'cs_iab2_00430', title: 'Contextual::IAB 2.0::Pets::Veterinary Medicine' },
    { id: 'cs_iab2_00432', title: 'Contextual::IAB 2.0::Pop Culture' },
    { id: 'cs_iab2_00433', title: 'Contextual::IAB 2.0::Pop Culture::Celebrity Deaths' },
    { id: 'cs_iab2_00434', title: 'Contextual::IAB 2.0::Pop Culture::Celebrity Families' },
    { id: 'cs_iab2_00435', title: 'Contextual::IAB 2.0::Pop Culture::Celebrity Homes' },
    { id: 'cs_iab2_00436', title: 'Contextual::IAB 2.0::Pop Culture::Celebrity Pregnancy' },
    { id: 'cs_iab2_00437', title: 'Contextual::IAB 2.0::Pop Culture::Celebrity Relationships' },
    { id: 'cs_iab2_00438', title: 'Contextual::IAB 2.0::Pop Culture::Celebrity Scandal' },
    { id: 'cs_iab2_00439', title: 'Contextual::IAB 2.0::Pop Culture::Celebrity Style' },
    { id: 'cs_iab2_00441', title: 'Contextual::IAB 2.0::Real Estate' },
    { id: 'cs_iab2_00442', title: 'Contextual::IAB 2.0::Real Estate::Apartments' },
    { id: 'cs_iab2_00445', title: 'Contextual::IAB 2.0::Real Estate::Developmental Sites' },
    { id: 'cs_iab2_00446', title: 'Contextual::IAB 2.0::Real Estate::Hotel Properties' },
    { id: 'cs_iab2_00447', title: 'Contextual::IAB 2.0::Real Estate::Houses' },
    { id: 'cs_iab2_00448', title: 'Contextual::IAB 2.0::Real Estate::Industrial Property' },
    { id: 'cs_iab2_00449', title: 'Contextual::IAB 2.0::Real Estate::Land and Farms' },
    { id: 'cs_iab2_00450', title: 'Contextual::IAB 2.0::Real Estate::Office Property' },
    {
      id: 'cs_iab2_00451',
      title: 'Contextual::IAB 2.0::Real Estate::Real Estate Buying and Selling',
    },
    {
      id: 'cs_iab2_00452',
      title: 'Contextual::IAB 2.0::Real Estate::Real Estate Renting and Leasing',
    },
    { id: 'cs_iab2_00443', title: 'Contextual::IAB 2.0::Real Estate::Retail Property' },
    { id: 'cs_iab2_00444', title: 'Contextual::IAB 2.0::Real Estate::Vacation Properties' },
    { id: 'cs_iab2_00453', title: 'Contextual::IAB 2.0::Religion and Spirituality' },
    { id: 'cs_iab2_00454', title: 'Contextual::IAB 2.0::Religion and Spirituality::Agnosticism' },
    { id: 'cs_iab2_00456', title: 'Contextual::IAB 2.0::Religion and Spirituality::Astrology' },
    { id: 'cs_iab2_00457', title: 'Contextual::IAB 2.0::Religion and Spirituality::Atheism' },
    { id: 'cs_iab2_00458', title: 'Contextual::IAB 2.0::Religion and Spirituality::Buddhism' },
    { id: 'cs_iab2_00459', title: 'Contextual::IAB 2.0::Religion and Spirituality::Christianity' },
    { id: 'cs_iab2_00460', title: 'Contextual::IAB 2.0::Religion and Spirituality::Hinduism' },
    { id: 'cs_iab2_00461', title: 'Contextual::IAB 2.0::Religion and Spirituality::Islam' },
    { id: 'cs_iab2_00462', title: 'Contextual::IAB 2.0::Religion and Spirituality::Judaism' },
    { id: 'cs_iab2_00463', title: 'Contextual::IAB 2.0::Religion and Spirituality::Sikhism' },
    { id: 'cs_iab2_00455', title: 'Contextual::IAB 2.0::Religion and Spirituality::Spirituality' },
    { id: 'cs_iab2_00464', title: 'Contextual::IAB 2.0::Science' },
    { id: 'cs_iab2_00465', title: 'Contextual::IAB 2.0::Science::Biological Sciences' },
    { id: 'cs_iab2_00466', title: 'Contextual::IAB 2.0::Science::Chemistry' },
    { id: 'cs_iab2_00467', title: 'Contextual::IAB 2.0::Science::Environment' },
    { id: 'cs_iab2_00468', title: 'Contextual::IAB 2.0::Science::Genetics' },
    { id: 'cs_iab2_00469', title: 'Contextual::IAB 2.0::Science::Geography' },
    { id: 'cs_iab2_00470', title: 'Contextual::IAB 2.0::Science::Geology' },
    { id: 'cs_iab2_00471', title: 'Contextual::IAB 2.0::Science::Physics' },
    { id: 'cs_iab2_00472', title: 'Contextual::IAB 2.0::Science::Space and Astronomy' },
    { id: 'cs_iab2_00473', title: 'Contextual::IAB 2.0::Shopping' },
    { id: 'cs_iab2_00474', title: 'Contextual::IAB 2.0::Shopping::Couponing' },
    { id: 'cs_iab2_00483', title: 'Contextual::IAB 2.0::Sports' },
    { id: 'cs_iab2_00484', title: 'Contextual::IAB 2.0::Sports::American Football' },
    { id: 'cs_iab2_00507', title: 'Contextual::IAB 2.0::Sports::Australian Rules Football' },
    { id: 'cs_iab2_00518', title: 'Contextual::IAB 2.0::Sports::Auto Racing' },
    { id: 'cs_iab2_00519', title: 'Contextual::IAB 2.0::Sports::Auto Racing::Motorcycle Sports' },
    { id: 'cs_iab2_00534', title: 'Contextual::IAB 2.0::Sports::Badminton' },
    { id: 'cs_iab2_00545', title: 'Contextual::IAB 2.0::Sports::Baseball' },
    { id: 'cs_iab2_00547', title: 'Contextual::IAB 2.0::Sports::Basketball' },
    { id: 'cs_iab2_00548', title: 'Contextual::IAB 2.0::Sports::Beach Volleyball' },
    { id: 'cs_iab2_00549', title: 'Contextual::IAB 2.0::Sports::Bodybuilding' },
    { id: 'cs_iab2_00550', title: 'Contextual::IAB 2.0::Sports::Bowling' },
    { id: 'cs_iab2_00485', title: 'Contextual::IAB 2.0::Sports::Boxing' },
    { id: 'cs_iab2_00486', title: 'Contextual::IAB 2.0::Sports::Cheerleading' },
    { id: 'cs_iab2_00487', title: 'Contextual::IAB 2.0::Sports::College Sports' },
    { id: 'cs_iab2_00491', title: 'Contextual::IAB 2.0::Sports::Cricket' },
    { id: 'cs_iab2_00492', title: 'Contextual::IAB 2.0::Sports::Cycling' },
    { id: 'cs_iab2_00493', title: 'Contextual::IAB 2.0::Sports::Darts' },
    { id: 'cs_iab2_00494', title: 'Contextual::IAB 2.0::Sports::Disabled Sports' },
    { id: 'cs_iab2_00495', title: 'Contextual::IAB 2.0::Sports::Diving' },
    { id: 'cs_iab2_00496', title: 'Contextual::IAB 2.0::Sports::Equine Sports' },
    { id: 'cs_iab2_00497', title: 'Contextual::IAB 2.0::Sports::Equine Sports::Horse Racing' },
    { id: 'cs_iab2_00498', title: 'Contextual::IAB 2.0::Sports::Extreme Sports' },
    {
      id: 'cs_iab2_00499',
      title: 'Contextual::IAB 2.0::Sports::Extreme Sports::Canoeing and Kayaking',
    },
    { id: 'cs_iab2_00500', title: 'Contextual::IAB 2.0::Sports::Extreme Sports::Climbing' },
    { id: 'cs_iab2_00501', title: 'Contextual::IAB 2.0::Sports::Extreme Sports::Paintball' },
    { id: 'cs_iab2_00502', title: 'Contextual::IAB 2.0::Sports::Extreme Sports::Scuba Diving' },
    { id: 'cs_iab2_00503', title: 'Contextual::IAB 2.0::Sports::Extreme Sports::Skateboarding' },
    { id: 'cs_iab2_00504', title: 'Contextual::IAB 2.0::Sports::Extreme Sports::Snowboarding' },
    {
      id: 'cs_iab2_00505',
      title: 'Contextual::IAB 2.0::Sports::Extreme Sports::Surfing and Bodyboarding',
    },
    {
      id: 'cs_iab2_00506',
      title: 'Contextual::IAB 2.0::Sports::Extreme Sports::Waterskiing and Wakeboarding',
    },
    { id: 'cs_iab2_00508', title: 'Contextual::IAB 2.0::Sports::Fantasy Sports' },
    { id: 'cs_iab2_00509', title: 'Contextual::IAB 2.0::Sports::Field Hockey' },
    { id: 'cs_iab2_00510', title: 'Contextual::IAB 2.0::Sports::Figure Skating' },
    { id: 'cs_iab2_00511', title: 'Contextual::IAB 2.0::Sports::Fishing Sports' },
    { id: 'cs_iab2_00512', title: 'Contextual::IAB 2.0::Sports::Golf' },
    { id: 'cs_iab2_00513', title: 'Contextual::IAB 2.0::Sports::Gymnastics' },
    { id: 'cs_iab2_00514', title: 'Contextual::IAB 2.0::Sports::Hunting and Shooting' },
    { id: 'cs_iab2_00515', title: 'Contextual::IAB 2.0::Sports::Ice Hockey' },
    { id: 'cs_iab2_00516', title: 'Contextual::IAB 2.0::Sports::Inline Skating' },
    { id: 'cs_iab2_00517', title: 'Contextual::IAB 2.0::Sports::Lacrosse' },
    { id: 'cs_iab2_00520', title: 'Contextual::IAB 2.0::Sports::Martial Arts' },
    { id: 'cs_iab2_00521', title: 'Contextual::IAB 2.0::Sports::Olympic Sports' },
    {
      id: 'cs_iab2_00522',
      title: 'Contextual::IAB 2.0::Sports::Olympic Sports::Summer Olympic Sports',
    },
    {
      id: 'cs_iab2_00523',
      title: 'Contextual::IAB 2.0::Sports::Olympic Sports::Winter Olympic Sports',
    },
    { id: 'cs_iab2_00524', title: 'Contextual::IAB 2.0::Sports::Poker and Professional Gambling' },
    { id: 'cs_iab2_00525', title: 'Contextual::IAB 2.0::Sports::Rodeo' },
    { id: 'cs_iab2_00526', title: 'Contextual::IAB 2.0::Sports::Rowing' },
    { id: 'cs_iab2_00527', title: 'Contextual::IAB 2.0::Sports::Rugby' },
    { id: 'cs_iab2_00528', title: 'Contextual::IAB 2.0::Sports::Rugby::Rugby League' },
    { id: 'cs_iab2_00529', title: 'Contextual::IAB 2.0::Sports::Rugby::Rugby Union' },
    { id: 'cs_iab2_00530', title: 'Contextual::IAB 2.0::Sports::Sailing' },
    { id: 'cs_iab2_00531', title: 'Contextual::IAB 2.0::Sports::Skiing' },
    { id: 'cs_iab2_00532', title: 'Contextual::IAB 2.0::Sports::Snooker/Pool/Billiards' },
    { id: 'cs_iab2_00533', title: 'Contextual::IAB 2.0::Sports::Soccer' },
    { id: 'cs_iab2_00535', title: 'Contextual::IAB 2.0::Sports::Softball' },
    { id: 'cs_iab2_00536', title: 'Contextual::IAB 2.0::Sports::Squash' },
    { id: 'cs_iab2_00537', title: 'Contextual::IAB 2.0::Sports::Swimming' },
    { id: 'cs_iab2_00538', title: 'Contextual::IAB 2.0::Sports::Table Tennis' },
    { id: 'cs_iab2_00539', title: 'Contextual::IAB 2.0::Sports::Tennis' },
    { id: 'cs_iab2_00540', title: 'Contextual::IAB 2.0::Sports::Track and Field' },
    { id: 'cs_iab2_00541', title: 'Contextual::IAB 2.0::Sports::Volleyball' },
    { id: 'cs_iab2_00542', title: 'Contextual::IAB 2.0::Sports::Walking' },
    { id: 'cs_iab2_00543', title: 'Contextual::IAB 2.0::Sports::Water Polo' },
    { id: 'cs_iab2_00544', title: 'Contextual::IAB 2.0::Sports::Weightlifting' },
    { id: 'cs_iab2_00546', title: 'Contextual::IAB 2.0::Sports::Wrestling' },
    { id: 'cs_iab2_00552', title: 'Contextual::IAB 2.0::Style and Fashion' },
    { id: 'cs_iab2_00553', title: 'Contextual::IAB 2.0::Style and Fashion::Beauty' },
    { id: 'cs_iab2_00554', title: 'Contextual::IAB 2.0::Style and Fashion::Beauty::Hair Care' },
    {
      id: 'cs_iab2_00555',
      title: 'Contextual::IAB 2.0::Style and Fashion::Beauty::Makeup and Accessories',
    },
    { id: 'cs_iab2_00556', title: 'Contextual::IAB 2.0::Style and Fashion::Beauty::Nail Care' },
    {
      id: 'cs_iab2_00557',
      title: 'Contextual::IAB 2.0::Style and Fashion::Beauty::Natural and Organic Beauty',
    },
    {
      id: 'cs_iab2_00558',
      title: 'Contextual::IAB 2.0::Style and Fashion::Beauty::Perfume and Fragrance',
    },
    { id: 'cs_iab2_00559', title: 'Contextual::IAB 2.0::Style and Fashion::Beauty::Skin Care' },
    { id: 'cs_iab2_00574', title: 'Contextual::IAB 2.0::Style and Fashion::Body Art' },
    { id: 'cs_iab2_00575', title: 'Contextual::IAB 2.0::Style and Fashion::Childrens Clothing' },
    { id: 'cs_iab2_00576', title: 'Contextual::IAB 2.0::Style and Fashion::Designer Clothing' },
    { id: 'cs_iab2_00577', title: 'Contextual::IAB 2.0::Style and Fashion::Fashion Trends' },
    { id: 'cs_iab2_00578', title: 'Contextual::IAB 2.0::Style and Fashion::High Fashion' },
    { id: 'cs_iab2_00579', title: 'Contextual::IAB 2.0::Style and Fashion::Mens Fashion' },
    {
      id: 'cs_iab2_00580',
      title: 'Contextual::IAB 2.0::Style and Fashion::Mens Fashion::Mens Accessories',
    },
    {
      id: 'cs_iab2_00581',
      title:
        'Contextual::IAB 2.0::Style and Fashion::Mens Fashion::Mens Accessories::Mens Jewelry and Watches',
    },
    {
      id: 'cs_iab2_00582',
      title: 'Contextual::IAB 2.0::Style and Fashion::Mens Fashion::Mens Clothing',
    },
    {
      id: 'cs_iab2_00583',
      title:
        'Contextual::IAB 2.0::Style and Fashion::Mens Fashion::Mens Clothing::Mens Business Wear',
    },
    {
      id: 'cs_iab2_00584',
      title:
        'Contextual::IAB 2.0::Style and Fashion::Mens Fashion::Mens Clothing::Mens Casual Wear',
    },
    {
      id: 'cs_iab2_00585',
      title:
        'Contextual::IAB 2.0::Style and Fashion::Mens Fashion::Mens Clothing::Mens Formal Wear',
    },
    {
      id: 'cs_iab2_00586',
      title: 'Contextual::IAB 2.0::Style and Fashion::Mens Fashion::Mens Clothing::Mens Outerwear',
    },
    {
      id: 'cs_iab2_00587',
      title: 'Contextual::IAB 2.0::Style and Fashion::Mens Fashion::Mens Clothing::Mens Sportswear',
    },
    {
      id: 'cs_iab2_00588',
      title:
        'Contextual::IAB 2.0::Style and Fashion::Mens Fashion::Mens Clothing::Mens Underwear and Sleepwear',
    },
    {
      id: 'cs_iab2_00589',
      title: 'Contextual::IAB 2.0::Style and Fashion::Mens Fashion::Mens Shoes and Footwear',
    },
    { id: 'cs_iab2_00590', title: 'Contextual::IAB 2.0::Style and Fashion::Personal Care' },
    {
      id: 'cs_iab2_00591',
      title: 'Contextual::IAB 2.0::Style and Fashion::Personal Care::Bath and Shower',
    },
    {
      id: 'cs_iab2_00592',
      title: 'Contextual::IAB 2.0::Style and Fashion::Personal Care::Deodorant and Antiperspirant',
    },
    {
      id: 'cs_iab2_00593',
      title: 'Contextual::IAB 2.0::Style and Fashion::Personal Care::Oral care',
    },
    {
      id: 'cs_iab2_00594',
      title: 'Contextual::IAB 2.0::Style and Fashion::Personal Care::Shaving',
    },
    { id: 'cs_iab2_00595', title: 'Contextual::IAB 2.0::Style and Fashion::Street Style' },
    { id: 'cs_iab2_00560', title: 'Contextual::IAB 2.0::Style and Fashion::Womens Fashion' },
    {
      id: 'cs_iab2_00561',
      title: 'Contextual::IAB 2.0::Style and Fashion::Womens Fashion::Womens Accessories',
    },
    {
      id: 'cs_iab2_00562',
      title:
        'Contextual::IAB 2.0::Style and Fashion::Womens Fashion::Womens Accessories::Womens Glasses',
    },
    {
      id: 'cs_iab2_00563',
      title:
        'Contextual::IAB 2.0::Style and Fashion::Womens Fashion::Womens Accessories::Womens Handbags and Wallets',
    },
    {
      id: 'cs_iab2_00564',
      title:
        'Contextual::IAB 2.0::Style and Fashion::Womens Fashion::Womens Accessories::Womens Hats and Scarves',
    },
    {
      id: 'cs_iab2_00565',
      title:
        'Contextual::IAB 2.0::Style and Fashion::Womens Fashion::Womens Accessories::Womens Jewelry and Watches',
    },
    {
      id: 'cs_iab2_00566',
      title: 'Contextual::IAB 2.0::Style and Fashion::Womens Fashion::Womens Clothing',
    },
    {
      id: 'cs_iab2_00567',
      title:
        'Contextual::IAB 2.0::Style and Fashion::Womens Fashion::Womens Clothing::Womens Business Wear',
    },
    {
      id: 'cs_iab2_00568',
      title:
        'Contextual::IAB 2.0::Style and Fashion::Womens Fashion::Womens Clothing::Womens Casual Wear',
    },
    {
      id: 'cs_iab2_00569',
      title:
        'Contextual::IAB 2.0::Style and Fashion::Womens Fashion::Womens Clothing::Womens Formal Wear',
    },
    {
      id: 'cs_iab2_00570',
      title:
        'Contextual::IAB 2.0::Style and Fashion::Womens Fashion::Womens Clothing::Womens Intimates and Sleepwear',
    },
    {
      id: 'cs_iab2_00571',
      title:
        'Contextual::IAB 2.0::Style and Fashion::Womens Fashion::Womens Clothing::Womens Outerwear',
    },
    {
      id: 'cs_iab2_00572',
      title:
        'Contextual::IAB 2.0::Style and Fashion::Womens Fashion::Womens Clothing::Womens Sportswear',
    },
    {
      id: 'cs_iab2_00573',
      title: 'Contextual::IAB 2.0::Style and Fashion::Womens Fashion::Womens Shoes and Footwear',
    },
    { id: 'cs_iab2_00596', title: 'Contextual::IAB 2.0::Technology and Computing' },
    {
      id: 'cs_iab2_00597',
      title: 'Contextual::IAB 2.0::Technology and Computing::Artificial Intelligence',
    },
    {
      id: 'cs_iab2_00598',
      title: 'Contextual::IAB 2.0::Technology and Computing::Augmented Reality',
    },
    { id: 'cs_iab2_00599', title: 'Contextual::IAB 2.0::Technology and Computing::Computing' },
    {
      id: 'cs_iab2_00600',
      title: 'Contextual::IAB 2.0::Technology and Computing::Computing::Computer Networking',
    },
    {
      id: 'cs_iab2_00601',
      title: 'Contextual::IAB 2.0::Technology and Computing::Computing::Computer Peripherals',
    },
    {
      id: 'cs_iab2_00602',
      title:
        'Contextual::IAB 2.0::Technology and Computing::Computing::Computer Software and Applications',
    },
    {
      id: 'cs_iab2_00603',
      title:
        'Contextual::IAB 2.0::Technology and Computing::Computing::Computer Software and Applications::3-D Graphics',
    },
    {
      id: 'cs_iab2_00608',
      title:
        'Contextual::IAB 2.0::Technology and Computing::Computing::Computer Software and Applications::Antivirus Software',
    },
    {
      id: 'cs_iab2_00609',
      title:
        'Contextual::IAB 2.0::Technology and Computing::Computing::Computer Software and Applications::Browsers',
    },
    {
      id: 'cs_iab2_00610',
      title:
        'Contextual::IAB 2.0::Technology and Computing::Computing::Computer Software and Applications::Computer Animation',
    },
    {
      id: 'cs_iab2_00611',
      title:
        'Contextual::IAB 2.0::Technology and Computing::Computing::Computer Software and Applications::Databases',
    },
    {
      id: 'cs_iab2_00612',
      title:
        'Contextual::IAB 2.0::Technology and Computing::Computing::Computer Software and Applications::Desktop Publishing',
    },
    {
      id: 'cs_iab2_00613',
      title:
        'Contextual::IAB 2.0::Technology and Computing::Computing::Computer Software and Applications::Digital Audio',
    },
    {
      id: 'cs_iab2_00614',
      title:
        'Contextual::IAB 2.0::Technology and Computing::Computing::Computer Software and Applications::Graphics Software',
    },
    {
      id: 'cs_iab2_00615',
      title:
        'Contextual::IAB 2.0::Technology and Computing::Computing::Computer Software and Applications::Operating Systems',
    },
    {
      id: 'cs_iab2_00604',
      title:
        'Contextual::IAB 2.0::Technology and Computing::Computing::Computer Software and Applications::Photo Editing Software',
    },
    {
      id: 'cs_iab2_00605',
      title:
        'Contextual::IAB 2.0::Technology and Computing::Computing::Computer Software and Applications::Shareware and Freeware',
    },
    {
      id: 'cs_iab2_00606',
      title:
        'Contextual::IAB 2.0::Technology and Computing::Computing::Computer Software and Applications::Video Software',
    },
    {
      id: 'cs_iab2_00607',
      title:
        'Contextual::IAB 2.0::Technology and Computing::Computing::Computer Software and Applications::Web Conferencing',
    },
    {
      id: 'cs_iab2_00616',
      title:
        'Contextual::IAB 2.0::Technology and Computing::Computing::Data Storage and Warehousing',
    },
    {
      id: 'cs_iab2_00617',
      title: 'Contextual::IAB 2.0::Technology and Computing::Computing::Desktops',
    },
    {
      id: 'cs_iab2_00618',
      title:
        'Contextual::IAB 2.0::Technology and Computing::Computing::Information and Network Security',
    },
    {
      id: 'cs_iab2_00619',
      title: 'Contextual::IAB 2.0::Technology and Computing::Computing::Internet',
    },
    {
      id: 'cs_iab2_00620',
      title: 'Contextual::IAB 2.0::Technology and Computing::Computing::Internet::Cloud Computing',
    },
    {
      id: 'cs_iab2_00623',
      title: 'Contextual::IAB 2.0::Technology and Computing::Computing::Internet::Email',
    },
    {
      id: 'cs_iab2_00624',
      title:
        'Contextual::IAB 2.0::Technology and Computing::Computing::Internet::Internet for Beginners',
    },
    {
      id: 'cs_iab2_00625',
      title:
        'Contextual::IAB 2.0::Technology and Computing::Computing::Internet::Internet of Things',
    },
    {
      id: 'cs_iab2_00626',
      title:
        'Contextual::IAB 2.0::Technology and Computing::Computing::Internet::IT and Internet Support',
    },
    {
      id: 'cs_iab2_00627',
      title: 'Contextual::IAB 2.0::Technology and Computing::Computing::Internet::Search',
    },
    {
      id: 'cs_iab2_00628',
      title:
        'Contextual::IAB 2.0::Technology and Computing::Computing::Internet::Social Networking',
    },
    {
      id: 'cs_133494',
      title: 'Contextual::IAB 2.0::Technology and Computing::Computing::Internet::Web Conferencing',
    },
    {
      id: 'cs_iab2_00629',
      title:
        'Contextual::IAB 2.0::Technology and Computing::Computing::Internet::Web Design and HTML',
    },
    {
      id: 'cs_iab2_00621',
      title: 'Contextual::IAB 2.0::Technology and Computing::Computing::Internet::Web Development',
    },
    {
      id: 'cs_iab2_00622',
      title: 'Contextual::IAB 2.0::Technology and Computing::Computing::Internet::Web Hosting',
    },
    {
      id: 'cs_iab2_00630',
      title: 'Contextual::IAB 2.0::Technology and Computing::Computing::Laptops',
    },
    {
      id: 'cs_iab2_00631',
      title: 'Contextual::IAB 2.0::Technology and Computing::Computing::Programming Languages',
    },
    {
      id: 'cs_iab2_00632',
      title: 'Contextual::IAB 2.0::Technology and Computing::Consumer Electronics',
    },
    {
      id: 'cs_iab2_00633',
      title:
        'Contextual::IAB 2.0::Technology and Computing::Consumer Electronics::Cameras and Camcorders',
    },
    {
      id: 'cs_iab2_00634',
      title:
        'Contextual::IAB 2.0::Technology and Computing::Consumer Electronics::Home Entertainment Systems',
    },
    {
      id: 'cs_iab2_00635',
      title: 'Contextual::IAB 2.0::Technology and Computing::Consumer Electronics::Smartphones',
    },
    {
      id: 'cs_iab2_00636',
      title:
        'Contextual::IAB 2.0::Technology and Computing::Consumer Electronics::Tablets and E-readers',
    },
    {
      id: 'cs_iab2_00637',
      title:
        'Contextual::IAB 2.0::Technology and Computing::Consumer Electronics::Wearable Technology',
    },
    { id: 'cs_iab2_00638', title: 'Contextual::IAB 2.0::Technology and Computing::Robotics' },
    {
      id: 'cs_iab2_00639',
      title: 'Contextual::IAB 2.0::Technology and Computing::Virtual Reality',
    },
    { id: 'cs_iab2_00640', title: 'Contextual::IAB 2.0::Television' },
    { id: 'cs_iab2_00641', title: 'Contextual::IAB 2.0::Television::Animation TV' },
    { id: 'cs_iab2_00645', title: 'Contextual::IAB 2.0::Television::Childrens TV' },
    { id: 'cs_iab2_00646', title: 'Contextual::IAB 2.0::Television::Comedy TV' },
    { id: 'cs_iab2_00647', title: 'Contextual::IAB 2.0::Television::Drama TV' },
    { id: 'cs_iab2_00648', title: 'Contextual::IAB 2.0::Television::Factual TV' },
    { id: 'cs_iab2_00649', title: 'Contextual::IAB 2.0::Television::Holiday TV' },
    { id: 'cs_iab2_00650', title: 'Contextual::IAB 2.0::Television::Music TV' },
    { id: 'cs_iab2_00651', title: 'Contextual::IAB 2.0::Television::Reality TV' },
    { id: 'cs_iab2_00652', title: 'Contextual::IAB 2.0::Television::Science Fiction TV' },
    { id: 'cs_iab2_00642', title: 'Contextual::IAB 2.0::Television::Soap Opera TV' },
    { id: 'cs_iab2_00643', title: 'Contextual::IAB 2.0::Television::Special Interest TV' },
    { id: 'cs_iab2_00644', title: 'Contextual::IAB 2.0::Television::Sports TV' },
    { id: 'cs_iab2_00653', title: 'Contextual::IAB 2.0::Travel' },
    { id: 'cs_iab2_00654', title: 'Contextual::IAB 2.0::Travel::Travel Accessories' },
    { id: 'cs_iab2_00655', title: 'Contextual::IAB 2.0::Travel::Travel Locations' },
    { id: 'cs_iab2_00656', title: 'Contextual::IAB 2.0::Travel::Travel Locations::Africa Travel' },
    { id: 'cs_iab2_00657', title: 'Contextual::IAB 2.0::Travel::Travel Locations::Asia Travel' },
    {
      id: 'cs_iab2_00658',
      title: 'Contextual::IAB 2.0::Travel::Travel Locations::Australia and Oceania Travel',
    },
    { id: 'cs_iab2_00659', title: 'Contextual::IAB 2.0::Travel::Travel Locations::Europe Travel' },
    {
      id: 'cs_iab2_00660',
      title: 'Contextual::IAB 2.0::Travel::Travel Locations::North America Travel',
    },
    { id: 'cs_iab2_00661', title: 'Contextual::IAB 2.0::Travel::Travel Locations::Polar Travel' },
    {
      id: 'cs_iab2_00662',
      title: 'Contextual::IAB 2.0::Travel::Travel Locations::South America Travel',
    },
    { id: 'cs_iab2_00663', title: 'Contextual::IAB 2.0::Travel::Travel Preparation' },
    { id: 'cs_iab2_00664', title: 'Contextual::IAB 2.0::Travel::Travel Type' },
    { id: 'cs_iab2_00665', title: 'Contextual::IAB 2.0::Travel::Travel Type::Adventure Travel' },
    { id: 'cs_iab2_00672', title: 'Contextual::IAB 2.0::Travel::Travel Type::Air Travel' },
    { id: 'cs_iab2_00673', title: 'Contextual::IAB 2.0::Travel::Travel Type::Beach Travel' },
    { id: 'cs_iab2_00674', title: 'Contextual::IAB 2.0::Travel::Travel Type::Bed and Breakfasts' },
    { id: 'cs_iab2_00675', title: 'Contextual::IAB 2.0::Travel::Travel Type::Budget Travel' },
    { id: 'cs_iab2_00676', title: 'Contextual::IAB 2.0::Travel::Travel Type::Business Travel' },
    { id: 'cs_iab2_00677', title: 'Contextual::IAB 2.0::Travel::Travel Type::Camping' },
    { id: 'cs_iab2_00678', title: 'Contextual::IAB 2.0::Travel::Travel Type::Cruises' },
    { id: 'cs_iab2_00679', title: 'Contextual::IAB 2.0::Travel::Travel Type::Day Trips' },
    { id: 'cs_iab2_00666', title: 'Contextual::IAB 2.0::Travel::Travel Type::Family Travel' },
    {
      id: 'cs_iab2_00667',
      title: 'Contextual::IAB 2.0::Travel::Travel Type::Honeymoons and Getaways',
    },
    { id: 'cs_iab2_00668', title: 'Contextual::IAB 2.0::Travel::Travel Type::Hotels and Motels' },
    { id: 'cs_iab2_00669', title: 'Contextual::IAB 2.0::Travel::Travel Type::Rail Travel' },
    { id: 'cs_iab2_00670', title: 'Contextual::IAB 2.0::Travel::Travel Type::Road Trips' },
    { id: 'cs_iab2_00671', title: 'Contextual::IAB 2.0::Travel::Travel Type::Spas' },
    { id: 'cs_iab2_00680', title: 'Contextual::IAB 2.0::Video Gaming' },
    { id: 'cs_iab2_00681', title: 'Contextual::IAB 2.0::Video Gaming::Console Games' },
    { id: 'cs_iab2_00682', title: 'Contextual::IAB 2.0::Video Gaming::eSports' },
    { id: 'cs_iab2_00683', title: 'Contextual::IAB 2.0::Video Gaming::Mobile Games' },
    { id: 'cs_iab2_00684', title: 'Contextual::IAB 2.0::Video Gaming::PC Games' },
    { id: 'cs_iab2_00685', title: 'Contextual::IAB 2.0::Video Gaming::Video Game Genres' },
    {
      id: 'cs_iab2_00686',
      title: 'Contextual::IAB 2.0::Video Gaming::Video Game Genres::Action Video Games',
    },
    {
      id: 'cs_iab2_00691',
      title: 'Contextual::IAB 2.0::Video Gaming::Video Game Genres::Action-Adventure Video Games',
    },
    {
      id: 'cs_iab2_00692',
      title: 'Contextual::IAB 2.0::Video Gaming::Video Game Genres::Adventure Video Games',
    },
    {
      id: 'cs_iab2_00693',
      title: 'Contextual::IAB 2.0::Video Gaming::Video Game Genres::Casual Games',
    },
    {
      id: 'cs_iab2_00694',
      title: 'Contextual::IAB 2.0::Video Gaming::Video Game Genres::Educational Video Games',
    },
    {
      id: 'cs_iab2_00695',
      title:
        'Contextual::IAB 2.0::Video Gaming::Video Game Genres::Exercise and Fitness Video Games',
    },
    { id: 'cs_iab2_00696', title: 'Contextual::IAB 2.0::Video Gaming::Video Game Genres::MMOs' },
    {
      id: 'cs_iab2_00697',
      title: 'Contextual::IAB 2.0::Video Gaming::Video Game Genres::Music and Party Video Games',
    },
    {
      id: 'cs_iab2_00698',
      title: 'Contextual::IAB 2.0::Video Gaming::Video Game Genres::Puzzle Video Games',
    },
    {
      id: 'cs_iab2_00687',
      title: 'Contextual::IAB 2.0::Video Gaming::Video Game Genres::Role-Playing Video Games',
    },
    {
      id: 'cs_iab2_00688',
      title: 'Contextual::IAB 2.0::Video Gaming::Video Game Genres::Simulation Video Games',
    },
    {
      id: 'cs_iab2_00689',
      title: 'Contextual::IAB 2.0::Video Gaming::Video Game Genres::Sports Video Games',
    },
    {
      id: 'cs_iab2_00690',
      title: 'Contextual::IAB 2.0::Video Gaming::Video Game Genres::Strategy Video Games',
    },
    { id: 'cs_132399', title: 'Contextual::Seasonal Segments' },
    { id: 'cs_132400', title: 'Contextual::Seasonal Segments::Labor Day' },
    { id: 'cs_132401', title: 'Contextual::Seasonal Segments::Black Friday' },
    { id: 'cs_132402', title: 'Contextual::Seasonal Segments::4th of July' },
    { id: 'cs_132403', title: 'Contextual::Seasonal Segments::Cyber Monday' },
    { id: 'cs_132404', title: 'Contextual::Seasonal Segments::Back to School' },
    { id: 'cs_132405', title: 'Contextual::Seasonal Segments::Christmas' },
    { id: 'cs_132406', title: 'Contextual::Seasonal Segments::New Years Eve' },
    { id: 'cs_', title: 'Brand Protection' },
    { id: 'cs_', title: 'Brand Protection::Rating' },
    { id: 'cs_210000', title: 'Brand Protection::Rating::G' },
    { id: 'cs_210001', title: 'Brand Protection::Rating::PG13' },
    { id: 'cs_210002', title: 'Brand Protection::Rating::R' },
    { id: 'cs_', title: 'Brand Protection::Non Standard' },
    { id: 'cs_', title: 'Brand Protection::Non Standard::Safe From' },
    { id: 'cs_240000', title: 'Brand Protection::Non Standard::Safe From::Safe from Pornography' },
    { id: 'cs_240001', title: 'Brand Protection::Non Standard::Safe From::Safe from Gambling' },
    { id: 'cs_240002', title: 'Brand Protection::Non Standard::Safe From::Safe from Offensive' },
    {
      id: 'cs_240003',
      title: 'Brand Protection::Non Standard::Safe From::Safe from Online Illegal',
    },
    { id: 'cs_240004', title: 'Brand Protection::Non Standard::Safe From::Safe from UGC' },
    { id: 'cs_240005', title: 'Brand Protection::Non Standard::Safe From::Safe from Accidents' },
    {
      id: 'cs_240006',
      title: 'Brand Protection::Non Standard::Safe From::Safe from Health Issues',
    },
    { id: 'cs_240007', title: 'Brand Protection::Non Standard::Safe From::Safe from Alcohol' },
    { id: 'cs_240008', title: 'Brand Protection::Non Standard::Safe From::Safe from Drugs' },
    { id: 'cs_240009', title: 'Brand Protection::Non Standard::Safe From::Safe from Tobacco' },
    { id: 'cs_240011', title: 'Brand Protection::Non Standard::Safe From::Safe from Terrorism' },
    { id: 'cs_240012', title: 'Brand Protection::Non Standard::Safe From::Safe from Weapons' },
    { id: 'cs_240013', title: 'Brand Protection::Non Standard::Safe From::Safe from Manga' },
    { id: 'cs_240014', title: 'Brand Protection::Non Standard::Safe From::Safe from NSFW' },
    {
      id: 'cs_240015',
      title: 'Brand Protection::Non Standard::Safe From::Safe from Click Baiting',
    },
    {
      id: 'cs_240016',
      title: 'Brand Protection::Non Standard::Safe From::Safe from Negative News',
    },
    {
      id: 'cs_240017',
      title: 'Brand Protection::Non Standard::Safe From::Safe from Crime Violence',
    },
    { id: 'cs_', title: 'Language' },
    { id: 'cs_300001', title: 'Language::Danish' },
    { id: 'cs_300002', title: 'Language::Dutch' },
    { id: 'cs_300003', title: 'Language::English' },
    { id: 'cs_300004', title: 'Language::Finnish' },
    { id: 'cs_300005', title: 'Language::French' },
    { id: 'cs_300006', title: 'Language::German' },
    { id: 'cs_300007', title: 'Language::Italian' },
    { id: 'cs_300008', title: 'Language::Norwegian' },
    { id: 'cs_300009', title: 'Language::Polish' },
    { id: 'cs_300010', title: 'Language::Portuguese' },
    { id: 'cs_300011', title: 'Language::Russian' },
    { id: 'cs_300012', title: 'Language::Spanish' },
    { id: 'cs_300013', title: 'Language::Swedish' },
    { id: 'cs_300014', title: 'Language::Japanese' },
    { id: 'cs_300015', title: 'Language::Chinese' },
    { id: 'cs_300016', title: 'Language::Arabic' },
    { id: 'cs_300017', title: 'Language::Czech' },
    { id: 'cs_300018', title: 'Language::Korean' },
    { id: 'cs_300019', title: 'Language::Serbian' },
    { id: 'cs_300020', title: 'Language::Croatian' },
  ];

  return generateIRISSegmentList(options, currentValues);
};

export const generateZefrContextualSegmentList = (currentValues: string[]): IMenuList => {
  const options: IIRISOption[] = [
    { id: 'zfr_3', title: 'News & Politics' },
    { id: 'zfr_4', title: 'Gaming' },
    { id: 'zfr_5', title: 'Music' },
    { id: 'zfr_6', title: 'Kids Content' },
    { id: 'zfr_7', title: 'Movies' },
    { id: 'zfr_8', title: 'Television' },
    { id: 'zfr_9', title: 'Sports' },
    { id: 'zfr_10', title: 'Pets' },
    { id: 'zfr_11', title: 'Home Improvement' },
    { id: 'zfr_12', title: 'Food & Drink' },
    { id: 'zfr_13', title: 'Food & Cooking' },
    { id: 'zfr_14', title: 'Fashion' },
    { id: 'zfr_15', title: 'Business' },
    { id: 'zfr_16', title: 'Beverages' },
    { id: 'zfr_17', title: 'Beauty & Personal Care' },
    { id: 'zfr_18', title: 'Automotive' },
    { id: 'zfr_19', title: 'Health & Wellness' },
    { id: 'zfr_20', title: 'Travel & Tourism' },
    { id: 'zfr_21', title: 'Technology' },
    { id: 'zfr_22', title: 'Brand Safety' },
    { id: 'zfr_all', title: 'Reviewed by Zefr' },
  ];

  return generateIRISSegmentList(options, currentValues);
};

export const generateAdvancedContextualContextualSegmentList = (
  currentValues: string[],
): IMenuList => {
  const options: IIRISOption[] = [
    { id: 'sp_62553', title: 'sp_62553-Commercial Vehicles' },
    { id: 'sp_62554', title: 'sp_62554-Trucks' },
    { id: 'sp_62555', title: 'sp_62555-Luxury Cars' },
    { id: 'sp_62556', title: 'sp_62556-Off-Road Vehicles' },
    { id: 'sp_62557', title: 'sp_62557-Electric/Hybrid Vehicles' },
    { id: 'sp_62558', title: 'sp_62558-Motorcycles' },
    { id: 'sp_62559', title: 'sp_62559-Auto News & Reviews' },
    { id: 'sp_62560', title: 'sp_62560-SUVs' },
    { id: 'sp_62561', title: 'sp_62561-Van & Minivans' },
    { id: 'sp_62562', title: 'sp_62562-Vehicle Maintenance' },
    { id: 'sp_62563', title: 'sp_62563-Auto Safety' },
    { id: 'sp_62564', title: 'sp_62564-Certified Pre-Owned Vehicles' },
    { id: 'sp_62565', title: 'sp_62565-Childrens Literature' },
    { id: 'sp_62566', title: 'sp_62566-Literature & Books' },
    { id: 'sp_62567', title: 'sp_62567-Comic Books' },
    { id: 'sp_62568', title: 'sp_62568-Ranching & Livestock' },
    { id: 'sp_62569', title: 'sp_62569-Manufacturing News' },
    { id: 'sp_62570', title: 'sp_62570-Finance News' },
    { id: 'sp_62571', title: 'sp_62571-Business News' },
    { id: 'sp_62572', title: 'sp_62572-Startups & Entrepreneurs' },
    { id: 'sp_62573', title: 'sp_62573-Corporations in the News' },
    { id: 'sp_62574', title: 'sp_62574-Running Small Businesses' },
    { id: 'sp_62575', title: 'sp_62575-Tech Startup News' },
    { id: 'sp_62576', title: 'sp_62576-Leadership & Management' },
    { id: 'sp_62577', title: 'sp_62577-Sales & Account Management' },
    { id: 'sp_62578', title: 'sp_62578-Human Resources' },
    { id: 'sp_62579', title: 'sp_62579-Clean Energy & Sustainability' },
    { id: 'sp_62580', title: 'sp_62580-Bakers & Pastry Chefs' },
    { id: 'sp_62581', title: 'sp_62581-Electricians' },
    { id: 'sp_62582', title: 'sp_62582-Catering' },
    { id: 'sp_62583', title: 'sp_62583-Plumbing' },
    { id: 'sp_62584', title: 'sp_62584-Farming & Food Production' },
    { id: 'sp_62585', title: 'sp_62585-Career Planning' },
    { id: 'sp_62586', title: 'sp_62586-Job Searching' },
    { id: 'sp_62587', title: 'sp_62587-Relocating for Work' },
    { id: 'sp_62588', title: 'sp_62588-K-12 Education' },
    { id: 'sp_62589', title: 'sp_62589-College Degrees' },
    { id: 'sp_62590', title: 'sp_62590-Financial Aid' },
    { id: 'sp_62591', title: 'sp_62591-Scholarships' },
    { id: 'sp_62592', title: 'sp_62592-Online Education' },
    { id: 'sp_62593', title: 'sp_62593-Events, Concerts, Parades & Festivals' },
    { id: 'sp_62594', title: 'sp_62594-Broadway & Theater' },
    { id: 'sp_62595', title: 'sp_62595-Sports Tickets' },
    { id: 'sp_62596', title: 'sp_62596-Pregnancy & Childbirth' },
    { id: 'sp_62597', title: 'sp_62597-Parenting Advice' },
    { id: 'sp_62598', title: 'sp_62598-Family Time' },
    { id: 'sp_62599', title: 'sp_62599-Dating & Marriage' },
    { id: 'sp_62600', title: 'sp_62600-Parents of Children' },
    { id: 'sp_62601', title: 'sp_62601-Parents of Teenagers' },
    { id: 'sp_62602', title: 'sp_62602-Art Exhibitions' },
    { id: 'sp_62603', title: 'sp_62603-Candy' },
    { id: 'sp_62604', title: 'sp_62604-Slow Cooker Recipes' },
    { id: 'sp_62605', title: 'sp_62605-BBQ & Grilling Tips' },
    { id: 'sp_62606', title: 'sp_62606-Health Food' },
    { id: 'sp_62607', title: 'sp_62607-Breweries & Beers' },
    { id: 'sp_62608', title: 'sp_62608-Dessert Recipes' },
    { id: 'sp_62609', title: 'sp_62609-All-Occassion Recipes' },
    { id: 'sp_62610', title: 'sp_62610-Organic Food & Drinks' },
    { id: 'sp_62611', title: 'sp_62611-Wines' },
    { id: 'sp_62612', title: 'sp_62612-Recycling' },
    { id: 'sp_62613', title: 'sp_62613-Green Lifestyles' },
    { id: 'sp_62614', title: 'sp_62614-Diets' },
    { id: 'sp_62615', title: 'sp_62615-Weight Loss' },
    { id: 'sp_62616', title: 'sp_62616-Nutrition' },
    { id: 'sp_62617', title: 'sp_62617-Exercise & Fitness' },
    { id: 'sp_62618', title: 'sp_62618-Alternative Wellness' },
    { id: 'sp_62619', title: 'sp_62619-Parties & Entertaining' },
    { id: 'sp_62620', title: 'sp_62620-Hiking' },
    { id: 'sp_62621', title: 'sp_62621-Photography' },
    { id: 'sp_62622', title: 'sp_62622-Yoga' },
    { id: 'sp_62623', title: 'sp_62623-Hunting' },
    { id: 'sp_62624', title: 'sp_62624-Fishing' },
    { id: 'sp_62625', title: 'sp_62625-Gardening & Landscaping' },
    { id: 'sp_62626', title: 'sp_62626-Home Improvement' },
    { id: 'sp_62627', title: 'sp_62627-Headaches & Migraines' },
    { id: 'sp_62628', title: 'sp_62628-Alzheimers & Brain Health' },
    { id: 'sp_62629', title: 'sp_62629-Mental Health' },
    { id: 'sp_62630', title: 'sp_62630-Eye Health & Vision' },
    { id: 'sp_62631', title: 'sp_62631-Pregnancy' },
    { id: 'sp_62632', title: 'sp_62632-Diabetes' },
    { id: 'sp_62633', title: 'sp_62633-Movies, Films & TV' },
    { id: 'sp_62634', title: 'sp_62634-Movies & Films' },
    { id: 'sp_62635', title: 'sp_62635-Pop Music' },
    { id: 'sp_62636', title: 'sp_62636-Music News' },
    { id: 'sp_62637', title: 'sp_62637-Politics & Elections' },
    { id: 'sp_62638', title: 'sp_62638-National News' },
    { id: 'sp_62639', title: 'sp_62639-Weather' },
    { id: 'sp_62640', title: 'sp_62640-Health Insurances' },
    { id: 'sp_62641', title: 'sp_62641-Student Loans' },
    { id: 'sp_62642', title: 'sp_62642-Credit Cards' },
    { id: 'sp_62643', title: 'sp_62643-Health Insurance, ACA & AHCA' },
    { id: 'sp_62644', title: 'sp_62644-Credit Card Reviews' },
    { id: 'sp_62645', title: 'sp_62645-Mortgages & Loans' },
    { id: 'sp_62646', title: 'sp_62646-Stock Market' },
    { id: 'sp_62647', title: 'sp_62647-Economic News' },
    { id: 'sp_62648', title: 'sp_62648-Personal Finance & Investments' },
    { id: 'sp_62649', title: 'sp_62649-College Finances' },
    { id: 'sp_62650', title: 'sp_62650-Pet Advice' },
    { id: 'sp_62651', title: 'sp_62651-Celebrity Buzz' },
    { id: 'sp_62652', title: 'sp_62652-Renting Apartments' },
    { id: 'sp_62653', title: 'sp_62653-Real Estate' },
    { id: 'sp_62654', title: 'sp_62654-Buying & Selling Homes' },
    { id: 'sp_62655', title: 'sp_62655-House Hunting' },
    { id: 'sp_62656', title: 'sp_62656-Religion & Spirituality' },
    { id: 'sp_62657', title: 'sp_62657-Pollution' },
    { id: 'sp_62658', title: 'sp_62658-Space & Astronomy' },
    { id: 'sp_62659', title: 'sp_62659-DNA & Genetics' },
    { id: 'sp_62660', title: 'sp_62660-Science Theories' },
    { id: 'sp_62661', title: 'sp_62661-Mass Merchandisers' },
    { id: 'sp_62662', title: 'sp_62662-Clothing & Accessories' },
    { id: 'sp_62663', title: 'sp_62663-Grocery Stores' },
    { id: 'sp_62664', title: 'sp_62664-Couponing' },
    { id: 'sp_62665', title: 'sp_62665-Holiday Gift Ideas' },
    { id: 'sp_62666', title: 'sp_62666-Strength Training' },
    { id: 'sp_62667', title: 'sp_62667-Soccer' },
    { id: 'sp_62668', title: 'sp_62668-Skiing' },
    { id: 'sp_62669', title: 'sp_62669-Olympics' },
    { id: 'sp_62670', title: 'sp_62670-Tennis' },
    { id: 'sp_62671', title: 'sp_62671-Golf' },
    { id: 'sp_62672', title: 'sp_62672-Hockey' },
    { id: 'sp_62673', title: 'sp_62673-NASCAR' },
    { id: 'sp_62674', title: 'sp_62674-NBA Basketball' },
    { id: 'sp_62675', title: 'sp_62675-Baseball' },
    { id: 'sp_62676', title: 'sp_62676-NCAA College Basketball' },
    { id: 'sp_62677', title: 'sp_62677-Super Bowl' },
    { id: 'sp_62678', title: 'sp_62678-Fashion Shows & Red Carpet' },
    { id: 'sp_62679', title: 'sp_62679-Hairstyles for Women' },
    { id: 'sp_62680', title: 'sp_62680-Latest Womens Fashions' },
    { id: 'sp_62681', title: 'sp_62681-Personal Care' },
    { id: 'sp_62682', title: 'sp_62682-Makeup' },
    { id: 'sp_62683', title: 'sp_62683-Mens Shaving & Grooming' },
    { id: 'sp_62684', title: 'sp_62684-Skincare' },
    { id: 'sp_62685', title: 'sp_62685-IT Solutions' },
    { id: 'sp_62686', title: 'sp_62686-Cyber Security' },
    { id: 'sp_62687', title: 'sp_62687-Cloud Data' },
    { id: 'sp_62688', title: 'sp_62688-Mobile Service Plans' },
    { id: 'sp_62689', title: 'sp_62689-Laptop Computers' },
    { id: 'sp_62690', title: 'sp_62690-Consumer Tech' },
    { id: 'sp_62691', title: 'sp_62691-Mobile Apps' },
    { id: 'sp_62692', title: 'sp_62692-Cell Phones: iPhones' },
    { id: 'sp_62693', title: 'sp_62693-Cell Phones: Android Phones' },
    { id: 'sp_62694', title: 'sp_62694-Android & iOS' },
    { id: 'sp_62695', title: 'sp_62695-Apple Products' },
    { id: 'sp_62696', title: 'sp_62696-Internet Service' },
    { id: 'sp_62697', title: 'sp_62697-Streaming & Premium TV Shows' },
    { id: 'sp_62698', title: 'sp_62698-Kids TV Shows' },
    { id: 'sp_62699', title: 'sp_62699-Budget Travel' },
    { id: 'sp_62700', title: 'sp_62700-Luxury Travel' },
    { id: 'sp_62701', title: 'sp_62701-Theme Parks & Amusement Parks' },
    { id: 'sp_62702', title: 'sp_62702-Airlines & Flights' },
    { id: 'sp_62703', title: 'sp_62703-Adventure Travel' },
    { id: 'sp_62704', title: 'sp_62704-Best Beaches' },
    { id: 'sp_62705', title: 'sp_62705-Vacation Planning' },
    { id: 'sp_62706', title: 'sp_62706-Video Games/Gaming' },
  ];

  return generateIRISSegmentList(options, currentValues);
};
